/*!
 * b8.js - A Retro Game Library. v0.1.0
 *
 * ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
 * ░░░░       ░░        ░        ░       ░░░     ░░░░░        ░░      ░░░░░
 * ▒▒▒▒  ▒▒▒▒  ▒  ▒▒▒▒▒▒▒  ▒▒▒▒▒▒▒  ▒▒▒▒  ▒  ▒▒▒  ▒▒▒▒▒▒▒▒▒▒  ▒  ▒▒▒▒▒▒▒▒▒▒
 * ▓▓▓▓       ▓▓      ▓▓▓      ▓▓▓       ▓▓▓     ▓▓▓▓▓▓▓▓▓▓▓  ▓▓      ▓▓▓▓▓
 * ████  ████  █  ███████  ███████  ███████  ███  ████  ████  ███████  ████
 * ████       ██        █        █  ████████     ██  ██      ███      █████
 * ████████████████████████████████████████████████████████████████████████
 *
 * b8.js is a retro game library designed to bring
 * the charm and simplicity of classic games to modern
 * web development. A fork of qx82, b8.js retains
 * the original's elegance while enhancing its features
 * for today's developers.
 *
 * ---
 *
 * Website: https://beepmini.com
 * Github: https://github.com/BinaryMoon/beepmini
 * BlueSky: https://bsky.app/profile/binarymoon.bsky.social
 *
 * ---
 *
 * MIT License
 *
 * Copyright (c) 2024 - 2025 BinaryMoon
 *
 * Permission is hereby granted, free of charge, to any
 * person obtaining a copy of this software and associated
 * documentation files (the "Software"), to deal in the
 * Software without restriction, including without limitation
 * the rights to use, copy, modify, merge, publish, distribute,
 * sublicense, and/or sell copies of the Software, and to
 * permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall
 * be included in all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
 * EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES
 * OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
 * IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY
 * CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
 * TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

/**
 * An example project structure.
 *
 * const playGame = {
 *   init: () => { }
 *   update: async ( dt ) => { }
 *   render: () => { }
 * }
 *
 * window.addEventListener( "load", () => {
 *   b8.init(
 *     async () => {
 *       // A function to run after everything loads.
 *       b8.Scene.add( 'game', playGame, 30 );
 *       b8.Scene.set( 'game' );
 *     },
 *     {
 *       // Optional config overrides go here.
 *       NAME: 'My Game',
 *       SCREEN_COLS: 30,
 *       SCREEN_ROWS: 30,
 *     }
 *   );
 * } );
 */

/**
 * Things to note:
 *
 * By default tiles are 12×12 pixels.
 * Coordinates (x, y) are pixels from the top-left.
 * Text grid uses (col, row) in 12×12 tile units.
 * All text widths (maxWidth, widthCols, heightRows) are in columns/rows, not pixels.
 * Time: dt and durations are in seconds. Core.getNow() returns seconds.
 * Fonts: fontName is the name passed to TextRenderer.loadFontAsync.
 * Public API is b8.* and b8.Async.*; arguments are validated.
 * Actors have animations; tiles do not.
 * ECS locations and queries use tile coordinates (col, row).
 * setTile origin: locate(col,row) offsets draw*, and print* by tile origin.
 * Actor ids go from 1 to 20. Animations include idle, move-left, move-right, move-up, move-down.
 * Useful tile ids 0 = blank, 1 = solid, 330 = wall, 206 = water, 268 = fire, 182 = tree, 352 = crate, 256 = gem.
 * Handy color ids: 0 = black, 5=blue, 8=red, 10=yellow, 12=green, 15 = white.
 * A and B buttons are mapped to ButtonA and ButtonB keys (eg keydown('ButtonA')).
 * Prefer the ECS over global variables where possible/ sensible.
 * ECS ids are ints returned from b8.ECS.create.
 */

const b8 = {};


( function( b8 ) {

	// User config passed to b8.init() is merged with this object.
	// You only need to override the values you want to change.
	b8.CONFIG = {
		// Enable debug?
		DEBUG: true,
		// The name of the project.
		NAME: "b8 Project",
		// The version of the project.
		VERSION: "1.0.0-dev",
		// Canvas settings
		CANVAS_SETTINGS: {
			// The ID to assign to the b8 canvas.
			CANVAS_ID: "b8-canvas",
			// If set, these CSS classes will be added to the b8 canvas.
			// This is an array of strings, each of which is a class name (without the "."),
			// for example: [ "foo", "bar", "qux" ]
			CANVAS_CLASSES: [],
			// If null then the canvas will be appended to the body.
			// If this is not null, then this is the element under which to create the rendering canvas.
			// This can be the ID of an HTML element, or an HTMLElement reference.
			CONTAINER: null,
		},
		// Sound effects settings
		SFX: {
			// Key presses whilst using an input dialog.
			TYPING: 'ui/click/003',
			MENU_UP: 'tone/beep/002',
			MENU_DOWN: 'tone/beep/001',
			MENU_SELECT: 'tone/beep/003',
		},
		// The font files.
		// The font files must be PNG files, with the characters in a grid.
		FONT_DEFAULT: "../assets/font-default-thin.png",
		FONT_TILES: "../assets/font-tiles.png",
		FONT_ACTORS: "../assets/font-actors.png",
		FONT_VFX: "../assets/font-vfx.png",
		// The characters in the text font file.
		// These are for the default font(s). If you use a different list you
		// will need to upate the font file to match.
		CHRS: `ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789*+=-<>_#&@%^~$£€¥¢!?:;'"/\\()[]{}.,©®™•…| `,
		// Size of each character tile in pixels.
		// Font image width should be:
		// charsPerRow * CHR_WIDTH
		CHR_WIDTH: 12,
		CHR_HEIGHT: 12,
		// Screen width and height in characters.
		SCREEN_ROWS: 25,
		SCREEN_COLS: 20,
		// EXPERIMENTAL
		// This is an experimental feature and is subject to change at any time.
		// The number of colors to use.
		// 1 = each tile is 2 colors (foreground and background).
		// 2 = each tile could be multiple colours. A background colour, and
		// shades of a foreground colour based upon the greyscale colours used
		// in the tiles.
		// Make sure to change the tiles image as well if you change this.
		SCREEN_COLORS: 1,
		// Disable to turn off CRT effect.
		CRT_ENABLE: true,
		// Enable/ Disable vignette effect.
		// This is a boolean value.
		CRT_VIGNETTE: true,
		// Color palette.
		// Colors count from 0.
		// The first color is the background color.
		// This can be as many colors as you want, but each color requires us to
		// store a scaled copy of the characters image in memory, so more colors
		// = more memory.
		// You can redefine the colors at runtime with b8.redefineColors([]).
		// Games on beepmini.com all use the default palette.
		COLORS: [
			"#0A0C1F", // 0. Very dark blue - almost black. The darkest colour.
			"#263264", // 1. Dark blue
			"#A0ABB6", // 2. Mid grey
			"#B2EFEB", // 3. Light blue
			"#3FB0F1", // 4. Mid blue
			"#3548A3", // 5. Blue
			"#420241", // 6. Dark red/ purple
			"#6A3E49", // 7. Brown
			"#C22D44", // 8. Red
			"#E08355", // 9. Orange
			"#FFC763", // 10. Yellow
			"#A7D171", // 11. Light green
			"#30AB62", // 12. Green
			"#1E7F82", // 13. Dark Green
			"#FF76D7", // 14. Pink
			"#F4F4F4", // 15. White
		],
		// The passkey for the game.
		// This is used when generating passcodes for levels.
		// It should be unique for each game so that passcodes are different for each game.
		// You can generate a passcode for a level with b8.Passcode.getCode( levelId ).
		// The passcode will be a 4-character code.
		PASSKEY: "b8IsAwesome",
		// If this is not null, then we will display a virtual joystick if the user
		// is on a mobile device.
		TOUCH_VJOY: true,
		// Cursor config:
		CURSOR: {
			// Blink interval in milliseconds.
			BLINK_INTERVAL: 400,
		},
		// If set, then special escape sequences can be used when printing (to set colors, etc).
		// These are the sequences that starts and end an escape sequence. See the documentation for
		// b8.print() for more info on escape sequences.
		// If you don't want this, comment out these line, or set them to null.
		PRINT_ESCAPE_START: "{{",
		PRINT_ESCAPE_END: "}}",
		// The first character to use for the border in printBox & menus.
		// The number is the index of the top left corner of a border pattern in the font file.
		// The method will use the 4 corners, and the top horizontal and left vertical sides.
		BORDER_CHAR: 54,
	};

} )( b8 );

( function( b8 ) {

	/**
	 * Initializes the API. This must be called before other functions and must
	 * finish executing before other API functions are called.
	 *
	 * Once the supplied callback is called, you can start using b8 functions.
	 *
	 * The options object uses the same format as b8.CONFIG and allows you to override
	 * any default configuration option. View b8.CONFIG for the list of options,
	 * their default values, and information about how the options are used.
	 *
	 * @param {Function} callback - The callback to call when initialization is done.
	 * @param {Object} [options] - Optional options object.
	 * @returns {void}
	 */
	b8.init = function( callback, options = {} ) {

		b8.Utilities.checkFunction( "callback", callback );
		b8.Utilities.checkObject( "options", options );

		// Combine options with b8.CONFIG using deep merge
		if ( options !== null ) {
			b8.CONFIG = b8.Utilities.deepMerge( b8.CONFIG, options );
		}

		return b8.Core.init( callback );

	}


	/**
	 * Starts the realtime game loop.
	 *
	 * This sets the update and render handlers that will be called automatically
	 * every frame.
	 *
	 * Use this for realtime games. For asynchronous or turn-based games you can
	 * update and render manually instead.
	 *
	 * The update handler receives delta time in seconds.
	 *
	 * @param {Function|null} [renderHandler=null] - Function called every frame to draw the screen.
	 * @param {Function|null} [updateHandler=null] - Function called every frame to update game state. Receives dt in seconds.
	 * @param {number} [fps=30] - The target frames per second.
	 * @returns {void}
	 */
	b8.frame = function( renderHandler = null, updateHandler = null, fps = 30 ) {

		b8.Core.preflight( "b8.frame" );

		if ( renderHandler !== null ) {
			b8.Utilities.checkFunction( "render handler", renderHandler );
		}

		if ( updateHandler !== null ) {
			b8.Utilities.checkFunction( "update handler", updateHandler );
		}

		b8.Utilities.checkNumber( "fps", fps );

		return b8.Core.setFrameHandlers( renderHandler, updateHandler, fps );

	}


	/**
	 * Forces the screen to render right now. Useful for immediate redraw in
	 * animations.
	 *
	 * You only need this if you are doing some kind of animation on your own
	 * and you want to redraw the screen immediately to show the current state.
	 *
	 * Otherwise the screen repaints automatically when waiting for user input.
	 *
	 * @returns {void}
	 */
	b8.render = function() {

		b8.Core.preflight( "b8.render" );

		return b8.Renderer.render();

	}


	/**
	 * Sets the foreground and/or background color.
	 *
	 * If you set the background color to -1, it will be transparent.
	 *
	 * @param {number} fg - The foreground color.
	 * @param {number} [bg=undefined] - The background color (optional).
	 * @returns {void}
	 */
	b8.color = function( fg, bg = undefined ) {

		b8.Core.preflight( "b8.color" );
		b8.Utilities.checkNumber( "fg", fg );

		if ( bg !== undefined ) {
			b8.Utilities.checkNumber( "bg", bg );
		}

		b8.Core.setColor( fg, bg );

	}


	/**
	 * Gets the current foreground color.
	 *
	 * @returns {number} The current foreground color.
	 */
	b8.getFgColor = function() {

		b8.Core.preflight( "getFgColor" );

		return b8.Core.drawState.fgColor;

	}


	/**
	 * Gets the current background color.
	 * -1 means transparent.
	 *
	 * @returns {number} The current background color.
	 */
	b8.getBgColor = function() {

		b8.Core.preflight( "b8.getBgColor" );

		return b8.Core.drawState.bgColor;

	}


	/**
	 * Clears the screen using the specified or current background color.
	 *
	 * @param {number} [bg=undefined] - Optional background color index.
	 * If provided, uses this index to get the color from the config. If not
	 * provided, uses the current background color (drawState.bgColor).
	 * @returns {void}
	 */
	b8.cls = function( bg = undefined ) {

		b8.Core.preflight( "b8.Core.cls" );

		if ( bg !== undefined ) {
			b8.Utilities.checkNumber( "bg", bg );
			b8.Core.cls( bg );
			return;
		}

		b8.Core.cls();

	}


	/**
	 * Places the cursor at the given screen column and row. All drawing and
	 * printing operations will start from here.
	 *
	 * @param {number} col - The column where the cursor is to be placed.
	 * @param {number} [row] - The row where the cursor is to be placed (optional).
	 * @returns {void}
	 */
	b8.locate = function( col, row ) {

		b8.Core.preflight( "b8.locate" );
		b8.Utilities.checkNumber( "col", col );

		if ( row !== undefined ) {
			b8.Utilities.checkNumber( "row", row );
		}

		b8.Core.setCursorLocation( col, row );

	}


	/**
	 * Returns the cursor's current column.
	 *
	 * @returns {number} The cursor's current column.
	 */
	b8.col = function() {

		b8.Core.preflight( "col" );

		return b8.Core.drawState.cursorCol;

	}


	/**
	 * Returns the cursor's current row.
	 *
	 * @returns {number} The cursor's current row.
	 */
	b8.row = function() {

		b8.Core.preflight( "row" );

		return b8.Core.drawState.cursorRow;

	}


	/**
	 * Shows or hides the cursor.
	 *
	 * @param {boolean} visible - If true, show the cursor. If false, hide the
	 * cursor.
	 * @returns {void}
	 */
	b8.cursor = function( visible ) {

		b8.Core.preflight( "cursor" );
		b8.Utilities.checkBoolean( "visible", visible );

		b8.CursorRenderer.setCursorVisible( visible );

	}


	/**
	 * Prints text at the cursor position, using the current foreground and
	 * background colors.
	 *
	 * The text can contain embedded newlines and they will behave as expected:
	 * printing will continue at the next line.
	 *
	 * Fonts use the defined tile size as their dimensions. Each character will
	 * be one tile. By default the tile size is: 12x12 pixels.
	 *
	 * If PRINT_ESCAPE_START and PRINT_ESCAPE_END are defined in CONFIG, then
	 * you can also use escape sequences. For example:
	 * - {{c1}} sets the color to 1
	 * - {{b2}} sets the background to 2 (red)
	 * - {{tfontname}} changes the current font to 'fontname' (temporary)
	 * - {{z}} resets the color and font to their states before printing started
	 *
	 * Note: Font changes using escape sequences are temporary and will be
	 * reset after the print operation is complete.
	 *
	 * See example-printing.html for an example.
	 *
	 * @param {string} text - The text to print.
	 * @param {number} [maxWidth=-1] - The maximum width to wrap text at. -1 for no wrapping.
	 * @returns {void}
	 */
	b8.print = function( text, maxWidth = -1, fontName = null ) {

		b8.Core.preflight( "b8.text" );

		b8.Utilities.checkString( "text", text );
		b8.Utilities.checkNumber( "maxWidth", maxWidth );

		let font = fontName;
		if ( null !== font ) {
			b8.Utilities.checkString( "fontName", fontName );
			font = b8.TextRenderer.getFontByName( fontName );
		}

		b8.TextRenderer.print( text, font, maxWidth );

	}


	/**
	 * Prints text centered horizontally in a field of the given width.
	 *
	 * If the text is bigger than the width, it will wrap.
	 *
	 * @param {string} text - The text to print.
	 * @param {number} width - The width of the space to place the text in, measured in Columns. Defaults to the screen width.
	 * @returns {void}
	 */
	b8.printCentered = function( text, width = b8.CONFIG.SCREEN_COLS, fontName = null ) {

		b8.Core.preflight( "b8.printCentered" );

		b8.Utilities.checkString( "text", text );
		b8.Utilities.checkNumber( "width", width );

		let font = fontName;
		if ( null !== font ) {
			b8.Utilities.checkString( "fontName", fontName );
			font = b8.TextRenderer.getFontByName( fontName );
		}

		b8.TextRenderer.printCentered( text, width, font );

	}


	/**
	 * Prints text aligned to the right in a space of the given width.
	 * If the text is bigger than the width, it will wrap.
	 *
	 * @param {string} text - The text to print.
	 * @param {number} width - The width of the space to place the text in, measured in Columns. Defaults to the screen width.
	 * @param {string} [fontName=null] - The font ID to use.
	 * @returns {void}
	 */
	b8.printRight = function( text, width = b8.CONFIG.SCREEN_COLS, fontName = null ) {

		b8.Core.preflight( "b8.printRight" );

		b8.Utilities.checkString( "text", text );
		b8.Utilities.checkNumber( "width", width );

		let font = fontName;
		if ( null !== font ) {
			b8.Utilities.checkString( "fontName", fontName );
			font = b8.TextRenderer.getFontByName( fontName );
		}

		b8.TextRenderer.printRight( text, width, font );

	}


	/**
	 * Draws text at an arbitrary pixel position on the screen, not following
	 * the "row and column" system.
	 *
	 * @param {number} x - The X coordinate of the top-left of the text.
	 * @param {number} y - The Y coordinate of the top-left of the text.
	 * @param {string} text - The text to print.
	 * @param {string} [fontName=null] - The font ID to use.
	 * @returns {void}
	 */
	b8.drawText = function( x, y, text, fontName = null ) {

		b8.Core.preflight( "b8.drawText" );
		b8.Utilities.checkNumber( "x", x );
		b8.Utilities.checkNumber( "y", y );
		b8.Utilities.checkString( "text", text );

		if ( fontName ) {
			b8.Utilities.checkString( "fontName", fontName );
		}

		b8.TextRenderer.drawText( x, y, text, fontName );

	}


	/**
	 * Measures the size of the given text without printing it.
	 *
	 * @param {string} text - The text to measure.
	 * @returns {Object} An object with {cols, rows} indicating the dimensions.
	 */
	b8.measure = function( text ) {

		b8.Core.preflight( "measure" );
		b8.Utilities.checkString( "text", text );

		return b8.TextRenderer.measure( text );

	}


	/**
	 * Prints a character at the current cursor position, advancing the cursor
	 * position.
	 *
	 * @param {number|string} charCode - The character to print, as an integer
	 * (ASCII code) or a one-character string.
	 * @param {number} [numTimes=1] - How many times to print the character.
	 * @param {string} fontName - The font id for the font to draw with.
	 * @returns {void}
	 */
	b8.printChar = function( charCode, numTimes = 1, fontName = null ) {

		b8.Core.preflight( "b8.printChar" );

		charCode = b8.convChar( charCode );
		b8.Utilities.checkInt( "charCode", charCode );
		b8.Utilities.checkInt( "numTimes", numTimes );

		if ( numTimes < 0 ) {
			b8.Utilities.fatal( "[b8.printChar] numTimes must be a positive integer" );
		}

		// Nothing to print.
		if ( 0 === numTimes ) {
			return;
		}

		let font = fontName;
		if ( null !== font ) {
			b8.Utilities.checkString( "fontName", fontName );
			font = b8.TextRenderer.getFontByName( fontName );
		}

		b8.TextRenderer.printChar( charCode, numTimes, font );

	}


	/**
	 * Prints a rectangle of the given size with the given character, starting
	 * at the current cursor position.
	 *
	 * @param {number} widthCols - Width of the rectangle in screen columns.
	 * @param {number} heightRows - Height of the rectangle in screen rows.
	 * @param {number|string} [charCode=8] - The character to print.
	 * @returns {void}
	 */
	b8.printRect = function( widthCols, heightRows, charCode = 8 ) {

		b8.Core.preflight( "b8.printRect" );
		charCode = b8.convChar( charCode );

		b8.Utilities.checkNumber( "widthCols", widthCols );
		b8.Utilities.checkNumber( "heightRows", heightRows );
		b8.Utilities.checkNumber( "charCode", charCode );

		b8.TextRenderer.printRect( widthCols, heightRows, charCode );

	}


	/**
	 * Prints a box with a border of the given size starting at the cursor
	 * position, using border-drawing characters.
	 *
	 * @param {number} widthCols - Width of the box in screen columns, including
	 * the border.
	 * @param {number} heightRows - Height of the box in screen rows, including
	 * the border.
	 * @param {boolean} [fill=true] - If true, fill the interior with spaces.
	 * @param {number} [borderChar=54] - The first border-drawing character to
	 * use.
	 * @returns {void}
	 */
	b8.printBox = function( widthCols, heightRows, fill = true, borderChar = b8.CONFIG.BORDER_CHAR ) {

		b8.Core.preflight( "b8.printBox" );
		borderChar = b8.convChar( borderChar );

		b8.Utilities.checkNumber( "widthCols", widthCols );
		b8.Utilities.checkNumber( "heightRows", heightRows );
		b8.Utilities.checkBoolean( "fill", fill );
		b8.Utilities.checkNumber( "borderChar", borderChar );

		b8.TextRenderer.printBox( widthCols, heightRows, fill, borderChar );

	}


	/**
	 * Draws an image (previously loaded with b8.loadImage).
	 *
	 * @param {number} x - The X coordinate of the top-left of the image.
	 * @param {number} y - The Y coordinate of the top-left of the image.
	 * @param {HTMLImageElement} image - The image to draw.
	 * @returns {void}
	 */
	b8.drawImage = function( x, y, image ) {

		b8.Utilities.checkInstanceOf( "image", image, HTMLImageElement );
		b8.Utilities.checkNumber( "x", x );
		b8.Utilities.checkNumber( "y", y );

		b8.Core.drawImage( image, x, y );

	}


	/**
	 * Draws a rectangular part of an image (previously loaded with
	 * b8.loadImage).
	 *
	 * @param {number} x - The X coordinate of the top-left of the image.
	 * @param {number} y - The Y coordinate of the top-left of the image.
	 * @param {HTMLImageElement} image - The image to draw.
	 * @param {number} srcX - The X coordinate of the top-left of the rectangle
	 * to be drawn.
	 * @param {number} srcY - The Y coordinate of the top-left of the rectangle
	 * to be drawn.
	 * @param {number} width - The width in pixels of the rectangle to be drawn.
	 * @param {number} height - The height in pixels of the rectangle to be
	 * drawn.
	 * @returns {void}
	 */
	b8.drawImageRect = function( x, y, image, srcX, srcY, width, height ) {

		b8.Utilities.checkInstanceOf( "image", image, HTMLImageElement );
		b8.Utilities.checkNumber( "x", x );
		b8.Utilities.checkNumber( "y", y );
		b8.Utilities.checkNumber( "srcX", srcX );
		b8.Utilities.checkNumber( "srcY", srcY );
		b8.Utilities.checkNumber( "width", width );
		b8.Utilities.checkNumber( "height", height );

		b8.Core.drawImage( image, x, y, srcX, srcY, width, height );

	}


	/**
	 * Draws a rectangle (border only) using the current foreground color.
	 *
	 * @param {number} x - The X coordinate of the top-left corner.
	 * @param {number} y - The Y coordinate of the top-left corner.
	 * @param {number} width - The width of the rectangle in pixels.
	 * @param {number} height - The height of the rectangle in pixels.
	 * @returns {void}
	 */
	b8.drawRect = function( x, y, width, height, lineWidth = 1 ) {

		b8.Utilities.checkNumber( "x", x );
		b8.Utilities.checkNumber( "y", y );
		b8.Utilities.checkNumber( "width", width );
		b8.Utilities.checkNumber( "height", height );
		b8.Utilities.checkNumber( "lineWidth", lineWidth );

		b8.Core.drawRect( x, y, width, height, lineWidth );

	}


	/**
	 * Draws a filled rectangle using the current foreground color.
	 *
	 * @param {number} x - The X coordinate of the top-left corner.
	 * @param {number} y - The Y coordinate of the top-left corner.
	 * @param {number} width - The width of the rectangle in pixels.
	 * @param {number} height - The height of the rectangle in pixels.
	 * @returns {void}
	 */
	b8.fillRect = function( x, y, width, height ) {

		b8.Utilities.checkNumber( "x", x );
		b8.Utilities.checkNumber( "y", y );
		b8.Utilities.checkNumber( "width", width );
		b8.Utilities.checkNumber( "height", height );

		b8.Core.fillRect( x, y, width, height );

	}


	/**
	 * Plays a sound (previously loaded with b8.loadSound).
	 *
	 * @param {HTMLAudioElement} sfx - The sound to play.
	 * @param {number} [volume=1] - The volume to play the sound at.
	 * @param {boolean} [loop=false] - If true, play the sound in a loop.
	 * @returns {void}
	 */
	b8.playSound = function( sfx, volume = 1, loop = false ) {

		b8.Utilities.checkInstanceOf( "sfx", sfx, HTMLAudioElement );

		sfx.currentTime = 0;
		sfx.volume = volume;
		sfx.loop = loop;

		sfx.play();

	}


	/**
	 * Draws a sprite, optionally rotated clockwise around its centre.
	 * Coordinates locate its top-left corner before rotation. Rotation disables
	 * image smoothing, though arbitrary angles may still have antialiased edges.
	 *
	 * @param {number|string} ch - The character code of the sprite.
	 * @param {number} x - The X position at which to draw.
	 * @param {number} y - The Y position at which to draw.
	 * @param {number} [degrees=0] - Clockwise rotation in degrees.
	 * @returns {void}
	 */
	b8.spr = function( ch, x, y, degrees = 0 ) {

		ch = b8.convChar( ch );

		b8.Utilities.checkNumber( "ch", ch );
		b8.Utilities.checkNumber( "x", x );
		b8.Utilities.checkNumber( "y", y );

		b8.Utilities.checkNumber( "degrees", degrees );

		// The renderer keeps zero-degree drawing on the normal sprite path.
		b8.TextRenderer.sprRotated( ch, x, y, degrees );

	}


	/**
	 * Draws an animated actor at the current cursor position.
	 *
	 * Actors use named animations such as:
	 * - idle
	 * - move-left
	 * - move-right
	 * - move-up
	 * - move-down
	 *
	 * Unlike tiles, actors support animation playback.
	 *
	 * @param {number|string} ch - The actor character code or character.
	 * @param {string} animation - The animation name to draw.
	 * @param {number} [offsetCol=0] - Horizontal offset as a fraction of the tile width.
	 * @param {number} [offsetRow=0] - Vertical offset as a fraction of the tile height.
	 * @returns {void}
	 */
	b8.drawActor = function( ch, animation, offsetCol = 0, offsetRow = 0 ) {

		ch = b8.convChar( ch );

		b8.Utilities.checkInt( "ch", ch );
		b8.Utilities.checkString( "animation", animation );

		b8.Actors.draw( ch, animation, offsetCol, offsetRow );

	}


	/**
	 * Draws a sprite on the screen.
	 *
	 * @param {number|string} ch - The character code of the sprite.
	 * @param {string} animation - The animation to play.
	 * @param {number} x - The X position at which to draw.
	 * @param {number} y - The Y position at which to draw.
	 * @param {number|null} startTime - The start time of the animation in milliseconds.
	 * @returns {boolean} True if the sprite was drawn, otherwise false.
	 */
	b8.sprActor = function( ch, animation, x, y, startTime = null ) {

		ch = b8.convChar( ch );

		b8.Utilities.checkInt( "ch", ch );
		b8.Utilities.checkString( "animation", animation );
		b8.Utilities.checkNumber( "x", x );
		b8.Utilities.checkNumber( "y", y );
		if ( startTime !== null ) b8.Utilities.checkNumber( "startTime", startTime );

		return b8.Actors.spr( ch, animation, x, y, startTime );

	}


	/**
	 * Checks if the given key is currently pressed or not.
	 *
	 * @param {string} keyName - The name of the key.
	 * @returns {boolean} True if the key is pressed, otherwise false.
	 */
	b8.key = function( keyName ) {

		b8.Core.preflight( "b8.key" );

		b8.Utilities.checkString( "keyName", keyName );

		return b8.Input.keyHeld( keyName );

	}


	/**
	 * Checks if the given key was JUST pressed on this frame.
	 *
	 * @param {string} keyName - The name of the key.
	 * @returns {boolean} True if the key was just pressed, otherwise false.
	 */
	b8.keyp = function( keyName ) {

		b8.Core.preflight( "b8.keyp" );

		b8.Utilities.checkString( "keyName", keyName );

		return b8.Input.keyJustPressed( keyName );

	}



	/**
	 * Play a song.
	 *
	 * @param {string} song - The name of the song to play.
	 * @returns {void}
	 */
	b8.playSong = function( song ) {

		b8.Utilities.checkString( "song", song );

		b8.Sound.playSong( song );

	}


	/**
	 * Play a sound effect.
	 *
	 * @param {string} sfx - The name of the sound effect to play.
	 * @returns {void}
	 */
	b8.playSfx = function( sfx ) {

		b8.Utilities.checkString( "sfx", sfx );

		b8.Sfx.play( sfx );

	}


	/**
	 * Redefines the colors, assigning new RGB values to each color.
	 *
	 * @param {Array<number>} colors - An array of RGB values.
	 * @returns {void}
	 */
	b8.redefineColors = function( colors ) {

		b8.Core.preflight( "b8.redefineColors" );

		b8.Utilities.checkArray( "colors", colors );

		b8.Core.defineColors( colors );

	}


	/**
	 * Sets the current font for text-based operations.
	 *
	 * @param {string} [fontName="default"] - The font ID to set. Pass null or
	 * omit to reset to default font.
	 * @returns {void}
	 */
	b8.setFont = function( fontName ) {

		b8.Core.preflight( "b8.setFont" );

		fontName = fontName || "default-thin";
		b8.Utilities.checkString( "fontName", fontName );

		b8.TextRenderer.setFont( fontName );

	}


	/**
	 * Returns the current font.
	 *
	 * @returns {string} The current font.
	 */
	b8.getFont = function() {

		b8.Core.preflight( "b8.getFont" );

		b8.TextRenderer.getFont();

	}


	/**
	 * Returns the font object for the given font name.
	 *
	 * @param {string} fontName - The name of the font.
	 * @returns {Object} The font object.
	 */
	b8.getFontByName = function( fontName ) {

		b8.Utilities.checkString( "fontName", fontName );

		return b8.TextRenderer.getFontByName( fontName );

	}


	/**
	 * Sets the current tile font for text-based operations.
	 *
	 * @param {string} [fontName="tiles"] - The font ID to set. Pass null or
	 * omit to reset to default font.
	 * @returns {void}
	 */
	b8.setTileFont = function( fontName ) {

		b8.Core.preflight( "b8.setTileFont" );

		fontName = fontName || "tiles";
		b8.Utilities.checkString( "fontName", fontName );

		b8.TextRenderer.setTileFont( fontName );

	}


	/**
	 * Converts a character code to its integer representation if needed.
	 *
	 * @param {number|string} charCode - The character code to convert.
	 * @returns {number} The integer representation of the character code.
	 */
	b8.convChar = function( charCode ) {

		if ( typeof ( charCode ) === "string" && charCode.length > 0 ) {
			return charCode.charCodeAt( 0 );
		}

		return charCode;

	}


	/**
	 * Stops a sound (previously loaded with b8.playSound).
	 *
	 * @param {HTMLAudioElement} sfx - The sound to stop playing.
	 * @returns {void}
	 */
	b8.stopSound = function( sfx ) {

		b8.Utilities.checkInstanceOf( "sfx", sfx, HTMLAudioElement );
		sfx.currentTime = 0;
		sfx.pause();

	}


	/**
	 * Returns the raw canvas 2D context for drawing.
	 *
	 * @returns {CanvasRenderingContext2D} The raw HTML 2D canvas context.
	 */
	b8.getContext = function() {

		return b8.Core.getContext();

	}


	/**
	 * Saves the contents of the screen into an ImageData object and returns it.
	 *
	 * @returns {ImageData} An ImageData object with the screen's contents.
	 */
	b8.saveScreen = function() {

		return b8.Core.saveScreen();

	}


	/**
	 * Run a screenshake effect.
	 *
	 * @param {number} duration - The duration of the screenshake effect in seconds.
	 * @returns {boolean} Returns true if the screenshake effect was successfully triggered.
	 */
	b8.screenShake = function( duration = 0.5 ) {

		b8.Utilities.checkNumber( "duration", duration );

		return b8.Renderer.shakeScreen( duration );

	}


	/**
	 * Restores the contents of the screen using an ImageData object.
	 *
	 * @param {ImageData} screenData - The ImageData object with the screen's
	 * contents.
	 * @returns {void}
	 */
	b8.restoreScreen = function( screenData ) {

		return b8.Core.restoreScreen( screenData );

	}


	/**
	 * Returns the text with line breaks inserted so that it fits within the
	 * given width.
	 *
	 * @param {string} text - The text to wrap.
	 * @param {number} maxWidth - The maximum width to wrap text at.
	 * @returns {string} The wrapped text.
	 */
	b8.wrapText = function( text, maxWidth ) {

		b8.Utilities.checkString( "text", text );
		b8.Utilities.checkNumber( "maxWidth", maxWidth );

		return b8.TextRenderer.wrapText( text, maxWidth );

	}


	/**
	 * Speaks the given text using the Web Speech API.
	 *
	 * @param {string} text - The text to speak.
	 * @param {Object} [options] - Optional settings for speech synthesis.
	 * @param {number} [options.pitch=1] - The pitch of the voice.
	 * @param {number} [options.rate=1] - The rate of speech.
	 * @param {number} [options.volume=1] - The volume of the speech.
	 * @returns {void}
	 */
	b8.speak = function( text, options = {} ) {

		// Speech synthesis is not supported in this browser.
		if ( !window.speechSynthesis ) return;

		b8.Utilities.checkString( "text", text );
		b8.Utilities.checkObject( "options", options );

		const utterance = new SpeechSynthesisUtterance( text );

		// Optional settings
		utterance.pitch = options.pitch ?? 1;
		utterance.rate = options.rate ?? 1;
		utterance.volume = options.volume ?? 1;

		speechSynthesis.speak( utterance );

	};


	/**
	 * Resets the entire b8 state, as if it was just initialized.
	 * This can be useful for restarting a game.
	 *
	 * @returns {void}
	 */
	b8.reset = function() {

		b8.Core.resetAll();

	};

} )( b8 );

( function( b8 ) {

	// Initialize a flag to track if we're already paused.
	b8._asyncActive = false;

	/**
	 * ASYNC API FUNCTIONS
	 * These functions must be called with 'await'.
	 * For example:
	 *
	 * const k = await b8.Async.key();
	 * console.log("The user pressed " + k);
	 */
	b8.Async = b8.Async || {};

	/**
	 * Create a Proxy for b8.Async to intercept method calls.
	 *
	 * The Proxy's get handler wraps each function so that:
	 * 1. If no async function is currently active:
	 *    - It sets the _asyncActive flag.
	 *    - It pauses the scene.
	 * 2. It calls b8.Core.preflight with the method's name.
	 * 3. It runs the original method.
	 * 4. In the finally block, if the pause was applied:
	 *    - It resumes the scene.
	 *    - It resets the _asyncActive flag.
	 *
	 * This mechanism prevents nested async calls from applying the pause/resume logic more than once.
	 */
	b8.Async = new Proxy(
		b8.Async,
		{
			get( target, prop, receiver ) {

				const orig = Reflect.get( target, prop, receiver );

				if ( typeof orig === "function" ) {

					// Return a wrapped function for any async API function
					return async function( ...args ) {

						// Only wrap if no async function is already active.
						let doWrap = !b8._asyncActive;
						if ( doWrap ) {
							// Mark that an async function has started.
							b8._asyncActive = true;
							// console.log( `pause b8.Async.${prop}` );
							// Pause the scene to wait for the async call.
							b8.Scene.pause();
						}

						try {

							// Call preflight check with the method identifier.
							b8.Core.preflight( `b8.Async.${prop}` );
							// Execute the original async function with its arguments.
							const result = await orig.apply( this, args );
							return result;

						} finally {

							// Only resume and reset _asyncActive if it was this call that paused.
							if ( doWrap ) {
								b8.Scene.resume();
								b8._asyncActive = false;
							}

						}

					}

				}

				// Return non-function properties as-is.
				return orig;

			}
		}

	);


	/**
	 * Waits until the user presses a key and returns it.
	 *
	 * @returns {Promise<string>} The name of the key that was pressed.
	 */
	b8.Async.key = async function() {

		return await b8.Input.readKeyAsync();

	}


	/**
	 * Waits until the user clicks/ taps the pointer and returns its position.
	 *
	 * @returns {Promise<{x: number, y: number}>} The pointer position.
	 */
	b8.Async.pointer = async function() {

		return await b8.Input.readPointerAsync();

	}


	/**
	 * Waits until the user inputs a line of text, then returns it.
	 *
	 * @param {string} [prompt="Enter text:"] - An optional prompt to display before the input.
	 * @param {string} [initString=""] - The initial string presented for the user to edit.
	 * @param {number} [maxLen=-1] - The maximum length of the string the user can type. -1 means no limit.
	 * @param {number} [maxWidth=-1] - The maximum width of the input line in characters. -1 means no wrapping.
	 * @returns {Promise<string>} The input text.
	 */
	b8.Async.readLine = async function( prompt = "Enter text:", initString = "", maxLen = -1, maxWidth = -1 ) {

		b8.Utilities.checkString( "initString", initString );
		b8.Utilities.checkString( "prompt", prompt );
		b8.Utilities.checkNumber( "maxLen", maxLen );
		b8.Utilities.checkNumber( "maxWidth", maxWidth );

		return await b8.Input.readLine( prompt, initString, maxLen, maxWidth );

	}


	/**
	 * Shows a menu of choices and waits for the user to pick an option.
	 *
	 * @param {string[]} choices - An array of choices.
	 * @param {Object} [options={}] - Additional options for the menu.
	 * @returns {Promise<number>} The index of the selected item or -1 if canceled.
	 */
	b8.Async.menu = async function( choices, options = {} ) {

		b8.Utilities.checkArray( "choices", choices );
		b8.Utilities.checkObject( "options", options );

		return await b8.Menu.display( choices, options );

	}


	/**
	 * Displays a dialog with the given prompt and choices.
	 *
	 * This allows shows a user the prompt and waits for them to select a choice.
	 * You can use this for information (with an 'ok' response) or for multiple
	 * choice answers.
	 *
	 * @param {string} prompt - The text to show.
	 * @param {string[]} [choices=["OK"]] - The choices to present to the user.
	 * @param {Object} [options={}] - Additional options for the dialog. Uses b8.Menu.display options.
	 * @returns {Promise<number>} The index of the selected item.
	 */
	b8.Async.dialog = async function( prompt, choices = [ "OK" ], options = {} ) {

		b8.Utilities.checkString( "prompt", prompt );
		b8.Utilities.checkArray( "choices", choices );

		return b8.Async.menu( choices, { prompt, center: true, ...options } );

	}


	/**
	 * Displays a dialog with the given prompt and choices, showing the text character by character.
	 *
	 * @param {string} prompt - The text to show.
	 * @param {string[]} [choices=["OK"]] - The choices to present to the user.
	 * @param {number} [wrapWidth=-1] - The width at which to wrap the text.
	 * @param {number} [delay=0.05] - The delay between characters in seconds.
	 * @param {Object} [options={}] - Additional options for the dialog. Uses b8.Menu.display options.
	 * @returns {Promise<number>} The index of the selected item.
	 */
	b8.Async.dialogTypewriter = async function( prompt, choices = [ "OK" ], wrapWidth = -1, delay = 0.05, options = {} ) {

		b8.Utilities.checkString( "prompt", prompt );
		b8.Utilities.checkArray( "choices", choices );
		b8.Utilities.checkNumber( "delay", delay );

		if ( wrapWidth > 0 ) {
			prompt = b8.TextRenderer.wrapText( prompt, wrapWidth );
		}

		return await b8.Async.menu( choices, { prompt, typewriter: true, center: true, ...options } );

	}


	/**
	 * Shows text slowly, character by character, as in a typewriter.
	 *
	 * @param {string} text - The text to print.
	 * @param {number} [delay=0.05] - The delay between characters in seconds.
	 * @returns {Promise<void>} Resolves after the text is printed.
	 */
	b8.Async.typewriter = async function( text, wrapWidth = -1, delay = 0.035, fontName = null, ) {

		b8.Utilities.checkString( "text", text );
		b8.Utilities.checkNumber( "wrapWidth", wrapWidth );
		b8.Utilities.checkNumber( "delay", delay );

		let font = fontName;
		if ( null !== font ) {
			b8.Utilities.checkString( "fontName", fontName );
			font = b8.TextRenderer.getFontByName( fontName );
		}

		await b8.TextRenderer.printTypewriter( text, wrapWidth, delay, font );

	}


	/**
	 * Loads an image from the given URL.
	 *
	 * @param {string} url - The URL of the image.
	 * @returns {Promise<HTMLImageElement>} The loaded image.
	 */
	b8.Async.loadImage = async function( url ) {

		b8.Utilities.checkString( "url", url );

		return await b8.Core.loadImage( url );

	}


	/**
	 * Loads a sound file from the given URL.
	 *
	 * @param {string} url - The URL of the sound file.
	 * @returns {Promise<HTMLAudioElement>} The loaded sound.
	 */
	b8.Async.loadSound = async function( url ) {

		return new Promise(
			( resolve ) => {

				const audio = new Audio();
				audio.oncanplaythrough = () => resolve( audio );
				audio.src = url;
				audio.load();

			}
		);

	}


	/**
	 * Loads a font for later use in drawing text.
	 *
	 * @param {string} fontImageFile - The URL of the font image file.
	 * @param {number} [tileSizeWidthMultiplier=1] - The width multiplier for the tile size.
	 * @param {number} [tileSizeHeightMultiplier=1] - The height multiplier for the tile size.
	 * @returns {Promise<string>} The font ID.
	 */
	b8.Async.loadFont = async function( fontImageFile, tileSizeWidthMultiplier = 1, tileSizeHeightMultiplier = 1 ) {

		b8.Utilities.checkString( "fontImageFile", fontImageFile );
		b8.Utilities.checkNumber( "tileSizeWidthMultiplier", tileSizeWidthMultiplier );
		b8.Utilities.checkNumber( "tileSizeHeightMultiplier", tileSizeHeightMultiplier );

		const fontName = "FONT@" + b8.Utilities.makeUrlPretty( fontImageFile );
		await b8.TextRenderer.loadFontAsync( fontName, fontImageFile, tileSizeWidthMultiplier, tileSizeHeightMultiplier );

		return fontName;

	}


	/**
	 * Waits for a given number of seconds and then continues execution.
	 *
	 * @param {number} seconds - The duration to wait.
	 * @returns {Promise<void>} Resolves after the specified time.
	 */
	b8.Async.wait = async function( seconds ) {

		b8.Utilities.checkNumber( "seconds", seconds );
		b8.Renderer.render();

		return await new Promise( resolve => setTimeout( resolve, Math.round( seconds * 1000 ) ) );

	}


	/**
	 * Waits for the user to press a key to continue.
	 *
	 * @returns {Promise<void>} Resolves when the user presses a key.
	 */
	b8.Async.waitForContinue = async function() {

		while ( true ) {
			const key = await b8.Async.key();
			if ( key.includes( "Enter" ) || key.includes( "ButtonA" ) || key.includes( " " ) ) break;
		}

	}

} )( b8 );

( function( b8 ) {

	b8.Hooks = {};

	/**
	 * Internal hook management for actions and filters.
	 * This is used to manage the hooks for actions and filters in the b8 framework.
	 * It allows you to add, execute, and remove hooks for various game events.
	 */
	const actions = {};
	const filters = {};


	/**
	 * Adds a hook to the specified store with the given name, callback, and priority.
	 * This function is used internally to manage hooks for actions and filters.
	 *
	 * @param {Object} store - The store to add the hook to (actions or filters).
	 * @param {string} hookName - The name of the hook.
	 * @param {Function} callback - The function to call when the hook is triggered.
	 * @param {number} [priority=10] - The priority of the hook.
	 * @returns {void}
	 */
	function _add( store, hookName, callback, priority = 10 ) {

		if ( !store[ hookName ] ) store[ hookName ] = [];
		store[ hookName ].push( { callback, priority } );
		store[ hookName ].sort( ( a, b ) => a.priority - b.priority );

	}


	/**
	 * Resets all registered actions and filters.
	 *
	 * @returns {void}
	 */
	b8.Hooks.reset = function() {

		for ( const key in actions ) delete actions[ key ];
		for ( const key in filters ) delete filters[ key ];

	}


	/**
	 * Registers an action hook.
	 * This allows you to add a callback that will be executed when the action is triggered.
	 * You can use this to modify game behavior or add custom functionality.
	 *
	 * @param {string} hookName - The name of the action hook.
	 * @param {Function} callback - The function to call when the action is triggered.
	 * @param {number} [priority=10] - The priority of the action hook.
	 * @returns {void}
	 */
	b8.Hooks.addAction = function( hookName, callback, priority = 10 ) {

		_add( actions, hookName, callback, priority );

	}


	/**
	 * Executes all registered actions for the given hook name.
	 * This will call each action's callback with the provided arguments.
	 *
	 * @example
	 * b8.Hooks.doAction( 'onPlayerMove', playerId, newX, newY, dx, dy );
	 *
	 * @param {string} hookName - The name of the action hook to execute.
	 * @param {...*} args - The arguments to pass to the action callbacks.
	 * @returns {void}
	 *
	 */
	b8.Hooks.doAction = function( hookName, ...args ) {

		if ( !actions[ hookName ] ) return;
		for ( const { callback } of actions[ hookName ] ) {
			callback( ...args );
		}

	}


	/**
	 * Registers a filter hook.
	 * This allows you to add a callback that will modify a value before it is returned.
	 * You can use this to change game data or apply transformations.
	 *
	 * @param {string} hookName - The name of the filter hook.
	 * @param {Function} callback - The function to call when the filter is applied.
	 * @param {number} [priority=10] - The priority of the filter hook.
	 * @returns {void}
	 */
	b8.Hooks.addFilter = function( hookName, callback, priority = 10 ) {

		_add( filters, hookName, callback, priority );

	}


	/**
	 * Applies all registered filters for the given hook name to a value.
	 * This will call each filter's callback with the value and any additional arguments,
	 * returning the modified value.
	 *
	 * @example
	 * const modifiedValue = b8.Hooks.applyFilters( 'modifyPlayerSpeed', playerSpeed, playerId );
	 *
	 * @param {string} hookName - The name of the filter hook to apply.
	 * @param {*} value - The initial value to filter.
	 * @param {...*} args - Additional arguments to pass to the filter callbacks.
	 * @return {*} The modified value after all filters have been applied.
	 */
	b8.Hooks.applyFilters = function( hookName, value, ...args ) {

		if ( !filters[ hookName ] ) return value;

		let result = value;
		for ( const { callback } of filters[ hookName ] ) {
			result = callback( result, ...args );
		}

		return result;

	}


	/**
	 * Removes a specific action hook by its name and callback.
	 * This is useful for cleaning up hooks that are no longer needed.
	 *
	 * @param {string} hookName - The name of the action hook to remove.
	 * @param {Function} callback - The callback function to remove.
	 * @returns {void}
	 */
	b8.Hooks.removeAction = function( hookName, callback ) {

		actions[ hookName ] = ( actions[ hookName ] || [] ).filter( h => h.callback !== callback );

	}


	/**
	 * Removes a specific filter hook by its name and callback.
	 * This is useful for cleaning up filters that are no longer needed.
	 *
	 * @param {string} hookName - The name of the filter hook to remove.
	 * @param {Function} callback - The callback function to remove.
	 * @returns {void}
	 */
	b8.Hooks.removeFilter = function( hookName, callback ) {

		filters[ hookName ] = ( filters[ hookName ] || [] ).filter( h => h.callback !== callback );

	}


} )( b8 );

( function( b8 ) {

	/**
	 * Virtual joystick setup.
	 *
	 * This module sets up a virtual joystick for mobile devices.
	 * The joystick has two parts: a directional pad on the left side of the screen,
	 * and three buttons on the right side of the screen.
	 * The directional pad has up, down, left, and right buttons.
	 * The right side has three buttons, labeled A, B, and =.
	 */
	b8.Joystick = {};

	let repeatIntervals = null;

	const VJOY_HTML = `
<div class="vjoy-options">
	<button id='vjoy-button-ter' class='vjoy-button'>Start</button>
	<button id='vjoy-button-screenshot' class='vjoy-button'>Snap</button>
</div>
<div class="vjoy-controls">
	<div class="vjoy-dpad">
	<button id='vjoy-button-up' class='vjoy-button'><span>U</span></button>
	<button id='vjoy-button-right' class='vjoy-button'><span>R</span></button>
	<button id='vjoy-button-left' class='vjoy-button'><span>L</span></button>
	<button id='vjoy-button-down' class='vjoy-button'><span>D</span></button>
	</div>
	<div class="vjoy-buttons">
	<button id='vjoy-button-pri' class='vjoy-button'><span>A</span></button>
	<button id='vjoy-button-sec' class='vjoy-button'><span>B</span></button>
	</div>
</div>`;


	/**
	 * The CSS for the virtual joystick.
	 *
	 * @type {string}
	 */
	const VJOY_CSS = `
:root {
	--b8-vjoy-button-color: #333;
	--b8-vjoy-button-size: 14vw;
	--b8-vjoy-button-dpad-size: calc(var(--b8-vjoy-button-size) * 2);
	--b8-console-radius: 2rem;
	--b8-border-radius: calc(var(--b8-vjoy-button-dpad-size) / 5);
}

.vjoy-container,
.vjoy-container * {
	box-sizing: border-box;
	user-select: none;
	-webkit-user-select: none;
	-webkit-touch-callout: none;
	touch-action: none;
}

.vjoy-container {
	position: relative;
	width: 100%;
	padding: 8vw 4vw 8vw 6vw;
	background: deeppink;
	border-radius: 0 0 var(--b8-console-radius) var(--b8-console-radius);
}

.vjoy-options {
	border-radius: 5rem;
	position: absolute;
	display: flex;
	gap: 2vw;
	align-items: center;
	padding: 2vw;
	border-radius: 2rem;
	background: inherit;
	top: -4vw;
	left: 50%;
	transform: translateX(-50%);
}

.vjoy-controls {
	display: flex;
	gap: 5vw;
	justify-content: space-between;
	align-items: center;
}

.vjoy-dpad {
	aspect-ratio: 1;
	max-width: 10rem;
	width: var(--b8-vjoy-button-dpad-size);
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-template-rows: 1fr 1fr;
	flex-wrap: wrap;
	transform: rotate(45deg);
	border-radius: var(--b8-border-radius);
	background:black;
	gap: 1px;
	border: 2px solid black;
}

.vjoy-dpad button {
	width: 100%;
	height: 100%;
	position: relative;
}
.vjoy-dpad button span {
	transform: rotate(-45deg);
}
.vjoy-dpad button:after {
	position: absolute;
	content: '';
	display: block;
	width: 100%;
	height: 100%;
	background: rgba(0,0,0,0);
	transform: rotate(-45deg);
}

#vjoy-button-up {
	border-radius: var(--b8-border-radius) 0 0 0;
}
#vjoy-button-down {
	border-radius: 0 0 var(--b8-border-radius) 0;
}
#vjoy-button-left {
	border-radius: 0 0 0 var(--b8-border-radius);
}
#vjoy-button-right {
	border-radius: 0 var(--b8-border-radius) 0 0;
}

#vjoy-button-up:after {
	transform: rotate(-45deg) translateY(-30%) scale(1.1);
}
#vjoy-button-down:after {
	transform: rotate(-45deg) translateY(30%) scale(1.1);
}
#vjoy-button-left:after {
	transform: rotate(-45deg) translateX(-30%) scale(1.1);
}
#vjoy-button-right:after {
	transform: rotate(-45deg) translateX(30%) scale(1.1);
}

.vjoy-buttons {
	display: flex;
	gap: 2vw;
	transform: rotate(-45deg);
	border: 0.8vw solid rgba(0,0,0,0.2);
	border-radius: calc( var(--b8-border-radius) + 1vw );
	padding: 1vw;
}

.vjoy-buttons button {
	width: var(--b8-vjoy-button-size);
	max-width: 5rem;
	max-height: 5rem;
	height: var(--b8-vjoy-button-size);
	border-radius: var(--b8-border-radius);
	touch-action: none;
	border: 2px solid black;
	position: relative;
}

.vjoy-buttons button:after {
	position: absolute;
	content: '';
	display: block;
	width: 100%;
	height: 100%;
	background: rgba(0,0,0,0);
	transform: scale(1.2);
}

.vjoy-buttons button span {
	transform: rotate(45deg);
}

.vjoy-button {
	background: var(--b8-vjoy-button-color) !important;
	border: none;
	font-family: arial, sans-serif;
	font-size: 12px;
	font-weight: 600;
	color: #aaa !important;
	user-select: none;
	touch-callout: none;
	-webkit-user-select: none;
	-webkit-touch-callout: none;
	text-shadow: 0 -2px 0 black;
	padding: 0;
	display: flex;
	justify-content: center;
	align-items: center;
	letter-spacing: 0.1em;
	text-transform: uppercase;
}

.vjoy-button:hover,
.vjoy-button:focus,
.vjoy-button:active {
	background: black;
	outline: none;
}

#vjoy-button-screenshot,
#vjoy-button-ter {
	width: calc(var(--b8-vjoy-button-size) * 1.4);
	padding: 1vw;
	border-radius: 1rem;
}
`;

	/**
	 * Sets up the virtual joystick.
	 *
	 * @returns {void}
	 */
	b8.Joystick.setup = function() {

		b8.Utilities.log( "Setting up virtual joystick..." );

		// Add controller styles.
		const styleEl = document.createElement( "style" );
		styleEl.setAttribute( "type", "text/css" );
		styleEl.innerText = VJOY_CSS;
		document.body.appendChild( styleEl );

		// Create a container element
		const container = document.createElement( 'div' );
		container.className = 'vjoy-container';
		container.innerHTML = VJOY_HTML;

		b8.Core.getBeepContainerEl().appendChild( container );

		setTimeout( b8.Joystick.continueSetup, 10 );

	}


	/**
	 * Continues setting up the virtual joystick.
	 *
	 * @returns {void}
	 */
	b8.Joystick.continueSetup = function() {

		b8.Joystick.setUpButton( "vjoy-button-up", "ArrowUp" );
		b8.Joystick.setUpButton( "vjoy-button-down", "ArrowDown" );
		b8.Joystick.setUpButton( "vjoy-button-left", "ArrowLeft" );
		b8.Joystick.setUpButton( "vjoy-button-right", "ArrowRight" );
		b8.Joystick.setUpButton( "vjoy-button-pri", "ButtonA" );
		b8.Joystick.setUpButton( "vjoy-button-sec", "ButtonB" );
		b8.Joystick.setUpButton( "vjoy-button-ter", "Enter" );
		b8.Joystick.setUpButton( "vjoy-button-screenshot", "0" );

		// Prevent touches on the document body from doing what they usually do (opening
		// context menus, selecting stuff, etc).
		document.body.addEventListener( "touchstart", e => e.preventDefault() );

	}


	/**
	 * Sets up a virtual joystick button.
	 * If buttonKeyName is null, the button will be hidden.
	 * Otherwise, the button will be set up to simulate the given key.
	 *
	 * @param {string} buttonId - The ID of the button element
	 * @param {string} buttonKeyName - The key name to simulate
	 * @returns {void}
	 */
	b8.Joystick.setUpButton = function( buttonId, buttonKeyName ) {

		const button = b8.Utilities.assert(
			document.getElementById( buttonId ),
			"Could not find button ID " + buttonId
		);

		if ( buttonKeyName === null ) {
			// This means the user wants to hide the button.
			button.style.display = "none";
			return;
		}

		[ "pointerdown", "pointerstart" ].forEach( eventName => {
			button.addEventListener(
				eventName,
				( e ) => {
					e.preventDefault();
					b8.Joystick.handleButtonEvent( buttonKeyName, true, e );
				},
				{ passive: false }
			);
		} );

		// Cancel the button press if the pointer moves off the button.
		[ "pointerout", "pointerup", "pointerleave" ].forEach( eventName => {
			button.addEventListener(
				eventName,
				( e ) => b8.Joystick.handleButtonEvent( buttonKeyName, false, e )
			);
		} );

		button.addEventListener(
			"pointermove",
			( e ) => {
				// Prevent default behavior for pointermove events.
				e.preventDefault();
			},
			{ passive: false }
		);

		button.addEventListener(
			"contextmenu",
			( e ) => e.preventDefault()
		);

	}


	/**
	 * Handles a button event.
	 *
	 * @param {string} buttonKeyName - The key name to simulate
	 * @param {boolean} down - Whether the button is being pressed or released
	 * @param {Event} evt - The event object
	 * @returns {void}
	 */
	b8.Joystick.handleButtonEvent = function( buttonKeyName, down, evt ) {

		// Add key property to event.
		evt.key = buttonKeyName;

		// Initialize repeat intervals container if not already created.
		if ( !repeatIntervals ) {
			repeatIntervals = {};
		}

		if ( down ) {

			// Call onKeyDown immediately.
			b8.Input.onKeyDown( evt );

			// If no timer exists for this button, start one.
			if ( !repeatIntervals[ buttonKeyName ] ) {
				repeatIntervals[ buttonKeyName ] = {};

				// Set a timeout for the initial delay.
				repeatIntervals[ buttonKeyName ].initialTimeout = setTimeout(
					function() {
						// After the delay, start repeating.
						repeatIntervals[ buttonKeyName ].interval = setInterval(
							function() {
								b8.Input.onKeyDown( evt );
							},
							150
						);
					},
					150
				);
			}

		} else {

			// Clear any timers if they exist.
			if ( repeatIntervals[ buttonKeyName ] ) {
				if ( repeatIntervals[ buttonKeyName ].initialTimeout ) {
					clearTimeout( repeatIntervals[ buttonKeyName ].initialTimeout );
				}
				if ( repeatIntervals[ buttonKeyName ].interval ) {
					clearInterval( repeatIntervals[ buttonKeyName ].interval );
				}
				delete repeatIntervals[ buttonKeyName ];
			}

			b8.Input.onKeyUp( evt );

		}

		evt.preventDefault();

	}

} )( b8 );

( function( b8 ) {

	b8.Vfx = {};


	// List of animations and properties.
	b8.Vfx.animations = {
		'fire': {
			frames: [ 0, 1, 2, 3 ],
			loop: true,
		},
		'explosion': {
			frames: [ 6, 7, 8, 9, 10 ],
			fps: 8,
		},
		'portal': {
			frames: [ 12, 13 ],
			loop: true,
		},
		'cursor': {
			frames: [ 14, 15, 16, 15 ],
			loop: true,
			fps: 6,
		},
		'swipe': {
			frames: [ 18, 19, 20, 20, 20, 21, 22 ],
			fps: 24,
		},
		'skull': {
			frames: [ 24, 24, 24, 25, 26, 27, 28, 28, 29 ],
			fps: 12,
		},
		'fire-small': {
			frames: [ 30, 31, 32, 33 ],
			loop: true,
		},
		'fire-stand': {
			frames: [ 36, 37, 38, 39 ],
			loop: true,
		},
		'shrink': {
			frames: [ 34, 35, 40, 41 ],
			fps: 18,
		}
	};


	// Pixel directions for outline drawing.
	// Each entry is an [x, y] offset.
	const outlineDirections = [
		[ -1, -1 ],
		[ 1, 1 ],
		[ 1, -1 ],
		[ -1, 1 ],
		[ -1, 0 ],
		[ 1, 0 ],
		[ 0, -1 ],
		[ 0, 1 ]
	];


	// Loop through Vfx animations and set default fps and loop values.
	for ( const key in b8.Vfx.animations ) {
		const anim = b8.Vfx.animations[ key ];
		if ( anim.fps === undefined ) anim.fps = 4;
		if ( anim.loop === undefined ) anim.loop = false;
	}


	/**
	 * Draw Vfx at a specific position.
	 *
	 * @param {Object} animation The animation to draw.
	 * @param {number} x The x position to draw the Vfx.
	 * @param {number} y The y position to draw the Vfx.
	 * @param {number} startTime The start time of the animation.
	 * @returns {void}
	 */
	const drawVfx = function( animation, x, y, startTime ) {

		const font = b8.TextRenderer.curVfx_;
		const frame = Math.abs( b8.Animation.frame( animation, startTime ) );
		const direction = frame >= 0 ? 0 : 1;

		b8.TextRenderer.spr(
			frame,
			x, y,
			font,
			direction || 0
		);

		b8.Core.drawState.cursorCol++;

	};


	/**
	 * Draw Vfx at a specific position with an outline.
	 * The outline uses the specified background color for the border.
	 * The Vfx will have a transparent background with the border in the background colour.
	 *
	 * @param {Object} animation The animation to draw.
	 * @param {number} x The x position to draw the Vfx.
	 * @param {number} y The y position to draw the Vfx.
	 * @param {number} startTime The start time of the animation.
	 * @returns {void}
	 */
	const drawVfxOutline = function( animation, x, y, startTime ) {

		const font = b8.TextRenderer.curVfx_;
		const frame = Math.abs( b8.Animation.frame( animation, startTime ) );
		const direction = frame >= 0 ? 0 : 1;

		const currentFg = b8.Core.drawState.fgColor;
		const currentBg = b8.Core.drawState.bgColor;

		// Set outline color to background color.
		b8.Core.setColor( currentBg, -1 );

		for ( const dir of outlineDirections ) {
			b8.TextRenderer.spr(
				frame,
				x + dir[ 0 ], y + dir[ 1 ],
				font,
				direction || 0
			);
		}

		// Reset color to foreground color and draw main Vfx.
		b8.Core.setColor( currentFg, -1 );

		b8.TextRenderer.spr(
			frame,
			x, y,
			font,
			direction || 0
		);

		b8.Core.setColor( currentFg, currentBg );

		b8.Core.drawState.cursorCol++;

	};


	/**
	 * Draw Vfx at the current cursor position.
	 *
	 * @param {string} animation The animation to draw.
	 * @param {number} startTime The start time of the animation. If null, uses the core start time. If startTime is in the future, the animation will delayed until startTime.
	 * @param {number} [offsetCol=0] The x offset to apply to the drawing position.
	 * @param {number} [offsetRow=0] The y offset to apply to the drawing position.
	 * @return {boolean} Returns true if the animation is still playing, false if it has finished.
	 */
	b8.Vfx.draw = function( animation, startTime, offsetCol = 0, offsetRow = 0 ) {

		if ( startTime !== null ) b8.Utilities.checkNumber( "startTime", startTime );
		b8.Utilities.checkNumber( "offsetCol", offsetCol );
		b8.Utilities.checkNumber( "offsetRow", offsetRow );

		return b8.Vfx.spr(
			animation,
			startTime,
			( b8.Core.drawState.cursorCol + offsetCol ) * b8.CONFIG.CHR_WIDTH,
			( b8.Core.drawState.cursorRow + offsetRow ) * b8.CONFIG.CHR_HEIGHT,
		);

	};


	/**
	 * Draw Vfx outline at the current cursor position.
	 * The outline uses the current background color for the border.
	 * The Vfx will have a transparent background with the border in the background colour.
	 *
	 * @param {string} animation The animation to draw.
	 * @param {number} startTime The start time of the animation. If null, uses the core start time. If startTime is in the future, the animation will delayed until startTime.
	 * @param {number} [offsetCol=0] The x offset to apply to the drawing position.
	 * @param {number} [offsetRow=0] The y offset to apply to the drawing position.
	 * @return {boolean} Returns true if the animation is still playing, false if it has finished.
	 */
	b8.Vfx.drawOutline = function( animation, startTime, offsetCol = 0, offsetRow = 0 ) {

		if ( startTime !== null ) b8.Utilities.checkNumber( "startTime", startTime );
		b8.Utilities.checkNumber( "offsetCol", offsetCol );
		b8.Utilities.checkNumber( "offsetRow", offsetRow );

		return b8.Vfx.sprOutline(
			animation,
			startTime,
			( b8.Core.drawState.cursorCol + offsetCol ) * b8.CONFIG.CHR_WIDTH,
			( b8.Core.drawState.cursorRow + offsetRow ) * b8.CONFIG.CHR_HEIGHT,
		);

	};


	/**
	 * Draw Vfx at a specific x, y position.
	 *
	 * @param {string} animation The animation to draw.
	 * @param {number|null} startTime The start time of the animation. If null, uses the core start time. If startTime is in the future, the animation will delayed until startTime.
	 * @param {number} x The x position to draw the Vfx.
	 * @param {number} y The y position to draw the Vfx.
	 * @returns {boolean} Returns true if the animation is still playing, false if it has finished.
	 */
	b8.Vfx.spr = function( animation, startTime, x, y ) {

		if ( startTime !== null ) b8.Utilities.checkNumber( "startTime", startTime );
		b8.Utilities.checkNumber( "x", x );
		b8.Utilities.checkNumber( "y", y );

		if ( startTime > b8.Core.getNow() ) return true;

		const anim = b8.Vfx.get( animation );

		if ( !b8.Animation.shouldLoop( anim, startTime ) ) return false;

		drawVfx(
			anim,
			x, y,
			startTime
		);

		return true;

	};


	/**
	 * Draw Vfx at a specific x, y position with an outline.
	 * The outline uses the current background color for the border.
	 * The Vfx will have a transparent background with the border in the background colour.
	 *
	 * @param {string} animation The animation to draw.
	 * @param {number|null} startTime The start time of the animation. If null, uses the core start time. If startTime is in the future, the animation will delayed until startTime.
	 * @param {number} x The x position to draw the Vfx.
	 * @param {number} y The y position to draw the Vfx.
	 * @returns {boolean} Returns true if the animation is still playing, false if it has finished.
	 */
	b8.Vfx.sprOutline = function( animation, startTime, x, y ) {

		if ( startTime !== null ) b8.Utilities.checkNumber( "startTime", startTime );
		b8.Utilities.checkNumber( "x", x );
		b8.Utilities.checkNumber( "y", y );

		if ( startTime > b8.Core.getNow() ) return true;

		const anim = b8.Vfx.get( animation );

		if ( !b8.Animation.shouldLoop( anim, startTime ) ) return false;

		drawVfxOutline(
			anim,
			x, y,
			startTime
		);

		return true;

	};


	/**
	 * Get Vfx animation by id.
	 *
	 * @param {string} animation The animation id.
	 * @returns {Object|undefined} The Vfx animation object, or undefined if not found.
	 */
	b8.Vfx.get = function( animation ) {

		b8.Utilities.checkString( "animation", animation );
		if ( b8.Vfx.animations[ animation ] === undefined ) {
			b8.Utilities.fatal( "Invalid Vfx animation: " + animation );
		}

		return b8.Vfx.animations[ animation ];

	};


	/**
	 * Checks if the Vfx animation has finished looping.
	 *
	 * @param {string|Object} animation The animation to check.
	 * @param {number} startTime The start time of the animation.
	 * @returns {boolean} Returns true if the animation should continue, false if it has finished looping.
	 */
	b8.Vfx.shouldLoop = function( anim, startTime ) {

		if ( typeof anim === 'string' ) {
			anim = b8.Vfx.get( anim );
		}

		b8.Utilities.checkObject( "anim", anim );
		b8.Utilities.checkNumber( "startTime", startTime );

		return b8.Animation.shouldLoop( anim, startTime );

	};

} )( b8 );

( function( b8 ) {

	b8.Utilities = {};


	/**
	 * Shows a fatal error and throws an exception.
	 *
	 * @param {string} errorMessage - The error message to show.
	 * @throws {Error} The error message.
	 * @returns {void}
	 */
	b8.Utilities.fatal = function( errorMessage ) {

		const error = ( errorMessage instanceof Error ) ? errorMessage : new Error( String( errorMessage ) );

		b8.Utilities.error( "Fatal error: " + error.message );

		try {
			b8.Core.handleCrash( error );
		} catch ( e ) {
			b8.Utilities.error( "Error in b8.Core.handleCrash: " + e + " while handling error " + error.message );
		}

		throw new Error( error );

	}


	/**
	 * Asserts that the given condition is true, else shows a fatal error.
	 *
	 * @param {boolean} cond - The condition that you fervently hope will be true.
	 * @param {string} msg - The error message to show if the condition is false.
	 * @returns {boolean} The 'cond' parameter.
	 */
	b8.Utilities.assert = function( cond, msg ) {

		if ( !cond ) {
			b8.Utilities.fatal( msg || "Assertion Failed" );
		}

		return cond;

	}


	/**
	 * Same as b8.Utilities.assert() but only asserts if b8.CONFIG.DEBUG is true.
	 *
	 * @param {boolean} cond - The condition that you fervently hope will be true.
	 * @param {string} msg - The error message to show if the condition is false.
	 * @returns {boolean} The 'cond' parameter.
	 */
	b8.Utilities.assertDebug = function( cond, msg ) {

		if ( !cond ) {

			if ( b8.CONFIG.DEBUG ) {
				warn( "DEBUG ASSERT failed: " + msg );
			} else {
				b8.Utilities.fatal( msg );
			}

		}

		return cond;

	}


	/**
	 * Asserts that two values are equal.
	 *
	 * @param {any} expected - What you expect the value to be.
	 * @param {any} actual - What the value actually is.
	 * @param {string} what - A description of what the value is.
	 * @returns {any} The 'actual' parameter.
	 */
	b8.Utilities.assertEquals = function( expected, actual, what ) {

		if ( expected !== actual ) {
			b8.Utilities.fatal( `${what}: expected ${expected} but got ${actual}` );
		}

		return actual;

	}


	/**
	 * Checks the type of a variable and throws an exception if it's incorrect.
	 *
	 * @param {string} varName - The name of the variable.
	 * @param {any} varValue - The value of the variable.
	 * @param {string} varType - The expected type of the variable.
	 * @returns {any} The 'varValue' parameter.
	 */
	b8.Utilities.checkType = function( varName, varValue, varType ) {

		b8.Utilities.assert( varName, "checkType: varName must be provided." );
		b8.Utilities.assert( varType, "checkType: varType must be provided." );
		b8.Utilities.assert(
			typeof ( varValue ) === varType,
			`${varName} should be of type ${varType} but was ${typeof ( varValue )}: ${varValue}`
		);

		return varValue;

	}


	/**
	 * Checks that a variable is a number.
	 *
	 * @param {string} varName - The name of the variable.
	 * @param {any} varValue - The value of the variable.
	 * @param {number} [optMin=undefined] - The minimum acceptable value for the variable.
	 * @param {number} [optMax=undefined] - The maximum acceptable value for the variable.
	 * @returns {number} The 'varValue' parameter.
	 */
	b8.Utilities.checkNumber = function( varName, varValue, optMin = undefined, optMax = undefined ) {

		b8.Utilities.checkType( varName, varValue, "number" );

		if ( isNaN( varValue ) ) {
			b8.Utilities.fatal( `${varName} should be a number but is NaN` );
		}

		if ( !isFinite( varValue ) ) {
			b8.Utilities.fatal( `${varName} should be a number but is infinite: ${varValue}` );
		}

		if ( optMin !== undefined ) {
			b8.Utilities.assert( varValue >= optMin, `${varName} should be >= ${optMin} but is ${varValue}` );
		}

		if ( optMax !== undefined ) {
			b8.Utilities.assert( varValue <= optMax, `${varName} should be <= ${optMax} but is ${varValue}` );
		}

		return varValue;

	}


	/**
	 * Checks that a variable is an integer.
	 *
	 * @param {string} varName - The name of the variable.
	 * @param {any} varValue - The value of the variable.
	 * @param {number} [optMin=undefined] - The minimum acceptable value.
	 * @param {number} [optMax=undefined] - The maximum acceptable value.
	 * @returns {number} The varValue parameter.
	 */
	b8.Utilities.checkInt = function( varName, varValue, optMin, optMax ) {

		b8.Utilities.checkNumber( varName, varValue, optMin, optMax );

		if ( !Number.isInteger( varValue ) ) {
			b8.Utilities.fatal( `${varName} should be an integer but is ${varValue}` );
		}

		return varValue;

	}


	/**
	 * Checks that a variable is a string.
	 *
	 * @param {string} varName - The name of the variable.
	 * @param {any} varValue - The value of the variable.
	 * @returns {string} The 'varValue' parameter.
	 */
	b8.Utilities.checkString = function( varName, varValue ) {

		return b8.Utilities.checkType( varName, varValue, "string" );

	}


	/**
	 * Checks that a variable is a boolean.
	 *
	 * @param {string} varName - The name of the variable.
	 * @param {any} varValue - The value of the variable.
	 * @returns {boolean} The 'varValue' parameter.
	 */
	b8.Utilities.checkBoolean = function( varName, varValue ) {

		return b8.Utilities.checkType( varName, varValue, "boolean" );

	}


	/**
	 * Checks that a variable is a function.
	 *
	 * @param {string} varName - The name of the variable.
	 * @param {any} varValue - The value of the variable.
	 * @returns {Function} The 'varValue' parameter.
	 */
	b8.Utilities.checkFunction = function( varName, varValue ) {

		if ( varValue === null ) {
			b8.Utilities.fatal( `${varName} should be a function, but was null` );
		}

		return b8.Utilities.checkType( varName, varValue, "function" );

	}


	/**
	 * Checks that a variable is an object.
	 *
	 * @param {string} varName - The name of the variable.
	 * @param {any} varValue - The value of the variable.
	 * @returns {Object} The 'varValue' parameter.
	 */
	b8.Utilities.checkObject = function( varName, varValue ) {

		if ( varValue === null ) {
			b8.Utilities.fatal( `${varName} should be an object, but was null` );
		}

		return b8.Utilities.checkType( varName, varValue, "object" );

	}


	/**
	 * Checks that a variable is an instance of a given class.
	 *
	 * @param {string} varName - The name of the variable.
	 * @param {any} varValue - The value of the variable.
	 * @param {Function} expectedClass - The expected class.
	 * @returns {any} The 'varValue' parameter.
	 */
	b8.Utilities.checkInstanceOf = function( varName, varValue, expectedClass ) {

		b8.Utilities.assert(
			varValue instanceof expectedClass,
			`${varName} should be an instance of ${expectedClass.name} but was not, it's: ${varValue}`
		);

		return varValue;

	}


	/**
	 * Checks that a variable is set (not undefined or null).
	 *
	 * @param {string} varName - The name of the variable.
	 * @param {any} varValue - The value of the variable.
	 * @returns {any} The 'varValue' parameter.
	 */
	b8.Utilities.checkIsSet = function( varName, varValue ) {

		b8.Utilities.assert(
			varValue !== undefined && varValue !== null,
			`${varName} should be set but was: ${varValue}`
		);

		return varValue;

	}


	/**
	 * Checks that a variable is an array.
	 *
	 * @param {string} varName - The name of the variable.
	 * @param {any} varValue - The value of the variable.
	 * @returns {Array} The 'varValue' parameter.
	 */
	b8.Utilities.checkArray = function( varName, varValue ) {

		b8.Utilities.assert( Array.isArray( varValue ), `${varName} should be an array, but was: ${varValue}` );

		return varValue;

	}

	/**
	 * Prints a log to the console if b8.CONFIG.DEBUG is true.
	 *
	 * @param {string} msg - The message to print.
	 * @returns {void}
	 */
	b8.Utilities.log = function( msg ) {

		if ( b8.CONFIG.DEBUG ) {
			console.log( msg );
		}

	}


	/**
	 * Prints a warning to the console.
	 *
	 * @param {string} msg - The message to print.
	 * @returns {void}
	 */
	b8.Utilities.warn = function( msg ) {

		console.warn( msg );

	}


	/**
	 * Prints an error to the console.
	 *
	 * @param {string} msg - The message to print.
	 * @returns {void}
	 */
	b8.Utilities.error = function( msg ) {

		console.error( msg );

	}


	/**
	 * Makes a color transparent in an image.
	 *
	 * This function is asynchronous because it uses an HTMLImageElement.
	 *
	 * Uses a range because I found that occassionally the RGB values of a saved
	 * png are not exactly as they were set in the image. Possibly due to
	 * compression.
	 *
	 * @param {HTMLImageElement} img - The image to process.
	 * @param {array} color - The color to make transparent. By default this is pure magenta [255,0,255].
	 * @param {number} range - The range of RGB values to consider as the target color.
	 * @returns {Promise<HTMLCanvasElement>} The processed image.
	 */
	b8.Utilities.makeColorTransparent = async function( img, color = [ 255, 0, 255 ], range = 5 ) {

		// Create a canvas the same size as the image and draw the image on it.
		const canvas = document.createElement( "canvas" );
		const ctx = canvas.getContext( "2d" );

		canvas.width = img.width;
		canvas.height = img.height;

		ctx.drawImage( img, 0, 0 );

		// Get the image data.
		const imageData = ctx.getImageData( 0, 0, canvas.width, canvas.height );
		const data = imageData.data;

		// Loop through the image data and set the alpha channel to 0 for the specified color.
		for ( let i = 0; i < data.length; i += 4 ) {

			const r = data[ i ];
			const g = data[ i + 1 ];
			const b = data[ i + 2 ];

			// Check if the pixel's RGB values are within the range of the target color
			if (
				Math.abs( r - color[ 0 ] ) <= range &&
				Math.abs( g - color[ 1 ] ) <= range &&
				Math.abs( b - color[ 2 ] ) <= range
			) {
				data[ i + 3 ] = 0; // Set alpha to 0 (fully transparent)
			}

		}

		// Put the modified image data back on the canvas.
		ctx.putImageData( imageData, 0, 0 );

		return canvas;

	}


	/**
	 * Loads a file asynchronously.
	 *
	 * @param {string} url - The URL of the file.
	 * @returns {Promise<string>} A promise that resolves to the file content.
	 */
	b8.Utilities.loadFileAsync = function( url ) {

		return new Promise(
			( resolve, reject ) => {

				const xhr = new XMLHttpRequest();

				xhr.addEventListener(
					"load",
					() => {
						if ( xhr.status < 200 || xhr.status > 299 ) {
							reject( "HTTP request finished with status " + xhr.status );
						} else {
							resolve( xhr.responseText );
						}
					}
				);

				xhr.addEventListener( "error", e => reject( e ) );
				xhr.open( "GET", url );
				xhr.send();

			}

		);

	}


	/**
	 * Clamps a number, ensuring it's between a minimum and a maximum.
	 *
	 * @param {number} x - The number to clamp.
	 * @param {number} lo - The minimum value.
	 * @param {number} hi - The maximum value.
	 * @returns {number} The clamped number.
	 */
	b8.Utilities.clamp = function( x, lo, hi ) {

		return Math.min( Math.max( x, lo ), hi );

	}


	/**
	 * Clamps a number between 0 and 1.
	 *
	 * @param {number} x - The number to clamp.
	 * @returns {number} The clamped number.
	 */
	b8.Utilities.clamp01 = function( x ) {

		return b8.Utilities.clamp( x, 0, 1 );

	}


	/**
	 * Converts a hex color string to an RGB object.
	 *
	 * @param {string} hex - The hex color string (e.g., "#ff0000").
	 * @returns {object} An object with r, g, b properties.
	 */
	b8.Utilities.hexToRgb = function( hex ) {

		// Remove the "#" if present
		hex = hex.replace( "#", "" );

		// Parse the RGB values
		const bigint = parseInt( hex, 16 );

		return {
			r: ( bigint >> 16 ) & 255, // Extract red
			g: ( bigint >> 8 ) & 255,  // Extract green
			b: bigint & 255          // Extract blue
		};

	}


	/**
	 * Calculates the intersection between two integer number intervals.
	 *
	 * Given 2 ranges it will see if these ranges overlap and if they do it will
	 * optionally return the intersection range.
	 *
	 * For example if the first interval is [1, 5] and the second interval is [3, 7]
	 * the intersection is [3, 5].
	 *
	 * @param {number} as - The start of the first interval.
	 * @param {number} ae - The end of the first interval.
	 * @param {number} bs - The start of the second interval.
	 * @param {number} be - The end of the second interval.
	 * @param {Object} [result=null] - If provided, used to return the intersection.
	 * @returns {boolean} True if there is an intersection, false otherwise.
	 */
	b8.Utilities.intersectIntervals = function( as, ae, bs, be, result = null ) {

		b8.Utilities.checkNumber( "as", as );
		b8.Utilities.checkNumber( "ae", ae );
		b8.Utilities.checkNumber( "bs", bs );
		b8.Utilities.checkNumber( "be", be );

		if ( result ) {
			b8.Utilities.checkObject( "result", result );
		}

		const start = Math.max( as, bs );
		const end = Math.min( ae, be );

		if ( end >= start ) {
			if ( result ) {
				result.start = start;
				result.end = end;
			}
			return true;
		}

		return false;

	}


	/**
	 * Calculates the intersection of two rectangles.
	 *
	 * @param {Object} r1 - The first rectangle.
	 * @param {Object} r2 - The second rectangle.
	 * @param {number} [dx1=0] - The delta X to add to the first rectangle.
	 * @param {number} [dy1=0] - The delta Y to add to the first rectangle.
	 * @param {number} [dx2=0] - The delta X to add to the second rectangle.
	 * @param {number} [dy2=0] - The delta Y to add to the second rectangle.
	 * @param {Object} [result=null] - If provided, used to return the intersection.
	 * @returns {boolean} True if there is a non-empty intersection, false otherwise.
	 */
	b8.Utilities.intersectRects = function( r1, r2, dx1 = 0, dy1 = 0, dx2 = 0, dy2 = 0, result = null ) {

		b8.Utilities.checkObject( "r1", r1 );
		b8.Utilities.checkObject( "r2", r2 );
		b8.Utilities.checkNumber( "r1.x", r1.x );
		b8.Utilities.checkNumber( "r1.y", r1.y );
		b8.Utilities.checkNumber( "r1.w", r1.w );
		b8.Utilities.checkNumber( "r1.h", r1.h );
		b8.Utilities.checkNumber( "r2.x", r2.x );
		b8.Utilities.checkNumber( "r2.y", r2.y );
		b8.Utilities.checkNumber( "r2.w", r2.w );
		b8.Utilities.checkNumber( "r2.h", r2.h );
		b8.Utilities.checkNumber( "dx1", dx1 );
		b8.Utilities.checkNumber( "dx2", dx2 );
		b8.Utilities.checkNumber( "dy1", dy1 );
		b8.Utilities.checkNumber( "dy2", dy2 );

		if ( result ) {
			checkObject( "result", result );
		}

		const xint = intersectRects_xint;
		const yint = intersectRects_yint;

		if (
			!b8.Utilities.intersectIntervals(
				r1.x + dx1,
				r1.x + dx1 + r1.w - 1,
				r2.x + dx2,
				r2.x + dx2 + r2.w - 1, xint
			)
		) {
			return false;
		}

		if (
			!b8.Utilities.intersectIntervals(
				r1.y + dy1,
				r1.y + dy1 + r1.h - 1,
				r2.y + dy2,
				r2.y + dy2 + r2.h - 1, yint
			)
		) {
			return false;
		}

		if ( result ) {
			result.x = xint.start;
			result.w = xint.end - xint.start + 1;
			result.y = yint.start;
			result.h = yint.end - yint.start + 1;
		}

		return true;

	}

	const intersectRects_xint = {};
	const intersectRects_yint = {};


	/**
	 * Converts a string to a pretty URL-friendly format.
	 *
	 * @param {string} uglyStr - The string to convert.
	 * @returns {string} The pretty string.
	 */
	b8.Utilities.makeUrlPretty = function( uglyStr ) {

		b8.Utilities.checkString( "uglyStr", uglyStr );

		let str = uglyStr;

		// Convert to lowercase
		str = str.toLowerCase();

		// Replace spaces and slashes with hyphens
		str = str.replace( /[\s/]+/g, '-' );

		// Remove all non-url-safe characters except hyphens
		str = str.replace( /[^a-z0-9\-]+/g, '' );

		// Remove multiple consecutive hyphens
		str = str.replace( /-+/g, '-' );

		// Trim hyphens from start and end
		str = str.replace( /^-+|-+$/g, '' );

		return str;

	}


	/**
	 * Performs a deep merge of objects and returns new object. Does not modify
	 * objects (immutable) and merges arrays via concatenation.
	 *
	 * @param {...object} objects - Objects to merge
	 * @returns {object} New object with merged key/values
	 */
	b8.Utilities.deepMerge = function( ...objects ) {

		const isObject = obj => obj && typeof obj === 'object';

		return objects.reduce(
			( prev, obj ) => {
				Object.keys( obj ).forEach(
					( key ) => {

						const existingValue = prev[ key ];
						const newValue = obj[ key ];

						if ( Array.isArray( existingValue ) && Array.isArray( newValue ) ) {
							prev[ key ] = existingValue.concat( ...newValue );
						}
						else if ( isObject( existingValue ) && isObject( newValue ) ) {
							prev[ key ] = b8.Utilities.deepMerge( existingValue, newValue );
						}
						else {
							prev[ key ] = newValue;
						}
					}
				);

				return prev;
			},
			{}
		);

	}


	/**
	 * Deep merge objects and returns new object. Does not modify objects.
	 * Merges arrays by index.
	 *
	 * @param {...object} objects - Objects to merge
	 * @returns {object} New object with merged key/values
	 */
	b8.Utilities.deepMergeByIndex = function( ...objects ) {

		const isObject = obj => obj && typeof obj === 'object';

		return objects.reduce(
			( prev, obj ) => {
				Object.keys( obj ).forEach(
					( key ) => {

						const existingValue = prev[ key ];
						const newValue = obj[ key ];

						if ( Array.isArray( existingValue ) && Array.isArray( newValue ) ) {
							prev[ key ] = mergeArraysByIndex( existingValue, newValue );
						}
						else if ( isObject( existingValue ) && isObject( newValue ) ) {
							prev[ key ] = b8.Utilities.deepMergeByIndex( existingValue, newValue );
						}
						else {
							prev[ key ] = newValue;
						}
					}
				);
				return prev;
			},
			{}
		);
	};


	/**
	 * Pads a number with leading zeros to the specified length.
	 *
	 * Does not support negative numbers.
	 *
	 * @param {number} number - The number to pad.
	 * @param {number} length - The desired length of the output string.
	 * @returns {string} - The padded number as a string.
	 */
	b8.Utilities.padWithZeros = function( number, length ) {

		b8.Utilities.checkNumber( "number", number );
		b8.Utilities.checkInt( "length", length );

		if ( number < 0 ) {
			b8.Utilities.fatal( "padWithZeros does not support negative numbers" );
		}

		return number.toString().padStart( length, '0' );

	}


	/**
	 * Generate a new custom event.
	 *
	 * @param {string} eventName - The name of the event.
	 * @param {Object} [detail={}] - The event detail.
	 * @param {EventTarget} [target=document] - The target of the event.
	 * @returns {void}
	 */
	b8.Utilities.event = function( eventName, detail = {}, target = document ) {

		b8.Utilities.checkString( "eventName", eventName );
		b8.Utilities.checkObject( "detail", detail );
		b8.Utilities.checkObject( "target", target );

		// Prefix event name with b8.
		eventName = `b8.${eventName}`;

		// Create a custom event.
		const event = new CustomEvent( eventName, { detail } );

		// Dispatch the event.
		target.dispatchEvent( event );

	};


	/**
	 * Utility function to repeat an array a specified number of times.
	 *
	 * @param {Array} array - The array to repeat.
	 * @param {number} times - The number of times to repeat the array.
	 * @returns {Array} The repeated array.
	 */
	b8.Utilities.repeatArray = function( array, times ) {

		b8.Utilities.checkArray( "array", array );
		b8.Utilities.checkInt( "times", times, 0 );

		return Array( times ).fill().flatMap( () => array );

	};


	/**
	 * Downloads a file using a URL or data URI.
	 *
	 * @param {string} filename - The filename to save as.
	 * @param {string} src - The file URL, blob URL, or data URI.
	 * @returns {void}
	 */
	b8.Utilities.downloadFile = function( filename = '', src = '' ) {

		b8.Utilities.checkString( "filename", filename );
		b8.Utilities.checkString( "src", src );

		// Create a link element to use to download the image.
		const element = document.createElement( 'a' );
		element.setAttribute( 'href', src );
		element.setAttribute( 'download', filename );

		// Append the element to the body.
		document.body.appendChild( element );

		// Click the link to download.
		element.click();

		// Tidy up.
		document.body.removeChild( element );

	};


	/**
	 * Encodes data using CBOR and Base64.
	 *
	 * This only works for data types up to around 64k in size due to the use of
	 * btoa().
	 *
	 * @param {any} data - The data to encode.
	 * @returns {string} The encoded data as a Base64 string.
	 */
	b8.Utilities.encodeData = function( data ) {

		const cborString = CBOR.encode( data );
		const encodedString = btoa( String.fromCharCode.apply( null, new Uint8Array( cborString ) ) );
		return encodedString;

	};


	/**
	 * Decodes data from a Base64 string using CBOR.
	 *
	 * @param {string} data - The Base64 encoded data.
	 * @returns {any} The decoded data.
	 */
	b8.Utilities.decodeData = function( data ) {

		// Step 1: Decode the Base64 string back to a binary string
		const binaryString = atob( data );

		// Step 2: Convert the binary string to a Uint8Array
		const byteArray = new Uint8Array( binaryString.length );
		for ( let i = 0; i < binaryString.length; i++ ) {
			byteArray[ i ] = binaryString.charCodeAt( i );
		}

		// Step 3: Convert the Uint8Array to an ArrayBuffer
		const arrayBuffer = byteArray.buffer;

		// Step 4: Use CBOR.decode to convert the byte array back to the original data structure
		return CBOR.decode( arrayBuffer );

	};


	/**
	 * Merges two arrays by index, filling in missing indices with values from
	 * the default array.
	 *
	 * @param {Array} targetArr - The target array to merge into.
	 * @param {Array} defaultArr - The default array to merge from.
	 * @returns {Array} The merged array.
	 */
	function mergeArraysByIndex( Arr1, Arr2 ) {

		for ( let i = 0; i < Arr2.length; i++ ) {
			Arr1[ i ] = Arr2[ i ];
		}

		return Arr1;

	}


} )( b8 );

( function( b8 ) {

	/**
	 * Immediate-mode UI helpers.
	 *
	 * The UI system follows BeepMini’s ephemeral rendering model:
	 * - UI elements are declared during rendering
	 * - No persistent widget state is stored
	 * - Input is resolved immediately and consumed
	 *
	 * This keeps UI logic simple, predictable, and stateless.
	 */
	b8.UI = {};


	/**
	 * Registers a clickable region at the current cursor position.
	 *
	 * A region is defined in tile space using the current cursor as
	 * the top-left corner.
	 *
	 * This uses the input system’s tap model:
	 * - A tap persists until consumed
	 * - Only one region can consume a tap
	 *
	 * @param {Object} opts
	 * @param {number} [opts.width=1] Width in tiles
	 * @param {number} [opts.height=1] Height in tiles
	 * @param {boolean} [opts.disabled=false] Whether the region is inactive
	 * @returns {boolean} True if this region consumed the tap
	 */
	b8.UI.region = function( opts = {} ) {

		const rect = getCurrentRect_( opts );

		if ( rect.disabled ) return false;

		return consumeTapInRect_( rect );

	};


	/**
	 * Draws a simple text button at the current cursor position.
	 *
	 * @param {Object} opts
	 * @param {number} [opts.width=1]
	 * @param {number} [opts.height=1]
	 * @param {string} [opts.label=""]
	 * @param {string} [opts.style="flat"]
	 * @param {boolean} [opts.disabled=false]
	 * @returns {boolean}
	 */
	b8.UI.textButton = function( opts = {} ) {

		const col = b8.Core.drawState.cursorCol;
		const row = b8.Core.drawState.cursorRow;
		const width = opts.width ?? 1;
		const height = opts.height ?? 1;
		const label = opts.label ?? "";
		const style = opts.style ?? "plain";
		const disabled = !!opts.disabled;

		drawTextButton_( col, row, width, height, label, style, disabled );

		return b8.UI.region(
			{
				width,
				height,
				disabled,
			}
		);

	};


	/**
	 * Draws a simple icon button at the current cursor position.
	 *
	 * @param {Object} opts
	 * @param {number} [opts.width=1]
	 * @param {number} [opts.height=1]
	 * @param {number} opts.icon Character / tile index
	 * @param {boolean} [opts.disabled=false]
	 * @returns {boolean}
	 */
	b8.UI.iconButton = function( opts = {} ) {

		const col = b8.Core.drawState.cursorCol;
		const row = b8.Core.drawState.cursorRow;
		const width = opts.width ?? 1;
		const height = opts.height ?? 1;
		const icon = opts.icon ?? 0;
		const style = opts.style ?? "plain";
		const disabled = !!opts.disabled;

		drawIconButton_( col, row, width, height, icon, style, disabled );

		return b8.UI.region( {
			width,
			height,
			disabled,
		} );

	}


	/**
	 * Draws a progress bar. Uses the current background and foreground colours
	 * for the background of the bar and the bar fill.
	 *
	 * @param {number} [length=5] - The length of the progress bar.
	 * @param {number} [percent=0] - The percentage of the progress bar to fill.
	 * @return {void}
	 */
	b8.UI.progressBar = function( length = 5, percent = 0 ) {

		const col = b8.col();
		const row = b8.row();
		const pixelWidth = length * b8.CONFIG.CHR_WIDTH;

		const oldFg = b8.Core.drawState.fgColor;
		const oldBg = b8.Core.drawState.bgColor;

		// Draw the background.
		b8.color( oldBg );
		b8.fillRect(
			col * b8.CONFIG.CHR_WIDTH,
			row * b8.CONFIG.CHR_HEIGHT,
			pixelWidth,
			b8.CONFIG.CHR_HEIGHT
		);

		// Clamp percent to the range [0, 1].
		if ( percent <= 0 ) return;
		if ( percent > 1 ) percent = 1;

		// Draw the filled portion. The width is determined by the percentage of the
		// progress bar to fill.
		// Length in pixels of the filled portion.
		const filledPixels = Math.round( pixelWidth * percent );
		b8.color( oldFg, oldBg );
		b8.fillRect(
			col * b8.CONFIG.CHR_WIDTH,
			row * b8.CONFIG.CHR_HEIGHT,
			filledPixels,
			b8.CONFIG.CHR_HEIGHT
		);

	}


	/**
	 * Builds a rectangle from the current cursor position.
	 *
	 * @param {Object} opts
	 * @returns {{col:number,row:number,width:number,height:number,disabled:boolean}}
	 */
	function getCurrentRect_( opts = {} ) {

		return {
			col: b8.Core.drawState.cursorCol,
			row: b8.Core.drawState.cursorRow,
			width: opts.width ?? 1,
			height: opts.height ?? 1,
			disabled: !!opts.disabled,
		};

	}


	/**
	 * Tests whether a pending tap falls within a rectangle.
	 * If it does, the tap is consumed.
	 *
	 * This ensures:
	 * - One tap triggers only one UI element
	 * - Input remains deterministic and frame-independent
	 *
	 * @param {Object} rect
	 * @returns {boolean}
	 */
	function consumeTapInRect_( rect ) {

		if ( !b8.Input.hasTap() ) return false;

		const tap = b8.Input.tapTile();

		const hit =
			tap.col >= rect.col &&
			tap.col < rect.col + rect.width &&
			tap.row >= rect.row &&
			tap.row < rect.row + rect.height;

		if ( !hit ) return false;

		b8.Input.consumeTap();
		return true;

	}


	/**
	 * Draws the base button background.
	 *
	 * @param {number} col
	 * @param {number} row
	 * @param {number} width
	 * @param {number} height
	 * @returns {{fg:number,bg:number}} Previous colours
	 */
	function drawButtonBase_( width, height, style = "plain" ) {

		const oldFg = b8.Core.drawState.fgColor;
		const oldBg = b8.Core.drawState.bgColor;

		b8.TextRenderer.printStyledBox(
			width,
			height,
			{
				style
			}
		);

		return { oldFg, oldBg };

	}


	/**
	 * Draws a filled text button using tile cells.
	 *
	 * The button uses the current draw colours.
	 * The label is centered within the region.
	 *
	 * @param {number} col
	 * @param {number} row
	 * @param {number} width
	 * @param {number} height
	 * @param {string} label
	 * @param {string} style
	 * @param {boolean} disabled
	 * @returns {void}
	 */
	function drawTextButton_( col, row, width, height, label, style, disabled ) {

		const oldFg = b8.Core.drawState.fgColor;
		const oldBg = b8.Core.drawState.bgColor;

		drawButtonBase_( width, height, style );

		const labelSize = b8.TextRenderer.measure( label );
		const labelRow = row + ( height / 2 ) - ( labelSize.rows / 2 );

		b8.locate( col, labelRow );

		if ( disabled ) {
			b8.color( 8, oldBg );
		}

		b8.printCentered( label, width );

		b8.color( oldFg, oldBg );
		b8.locate( col, row );

	}


	/**
	 * Draws a button with a single character icon.
	 *
	 * @param {number} col
	 * @param {number} row
	 * @param {number} width
	 * @param {number} height
	 * @param {number} char Tile index / character code
	 * @param {boolean} disabled
	 * @returns {void}
	 */
	function drawIconButton_( col, row, width, height, icon, style, disabled ) {

		const oldFg = b8.Core.drawState.fgColor;
		const oldBg = b8.Core.drawState.bgColor;

		drawButtonBase_( width, height, style );

		const iconCol = col + ( width / 2 ) - 0.5;
		const iconRow = row + ( height / 2 ) - 0.5;

		b8.locate( iconCol, iconRow );

		if ( disabled ) {
			b8.color( 8, -1 );
		}

		b8.printChar( icon );

		b8.color( oldFg, oldBg );
		b8.locate( col, row );

	}

} )( b8 );
( function( b8 ) {

	/**
	 * A collection of functions for working with tilemaps.
	 * The tilemaps are created with the b8 Tilemap Editor.
	 *
	 * The tilemap format is a multi-dimensional array of arrays.
	 * The tile array is in the format:
	 * [0] = tile character code.
	 * [1] = foreground color code.
	 * [2] = background color code.
	 * [3] = collision flag.
	 * [4] = additional data.
	 */
	b8.Tilemap = {};

	b8.Tilemap.MAP_CHAR = 0;
	b8.Tilemap.MAP_FG = 1;
	b8.Tilemap.MAP_BG = 2;
	b8.Tilemap.MAP_COLLISION = 3;
	b8.Tilemap.MAP_DATA = 4;

	const WATER_TILES = [ 1, 188, 206 ];


	// Define a mapping from bitmask value to your desired tile name or index.
	const wallTiles = {
		'solid': {
			tiles: {
				0: 1,  // wall_isolated.
				1: 1,  // wall_end_bottom.
				2: 1,  // wall_end_left.
				3: 36,  // wall_corner_bottomLeft.
				4: 1,  // wall_end_top.
				5: 1,  // wall_vertical.
				6: 18,  // wall_corner_topLeft.
				7: 1,  // wall_t_right.
				8: 1,  // wall_end_right.
				9: 37,  // wall_corner_bottomRight.
				10: 1, // wall_horizontal.
				11: 1, // wall_t_bottom.
				12: 19, // wall_corner_topRight.
				13: 1, // wall_t_left.
				14: 1, // wall_t_top.
				15: 1  // wall_cross.
			}
		},
		'rounded': {
			tiles: {
				0: 1,  // wall_isolated.
				1: 42,  // wall_end_bottom.
				2: 23,  // wall_end_left.
				3: 36,  // wall_corner_bottomLeft.
				4: 41,  // wall_end_top.
				5: 1,  // wall_vertical.
				6: 18,  // wall_corner_topLeft.
				7: 1,  // wall_t_right.
				8: 24,  // wall_end_right.
				9: 37,  // wall_corner_bottomRight.
				10: 1, // wall_horizontal.
				11: 1, // wall_t_bottom.
				12: 19, // wall_corner_topRight.
				13: 1, // wall_t_left.
				14: 1, // wall_t_top.
				15: 1  // wall_cross.
			}
		},
		'half': {
			tiles: {
				0: 128,  // wall_isolated.
				1: 75,  // wall_end_bottom.
				2: 58,  // wall_end_left.
				3: 93,  // wall_corner_bottomLeft.
				4: 75,  // wall_end_top.
				5: 75,  // wall_vertical.
				6: 57,  // wall_corner_topLeft.
				7: 129,  // wall_t_right.
				8: 58,  // wall_end_right.
				9: 95,  // wall_corner_bottomRight.
				10: 58, // wall_horizontal.
				11: 111, // wall_t_bottom.
				12: 59, // wall_corner_topRight.
				13: 130, // wall_t_left.
				14: 112, // wall_t_top.
				15: 113  // wall_cross.
			}
		},
		'half_rounded': {
			tiles: {
				0: 128,  // wall_isolated.
				1: 148,  // wall_end_bottom.
				2: 166,  // wall_end_left.
				3: 93,  // wall_corner_bottomLeft.
				4: 149,  // wall_end_top.
				5: 75,  // wall_vertical.
				6: 57,  // wall_corner_topLeft.
				7: 129,  // wall_t_right.
				8: 167,  // wall_end_right.
				9: 95,  // wall_corner_bottomRight.
				10: 58, // wall_horizontal.
				11: 111, // wall_t_bottom.
				12: 59, // wall_corner_topRight.
				13: 130, // wall_t_left.
				14: 112, // wall_t_top.
				15: 113  // wall_cross.
			}
		},
		'pipe': {
			tiles: {
				0: 128,  // wall_isolated.
				1: 148,  // wall_end_bottom.
				2: 166,  // wall_end_left.
				3: 93,  // wall_corner_bottomLeft.
				4: 149,  // wall_end_top.
				5: [ 75, 77 ],  // wall_vertical.
				6: 57,  // wall_corner_topLeft.
				7: 129,  // wall_t_right.
				8: 167,  // wall_end_right.
				9: 95,  // wall_corner_bottomRight.
				10: [ 58, 94 ], // wall_horizontal.
				11: 111, // wall_t_bottom.
				12: 59, // wall_corner_topRight.
				13: 130, // wall_t_left.
				14: 112, // wall_t_top.
				15: [ 113, 131, 76 ]  // wall_cross.
			}
		},
		'thin': {
			tiles: {
				0: 110,  // wall_isolated.
				1: 173,  // wall_end_bottom.
				2: 172,  // wall_end_left.
				3: 164,  // wall_corner_bottomLeft.
				4: 154,  // wall_end_top.
				5: 72,  // wall_vertical.
				6: 146,  // wall_corner_topLeft.
				7: 126,  // wall_t_right.
				8: 155,  // wall_end_right.
				9: 165,  // wall_corner_bottomRight.
				10: 55, // wall_horizontal.
				11: 108, // wall_t_bottom.
				12: 147, // wall_corner_topRight.
				13: 127, // wall_t_left.
				14: 109, // wall_t_top.
				15: 73  // wall_cross.
			}
		},
		'barrier': {
			tiles: {
				0: 511,  // wall_isolated.
				1: 493,  // wall_end_bottom.
				2: 508,  // wall_end_left.
				3: 490,  // wall_corner_bottomLeft.
				4: 457,  // wall_end_top.
				5: 475,  // wall_vertical.
				6: 454,  // wall_corner_topLeft.
				7: 472,  // wall_t_right.
				8: 510,  // wall_end_right.
				9: 492,  // wall_corner_bottomRight.
				10: 509, // wall_horizontal.
				11: 491, // wall_t_bottom.
				12: 456, // wall_corner_topRight.
				13: 474, // wall_t_left.
				14: 455, // wall_t_top.
				15: 473  // wall_cross.
			}
		},
		'water': {
			edgeMode: "connect",
			tiles: {
				0: 519,  // wall_isolated.
				1: 532,  // wall_end_bottom.
				2: 535,  // wall_end_left.
				3: [ 36, 178 ],  // wall_corner_bottomLeft.
				4: 514,  // wall_end_top.
				5: WATER_TILES,  // wall_vertical.
				6: [ 18, 160 ],  // wall_corner_topLeft.
				7: WATER_TILES,  // wall_t_right.
				8: 536,  // wall_end_right.
				9: [ 37, 179 ],  // wall_corner_bottomRight.
				10: WATER_TILES, // wall_horizontal.
				11: WATER_TILES, // wall_t_bottom.
				12: [ 19, 161 ], // wall_corner_topRight.
				13: WATER_TILES, // wall_t_left.
				14: WATER_TILES, // wall_t_top.
				15: WATER_TILES  // wall_cross.
			}
		}
	};


	/**
	 * Convert a tilemap array to a string.
	 *
	 * This string can be loaded with b8.Tilemap.load.
	 *
	 * @param {Array} tilemap - The tilemap array to save.
	 * @returns {string} The encoded string
	 */
	b8.Tilemap.save = function( tilemap ) {

		b8.Utilities.checkArray( "tilemap", tilemap );

		return b8.Utilities.encodeData( tilemap );

	}


	/**
	 * Load a tilemap array from a string.
	 *
	 * @param {string} data The encoded string
	 * @returns {Array} The tilemap array
	 */
	b8.Tilemap.load = function( data ) {

		b8.Utilities.checkString( "data", data );

		return b8.Utilities.decodeData( data );

	}


	/**
	 * Visit every drawable tile in an area of a tilemap.
	 *
	 * Tilemaps can be placed on either the character grid or the pixel grid, but
	 * their source selection and tile colour rules are identical. Keeping those
	 * rules here prevents the public renderers from drifting apart.
	 *
	 * @param {Array} tilemap The tilemap array to draw.
	 * @param {number} tileStartCol Tile column to start drawing from.
	 * @param {number} tileStartRow Tile row to start drawing from.
	 * @param {number|null} width The width of the tilemap area to draw, in tiles.
	 * @param {number|null} height The height of the tilemap area to draw, in tiles.
	 * @param {Function} drawSprite Draws a normal tile at its position relative to the selected area.
	 * @param {Function} drawVfx Draws a visual-effect tile at its position relative to the selected area.
	 * @param {Function|null} [startDrawRow=null] Runs before each tilemap row is drawn.
	 * @returns {void}
	 */
	const drawTilemap_ = function( tilemap, tileStartCol, tileStartRow, width, height, drawSprite, drawVfx, startDrawRow = null ) {

		b8.Utilities.checkArray( "tilemap", tilemap );
		b8.Utilities.checkInt( "tileStartCol", tileStartCol );
		b8.Utilities.checkInt( "tileStartRow", tileStartRow );

		if ( !width ) {
			width = tilemap[ 0 ].length;
		}

		if ( !height ) {
			height = tilemap.length;
		}

		b8.Utilities.checkInt( "width", width );
		b8.Utilities.checkInt( "height", height );

		for ( let tileRow = tileStartRow; tileRow < tileStartRow + height; tileRow++ ) {
			if ( startDrawRow ) startDrawRow( tileRow - tileStartRow );

			for ( let tileCol = tileStartCol; tileCol < tileStartCol + width; tileCol++ ) {

				if ( !tilemap[ tileRow ] || tilemap[ tileRow ][ tileCol ] == null ) continue;

				const tile = tilemap[ tileRow ][ tileCol ];

				// Saved maps may contain numeric placeholders for empty cells. Only
				// complete tile arrays have character and colour data to render.
				if ( !Array.isArray( tile ) || tile.length < 3 ) continue;

				b8.color(
					tile[ b8.Tilemap.MAP_FG ],
					tile[ b8.Tilemap.MAP_BG ]
				);

				const tileChar = tile[ b8.Tilemap.MAP_CHAR ];
				const col = tileCol - tileStartCol;
				const row = tileRow - tileStartRow;

				if ( typeof tileChar === 'string' && tileChar.startsWith( 'vfx:' ) ) {
					drawVfx( tileChar.substring( 4 ), col, row );
					continue;
				}

				drawSprite( tileChar, col, row );

			}
		}

	};


	/**
	 * Draw a tilemap array to the screen.
	 *
	 * @param {Array} tilemap The tilemap array to draw.
	 * @param {number} [tileStartCol=0] Tile column to start drawing from.
	 * @param {number} [tileStartRow=0] Tile row to start drawing from.
	 * @param {number} [width=null] The width of the tilemap to draw.
	 * @param {number} [height=null] The height of the tilemap to draw.
	 * @returns {void}
	 */
	b8.Tilemap.draw = function( tilemap, tileStartCol = 0, tileStartRow = 0, width = null, height = null ) {

		const startRow = b8.Core.drawState.cursorRow;
		const startCol = b8.Core.drawState.cursorCol;

		drawTilemap_( tilemap, tileStartCol, tileStartRow, width, height, function( tileChar ) {
			b8.printChar( tileChar );
		}, function( animationName ) {

			b8.Vfx.draw( animationName, b8.Core.startTime );

		}, function( row ) {

			// Position the cursor at the start of each tilemap row.
			b8.locate( startCol, startRow + row );

		} );

	};


	/**
	 * Draw a tilemap at an arbitrary pixel position.
	 *
	 * This uses the same tilemap data as b8.Tilemap.draw, so maps returned by
	 * b8.Tilemap.load and b8.Tilemap.createFromArray need no conversion. The
	 * x and y coordinates position the top-left corner of the selected map area;
	 * each following tile is placed one tile-width or tile-height farther along.
	 * Unlike b8.Tilemap.draw, this function does not move the text cursor.
	 *
	 * @param {Array} tilemap The tilemap array to draw.
	 * @param {number} x The pixel x-coordinate at which to draw the map.
	 * @param {number} y The pixel y-coordinate at which to draw the map.
	 * @param {number} [tileStartCol=0] Tile column to start drawing from.
	 * @param {number} [tileStartRow=0] Tile row to start drawing from.
	 * @param {number} [width=null] The width of the tilemap area to draw, in tiles.
	 * @param {number} [height=null] The height of the tilemap area to draw, in tiles.
	 * @returns {void}
	 */
	b8.Tilemap.drawAt = function( tilemap, x, y, tileStartCol = 0, tileStartRow = 0, width = null, height = null ) {

		b8.Utilities.checkNumber( "x", x );
		b8.Utilities.checkNumber( "y", y );
		drawTilemap_( tilemap, tileStartCol, tileStartRow, width, height, function( tileChar, col, row ) {

			const tileX = x + col * b8.CONFIG.CHR_WIDTH;
			const tileY = y + row * b8.CONFIG.CHR_HEIGHT;

			b8.spr( tileChar, tileX, tileY );

		}, function( animationName, col, row ) {

			const tileX = x + col * b8.CONFIG.CHR_WIDTH;
			const tileY = y + row * b8.CONFIG.CHR_HEIGHT;
			const cursorCol = b8.Core.drawState.cursorCol;
			const cursorRow = b8.Core.drawState.cursorRow;

			// Vfx.spr shares its renderer with cursor-based drawing, so keep this
			// pixel renderer from changing the caller's cursor position.
			b8.Vfx.spr( animationName, b8.Core.startTime, tileX, tileY );
			b8.Core.drawState.cursorCol = cursorCol;
			b8.Core.drawState.cursorRow = cursorRow;

		} );

	};


	/**
	 * Create an empty tilemap array of the specified size.
	 *
	 * @param {number} width The width of the tilemap.
	 * @param {number} height The height of the tilemap.
	 * @returns {Array} The empty tilemap array.
	 */
	b8.Tilemap.createEmptyTilemap = function( width, height ) {

		let layout = [];

		for ( let y = 0; y < height; y++ ) {
			layout[ y ] = [];
			for ( let x = 0; x < width; x++ ) {

				layout[ y ][ x ] = b8.Tilemap.getDefaultTile();

			}
		}

		return layout;

	};


	/**
	 * Shift and wrap a tilemap array by the specified amount.
	 *
	 * @param {Array} tilemap The tilemap array to shift.
	 * @param {number} dx The amount to shift the tilemap in the x direction.
	 * @param {number} dy The amount to shift the tilemap in the y direction.
	 * @returns {void}
	 */
	b8.Tilemap.shift = function( tilemap, dx, dy ) {

		b8.Utilities.checkArray( "tilemap", tilemap );
		b8.Utilities.checkNumber( "dx", dx );
		b8.Utilities.checkNumber( "dy", dy );

		const width = tilemap[ 0 ].length;
		const height = tilemap.length;

		const newTilemap = b8.Tilemap.createEmptyTilemap( width, height );

		for ( let y = 0; y < height; y++ ) {
			for ( let x = 0; x < width; x++ ) {

				const newX = ( x + dx + width ) % width;
				const newY = ( y + dy + height ) % height;
				newTilemap[ newY ][ newX ] = [ ...tilemap[ y ][ x ] ];

			}
		}

		return newTilemap;

	};


	/**
	 * Resize a tilemap array to the specified width and height.
	 *
	 * @param {Array} tilemap The tilemap array to resize.
	 * @param {number} width The new width of the tilemap.
	 * @param {number} height The new height of the tilemap.
	 * @returns {Array} The resized tilemap array.
	 */
	b8.Tilemap.resize = function( tilemap, width, height ) {

		b8.Utilities.checkArray( "tilemap", tilemap );
		b8.Utilities.checkInt( "width", width );
		b8.Utilities.checkInt( "height", height );

		const newTilemap = b8.Tilemap.createEmptyTilemap( width, height );

		for ( let y = 0; y < height; y++ ) {
			for ( let x = 0; x < width; x++ ) {
				if ( tilemap[ y ] && tilemap[ y ][ x ] ) {

					newTilemap[ y ][ x ] = [ ...tilemap[ y ][ x ] ];

				}
			}
		}

		return newTilemap;

	};


	/**
	 * Get the default tile for a tilemap.
	 *
	 * @returns {Array} The default tile.
	 */
	b8.Tilemap.getDefaultTile = function() {

		return [
			0, // Tile
			15, // Fg
			0, // Bg
			false, // Collision
			{} // Data
		];

	};


	/**
	 * Set a tile in the tilemap array at the specified coordinates.
	 * This function modifies the tilemap array in place.
	 *
	 * @param {Array} tilemap The tilemap array to modify.
	 * @param {number} col The x coordinate of the tile to set.
	 * @param {number} row The y coordinate of the tile to set.
	 * @param {Array} tile The tile data to set at the specified coordinates.
	 * @returns {void}
	 */
	b8.Tilemap.setTile = function( tilemap, col, row, tile = null ) {

		b8.Utilities.checkArray( "tilemap", tilemap );
		b8.Utilities.checkInt( "col", col );
		b8.Utilities.checkInt( "row", row );

		if ( !tile ) {
			b8.Utilities.fatal( "Tile data is required." );
		}

		if ( !tilemap[ row ] ) {
			b8.Utilities.fatal( `Row ${row} does not exist in the tilemap.` );
		}

		if ( typeof tilemap[ row ][ col ] === "undefined" ) {
			b8.Utilities.fatal( `Column ${col} does not exist in row ${row} of the tilemap.` );
		}

		tilemap[ row ][ col ] = b8.Tilemap.normaliseTile( tile );

	};


	/**
	 * Convert a tile definition to a tile array.
	 *
	 * The tile definition can be an array or an object with properties.
	 * This function ensures that the tile array is in the correct format and all properties are set.
	 *
	 * @param {Array|Object} tile The tile definition to convert. t.t = tile id, t.fg = foreground color, t.bg = background color, t.coll = collision flag, t.data = additional 	data.
	 * @returns {Array} The converted tile array.
	 */
	b8.Tilemap.normaliseTile = function( tile ) {

		const newTile = b8.Tilemap.getDefaultTile();

		if ( Array.isArray( tile ) ) {
			for ( let i = 0; i < newTile.length; i++ ) {
				if ( typeof tile[ i ] !== "undefined" ) {
					newTile[ i ] = tile[ i ];
				}
			}

			return newTile;
		}

		if ( tile && typeof tile === "object" ) {
			if ( typeof tile.t !== "undefined" ) newTile[ 0 ] = tile.t;
			if ( typeof tile.fg !== "undefined" ) newTile[ 1 ] = tile.fg;
			if ( typeof tile.bg !== "undefined" ) newTile[ 2 ] = tile.bg;
			if ( typeof tile.coll !== "undefined" ) newTile[ 3 ] = !!tile.coll;
			if ( typeof tile.data !== "undefined" ) newTile[ 4 ] = tile.data;

			return newTile;
		}

		return newTile;

	};


	/**
	 * Get a text map and convert it to an array of arrays.
	 *
	 * An example text map might look like:
	 *    #######
	 *    #  1  #
	 *    # ### #
	 *    # 2 2 #
	 *    #######
	 *
	 * The tilemap array will include the tile character code, foreground color,
	 * background color, collision flag, and additional data.
	 *
	 * @param {string} mapText The text map to convert.
	 * @returns {Array} The converted tilemap array.
	 */
	b8.Tilemap.convertFromText = function( mapText ) {

		b8.Utilities.checkString( "text", mapText );

		// Don't trim the text as we want to preserve the whitespace.
		// These may be empty tiles.
		const lines = mapText.split( '\n' );

		// Remove any lines that are just whitespace.
		const filteredLines = lines.filter( line => line !== '' );
		if ( filteredLines.length === 0 ) b8.Utilities.fatal( "No valid lines found in the map text." );

		const map = filteredLines.map( row => row.split( '' ) );

		return map;

	}


	/**
	 * Check the validity of the encoded tilemap data.
	 *
	 * The checks are simple, but reduces liklihood of invalid data being used.
	 *
	 * @param {string} mapText The encoded tilemap data.
	 * @returns {boolean} True if the tilemap is valid, false otherwise.
	 */
	b8.Tilemap.validateTilemap = function( mapText ) {

		b8.Utilities.checkString( "text", mapText );

		const map = b8.Tilemap.load( mapText );

		// Check map is an array of arrays.
		if ( !Array.isArray( map ) || !Array.isArray( map[ 0 ] ) ) {
			return false;
		}

		// Check 0, 0 is a valid tile (is an array and length is greater than 3).
		if ( !Array.isArray( map[ 0 ][ 0 ] ) || map[ 0 ][ 0 ].length <= 3 ) {
			return false;
		}

		return true;

	}


	/**
	 * Create a tilemap from an array of arrays.
	 * The tilePattern is an object that maps tile characters to tile properties.
	 *
	 * @param {Array} grid The grid array to create the tilemap from.
	 * @param {Object} tilePattern The tile pattern object.
	 * @param {Object} [defaultTilePattern=null] The default tile pattern to use for empty tiles.
	 * @returns {Array} The created tilemap array.
	 */
	b8.Tilemap.createFromArray = function( grid, tilePattern, defaultTilePattern = null ) {

		b8.Utilities.checkArray( "grid", grid );
		b8.Utilities.checkObject( "tilePattern", tilePattern );

		if ( defaultTilePattern !== null ) {
			b8.Utilities.checkObject( "defaultTilePattern", defaultTilePattern );
		}

		if ( null === defaultTilePattern ) {
			defaultTilePattern = b8.Tilemap.getDefaultTile();
		}

		const tilemap = [];

		b8.Random.setSeed( grid[ 0 ].join( '' ) );

		for ( let y = 0; y < grid.length; y++ ) {
			tilemap[ y ] = [];
			for ( let x = 0; x < grid[ y ].length; x++ ) {

				// Set default properties.
				tilemap[ y ][ x ] = [ ...defaultTilePattern ];

				// If tile pattern not defined assume tile is empty and continue.
				if ( !tilePattern[ grid[ y ][ x ] ] ) continue;

				const tile = tilePattern[ grid[ y ][ x ] ];

				// Tile character code.
				let tileId = tile.t;

				// If tileId is a string and begins with "wall:" then compute bitmask.
				if ( typeof tileId === "string" && tileId.startsWith( "wall:" ) ) {
					tileId = pickWallTile( x, y, grid, tileId );
				}

				// If is an array of ids then do a weighted pick from those.
				if ( Array.isArray( tileId ) ) {
					tileId = b8.Random.pickWeighted( tileId );
				}

				// Foreground colour.
				if ( typeof tile.fg === 'undefined' ) tile.fg = 15;
				let fg = tile.fg;
				if ( Array.isArray( fg ) ) {
					fg = b8.Random.pickWeighted( fg );
				}

				// Background colour.
				if ( typeof tile.bg === 'undefined' ) tile.bg = 0;
				let bg = tile.bg;
				if ( Array.isArray( bg ) ) {
					bg = b8.Random.pickWeighted( bg );
				}

				tilemap[ y ][ x ] = [
					tileId,
					fg,
					bg,
					tile.coll || false,
					tile.data || {},
				];

			}
		}

		return tilemap;

	}


	/**
	 * Add a wall tile set to the tilemap system.
	 *
	 * The tile set should contain a `tiles` object with up to 16 tile ids,
	 * corresponding to the 16 possible bitmask values.
	 *
	 * Optional properties:
	 * - edgeMode: controls how map boundaries are handled.
	 *
	 * Supported edge modes:
	 * - "open": outside the map does not match anything.
	 * - "connect": outside the map always counts as a matching tile.
	 * - "wrap": outside the map wraps around to the opposite edge.
	 *
	 * @param {string} name The name of the wall tile set. This should match the name used in the tile pattern, e.g. "wall:brick".
	 * @param {Object} tileSet The wall tile set definition.
	 * @param {Object} tileSet.tiles The tile ids for each bitmask value.
	 * @param {string} [tileSet.edgeMode="open"] How map edges are handled: "open", "connect", or "wrap".
	 * @returns {void}
	 */
	b8.Tilemap.addWallTileSet = function( name, tileSet ) {

		b8.Utilities.checkString( "name", name );
		b8.Utilities.checkObject( "tileSet", tileSet );

		if ( wallTiles[ name ] ) {
			b8.Utilities.fatal( `Wall tile set with name ${name} already exists.` );
		}

		wallTiles[ name ] = normaliseWallTileSet( tileSet );

	}


	/**
	 * Get a wall tile set by name.
	 *
	 * @param {string} name The name of the wall tile set to get.
	 * @returns {Array} The wall tile set array.
	 */
	b8.Tilemap.getWallTileSet = function( name ) {

		b8.Utilities.checkString( "name", name );

		if ( !wallTiles[ name ] ) {
			b8.Utilities.fatal( `Wall tile set with name ${name} does not exist.` );
		}

		return { ...wallTiles[ name ] };

	}


	/**
	 * Select a wall tile from a predefined list based on the surrounding tiles.
	 * The grid is the 2D array of tile ids.
	 * The x and y are the coordinates of the tile to check.
	 *
	 * @param {number} x The x coordinate of the tile.
	 * @param {number} y The y coordinate of the tile.
	 * @param {Array} grid The 2D array of tile ids.
	 * @param {string} name The name of the wall tile to select. Picked from the default lists of wall patterns.
	 * @returns {number} The selected wall tile id.
	 */
	function pickWallTile( col, row, grid, name = null ) {

		const tileType = name.substring( 5 );

		if ( !wallTiles[ tileType ] ) {
			b8.Utilities.fatal( "Wall tile type not found: " + tileType );
		}

		const wallTileSet = wallTiles[ tileType ];

		const mask = computeBitmask(
			grid,
			col,
			row,
			wallTileSet.edgeMode || "open"
		);

		return wallTileSet.tiles[ mask ];

	};


	/**
	 * Normalise a wall tile set to ensure it has 16 entries, one for each bitmask value.
	 * If any entries are missing, they will be filled in with the corresponding entry from the default "solid" tile set.
	 *
	 * @param {Array} tileSet The wall tile set to normalise.
	 * @returns {Array} The normalised wall tile set.
	 */
	function normaliseWallTileSet( tileSet ) {

		if ( !tileSet.tiles ) {
			b8.Utilities.fatal( "Wall tile set must include a tiles object. eg { tiles: { 0: 1, 1: 2, ... } }" );
		}

		const defaultTileSet = wallTiles[ 'solid' ];
		const normalisedTileSet = {
			edgeMode: tileSet.edgeMode || "open",
			tiles: {}
		};

		for ( let i = 0; i < 16; i++ ) {
			if ( typeof tileSet.tiles[ i ] === "undefined" ) {
				normalisedTileSet.tiles[ i ] = defaultTileSet.tiles[ i ];
			} else {
				normalisedTileSet.tiles[ i ] = tileSet.tiles[ i ];
			}
		}

		return normalisedTileSet;

	}


	/**
	 * Compute a 4-bit bitmask for a wall tile based on its surrounding tiles.
	 *
	 * The bitmask checks the four adjacent tiles: north, east, south, and west.
	 * If an adjacent tile matches the current map character, the corresponding
	 * bit is added to the mask.
	 *
	 * Edge modes control how tiles outside the map bounds are treated:
	 * - "open": outside the map does not match anything.
	 * - "connect": outside the map always counts as a matching tile.
	 * - "wrap": outside the map wraps around to the opposite edge.
	 *
	 * Bit values: 1 = North, 2 = East, 4 = South, 8 = West.
	 *
	 * @param {Array} grid The 2D array of tile ids.
	 * @param {number} col The x coordinate of the tile.
	 * @param {number} row The y coordinate of the tile.
	 * @param {string} [edgeMode="open"] How map edges are handled: "open", "connect", or "wrap".
	 * @returns {number} The computed bitmask value from 0 to 15.
	 */
	function computeBitmask( grid, col, row, edgeMode = "open" ) {

		let bitmask = 0;
		const tileId = grid[ row ][ col ];

		if ( matchesTile( grid, col, row - 1, tileId, edgeMode ) ) bitmask += 1;
		if ( matchesTile( grid, col + 1, row, tileId, edgeMode ) ) bitmask += 2;
		if ( matchesTile( grid, col, row + 1, tileId, edgeMode ) ) bitmask += 4;
		if ( matchesTile( grid, col - 1, row, tileId, edgeMode ) ) bitmask += 8;

		return bitmask;

	}


	/**
	 * Check whether a tile matches the given tile id.
	 *
	 * If the coordinates are outside the map, the result depends on edgeMode:
	 * - "open": return false.
	 * - "connect": return true.
	 * - "wrap": wrap the coordinates around the map before checking.
	 *
	 * @param {Array} grid The 2D array of tile ids.
	 * @param {number} col The x coordinate to check.
	 * @param {number} row The y coordinate to check.
	 * @param {string|number} tileId The tile id to match against.
	 * @param {string} edgeMode How map edges are handled: "open", "connect", or "wrap".
	 * @returns {boolean} True if the tile matches.
	 */
	function matchesTile( grid, col, row, tileId, edgeMode ) {

		// Wrap coordinates around the map.
		if ( edgeMode === "wrap" ) {

			row = ( row + grid.length ) % grid.length;
			col = ( col + grid[ row ].length ) % grid[ row ].length;

		}

		// Outside map bounds.
		if (
			row < 0 ||
			row >= grid.length ||
			col < 0 ||
			col >= grid[ row ].length
		) {

			if ( edgeMode === "connect" ) {
				return true;
			}

			return false;

		}

		return grid[ row ][ col ] === tileId;

	}

} )( b8 );

( function( b8 ) {

	b8.TextRenderer = {};

	/**
	 * An array of character codes for each character in the chars string.
	 * This is used to look up the index of a character in the chars string.
	 *
	 * @type {number[]}
	 */
	const charMap = [];


	/**
	 * Built-in box style presets.
	 *
	 * Each style defines border tiles and whether the interior should be filled.
	 *
	 * @type {Object<string, Object>}
	 */
	const BOX_STYLES_ = {
		plain: {
			fill: true,
			borders: false,
			borderChar: null,
		},
		border: {
			fill: true,
			borderChar: b8.CONFIG.BORDER_CHAR,
		},
		round: {
			invertBorder: true,
			fill: true,
			borders: {
				NW: 18,
				NE: 19,
				SW: 36,
				SE: 37,
				V: 1,
				H: 1,
			},
		},
	};

	// b8.TextRendererFont for each font, keyed by font name. The default font is called "default".
	b8.TextRenderer.fonts_ = {};

	// Current font. This is never null after initialization. This is a reference
	// to a b8.TextRendererFont object. For a font to be set as current, it must have a
	// character width and height that are INTEGER MULTIPLES of b8.CONFIG.CHR_WIDTH and
	// b8.CONFIG.CHR_HEIGHT, respectively, to ensure the row/column system continues to work.
	b8.TextRenderer.curFont_ = null;

	// Current tiles. This is a reference to a b8.TextRendererFont object.
	b8.TextRenderer.curTiles_ = null;

	// Current visual effects font. This is a reference to a b8.TextRendererFont object.
	b8.TextRenderer.curVfx_ = null;


	/**
	 * Prepares the charMap array.
	 *
	 * This is a list of character codes for each character in the chars string.
	 * This is used to look up the index of a character in the chars string.
	 *
	 * @returns {void}
	 */
	b8.TextRenderer.prepareCharMap = function() {

		let charString = [ ...b8.CONFIG.CHRS ];

		for ( let i = 0; i < charString.length; i++ ) {
			charMap.push( charString[ i ].charCodeAt( 0 ) );
		}

	}


	/**
	 * Initializes the b8.TextRenderer with the default font.
	 *
	 * @returns {Promise<void>}
	 */
	b8.TextRenderer.initAsync = async function() {

		b8.Utilities.log( "b8.TextRenderer init." );

		// Prepare the text font.
		b8.TextRenderer.curFont_ = await b8.TextRenderer.loadFontAsync( "default-thin", b8.CONFIG.FONT_DEFAULT, 0.5, 1 );

		// Prepare the tiles font.
		b8.TextRenderer.curTiles_ = await b8.TextRenderer.loadFontAsync( "tiles", b8.CONFIG.FONT_TILES );

		// Prepare the actors/ player characters.
		b8.TextRenderer.curActors_ = await b8.TextRenderer.loadFontAsync( "actors", b8.CONFIG.FONT_ACTORS );

		// Prepare the visual effects font.
		b8.TextRenderer.curVfx_ = await b8.TextRenderer.loadFontAsync( "vfx", b8.CONFIG.FONT_VFX );

		// Prepare the charMap array.
		b8.TextRenderer.prepareCharMap();

	}


	/**
	 * Loads a new font asynchronously.
	 *
	 * @param {string} fontName - The name of the font.
	 * @param {string} fontImageFile - The URL of the image file for the font.
	 * @param {number} [tileSizeWidthMultiplier=1] - The tile size width multiplier for the font.
	 * @param {number} [tileSizeHeightMultiplier=1] - The tile size height multiplier for the font.
	 * @returns {Promise<void>}
	 */
	b8.TextRenderer.loadFontAsync = async function( fontName, fontImageFile, tileSizeWidthMultiplier = 1, tileSizeHeightMultiplier = 1 ) {

		b8.Utilities.checkString( "fontName", fontName );
		b8.Utilities.checkString( "fontImageFile", fontImageFile );

		const font = new b8.TextRendererFont( fontName, fontImageFile, tileSizeWidthMultiplier, tileSizeHeightMultiplier );
		await font.initAsync();

		b8.TextRenderer.fonts_[ fontName ] = font;

		return font;

	}


	/**
	 * Sets the current font.
	 *
	 * @param {string} fontName - The name of the font to set.
	 * @returns {void}
	 * @throws {Error} If the font is not found or its dimensions are not compatible.
	 */
	b8.TextRenderer.setFont = function( fontName ) {

		const font = b8.TextRenderer.getFontByName( fontName );

		if ( font ) {
			b8.TextRenderer.curFont_ = font;
		}

	}


	/**
	 * Get the current font.
	 *
	 * @returns {b8.TextRendererFont} The current font.
	 */
	b8.TextRenderer.getFont = function() {

		return b8.TextRenderer.curFont_;

	}


	/**
	 * Sets the current tiles font.
	 *
	 * @param {string} fontName - The name of the font to set.
	 * @returns {void}
	 */
	b8.TextRenderer.setTileFont = function( fontName ) {

		const font = b8.TextRenderer.getFontByName( fontName );

		if ( font ) {
			b8.TextRenderer.curTiles_ = font;
		}

	}


	/**
	 * Gets a font by name.
	 *
	 * @param {string} fontName - The name of the font to get.
	 * @returns {b8.TextRendererFont} The font.
	 */
	b8.TextRenderer.getFontByName = function( fontName ) {

		b8.Utilities.checkString( "fontName", fontName );
		const font = b8.TextRenderer.fonts_[ fontName ];

		if ( !font ) {
			b8.Utilities.fatal( `setFont(): font not found: ${fontName}` );
			return;
		}

		return font;

	}


	/**
	 * Prints text at the current cursor position.
	 *
	 * @param {string} text - The text to print.
	 * @param {b8.TextRendererFont} [font=null] - The font to use for printing.
	 * @param {number} [maxWidth=-1] - The maximum width to wrap text at.
	 * @returns {void}
	 */
	b8.TextRenderer.print = function( text, font = null, maxWidth = -1 ) {

		b8.TextRenderer.printFont_ = font || b8.TextRenderer.curFont_;

		// Property validation.
		b8.Utilities.checkString( "text", text );
		b8.Utilities.checkNumber( "maxWidth", maxWidth );
		if ( font !== null ) b8.Utilities.checkObject( "font", font );

		// Wrap text to specified width.
		text = b8.TextRenderer.wrapText( text, maxWidth, font );

		// Store the start location.
		let col = b8.Core.drawState.cursorCol;
		let row = b8.Core.drawState.cursorRow;

		// Store a backup of foreground/background colors and fonts.
		b8.TextRenderer.origFgColor_ = b8.Core.drawState.fgColor;
		b8.TextRenderer.origBgColor_ = b8.Core.drawState.bgColor;
		b8.TextRenderer.origFont_ = b8.TextRenderer.printFont_;

		const colInc = b8.TextRenderer.printFont_.getCharColCount();
		const rowInc = b8.TextRenderer.printFont_.getCharRowCount();

		const initialCol = col;

		for ( let i = 0; i < text.length; i++ ) {

			i = processEscapeSeq_( text, i );
			const ch = text.charCodeAt( i );

			// New line character so set a new line and reset the column.
			if ( ch === 10 ) {

				col = initialCol;
				row += rowInc;

			} else {

				// Get index for the character from charMap.
				const chIndex = charMap.indexOf( ch );

				if ( chIndex >= 0 ) {

					put_(
						chIndex,
						col, row,
						b8.Core.drawState.fgColor, b8.Core.drawState.bgColor,
						b8.TextRenderer.printFont_
					);
					col += colInc;

				}

			}

		}

		// Reset properties.
		b8.Core.drawState.cursorCol = col;
		b8.Core.drawState.cursorRow = row;
		b8.Core.drawState.fgColor = b8.TextRenderer.origFgColor_;
		b8.Core.drawState.bgColor = b8.TextRenderer.origBgColor_;

		b8.Renderer.markDirty();

	}


	/**
	 * Prints text character by character, as in a typewriter.
	 *
	 * @param {string} text - The text to print.
	 * @param {number} [maxWidth=-1] - The width to wrap text at.
	 * @param {number} [delay=0.05] - The delay between characters in seconds.
	 * @param {b8.TextRendererFont} [font=null] - The font to use.
	 * @returns {Promise<void>} Resolves after the text is printed.
	 */
	b8.TextRenderer.printTypewriter = async function( text, maxWidth = -1, delay = 0.05, font = null ) {

		b8.Utilities.checkString( "text", text );
		b8.Utilities.checkNumber( "maxWidth", maxWidth );
		b8.Utilities.checkNumber( "delay", delay );

		const startCol = b8.col();
		const startRow = b8.row();

		text = b8.TextRenderer.wrapText( text, maxWidth );

		for ( let i = 0; i <= text.length; i++ ) {

			// If this is the start of an escape sequence, skip to the end of it.
			if (
				b8.CONFIG.PRINT_ESCAPE_START &&
				text.substring( i, i + b8.CONFIG.PRINT_ESCAPE_START.length ) === b8.CONFIG.PRINT_ESCAPE_START
			) {

				const endPos = text.indexOf( b8.CONFIG.PRINT_ESCAPE_END, i + b8.CONFIG.PRINT_ESCAPE_START.length );

				if ( endPos >= 0 ) {
					i = endPos + b8.CONFIG.PRINT_ESCAPE_END.length;
				}

			}

			const c = text.charCodeAt( i );
			b8.Core.setCursorLocation( startCol, startRow );
			b8.TextRenderer.print( text.substring( 0, i ), font );

			if ( c !== 32 ) {
				await b8.Async.wait( delay );
			}

		}

	}


	/**
	 * Prints text centered within a given width.
	 *
	 * @param {string} text - The text to print.
	 * @param {number} width - The width to center the text within.
	 * @param {b8.TextRendererFont} [font=null] - The font to use.
	 * @returns {void}
	 */
	b8.TextRenderer.printCentered = function( text, width, font = null ) {

		b8.TextRenderer.printFont_ = font || b8.TextRenderer.curFont_;

		b8.Utilities.checkString( "text", text );
		b8.Utilities.checkNumber( "width", width );

		if ( !text ) {
			return;
		}

		const col = b8.Core.drawState.cursorCol;
		const rowInc = b8.TextRenderer.printFont_.getCharRowCount();

		// Split the text into lines.
		text = text.split( "\n" );

		// Remove last item from text if it is empty.
		if ( text[ text.length - 1 ] === "" ) text.pop();

		// Loop through each line of text.
		for ( let i = 0; i < text.length; i++ ) {

			const textWidth = b8.TextRenderer.measure( text[ i ] ).cols;
			const tempCol = col + ( width - textWidth ) / 2;

			b8.Core.drawState.cursorCol = tempCol;
			b8.TextRenderer.print( text[ i ], font, width );

			b8.Core.drawState.cursorRow += rowInc;

		}

		// Reset cursor position.
		b8.Core.drawState.cursorCol = col;

	}


	/**
	 * Prints text right-aligned within a given width.
	 *
	 * @param {string} text - The text to print.
	 * @param {number} width - The width to right-align the text within.
	 * @param {b8.TextRendererFont} [font=null] - The font to use.
	 * @returns {void}
	 */
	b8.TextRenderer.printRight = function( text, width, font = null ) {

		b8.TextRenderer.printFont_ = font || b8.TextRenderer.curFont_;

		b8.Utilities.checkString( "text", text );
		b8.Utilities.checkNumber( "width", width );

		if ( !text ) {
			return;
		}

		const col = b8.Core.drawState.cursorCol;
		const rowInc = b8.TextRenderer.printFont_.getCharRowCount();

		text = b8.TextRenderer.wrapText( text, width );

		// Split the text into lines.
		text = text.split( "\n" );

		// Remove last item from text if it is empty.
		if ( text[ text.length - 1 ] === "" ) text.pop();

		// Loop through each line of text.
		for ( let i = 0; i < text.length; i++ ) {

			let textWidth = b8.TextRenderer.measure( text[ i ] ).cols;
			const tempCol = col + width - textWidth;

			b8.Core.drawState.cursorCol = tempCol;
			b8.TextRenderer.print( text[ i ], font, width );

			b8.Core.drawState.cursorRow += rowInc;

		}

		// Reset cursor position.
		b8.Core.drawState.cursorCol = col;

	}


	/**
	 * Prints a character a specified number of times.
	 *
	 * @param {number} ch - The character to print.
	 * @param {number} n - The number of times to print the character.
	 * @param {b8.TextRendererFont} [font=null] - The font to use.
	 * @returns {void}
	 */
	b8.TextRenderer.printChar = function( ch, n, font = null ) {

		if ( n === undefined || isNaN( n ) ) {
			n = 1;
		}

		b8.Utilities.checkNumber( "ch", ch );
		b8.Utilities.checkNumber( "n", n );

		// Check cursorCol and cursorRow are within bounds.
		if (
			b8.Core.drawState.cursorCol < 0 ||
			b8.Core.drawState.cursorRow < 0 ||
			b8.Core.drawState.cursorCol >= b8.CONFIG.SCREEN_COLS ||
			b8.Core.drawState.cursorRow >= b8.CONFIG.SCREEN_ROWS
		) {
			// No need for an error. Just end silently.
			return;
		}

		while ( n-- > 0 ) {

			put_(
				ch,
				b8.Core.drawState.cursorCol,
				b8.Core.drawState.cursorRow,
				b8.Core.drawState.fgColor,
				b8.Core.drawState.bgColor,
				font
			);

			b8.Core.drawState.cursorCol++;

		}

		b8.Renderer.markDirty();

	}


	/**
	 * Prints a character as a "sprite" at a raw x, y position.
	 *
	 * @param {number} ch - The character to print.
	 * @param {number} x - The x-coordinate.
	 * @param {number} y - The y-coordinate.
	 * @param {b8.TextRendererFont} [font=null] - The font to use.
	 * @param {number} [direction=0] - The direction to face (0=up, 1=right, 2=down, 3=left).
	 * @returns {void}
	 */
	b8.TextRenderer.spr = function( ch, x, y, font = null, direction = 0 ) {

		b8.Utilities.checkNumber( "ch", ch );
		b8.Utilities.checkNumber( "x", x );
		b8.Utilities.checkNumber( "y", y );
		b8.Utilities.checkInt( "direction", direction );
		if ( font !== null ) b8.Utilities.checkObject( "font", font );

		putxy_(
			ch,
			x,
			y,
			b8.Core.drawState.fgColor,
			b8.Core.drawState.bgColor,
			font,
			direction
		);

	}


	/**
	 * Rotates a tile about its centre using the canvas's native image renderer.
	 * Reuses sprite drawing so palette colours and transparent backgrounds work.
	 *
	 * @param {number} ch - The tile character code.
	 * @param {number} x - The unrotated left edge in pixels.
	 * @param {number} y - The unrotated top edge in pixels.
	 * @param {number} degrees - Clockwise rotation in degrees.
	 * @returns {void}
	 */
	b8.TextRenderer.sprRotated = function( ch, x, y, degrees ) {

		const font = b8.TextRenderer.curTiles_;
		const width = font.getCharWidth();
		const height = font.getCharHeight();
		const ctx = b8.Core.offCtx;

		// Preserve the normal sprite path for full turns and avoid extra transforms.
		if ( degrees % 360 === 0 ) {
			b8.TextRenderer.spr( ch, x, y );
			return;
		}

		ctx.save();
		try {
			ctx.imageSmoothingEnabled = false;
			ctx.translate( Math.round( x ) + width / 2, Math.round( y ) + height / 2 );
			ctx.rotate( ( degrees % 360 ) * Math.PI / 180 );

			// Move the tile back around the pivot; putxy_ rounds its coordinates,
			// so use a translation to preserve half-pixel centres for odd tile sizes.
			ctx.translate( -width / 2, -height / 2 );
			b8.TextRenderer.spr( ch, 0, 0 );
		} finally {
			// Restore even if drawing fails, so later sprites are never rotated.
			ctx.restore();
		}

	}


	/**
	 * Draws text at the given pixel coordinates, with no cursor movement.
	 *
	 * @param {number} x - The x-coordinate.
	 * @param {number} y - The y-coordinate.
	 * @param {string} text - The text to draw.
	 * @param {string} [fontName] - The name of the font to use.
	 * @returns {void}
	 */
	b8.TextRenderer.drawText = function( x, y, text, fontName ) {

		b8.Utilities.checkNumber( "x", x );
		b8.Utilities.checkNumber( "y", y );
		b8.Utilities.checkString( "text", text );

		if ( fontName ) {
			b8.Utilities.checkString( "fontName", fontName );
		}

		const x0 = x;
		const font = fontName ? ( b8.TextRenderer.fonts_[ fontName ] || b8.TextRenderer.curFont_ ) : b8.TextRenderer.curFont_;

		if ( !font ) {
			b8.Utilities.warn( `Requested font '${fontName}' not found: not drawing text.` );
			return;
		}

		for ( let i = 0; i < text.length; i++ ) {
			const ch = text.charCodeAt( i );

			if ( ch === 10 ) {

				x = x0;
				y += font.getCharHeight();

			} else {

				// Font images follow CHRS order, not Unicode character codes.
				// Match print(): skip unsupported characters without advancing.
				const chIndex = charMap.indexOf( ch );
				if ( chIndex < 0 ) {
					continue;
				}

				putxy_(
					chIndex,
					x, y,
					b8.Core.drawState.fgColor,
					b8.Core.drawState.bgColor,
					font
				);

				x += font.getCharWidth();

			}

		}

	}


	/**
	 * Measures the dimensions of the text.
	 *
	 * @param {string} text - The text to measure.
	 * @param {b8.TextRendererFont} [font=null] - The font to use for measurement.
	 * @returns {{cols: number, rows: number}} The dimensions of the text.
	 */
	b8.TextRenderer.measure = function( text, font = null ) {

		b8.Utilities.checkString( "text", text );

		font = font || b8.TextRenderer.curFont_;

		if ( "" === text ) {
			return { cols: 0, rows: 0 }; // Special case
		}

		let rows = 1;
		let thisLineWidth = 0;
		let cols = 0;

		for ( let i = 0; i < text.length; i++ ) {
			i = processEscapeSeq_( text, i, true );
			const ch = text.charCodeAt( i );

			if ( ch === 10 ) {
				rows++;
				thisLineWidth = 0;
			} else {
				++thisLineWidth;
				cols = Math.max( cols, thisLineWidth );
			}
		}

		// Adjust the size of the cols and rows based on the size of the font.
		cols = cols * font.getCharColCount();
		rows = rows * font.getCharRowCount();

		return { cols, rows };

	}


	/**
	 * Prints a rectangle of a specified character.
	 *
	 * @param {number} width - The width of the rectangle.
	 * @param {number} height - The height of the rectangle.
	 * @param {number} ch - The character to fill the rectangle with.
	 * @returns {void}
	 */
	b8.TextRenderer.printRect = function( width, height, ch ) {

		b8.Utilities.checkNumber( "width", width );
		b8.Utilities.checkNumber( "height", height );
		b8.Utilities.checkNumber( "ch", ch );

		const startCol = b8.Core.drawState.cursorCol;
		const startRow = b8.Core.drawState.cursorRow;

		for ( let i = 0; i < height; i++ ) {
			b8.Core.drawState.cursorCol = startCol;
			b8.Core.drawState.cursorRow = startRow + i;
			b8.TextRenderer.printChar( ch, width );
		}

		b8.Core.drawState.cursorCol = startCol;
		b8.Core.drawState.cursorRow = startRow;

	}


	/**
	 * Prints a box with borders.
	 *
	 * @param {number} width - The width of the box.
	 * @param {number} height - The height of the box.
	 * @param {boolean} [fill=true] - Whether to fill the box.
	 * @param {number} [borderCh=b8.CONFIG.BORDER_CHAR] - The character to use for the border.
	 * @returns {void}
	 */
	b8.TextRenderer.printBox = function( width, height, fill = true, borderChar = b8.CONFIG.BORDER_CHAR ) {

		b8.Utilities.checkNumber( "width", width );
		b8.Utilities.checkNumber( "height", height );
		b8.Utilities.checkBoolean( "fill", fill );
		b8.Utilities.checkNumber( "borderChar", borderChar );

		const borders = fromBorderChar_( borderChar );

		b8.TextRenderer.drawBox( width, height, fill, borders );

	}


	/**
	 * Draws a box with borders.
	 *
	 * @param {number} width - The width of the box.
	 * @param {number} height - The height of the box.
	 * @param {boolean} [fill=true] - Whether to fill the box.
	 * @param {Object} [borders] - The borders to use.
	 * @param {number} [borders.NW] - The top-left corner character.
	 * @param {number} [borders.NE] - The top-right corner character.
	 * @param {number} [borders.SW] - The bottom-left corner character.
	 * @param {number} [borders.SE] - The bottom-right corner character.
	 * @param {number} [borders.V] - The vertical border character.
	 * @param {number} [borders.H] - The horizontal border character.
	 * @returns {void}
	 */
	b8.TextRenderer.drawBox = function(
		width,
		height,
		fill = true,
		borders = {},
		fillChar = 0,
		options = {}
	) {
		const startCol = b8.Core.drawState.cursorCol;
		const startRow = b8.Core.drawState.cursorRow;

		const oldFg = b8.Core.drawState.fgColor;
		const oldBg = b8.Core.drawState.bgColor;

		const borderFg = options.invertBorder ? oldBg : oldFg;
		const borderBg = options.invertBorder ? oldFg : oldBg;
		const fillFg = options.invertFill ? oldBg : oldFg;
		const fillBg = options.invertFill ? oldFg : oldBg;

		for ( let i = 0; i < height; i++ ) {
			b8.Core.drawState.cursorCol = startCol;
			b8.Core.drawState.cursorRow = startRow + i;

			if ( i === 0 ) {
				b8.color( borderFg, borderBg );
				b8.TextRenderer.printChar( borders.NW );
				b8.TextRenderer.printChar( borders.H, width - 2 );
				b8.TextRenderer.printChar( borders.NE );
			} else if ( i === height - 1 ) {
				b8.color( borderFg, borderBg );
				b8.TextRenderer.printChar( borders.SW );
				b8.TextRenderer.printChar( borders.H, width - 2 );
				b8.TextRenderer.printChar( borders.SE );
			} else {
				b8.color( borderFg, borderBg );
				b8.TextRenderer.printChar( borders.V );

				if ( fill && width > 2 ) {
					b8.color( fillFg, fillBg );
					b8.TextRenderer.printChar( fillChar, width - 2 );
				} else {
					b8.Core.drawState.cursorCol += width - 2;
				}

				b8.color( borderFg, borderBg );
				b8.TextRenderer.printChar( borders.V );
			}
		}

		b8.color( oldFg, oldBg );
		b8.Core.drawState.cursorCol = startCol;
		b8.Core.drawState.cursorRow = startRow;

	}


	/**
	 * Draws a box using a named style preset or explicit border definition.
	 *
	 * @param {number} width The width of the box.
	 * @param {number} height The height of the box.
	 * @param {Object} [options={}] Rendering options.
	 * @param {string} [options.style="plain"] Named style preset.
	 * @param {boolean} [options.fill] Whether to fill the box interior.
	 * @param {Object} [options.borders] Explicit border tiles.
	 * @returns {void}
	 */
	b8.TextRenderer.printStyledBox = function( width, height, options = {} ) {

		const style = resolveBoxStyle_( options.style || "plain" );

		const fill = options.fill ?? style.fill;
		const fillChar = options.fillChar ?? style.fillChar;
		const borders = options.borders ?? style.borders;

		const invertBorder = options.invertBorder ?? style.invertBorder;
		const invertFill = options.invertFill ?? style.invertFill;

		if ( !borders ) {
			b8.TextRenderer.printRect( width, height, fillChar );
			return;
		}

		b8.TextRenderer.drawBox(
			width,
			height,
			fill,
			borders,
			fillChar,
			{
				invertBorder,
				invertFill,
			}
		);

	}


	/**
	 * Wraps text to a given width.
	 *
	 * @param {string} text - The text to wrap.
	 * @param {number} maxWidth - The width to wrap the text to.
	 * @param {b8.TextRendererFont} fontName - The font to use.
	 * @returns {string} The wrapped text.
	 */
	b8.TextRenderer.wrapText = function( text, maxWidth, font = null ) {

		font = font || b8.TextRenderer.curFont_;

		// If 0 or less then don't wrap.
		if ( maxWidth <= 0 ) return text;

		// Split the text into lines.
		const lines = text.split( "\n" );

		// New list of lines.
		const wrappedLines = [];

		for ( const line of lines ) {

			const words = line.split( " " );
			let wrappedLine = "";

			for ( const word of words ) {

				const lineWidth = b8.TextRenderer.measure( ( wrappedLine + word ).trim() ).cols;

				// Is the line with the new word longer than the line width?
				if ( lineWidth > maxWidth ) {
					wrappedLines.push( wrappedLine.trim() );
					wrappedLine = "";
				}

				// Add a space between words.
				wrappedLine += word + " ";

			}

			wrappedLines.push( wrappedLine.trim() );

		}

		return wrappedLines.join( "\n" );

	}


	/**
	 * Puts a character at the specified row and column.
	 *
	 * @param {number} ch - The character to put.
	 * @param {number} col - The column.
	 * @param {number} row - The row.
	 * @param {number} fgColor - The foreground color.
	 * @param {number} bgColor - The background color.
	 * @returns {void}
	 */
	const put_ = function( ch, col, row, fgColor, bgColor, font = null, direction = 0 ) {

		// Calculate x and y row and column to place character.
		const x = Math.round( col * b8.CONFIG.CHR_WIDTH );
		const y = Math.round( row * b8.CONFIG.CHR_HEIGHT );

		putxy_( ch, x, y, fgColor, bgColor, font, direction );

	}


	/**
	 * Puts a character at the specified x and y coordinates.
	 *
	 * @param {number} ch - The character to put.
	 * @param {number} x - The x-coordinate.
	 * @param {number} y - The y-coordinate.
	 * @param {number} fgColor - The foreground color.
	 * @param {number} bgColor - The background color.
	 * @param {b8.TextRendererFont} [font=null] - The font to use.
	 * @returns {void}
	 */
	const putxy_ = function( ch, x, y, fgColor, bgColor, font = null, direction = 0 ) {

		font = font || b8.TextRenderer.curTiles_;

		const colCount = font.getColCount();
		const chrW = font.getCharWidth();
		const chrH = font.getCharHeight();
		const fontRow = Math.floor( ch / colCount );
		const fontCol = ch % colCount;

		// Round so assets are always drawn on whole pixels.
		x = Math.round( x );
		y = Math.round( y );

		// Draw the background.
		// If bgColor is -1 then don't draw the background.
		// Or make the background transparent.
		if ( bgColor >= 0 ) {
			b8.Core.offCtx.fillStyle = b8.Core.getColorHex( bgColor );
			b8.Core.offCtx.fillRect( x, y, chrW, chrH );
		}

		// Foreground and background are the same so don't draw anything else.
		if ( b8.CONFIG.SCREEN_COLORS === 1 && bgColor === fgColor ) {
			return;
		}

		// Flippety flip the tiles.
		if ( direction > 0 ) {

			// Save the current state of the canvas context if flipping is needed.
			b8.Core.offCtx.save();

			// Determine whether to flip horizontally or vertically
			const flipH = ( direction & 1 ) !== 0; // Check if bit 1 is set (horizontal flip)
			const flipV = ( direction & 2 ) !== 0; // Check if bit 2 is set (vertical flip)

			// Adjust the origin based on flip direction
			const translateX = flipH ? chrW : 0;
			const translateY = flipV ? chrH : 0;
			b8.Core.offCtx.translate( x + translateX, y + translateY );

			// Apply scaling to flip the image
			const scaleX = flipH ? -1 : 1;
			const scaleY = flipV ? -1 : 1;
			b8.Core.offCtx.scale( scaleX, scaleY );

			// Reset x and y to 0 because the translate operation adjusts the positioning
			x = 0;
			y = 0;

		}

		const color = b8.Utilities.clamp( fgColor, 0, b8.CONFIG.COLORS.length - 1 );
		const img = font.getImageForColor( color );

		b8.Core.offCtx.drawImage(
			img,
			fontCol * chrW,
			fontRow * chrH,
			chrW, chrH,
			x, y,
			chrW, chrH
		);

		// Restore the canvas context if flipping was needed.
		if ( direction > 0 ) {
			b8.Core.offCtx.restore();
		}

		b8.Renderer.markDirty();

	}


	/**
	 * Tries to run an escape sequence that starts at text[pos].
	 * Returns the position after the escape sequence ends.
	 * If pretend is true, then this will only parse but not run it.
	 *
	 * @param {string} text - The text containing the escape sequence.
	 * @param {number} startPos - The start position of the escape sequence.
	 * @param {boolean} [pretend=false] - Whether to only parse but not run the sequence.
	 * @returns {number} The position after the escape sequence ends.
	 */
	const processEscapeSeq_ = function( text, startPos, pretend = false ) {

		// Shorthand.
		const startSeq = b8.CONFIG.PRINT_ESCAPE_START;
		const endSeq = b8.CONFIG.PRINT_ESCAPE_END;

		// If no escape sequences are configured in b8.CONFIG, stop.
		if ( !startSeq || !endSeq ) {
			return startPos;
		}

		// Check that the start sequence is there.
		if ( text.substring( startPos, startPos + startSeq.length ) != startSeq ) {
			return startPos;
		}

		// Where does it end?
		const endPos = text.indexOf( endSeq, startPos + startSeq.length );

		if ( !pretend ) {
			// Get the contents of the sequence.
			const command = text.substring( startPos + startSeq.length, endPos );
			runEscapeCommand_( command );
		}

		return endPos + endSeq.length;

	}


	/**
	 * Runs an escape command.
	 *
	 * @param {string} command - The command to run.
	 * @returns {void}
	 */
	const runEscapeCommand_ = function( command ) {

		// If it contains commas, it's a compound command.
		if ( command.indexOf( ',' ) > 0 ) {
			const parts = command.split( ',' );
			for ( const part of parts ) runEscapeCommand_( part );
			return;
		}

		command = command.trim();

		if ( command === "" ) {
			return;
		}

		// The first character is the command's verb. The rest is the argument.
		const verb = command[ 0 ].toLowerCase();
		const arg = command.substring( 1 );

		// If the argument is not a number then the result will be NaN and
		// the default will be used.
		const argNum = 1 * arg;

		switch ( verb ) {
			// Set foreground color.
			case "f":
			case "c":
				b8.Core.drawState.fgColor = arg !== "" ? argNum : b8.TextRenderer.origFgColor_;
				break;

			// Set background color.
			case "b":
				b8.Core.drawState.bgColor = arg !== "" ? argNum : b8.TextRenderer.origBgColor_;
				break;

			// Change font.
			case "t":
				b8.TextRenderer.printFont_ = b8.TextRenderer.getFontByName( arg );
				break;

			// Reset state.
			case "z":
				b8.Core.drawState.fgColor = b8.TextRenderer.origFgColor_;
				b8.Core.drawState.bgColor = b8.TextRenderer.origBgColor_;
				// Use original font if available, otherwise default.
				b8.TextRenderer.printFont_ = b8.TextRenderer.origFont_ || b8.TextRenderer.fonts_[ "default" ];
				break;

			default:
				b8.Utilities.warn( "Unknown b8 print escape command: " + command );
		}

	}


	/**
	 * Regenerates the colors for all fonts.
	 *
	 * @returns {void}
	 */
	b8.TextRenderer.regenColors = function() {

		// Tell all the fonts to regenerate their glyph images.
		Object.values( b8.TextRenderer.fonts_ ).forEach( f => f.regenColors() );

	}


	/**
	 * Builds a border definition from the top-left tile of a 3x3 box set.
	 *
	 * Layout:
	 * NW H NE
	 * V  . .
	 * SW . SE
	 *
	 * @param {number} borderChar The top-left tile index.
	 * @returns {{NW:number,NE:number,SW:number,SE:number,V:number,H:number}}
	 */
	function fromBorderChar_( borderChar ) {

		const colCount = b8.TextRenderer.curTiles_.getColCount();

		return {
			NW: borderChar,
			NE: borderChar + 2,
			SW: borderChar + colCount + colCount,
			SE: borderChar + colCount + colCount + 2,
			V: borderChar + colCount,
			H: borderChar + 1,
		};

	}


	/**
	 * Resolves a box style into concrete border tiles.
	 *
	 * Styles may define either:
	 * - explicit borders
	 * - a borderChar seed for a 3x3 tile set
	 * - no borders at all
	 *
	 * @param {string} styleName The style name.
	 * @returns {{fill:boolean,borders:?Object,fillChar:number}}
	 */
	function resolveBoxStyle_( styleName ) {

		const style = BOX_STYLES_[ styleName ] || BOX_STYLES_.plain;

		return {
			fill: style.fill ?? true,
			fillChar: style.fillChar ?? 0,
			invertBorder: !!style.invertBorder,
			invertFill: !!style.invertFill,
			borders: style.borders ?? (
				style.borderChar != null
					? fromBorderChar_( style.borderChar )
					: null
			),
		};

	}

} )( b8 );

( function( b8 ) {

	b8.Textmode = {};


	/**
	 * Load a textmode tilemap from data.
	 * This is a wrapper for b8.Tilemap.load to keep the API consistent.
	 *
	 * @param {Object} data The textmode tilemap data.
	 * @returns {Promise} Resolves when the tilemap is loaded.
	 */
	b8.Textmode.load = async function( data ) {

		return b8.Tilemap.load( data );

	};


	/**
	 * Draw a textmode tilemap to the screen.
	 * This is a wrapper for b8.Tilemap.draw to keep the API consistent.
	 *
	 * @param {Array} tilemap The tilemap array to draw.
	 * @param {number} [tileX=0] The x-coordinate of the tile to start drawing from.
	 * @param {number} [tileY=0] The y-coordinate of the tile to start drawing from.
	 * @param {number} [width=null] The width of the tilemap to draw.
	 * @param {number} [height=null] The height of the tilemap to draw.
	 * @returns {void}
	 */
	b8.Textmode.draw = async function( tilemap, tileXOffset = 0, tileYOffset = 0, width = null, height = null ) {

		return b8.Tilemap.draw( tilemap, tileXOffset, tileYOffset, width, height );

	}


} )( b8 );

( function( b8 ) {

	/**
	 * Represents an individual font that can be used with b8.TextRenderer.
	 */
	b8.TextRendererFont = class {


		/**
		 * Constructs a font. NOTE: after construction, you must call await initAsync() to
		 * initialize the font.
		 *
		 * @param {string} fontName - The name of the font.
		 * @param {string} fontImageFile - The URL of the image file for the font.
		 * @param {number} [tileWidthMultiplier=1] - The tile width multiplier for the font.
		 * @param {number} [tileHeightMultiplier=1] - The tile height multiplier for the font.
		 */
		constructor( fontName, fontImageFile, tileWidthMultiplier = 1, tileHeightMultiplier = 1 ) {

			b8.Utilities.checkString( "fontName", fontName );
			b8.Utilities.checkString( "fontImageFile", fontImageFile );

			this.fontName_ = fontName;
			this.fontImageFile_ = fontImageFile;
			this.origImg_ = null;
			this.chrImages_ = [];
			this.imageWidth_ = 0;
			this.imageHeight_ = 0;
			this.colCount_ = 0;
			this.rowCount_ = 0;
			this.charWidth_ = 0;
			this.charHeight_ = 0;
			this.charColCount_ = 0;
			this.charRowCount_ = 0;
			this.tileWidthMultiplier_ = tileWidthMultiplier;
			this.tileHeightMultiplier_ = tileHeightMultiplier;

		}


		/**
		 * Sets up this font from the given character image file. It's assumed to contain the
		 * glyphs arranged in a 16x16 grid, so we will deduce the character size by dividing the
		 * width and height by 16.
		 *
		 * @returns {Promise<void>}
		 */
		async initAsync() {

			this.origImg_ = await b8.Image.loadImageAsync( this.fontImageFile_ );

			const imageCharWidth = b8.CONFIG.CHR_WIDTH * this.tileWidthMultiplier_;
			const imageCharHeight = b8.CONFIG.CHR_HEIGHT * this.tileHeightMultiplier_;

			b8.Utilities.assert(
				this.origImg_.width % imageCharWidth === 0 && this.origImg_.height % imageCharHeight === 0,
				`Font ${this.fontName_}: image ${this.fontImageFile_} has dimensions ` +
				`${this.origImg_.width}x${this.origImg_.height}.`
			);

			this.origImg_ = await b8.Utilities.makeColorTransparent( this.origImg_ );

			this.charWidth_ = imageCharWidth;
			this.charHeight_ = imageCharHeight;
			this.imageWidth_ = this.origImg_.width;
			this.imageHeight_ = this.origImg_.height;
			this.colCount_ = this.imageWidth_ / this.charWidth_;
			this.rowCount_ = this.imageHeight_ / this.charHeight_;
			// How many tiles wide and tall each character is.
			this.charColCount_ = this.tileWidthMultiplier_;
			this.charRowCount_ = this.tileHeightMultiplier_;

			await this.regenColors();

		}


		/**
		 * Returns the character width of the font.
		 *
		 * @returns {number} The width of each character in pixels.
		 */
		getCharWidth() {

			return this.charWidth_;

		}


		/**
		 * Returns the character height of the font.
		 *
		 * @returns {number} The height of each character in pixels.
		 */
		getCharHeight() {

			return this.charHeight_;

		}


		/**
		 * Returns the character width of the font.
		 *
		 * @returns {number} The width of each character in pixels.
		 */
		getCharColCount() {

			return this.charColCount_;

		}


		/**
		 * Returns the character height of the font.
		 *
		 * @returns {number} The height of each character in pixels.
		 */
		getCharRowCount() {

			return this.charRowCount_;

		}


		/**
		 * Returns the number of rows in the font image.
		 *
		 * @returns {number} The number of rows in the font image.
		 */
		getRowCount() {

			return this.rowCount_;

		}


		/**
		 * Returns the number of columns in the font image.
		 *
		 * @returns {number} The number of columns in the font image.
		 */
		getColCount() {

			return this.colCount_;

		}



		/**
		 * Returns the image for a given color number.
		 *
		 * @param {number} colorNumber - The color number.
		 * @returns {HTMLImageElement} The image for the specified color.
		 */
		getImageForColor( colorNumber ) {

			return this.chrImages_[ colorNumber ];

		}


		/**
		 * Regenerates the color text images.
		 *
		 * @returns {Promise<void>}
		 */
		async regenColors() {

			this.chrImages_ = [];

			// Loop through each color.
			for ( let c = 0; c < b8.CONFIG.COLORS.length; c++ ) {

				// Create a temp context to draw the font image to.
				const tempCanvas = document.createElement( 'canvas' );
				tempCanvas.width = this.origImg_.width;
				tempCanvas.height = this.origImg_.height;

				const ctx = tempCanvas.getContext( '2d' );

				// Clear the temp canvas.
				ctx.clearRect( 0, 0, this.origImg_.width, this.origImg_.height );

				// Draw the font image to the temp canvas (white over transparent background).
				ctx.globalCompositeOperation = 'source-over';
				ctx.drawImage( this.origImg_, 0, 0, this.origImg_.width, this.origImg_.height );

				// Now draw a filled rect with the desired color using the 'source-in' pixel
				// operation, which will tint the white pixels to that color.
				ctx.globalCompositeOperation = 'source-in';
				ctx.fillStyle = b8.CONFIG.COLORS[ c ];
				ctx.fillRect( 0, 0, this.origImg_.width, this.origImg_.height );

				// Now draw with multiply blend mode to add shading.
				// But only if we the config is set.
				if ( b8.CONFIG.SCREEN_COLORS === 2 ) {
					ctx.globalCompositeOperation = 'multiply';
					ctx.drawImage( this.origImg_, 0, 0, this.origImg_.width, this.origImg_.height );
				}

				this.chrImages_.push( tempCanvas );

			}

		}

	}


} )( b8 );

( function( b8 ) {

	b8.State = b8.State || {};


	// This is the key used to store the state in localStorage.
	let STORAGE_KEY = '';

	document.addEventListener(
		'b8.initComplete',
		function() {

			setStorageKey();

		},
		{ once: true }
	);


	/**
	 * Sets the storage key from the b8 config or defaults to 'b8.state'.
	 *
	 * @returns {void}
	 */
	function setStorageKey() {

		STORAGE_KEY = `b8.${b8.Utilities.makeUrlPretty( b8.CONFIG.NAME )}.state`;

	}


	/**
	 * Recursively wraps an object in a Proxy to make it reactive.
	 *
	 * @param {Object} target - The object to wrap.
	 * @returns {Proxy} - The reactive proxy.
	 */
	function createProxy( target ) {

		return new Proxy(
			target,
			{

				/**
				 * Get trap for the Proxy.
				 * Intercepts property access on the state object.
				 *
				 * @param {Object} obj - The original object being proxied.
				 * @param {string} prop - The property being accessed.
				 * @returns {*} - The value of the accessed property.
				 */
				get( obj, prop ) {

					const value = obj[ prop ];
					if ( typeof value === 'object' && value !== null ) {
						return createProxy( value );
					}
					return value;

				},


				/**
				 * Set trap for the Proxy.
				 * Intercepts property updates on the state object.
				 *
				 * @param {Object} obj - The original object being proxied.
				 * @param {string} prop - The property being updated.
				 * @param {*} value - The new value to assign to the property.
				 * @returns {boolean} - Returns true to indicate the operation was successful.
				 */
				set( obj, prop, value ) {

					obj[ prop ] = value;
					b8.Utilities.event( 'stateChange', { prop, value } );
					return true;

				}

			}
		);

	}


	/**
	 * Saves the current state to localStorage using CBOR and base64.
	 *
	 * @param {string} [key='b8.state'] - Optional localStorage key.
	 */
	b8.State.save = function( key = STORAGE_KEY ) {

		const encoded = b8.Utilities.encodeData(
			{
				time: Date.now(),
				data: b8.data
			}
		);

		localStorage.setItem( key, encoded );

		b8.State.lastSave = Date.now();

	}


	/**
	 * Resets the state to an empty object and re-initializes it.
	 * This does not clear localStorage.
	 *
	 * @returns {void}
	 */
	b8.State.reset = function() {

		setStorageKey();

		b8.data = null;

		b8.State.init( {} );

	};


	/**
	 * Loads state from localStorage, replacing State.data.
	 *
	 * @param {string} [key='b8.state'] - Optional localStorage key.
	 */
	b8.State.load = function( key = STORAGE_KEY ) {

		const b64 = localStorage.getItem( key );

		if ( !b64 ) {
			b8.Utilities.log( 'No state found for the given key.' );
			return;
		}

		const rawState = b8.Utilities.decodeData( b64 );
		if ( rawState.data ) {
			b8.data = createProxy( rawState.data );
		}

		if ( rawState.time ) {
			b8.State.lastSave = rawState.time;
		}

	}


	/**
	 * Sets default values for missing keys in the state.
	 * Does not overwrite existing values.
	 *
	 * @param {Object} defaults - An object containing default key/value pairs.
	 */
	b8.State.init = function( defaults ) {

		if ( !b8.data ) {
			b8.data = createProxy( {} );
		}

		// If there is a save file then load that too.
		if ( localStorage.getItem( STORAGE_KEY ) ) {
			b8.State.load();
		}

		b8.data = b8.Utilities.deepMergeByIndex( defaults, b8.data );

		b8.Utilities.log( 'State initialized:', b8.data );

	}


	/**
	 * Resets the state to its initial values.
	 * This is useful for starting a new game or resetting the application.
	 *
	 * @param {string} [key='b8.state'] - Optional localStorage key.
	 * @returns {void}
	 */
	b8.State.clear = function( key = STORAGE_KEY ) {

		b8.Utilities.log( 'Clearing state...' );

		localStorage.removeItem( key );
		b8.data = createProxy( {} );

	}


} )( b8 );

( function( b8 ) {

	b8.Sfx = {};


	/**
	 * Sound effect library.
	 *
	 * @see https://killedbyapixel.github.io/ZzFX/
	 * @see https://codepen.io/KilledByAPixel/pen/BaowKzv?editors=1000
	 * @type {Object}
	 */
	b8.Sfx.library = {

		'fx/action/drag': [ , 0, 293.6648, .1, , , 4, 6, 32, , , , , 1, 1.4, .1, , .7, .1 ],

		'fx/break/001': [ 2.1, , 339, .02, .07, .09, 4, .2, -7, , , , .05, 1, , .1, .16, .45, .02, .03 ],
		'fx/break/002': [ 1.5, , 157, .16, , 0, , .35, -24, 28, , , , .1, , .6, , .19, .01 ],
		'fx/break/003': [ 2, , 180, 0.05, 0.03, 0.04, , 2.42, 0.6, , , , , , , , 0.15, 0.39, 0.04 ],

		'fx/fight/dodge': [ 1.2, .3, 150, .05, , .05, , , -1, , , , , 4, , , , , .02 ],
		'fx/fight/hit': [ 5, , 185, , , , 3, 1.6, -7, , , , , , , .2, .19, .1, , .38, 985 ],

		'fx/robot/001': [ 1.13, , 172, .04, .18, .09, , .06, - 38, -2.6, -99, , , , 35, , .08 ],
		'fx/robot/002': [ 1.42, , 61, .01, .02, 0, 1, .21, , , 816, .01, .05, , -40, , .05, .71, .11 ],
		'fx/robot/003': [ .9, , 164, .04, .03, .14, , .8, 46, 66, , , , , , , , .71, .08, , 217 ],
		'fx/robot/004': [ , .02, 1638, , .05, .17, 1, , , , 490, .09, , , , .1, .05, .5, .03 ],

		'fx/sci-fi/radioactive': [ 1, 0, 130, .02, .9, .39, 2, .8, , , , , .13, .2, , .1, , .93, .06, .28 ],
		'fx/sci-fi/robot': [ 1, 0, 847, .02, .3, .9, 1, 1.67, , , -294, .04, .13, , , , .1 ],
		'fx/sci-fi/teleport': [ 1, , 85, .08, .1, .01, 1, 4, , -11, 1, .07, , .1, 101, , .05, .68, .4, .12, 1 ],
		'fx/sci-fi/warp': [ 3, 0, 713, .16, .09, .24, , .6, -29, -16, , , .09, .5, , , .23, .75, .15, .48 ],
		'fx/sci-fi/beam': [ 1, 0, 662, .82, .11, .33, 1, 0, , -0.2, , , , 1.2, , .26, .01 ],
		'fx/sci-fi/hover': [ 2, 0, 262.63, 0.1, 0.12, 0.3, 0, 2.4, -0.1, 0, 0, 0, 0.24, 0, 0, 0.1, 0.05, 0.98, 0.07, 0.17, 0 ],

		'fx/thud/001': [ 1.5, , 90, , .01, .03, 4, , , , , , , 9, 50, .2, , .2, .01 ],
		'fx/thud/002': [ 1, , 129, .01, , .15, , , , , , , , 5 ],

		'fx/machine/buzz': [ 1, 0, 130.8128, .1, .1, .34, 3, 1.88, , , , , , , , .1, , .5, .04 ],
		'fx/machine/hum': [ 1, 0, 63, , 1, , 1, 1.5, , , , , , , , 3.69, .08 ],
		'fx/machine/humm': [ 1, , 110, .03, .25, .15, 2, 1.32, , , , , .07, , -0.1, , .11, .77 ],
		'fx/machine/warp': [ 2, , 128, , .12, .26, , 4.7, , -1, -62, .06, .07, , 52, , , .66, .08 ],

		'fx/noise/001': [ 1.27, , 390, 0.01, 0.04, 0.02, 4, 0.71, 4.8, , , , , , , , 0.01, 0.6, 0.06 ],
		'fx/noise/002': [ 0.4, , 60, , 0.01, 0, 4, 0.55, 62, 89, -88, 0.06, , , 173, 0.6, , , 0.05 ],
		'fx/noise/003': [ 0.2, , 523.2511, .1, 3, 3, 4, 0, , , 2250, , .04, , 10, .01, , .82, 1, , 30 ],

		'fx/random/tone': [ 2, .8, 999, , , , 1, 2, , , , , 1, , , .1, .2 ],

		'fx/swoosh/001': [ 1, , 1500, 0.02, , 0.02, 4, 0.68, 5, , , , 0.01, 0.7, 136, , , , , 0.11 ],
		'fx/swoosh/002': [ 1.2, , 585, , .02, .16, 4, .25, , , , , , , , , , .55, .03 ],
		'fx/swoosh/003': [ .2, , 836, .11, , 0, 4, .91, 13, , , , .09, .1, -39, , , .06, .07 ],
		'fx/swoosh/004': [ 1.2, , 9220, .01, , , , 5, , , , , , 9 ],
		'fx/swoosh/005': [ 1.5, 0, 150, .05, , .05, , 1.3, , , , , , 3 ],
		'fx/swoosh/006': [ 2, , 12, , , 0.008, , 1.2, 23, -7, , , 0.05, 0.4, , , 0.15, 0.82, 0.03, 0.28, ],

		'fx/vehicle/engine': [ 1.2, 0, 25, .05, .3, .5, 3, 9, -0.01, , , , , , 13, .1, .2 ],
		'fx/vehicle/carhorn': [ 1.8, 0, 250, .02, .02, .2, 2, 2, , , , , .02, , , .02, .01, , , .1 ],
		'fx/vehicle/horn': [ 2, , 688, .02, .01, .007, 1, 2.6, , , , , .01, , 85, , .01, .85, .03, .11, -818 ],
		'fx/vehicle/truckhorn': [ 1.5, , 1376, .02, .01, .007, 1, 2.6, , , , , .01, , 85, , .01, .85, .8, .11, -818 ],
		'fx/vehicle/siren': [ 1.3, 0, 960, , 1, .01, , .8, -0.01, , -190, .5, , .05, , , 1 ],
		'fx/vehicle/submarine': [ 1.2, 0, 1975, .08, .56, .02, , , -0.4, , -322, .56, .41, , , , .25 ],
		'fx/vehicle/rocket': [ 1.5, 0, 941, .8, , .8, 4, .74, -222, , , , , .8, , 1 ],

		'game/coin/001': [ 1.2, 0, 1675, , .06, .24, 1, 1.82, , , 837, .06 ],
		'game/coin/002': [ 1.2, 0, 523.2511, .01, .06, .3, 1, 1.82, , , 837, .06 ],
		'game/coin/003': [ 0.6, 0, 1874, , 0.01, 0.25, 2, 0.76, , , 622, 0.1 ],
		'game/coin/004': [ 1, 0, 277, .03, .04, .06, 1, 1.8, 1, , 140, .06, .04, , , .1, , .99, .03 ],

		'game/collect/001': [ 1.1, 0, 450, , .01, .13, , 2.7, , -9.5, 500, .08, , , , , , .89 ],
		'game/collect/002': [ 1.05, , 10, .08, .07, .24, 2, 1.03, , , -374, .04, .09, , , , , .72, .15, .18 ],

		'game/die/001': [ 1.5, 0, 537, .02, .02, .22, 1, 1.59, -6.98, 4.97 ],
		'game/die/002': [ 1, , 321, .01, .06, .06, 1, 3.8, , -49, , , , .3, , , , .79, .09 ],
		'game/die/003': [ 1, 0, 344, .01, .02, .28, 1, 1.4, , , 50, , , , .3, .2, .15, .6, .06 ],
		'game/die/004': [ 0.5, 0, 43, 0.01, , 1, 2, , , , , , , , , 0.02, 0.01 ],

		'game/jump/001': [ 1, .1, 75, .03, .08, .17, 1, 1.88, 7.83, , , , , .4 ],
		'game/jump/002': [ 1.2, , 311, .03, .05, .05, , 2.2, , 9, , , .02, , 2.7, , , .97, .05, , 101 ],
		'game/jump/003': [ 1.5, , 65, .04, .1, .13, 1, 1.5, , -31, , , , .3, , , , .99, .03 ],
		'game/jump/004': [ , 0, 500, , .01, .13, , .2, 1.7, , -400 ],
		'game/jump/005': [ 1, , 341, , .14, .23, 1, 1.01, .9, , -132, .03, , .1, , .1, , .52, .22 ],
		'game/jump/006': [ 0.7, , 1496, .09, .09, .01, 3, .14, , , -870, , , , 3.2, .2, , .31, .02 ],

		'game/powerup/001': [ , , 188, .03, .09, .12, 1, 2.4, , 95, , , , , , , , .63, .08 ],
		'game/powerup/002': [ 0.8, 0, 413, .03, .05, .05, , 1.8, , 19, 177, .05, , , , , , .83, .02 ],
		'game/powerup/003': [ 0.5, , 643, , 0.1, 0.12, 1, 0.1, , 99, , , , , , , , 0.6, 0.02, , -732 ],
		'game/powerup/004': [ 1.18, , 143, .05, .08, .06, , .09, 25, 4.1, , , , , , , .01, .52, .09 ],
		'game/powerup/005': [ 1, , 18, 0.01, 0.01, 0.21, 1, 0.49, 9.9, , , , , , , , 0.04, 0.95, 0.02 ],
		'game/powerup/006': [ 0.5, , 426, .01, , .05, , 2.54, 49, , 9, .1, , , , , , .46, .15 ],
		'game/powerup/007': [ 1, , 158, .09, .18, .03, , 2.53, 11, -58, 63, .02, .01, .5, , , , .16 ],
		'game/powerup/008': [ 1.2, 0, 539, 0, .04, .29, 1, 1.92, , , 567, .02, .02, , , , .04 ],

		'instrument/bass/001': [ 2, 0, 65, .01, .08, .2, , 2.6, , , , , , .1, , , , .61, .02, , -1686 ],
		'instrument/bass/002': [ 2, 0, 73, .01, .08, .2, , 2.6, , , , , , .1, , , , .61, .02, , -1686 ],
		'instrument/bass/003': [ 2, 0, 82, .01, .08, .2, , 2.6, , , , , , .1, , , , .61, .02, , -1686 ],
		'instrument/bass/004': [ 2, 0, 87, .01, .08, .2, , 2.6, , , , , , .1, , , , .61, .02, , -1686 ],
		'instrument/bass/005': [ 2, 0, 97, .01, .08, .2, , 2.6, , , , , , .1, , , , .61, .02, , -1686 ],
		'instrument/bass/006': [ 2, 0, 110, .01, .08, .2, , 2.6, , , , , , .1, , , , .61, .02, , -1686 ],
		'instrument/bass/007': [ 2, 0, 123, .01, .08, .2, , 2.6, , , , , , .1, , , , .61, .02, , -1686 ],
		'instrument/bass/008': [ 2, 0, 130, .01, .08, .2, , 2.6, , , , , , .1, , , , .61, .02, , -1686 ],

		'instrument/drum/001': [ 1.5, 0, 86, , , , , 0.7, , , , 0.5, , 6.7, 1, 0.05 ],
		'instrument/drum/002': [ 0.7, 0, 270, , , 0.12, 3, 1.65, -2, , , , , 4.5, , 0.02 ],

		'tone/beep/001': [ 2, 0, 130, , .1, , 1, 1.5, , , , , , , , .1, .01 ],
		'tone/beep/002': [ 2, 0, 146, , .1, , 1, 1.5, , , , , , , , .1, .01 ],
		'tone/beep/003': [ 2, 0, 164, , .1, , 1, 1.5, , , , , , , , .1, .01 ],
		'tone/beep/004': [ 2, 0, 174, , .1, , 1, 1.5, , , , , , , , .1, .01 ],
		'tone/beep/005': [ 2, 0, 195, , .1, , 1, 1.5, , , , , , , , .1, .01 ],
		'tone/beep/006': [ 2, 0, 220, , .1, , 1, 1.5, , , , , , , , .1, .01 ],
		'tone/beep/007': [ 2, 0, 246, , .1, , 1, 1.5, , , , , , , , .1, .01 ],
		'tone/beep/008': [ 2, 0, 261, , .1, , 1, 1.5, , , , , , , , .1, .01 ],

		'tone/bell/001': [ 2, 0, 999, , , , , 1.5, , .3, -99, .1, 1.63, , , .11, .22 ],
		'tone/bell/002': [ , 0, 1600, .13, .52, .61, 1, 1.1, , , , , , .1, , .14 ],
		'tone/bell/random': [ 2, .1, 999, , , , , 1.5, , .3, -99, .1, 1.63, , , .11, .22 ],

		'tone/blip/001': [ 5, 0, 130, .02, .03, .02, , 2.8, , , , , , , , , , .7, .02 ],
		'tone/blip/002': [ 3, 0, 146, .02, .03, .02, , 2.8, , , , , , , , , , .7, .02 ],
		'tone/blip/003': [ 3, 0, 164, .02, .03, .02, , 2.8, , , , , , , , , , .7, .02 ],
		'tone/blip/004': [ 3, 0, 174, .02, .03, .02, , 2.8, , , , , , , , , , .7, .02 ],
		'tone/blip/005': [ 3, 0, 195, .02, .03, .02, , 2.8, , , , , , , , , , .7, .02 ],
		'tone/blip/006': [ 3, 0, 220, .02, .03, .02, , 2.8, , , , , , , , , , .7, .02 ],
		'tone/blip/007': [ 3, 0, 246, .02, .03, .02, , 2.8, , , , , , , , , , .7, .02 ],
		'tone/blip/008': [ 3, 0, 261, .02, .03, .02, , 2.8, , , , , , , , , , .7, .02 ],

		'tone/bloop/001': [ 1, , 110, .02, , .09, 1, .61, , , 556, .12, , , , .3, , , .02 ],
		'tone/bloop/002': [ 1, 0, 521.25, , .02, .03, 2, 0, , .1, 700, .01, , , 1, .1 ],
		'tone/bloop/003': [ 1.12, , 73, , 0.02, 0.11, 2, 1.18, , -0.1, , , , , , 0.3, , 0.55, 0.05, 0.23 ],
		'tone/bloop/004': [ 0.5, , 1368, .09, , 0, , 1.11, -76, 9.1, -490, , , , , , , .56 ],
		'tone/bloop/005': [ 2.03, , 413, , , 0.24, 2, 0.12, , , , , 0.11, , 317, 0.1, 0.13, , , 0.01 ],
		'tone/bloop/006': [ , 0, , .01, .02, .09, , .6, 17, -3, , , .1, , , , , .76, .08 ],
		'tone/bloop/007': [ 0.3, , 10, 0.06, , 0, 2, 2.3, , , 621, , , , , , , , 0.21, 0.26 ],
		'tone/bloop/008': [ 1.5, 0.05, 24, 0.01, 0.02, 0.01, 1, 3.9, -33, 0, 0, 0, 0, 0, 355, 0, 0, 0.65, 0, 0, 0 ],
		'tone/bloop/009': [ 2, 0.05, 226, 0, 0.08, 0.13, 0, 3.1, 0, 0, 0, 0, 0, 0, 0, 0.1, 0.02, 0.76, 0.04, 0, 105 ],
		'tone/bloop/010': [ 4, 0, 224, .02, .02, .08, 1, 1.7, -13.9, , , , , , 6.7 ],
		'tone/bloop/011': [ 1, , 283, .02, , .11, , .38, , , , , .07, , , .1, .08, .63, .02 ],
		'tone/bloop/012': [ 1, 0, 288, 0.05, 0.01, , , 2, - 10, , , , , , , , , 0.5, 0.1 ],
		'tone/bloop/013': [ 2, 0, 700, 0.01, , 0, , , , , , , , , , , , 0.1, 0.01 ],
		'tone/bloop/014': [ 2.21, , 107, 0.02, 0.04, 0.07, , 2.22, 2, 0.9, , , , 0.4, , 0.5, 0.15, 0.42, 0.04 ],
		'tone/bloop/015': [ 0.6, 0, 2200, , , 0.04, 3, 2, , , 800, 0.02, , 4.8, , 0.01, 0.1 ],

		'tone/jingle/001': [ 1.4, , 183, .07, .13, .34, , 3.3, , , 35, .06, .07, , , , .13, .95, .27, .11 ],
		'tone/jingle/002': [ .8, , 208, .02, .21, .13, 3, .2, , , 40, .06, .1, , , , , .91, .2, .27 ],
		'tone/jingle/003': [ .9, , 56, .15, .46, .08, , 1.6, -2, , -137, .01, .06, , , , .09, .77, .37, .13 ],
		'tone/jingle/004': [ .6, , 269, .03, .17, .41, , .2, , 1, 239, .08, .04, , , , , .72, .19, .43, -720 ],
		'tone/jingle/005': [ 1.3, 0, 130.81, 0.32, 0.35, 0.5, 3, 5.2, 0, 1, 50, 0, 0.14, 0, 0, 0, 0, 0.37, 0.04, 0.24, 0 ],
		'tone/jingle/006': [ 1, , 525, .18, .28, .17, 1, 1.24, 8.3, -9.7, -151, .03, .06, , , , , .93, .02, .14 ],
		'tone/jingle/007': [ .6, , 934, .12, .38, .93, 1, .27, , .4, -434, .08, .2, .1, , .1, .17, .55, 1, .46 ],
		'tone/jingle/008': [ 1.4, 0, 20, .04, , .6, , 1.31, , , -990, .06, .17, , , .04, .07 ],
		'tone/jingle/009': [ 1.2, 0, 80, .3, .4, .7, 2, .1, -0.73, 3.42, -430, .09, .17, , , , .19 ],
		'tone/jingle/010': [ 0.5, , 392, .06, .22, .5, 1, 1.85, -0.1, -0.9, 61, .05, .07, , , .1, , .96, .12 ],
		'tone/jingle/011': [ 0.5, , 146, .04, .23, .46, , .56, , -3.7, 658, .02, .15, .1, , , , .82, .13, .2 ],
		'tone/jingle/012': [ 1, , 284, .08, .2, .25, 1, 3, , , 50, .09, .06, , , , , .6, .28, .03, -1391 ],
		'tone/jingle/013': [ 1.5, , 430, .02, .12, .5, , .89, , -3.6, -133, .07, .13, , , .1, , .83, .23, .26 ],
		'tone/jingle/014': [ 0.4, , 22, .08, .22, .02, 1, .52, -4.2, -9.8, , , .14, , -18, .2, , , .05 ],
		'tone/jingle/015': [ , , 193, .04, .27, .42, 1, 1.71, 2.8, 4.9, , , .1, .2, , .1, , .55, .27, .47 ],
		'tone/jingle/016': [ 1.1, , 250, .07, .24, .26, , 2, , 164, 211, .07, .08, , , .1, , .75, .12, .09, 115 ],
		'tone/jingle/017': [ , , 103, .04, .11, .43, 1, .77, , , 57, .19, .05, , , .1, , .68, .24 ],

		'ui/click/001': [ 1.5, 0, 900, , .01, 0, 1, , -10, , -31, .02, , , , , , 1.2, , .16, -1448 ],
		'ui/click/002': [ 2.5, , 783, , .03, .02, 1, 2, , , 940, .03, , , , , .2, .6, , .06 ],
		'ui/click/003': [ 1.5, .01, 300, , , .02, 3, .22, , , -9, .2, , , , , , .5 ],
		'ui/click/004': [ 1, 0, 685, .01, .03, .17, 1, 1.4, , , , , , , , , , .63, .01, , 420 ],
		'ui/click/005': [ 6, , 205, , .02, 0, , 1.03, , , , , , , , , .12, .32 ],

		'weapon/explode/001': [ 1.5, 0, 333, .01, 0, .9, 4, 1.9, , , , , , .5, , .6 ],
		'weapon/explode/002': [ 1.1, 0, 418, 0, .02, .2, 4, 1.15, -8.5, , , , , .7, , .1 ],
		'weapon/explode/003': [ 1.2, 0, 82, .02, , .2, 4, 4, , , , , , .8, , .2, , .8, .09 ],
		'weapon/explode/004': [ 2, .2, 72, .01, .01, .2, 4, , , , , , , 1, , .5, .1, .5, .02 ],
		'weapon/explode/005': [ 2, , 1e3, .02, , .2, 1, 3, .1, , , , , 1, -30, .5, , .5 ],
		'weapon/explode/006': [ 1, , 485, .02, .2, .2, 4, .11, -3, .1, , , .05, 1.1, , .4, , .57, .5 ],
		'weapon/explode/007': [ 0.8, , 372, 0.02, 0.02, 0.5, 4, 2.29, 0.2, , , , , 0.6, , 0.6, , 0.7, 0.04, 0.19 ],
		'weapon/explode/008': [ 1.05, , 591, .03, .13, .51, 4, 3.02, .6, .1, , , .04, 1.6, , 1, , .46, .13 ],
		'weapon/explode/009': [ 1.99, , 770, 0.03, 0.19, 0.35, , 0.26, , , , , , 2, -50, 0.1, 0.27, 0.48, 0.06 ],
		'weapon/explode/010': [ 1.5, , 98, .08, .18, .02, 2, 2.47, 36, .5, , , .04, .1, , .9, .44, , .04 ],
		'weapon/explode/011': [ 1, , 400, , .03, .21, 3, .85, .5, , , , , 1.8, , .5, , .97, .05 ],
		'weapon/explode/012': [ 1, , 485, .02, .07, .03, 4, .11, -3, .1, , , .05, 1.1, , .4, , .57, .09 ],
		'weapon/explode/013': [ , , 30, .09, .12, .35, 4, 3, 4, , , , , 1.3, , .6, , .36, .21 ],

		'weapon/lazer/001': [ 1.5, 0, 515, .05, .07, .09, 1, 2.8, , , 302, .06, .1, , 3.5, .1, .08, .75, .04 ],
		'weapon/lazer/002': [ , 0, 925, .04, .3, .6, 1, .3, , 6.27, -184, .09, .17 ],
		'weapon/lazer/003': [ 1, , 375, .01, .06, , 2, 2.3, 18, -10, , , , , 18, , , .56, .14 ],
		'weapon/lazer/004': [ 0.5, , 2e3, , 0.05, 0, , 1.11, -17, , 197, 0.01, , 0.2, , , , , 0.16 ],
		'weapon/lazer/005': [ .9, , 752, .03, .01, .02, , 1.4, , , -10, .01, , , 3.4, , , .68, .03, , 106 ],
		'weapon/lazer/006': [ 1.9, , 221, .01, .05, .06, 1, 3.9, -2, , 116, .05, , , , , , .65, .02, , 452 ],
		'weapon/lazer/007': [ 1, , 659, .01, .04, , 1, .4, , -75, 179, .06, , , .2, , , .57 ],

		'world/footstep/001': [ 1.1, 0.05, 157, 0.03, 0.04, 0.04, 4, 4.9, 78, -13, 0, 0, 0.07, 0, 0, 0, 0, 0.91, 0.02, 0.33, 0 ],
		'world/footstep/002': [ .1, 1, 300, .05, .1, .05, 4, .2, -100, , -50, .07, , .5, , .4, , , , .05 ],
		'world/footstep/003': [ 3, , 5, , .06, .01, 2, 2.25, -19, -79, 409, .01, , , 6.6, , .2, .57, , .8 ],

		'world/nature/frog': [ 0.5, , 160, .03, .03, .02, , 1.52, -23, 93, 662, .02, , , , .1, , , .07, .01 ],
		'world/nature/dolphin': [ 0.5, 0, 448, .01, .1, .3, 3, .39, -0.5, , , , , , .2, .1, .08 ],
		'world/nature/whale': [ 1.2, 0, 1306, .8, .08, .02, 1, , , , , , .48, , -0.1, .11, .25 ],
		'world/nature/mouse': [ 1.2, 0, 1e3, .02, , .01, 2, , 18, , 475, .01, .01 ],
		'world/nature/small-dog': [ 1, , 759, .01, , .01, 1, .97, 15, , , , , , 3.1, , , .76, .04 ],
		'world/nature/tweet': [ 0.7, , 1305, , , .03, 1, .75, , 23, 694, .01, , , 3.9, , , , .01 ],

		'world/water/splash': [ 2, , 94, .07, .1, .33, 4, .6, 1, , , , , .1, 1, .1, .1, .45, .15 ],
		'world/water/wave': [ 1, 0, 40, .5, , 1.5, , 11, , , , , , 199 ],
		'world/water/pop': [ 1, 0, 103, , 0.02, 0.06, , 1.24, - 18, 4.4, , , , 0.7, , 0.1, , 0.95, 0.03 ],

		'world/weather/thunder': [ 1.2, 0, 471, , .09, .47, 4, 1.06, -6.7, , , , , .9, 61, .1, , .82, .09, .13 ],

	};


	/**
	 * Play a named sound effect.
	 *
	 * @param {string} sfx - The name of the sound effect to play.
	 * @throws {Error} If the sfx is not found.
	 * @returns	{void}
	 */
	b8.Sfx.play = function( sfx = '' ) {

		// Quit if no sound specified.
		if ( !sfx ) return;

		// Check the sfx is a string.
		b8.Utilities.checkString( 'sfx', sfx );

		// SFX not found.
		if ( !b8.Sfx.library[ sfx ] ) {
			b8.Utilities.fatal( `SFX ${sfx} not found.` );
		}

		b8.Sfx.playFromArray( b8.Sfx.library[ sfx ] );

	}


	/**
	 * Play a sound effect from a ZzFX array.
	 *
	 * This array can be generated with ZzFX. It should be just the array beginning and ending with square brackets.
	 *
	 * @see https://killedbyapixel.github.io/ZzFX/
	 *
	 * @param {Array} sfxArray - The sound effect array to play.
	 * @returns {void}
	 */
	b8.Sfx.playFromArray = function( sfxArray = [] ) {

		// Check the sfx is an array.
		b8.Utilities.checkArray( 'sfxArray', sfxArray );

		// Play the raw sound effect.
		zzfx( ...sfxArray );
	}


	/**
	 * Add a sound effect to the library.
	 *
	 * @param {string} sfxName - The name of the sound effect.
	 * @param {Array} sfxArray The sound effect array.
	 * @throws {Error} If the sfxName is not a string.
	 * @throws {Error} If the sfxArray is not an array.
	 * @return {void}
	 */
	b8.Sfx.add = function( sfxName, sfxArray ) {

		b8.Utilities.checkString( 'sfxName', sfxName );
		b8.Utilities.checkArray( 'sfxArray', sfxArray );

		b8.Sfx.library[ sfxName ] = sfxArray;

	}


	/**
	 * Get the list of sfx from the library.
	 *
	 * @return {Array} The list of sfx.
	 */
	b8.Sfx.get = function() {

		return Object.keys( b8.Sfx.library );

	}

} )( b8 );

( function( b8 ) {

	/**
	 * Stores all scenes by name.
	 *
	 * @type {Object}
	 */
	b8.Scene = {};


	/**
	 * Holds the current active scene object.
	 *
	 * @type {Object|null}
	 */
	let activeScene = null;

	const sceneList = {};


	/**
	 * Adds a new scene to the scene manager.
	 *
	 * A scene should be a javascript object with at least one of the following functions:
	 *
	 * - init (optional): A function that will be called when the scene is set.
	 * - update (optional): A function that will be called multiple times a frame and passed a deltatime value as a parameter.
	 * - render (optional): A function that will be called every frame.
	 *
	 * Init can be used to set up the scene. For asynchronous games you can add a while loop here and use await functions (eg for keypresses) and then render yourself.
	 * For synchronous games you can use the update and render functions to manage game logic and rendering efficiently.
	 *
	 * eg:
	 * const game = {
	 *  init: () => { }
	 *  update: ( dt ) => { }
	 *  render: () => { }
	 * }
	 *
	 * @param {string} name - The name of the scene.
	 * @param {object} gameObject - An object that includes init, update, and
	 * render methods as well as other properties for the scene. If update and
	 * render are set then these will be passed to `b8.frame`.
	 * @param {number} frameRate - The frame rate at which to update and render
	 */
	b8.Scene.add = function( name, gameObject = null, frameRate = 30 ) {

		b8.Utilities.checkString( 'name', name );

		if ( gameObject !== null ) {
			b8.Utilities.checkObject( 'gameObject', gameObject );
		}

		b8.Utilities.checkInt( 'frameRate', frameRate );

		const init = gameObject.init || null;
		const update = gameObject.update || null;
		const render = gameObject.render || null;

		sceneList[ name ] = { init, update, render, frameRate };

	};


	/**
	 * Removes a scene by name.
	 *
	 * @returns {void}
	 */
	b8.Scene.reset = function() {

		for ( const key in sceneList ) {
			if ( Object.prototype.hasOwnProperty.call( sceneList, key ) ) {
				delete sceneList[ key ];
			}
		}

		activeScene = null;

	}


	/**
	 * Switches to a specified scene by name.
	 *
	 * @param {string} name - The name of the scene to switch to.
	 */
	b8.Scene.set = function( name ) {

		b8.Utilities.checkString( 'name', name );

		if ( !sceneList[ name ] ) {
			b8.Utilities.fatal( `Scene "${name}" does not exist.` );
		}

		// Stop the current game loop.
		b8.Core.stopFrame();

		// Store the active scene.
		activeScene = name;

		// Clear any inputs.
		if ( b8.Input && typeof b8.Input.onEndFrame === 'function' ) {
			b8.Input.onEndFrame();
		}

		// Get the scene object.
		const currentScene = sceneList[ name ];

		// If there's an init method, call it.
		if ( currentScene.init ) {
			currentScene.init();
		}

		// If there's an update or render method, call frame to create a synchronous game.
		if ( currentScene.update || currentScene.render ) {
			b8.frame( currentScene.render, currentScene.update, currentScene.frameRate );
		}

	};


	/**
	 * Pauses the current scene.
	 *
	 * @param {string} name - The name of the scene to pause.
	 * @returns {void}
	 */
	b8.Scene.pause = function() {

		b8.frame( null );

	};


	/**
	 * Resumes the current scene.
	 *
	 * @returns {void}
	 */
	b8.Scene.resume = function() {

		// If there's no active scene, do nothing.
		if ( !activeScene ) {
			return;
		}

		// Get the currentScene.
		const currentScene = sceneList[ activeScene ];

		// If there's an update or render method, call frame to create a synchronous game.
		if ( currentScene.update || currentScene.render ) {
			b8.frame(
				currentScene.render || ( () => { } ),
				currentScene.update || ( () => { } ),
				currentScene.frameRate || 30
			);
		}

	};


	/**
	 * Gets the current active scene.
	 *
	 * @returns {Object|null} The active scene object, or null if no scene is active.
	 */
	b8.Scene.get = function() {

		return activeScene;

	};


	/**
	 * Gets all scenes.
	 *
	 * @returns {Object} All scenes.
	 */
	b8.Scene.getAll = function() {

		return sceneList;

	};

} )( b8 );

( function( b8 ) {

	// Define the Renderer object inside b8.
	b8.Renderer = {};

	// Has the screen updated.
	let dirty = false;

	// Effects.
	let screenshakeDuration = 0;

	// Variables for screen effects.
	let vignetteGradient = null;
	let scanPattern = null;


	/**
	 * Initialization function that precomputes bloom and scanline ranges.
	 *
	 * @returns {void}
	 */
	const initCrt = () => {

		if ( !b8.Core.realCtx ) {

			setTimeout( initCrt, 10 );
			return;

		}

		if ( !vignetteGradient ) {

			// A gradient for the vignette effect.
			vignetteGradient = b8.Core.realCtx.createRadialGradient(
				b8.Core.realCanvas.width / 2,
				b8.Core.realCanvas.height / 2,
				Math.max( b8.Core.realCanvas.width, b8.Core.realCanvas.height ) * 0.4,

				b8.Core.realCanvas.width / 2,
				b8.Core.realCanvas.height / 2,
				Math.max( b8.Core.realCanvas.width, b8.Core.realCanvas.height ) * 0.9
			);
			vignetteGradient.addColorStop( 0, 'rgba(255,255,255,0)' );
			vignetteGradient.addColorStop( 0.7, 'rgba(0,0,0,0.5)' );
			vignetteGradient.addColorStop( 1, 'rgba(0,0,0,1)' );

		}

		if ( !scanPattern ) {

			// tiny 2px device-pixel scanline pattern
			const pat = new OffscreenCanvas( 1, 2 );
			const patCtx = pat.getContext( '2d' );
			patCtx.fillStyle = 'rgba(0,0,0,0.15)';
			patCtx.fillRect( 0, 1, 1, 1 );
			patCtx.fillStyle = 'rgba(255,255,255,0.0)';
			patCtx.fillRect( 0, 0, 1, 1 );
			scanPattern = b8.Core.realCtx.createPattern( pat, 'repeat' );

		}

	}

	document.addEventListener( 'b8.initComplete', initCrt );


	/**
	 * Renders the screen.
	 *
	 * @returns {void}
	 */
	b8.Renderer.render = function() {

		if ( b8.Core.crashed ) return;

		b8.Core.realCtx.imageSmoothingEnabled = false;

		// Canvas Drawing location.
		let x = 0;
		let y = 0;

		// Do screenshake.
		if ( screenshakeDuration > 0 ) {

			let amount = screenshakeDuration * b8.CONFIG.CHR_WIDTH;

			x = Math.round( ( Math.random() * amount ) - ( amount / 2 ) );
			y = Math.round( ( Math.random() * amount ) - ( amount / 2 ) );

			x = b8.Utilities.clamp( x, -6, 6 );
			y = b8.Utilities.clamp( y, -6, 6 );

			screenshakeDuration -= b8.Core.deltaTime;

		}

		// Reset real ctx composite mode.
		b8.Core.realCtx.globalCompositeOperation = 'source-over';

		// Clear the real canvas.
		b8.Core.realCtx.clearRect(
			0, 0,
			b8.Core.realCanvas.width,
			b8.Core.realCanvas.height
		);

		// Draw the offscreen canvas to the real canvas, scaling it up.
		b8.Core.realCtx.drawImage(
			b8.Core.offCanvas,
			x, y,
			b8.Core.realCanvas.width,
			b8.Core.realCanvas.height
		);

		dirty = false;

		b8.CursorRenderer.draw( b8.Core.realCtx );

		applyScanlines();
		applyVignette();

	}


	/**
	 * Resets the renderer state, stopping any ongoing effects.
	 *
	 * @returns {void}
	 */
	b8.Renderer.reset = function() {

		dirty = false;
		screenshakeDuration = 0;

	}


	/**
	 * Applies a vignette effect to the screen.
	 *
	 * @returns {void}
	 */
	const applyVignette = () => {

		if ( !vignetteGradient ) return;

		if ( !b8.CONFIG.CRT_ENABLE ) return;

		b8.Core.realCtx.save();
		b8.Core.realCtx.globalCompositeOperation = 'multiply';
		b8.Core.realCtx.fillStyle = vignetteGradient;
		b8.Core.realCtx.fillRect( 0, 0, b8.Core.realCanvas.width, b8.Core.realCanvas.height );
		b8.Core.realCtx.restore();

	};


	/**
	 * Applies a scanline effect to the screen.
	 *
	 * @returns {void}
	 */
	const applyScanlines = () => {

		if ( !scanPattern ) return;

		if ( !b8.CONFIG.CRT_ENABLE ) return;

		b8.Core.realCtx.save();
		b8.Core.realCtx.globalCompositeOperation = 'soft-light';
		b8.Core.realCtx.fillStyle = scanPattern;
		b8.Core.realCtx.fillRect( 0, 0, b8.Core.realCanvas.width, b8.Core.realCanvas.height );
		b8.Core.realCtx.restore();

	};


	/**
	 * Triggers the screenshake effect.
	 *
	 * @param {number} durationSeconds - The duration of the screenshake effect in seconds.
	 * @returns {boolean} Returns true if the screenshake effect was successfully triggered.
	 */
	b8.Renderer.shakeScreen = function( durationSeconds = 0.25 ) {

		b8.Utilities.checkNumber( "duration", durationSeconds );

		if ( durationSeconds <= 0 ) {
			b8.Utilities.warn( `Screenshake duration must be positive. Currently: ${durationSeconds}` );
			return false;
		}

		screenshakeDuration = durationSeconds;

		return true;

	}


	/**
	 * Marks the screen as dirty, so it will be re-rendered on the next frame.
	 *
	 * @returns {void}
	 */
	b8.Renderer.markDirty = function() {

		if ( dirty ) {
			return;
		}

		dirty = true;
		setTimeout( b8.Renderer.render, 1 );

	}


} )( b8 );

( function( b8 ) {

	b8.Random = {};

	/**
	 * The seed for the random number generator.
	 *
	 * @type {number}
	 */
	let randomSeed = null;


	/**
	 * Sets the seed for the random number generator.
	 * If the seed is null, the random number generator will reset to use the current time.
	 *
	 * @param {number|string} seed - The seed to use for the random number generator.
	 * @returns {void}
	 */
	b8.Random.setSeed = function( seed = null ) {

		// If no seed provided, use current time.
		if ( seed === null ) seed = Date.now();

		// Convert seed string to number.
		if ( typeof seed === "string" ) {
			seed = seed.split( "" ).reduce( ( a, b ) => a + b.charCodeAt( 0 ), 0 );
		}

		// Extra mixing step using xorshift.
		seed ^= seed << 13;
		seed ^= seed >> 17;
		seed ^= seed << 5;
		seed >>>= 0; // Ensure an unsigned 32-bit integer

		// Set the global seed value.
		randomSeed = seed;

		// Burn a few random numbers to mix up initial values.
		for ( let i = 0; i < 10; i++ ) {
			b8.Random.num();
		}

	}


	/**
	 * Returns the seed for the random number generator.
	 *
	 * @returns {number} The seed for the random number generator.
	 */
	b8.Random.getSeed = function() {

		return randomSeed;

	}


	/**
	 * Returns a random number between 0 and 1.
	 *
	 * @returns {number} A random number between 0 and 1.
	 */
	b8.Random.num = function() {

		const a = 1664525;
		const c = 1013904223;
		const m = 4294967296;

		randomSeed = ( randomSeed * a + c ) % m;

		return randomSeed / m;

	}


	/**
	 * Returns a random number (float) in the given closed interval.
	 *
	 * @param {number} min - The minimum value (inclusive).
	 * @param {number} max - The maximum value (inclusive).
	 * @returns {number} A random number between min and max.
	 */
	b8.Random.range = function( min = 0, max = 1 ) {

		b8.Utilities.checkNumber( "min", min );
		b8.Utilities.checkNumber( "max", max );

		// If max and min are the wrong way around, reverse them.
		if ( max <= min ) {
			const tmp = max;
			max = min;
			min = tmp;
		}

		return min + b8.Random.num() * ( max - min );

	}


	/**
	 * Returns a random integer in the given closed interval.
	 *
	 * @param {number} min - The minimum value (inclusive).
	 * @param {number} max - The maximum value (inclusive).
	 * @returns {number} A random integer between min and max.
	 */
	b8.Random.int = function( min, max ) {

		b8.Utilities.checkInt( "min", min );
		b8.Utilities.checkInt( "max", max );

		const randomValue = b8.Random.range( min, max );
		return Math.round( randomValue );

	}


	/**
	 * Returns a randomly picked element of the given array.
	 *
	 * @param {Array} array - The array to pick from.
	 * @returns {any} A randomly picked element of the array, or null if the array is empty.
	 */
	b8.Random.pick = function( array ) {

		b8.Utilities.checkArray( "array", array );

		// Pick a random number from 0 to array.length.
		const index = b8.Random.int( 0, array.length - 1 );

		return array[ index ];

	}


	const weightedArrayCache = new Map();


	/**
	 * Returns a randomly picked element of the given array, with a weighted probability.
	 *
	 * @param {Array} array - The array to pick from, with each element repeated a number of times.
	 * @param {number} decayFactor - The decay factor for the weighted array.
	 * @returns {any} A randomly picked element of the array, or null if the array is empty.
	 */
	b8.Random.pickWeighted = function( array, decayFactor = 0.2 ) {

		b8.Utilities.checkArray( "array", array );

		// Create a unique cache key based on the array and decayFactor
		const cacheKey = JSON.stringify( array ) + `|${decayFactor}`;

		// Check if the weighted array is already cached
		let weightedArray = weightedArrayCache.get( cacheKey );

		if ( !weightedArray ) {
			weightedArray = b8.Random.weightedArray( array, decayFactor );
			weightedArrayCache.set( cacheKey, weightedArray );
		}

		return b8.Random.pick( weightedArray );

	};


	/**
	 * Shuffles an array, randomly reordering the elements.
	 * Does not modify the original array. Returns the shuffled array.
	 *
	 * @param {Array} array - The array to shuffle.
	 * @returns {Array} The shuffled array.
	 */
	b8.Random.shuffleArray = function( array ) {

		b8.Utilities.checkArray( "array", array );

		array = array.slice();

		for ( let i = 0; i < array.length; i++ ) {
			const j = b8.Random.int( 0, array.length - 1 );
			const tmp = array[ i ];
			array[ i ] = array[ j ];
			array[ j ] = tmp;
		}

		return array;

	}


	/**
	 * Returns a random boolean value based upon the probability percentage provided.
	 *
	 * @param {number} probability - A percentage value between 0 and 100 representing the chance of returning true.
	 * @returns {boolean} True with the specified probability, false otherwise.
	 */
	b8.Random.chance = function( probability ) {

		b8.Utilities.checkNumber( "probability", probability );

		return b8.Random.num() <= ( probability / 100 );

	}


	/**
	 * Returns a weighted array of elements.
	 * The array uses a decay factor to determine the number of times each element should be repeated.
	 *
	 * @param {Array} array - The array to weight.
	 * @param {number} decayFactor - The decay factor for the weighted array.
	 * @returns {Array} The weighted array.
	 */
	b8.Random.weightedArray = function( array, decayFactor = 0.2 ) {

		b8.Utilities.checkArray( "array", array );

		const weightedArray = [];

		for ( let i = 0; i < array.length; i++ ) {
			const count = Math.pow( decayFactor, i ) * 10;
			for ( let j = 0; j < count; j++ ) {
				weightedArray.push( array[ i ] );
			}
		}

		return weightedArray;

	}


	/**
	 * Returns a consistent pseudo-random number between 0 and 1 for the given 2D coordinates and seed.
	 * Uses a simple hash function to generate the number.
	 *
	 * @param {number} x - The x coordinate.
	 * @param {number} y - The y coordinate.
	 * @param {number} seed - The seed value.
	 * @returns {number} A pseudo-random number between 0 and 1.
	 */
	b8.Random.coord2D = function( x, y, seed ) {

		let h = 2166136261 ^ seed;
		h = Math.imul( h ^ x, 16777619 );
		h = Math.imul( h ^ y, 16777619 );
		h ^= h >>> 13; h = Math.imul( h, 0x85ebca6b );
		h ^= h >>> 16;
		return ( h >>> 0 ) / 4294967296;

	}


	/**
	 * Returns a smooth noise value between 0 and 1 for the given 2D coordinates and seed.
	 * Uses bilinear interpolation between the corner values.
	 *
	 * @param {number} x - The x coordinate.
	 * @param {number} y - The y coordinate.
	 * @param {number} seed - The seed value.
	 * @param {number} freq - The frequency of the noise.
	 * @returns {number} A smooth noise value between 0 and 1.
	 */
	b8.Random.smooth2D = function( x, y, seed = 0, freq = 1 ) {

		// scale space to control feature size
		x *= freq;
		y *= freq;

		const ix = Math.floor( x );
		const iy = Math.floor( y );
		const fx = x - ix;
		const fy = y - iy;

		// corner values from your coord-based RNG
		const v00 = b8.Random.coord2D( ix, iy, seed );
		const v10 = b8.Random.coord2D( ix + 1, iy, seed );
		const v01 = b8.Random.coord2D( ix, iy + 1, seed );
		const v11 = b8.Random.coord2D( ix + 1, iy + 1, seed );

		// fade curves for smooth interpolation
		const u = b8.Math.fade( fx );
		const v = b8.Math.fade( fy );

		// bilinear interpolation
		const i1 = b8.Math.lerp( v00, v10, u );
		const i2 = b8.Math.lerp( v01, v11, u );
		return b8.Math.lerp( i1, i2, v );

	}


	/**
	 * Resets the random number generator to its initial state.
	 * This clears the seed and any cached weighted arrays.
	 *
	 * @returns {void}
	 */
	b8.Random.reset = function() {

		randomSeed = null;
		weightedArrayCache.clear();

		b8.Random.setSeed();

	}



} )( b8 );


( function( b8 ) {

	b8.Path = {};


	b8.Path.AnimationMode = {
		LOOP: 'loop',
		PINGPONG: 'pingpong',
		ONCE: 'once',
	};


	const movementMap = {
		U: { dx: 0, dy: -1 },
		D: { dx: 0, dy: 1 },
		L: { dx: -1, dy: 0 },
		R: { dx: 1, dy: 0 },
	};


	/**
	 * Parse a path code string into an array of steps.
	 *
	 * Commands:
	 * U - move up
	 * D - move down
	 * L - move left
	 * R - move right
	 * P - pause (stay in place)
	 * F + direction - face direction without moving (U/D/L/R)
	 *
	 * Each command can be followed by a number to indicate how many steps to take.
	 * If no number is provided, it defaults to 1.
	 *
	 * Examples:
	 * "UUDDLRLR" - up 2, down 2, left 1, right 1, left 1, right 1
	 * "U2R3DL4P2" - up 2, right 3, down 1, left 4, pause 2
	 * "FUR2FD2P3" - face up, right 2, face down 2, pause 3
	 * "R5P" - right 5, pause 1
	 *
	 * @param {string} code - The path code string.
	 * @param {number} startCol - The starting column position.
	 * @param {number} startRow - The starting row position.
	 * @param {string} initialDir - The initial facing direction ('U', 'D', 'L', 'R').
	 * @returns {Array} Array of steps with col, row, and dir properties.
	 */
	b8.Path.parseCode = function( code, startCol = 0, startRow = 0, initialDir = 'D' ) {

		b8.Utilities.checkString( 'code', code );
		b8.Utilities.checkNumber( 'startRow', startRow );
		b8.Utilities.checkNumber( 'startCol', startCol );
		b8.Utilities.checkString( 'initialDir', initialDir );
		if ( !'UDLR'.includes( initialDir ) ) {
			b8.Utilities.fatal( 'Path.parseCode: initialDir must be one of U, D, L, R' );
		}

		let col = startCol;
		let row = startRow;
		let currentDir = initialDir;
		const steps = [];

		const cleaned = code.replace( /\s+/g, '' ).toUpperCase();
		let i = 0;

		while ( i < cleaned.length ) {

			const cmd = cleaned[ i ];

			// simple movement command
			if ( 'UDLR'.includes( cmd ) ) {

				i++;

				const { count, index } = _parseNumber( cleaned, i );
				i = index;

				for ( let n = 0; n < count; n++ ) {
					col += movementMap[ cmd ].dx;
					row += movementMap[ cmd ].dy;
					_pushStep( steps, col, row, cmd );
				}

				continue;

			}

			// pause command
			// This duplicates the current position in the steps array.
			if ( cmd === 'P' ) {

				i++;
				const { count, index } = _parseNumber( cleaned, i );
				i = index;

				_pushStep( steps, col, row, currentDir, count );

				continue;

			}

			// facing command: F + U/D/L/R + optional number
			if ( cmd === 'F' ) {

				i++;
				const faceDir = cleaned[ i ];
				if ( !'UDLR'.includes( faceDir ) ) {
					b8.Utilities.fatal( 'F must be followed by U/D/L/R, got: ' + faceDir );
				}
				i++;

				const { count, index } = _parseNumber( cleaned, i );
				i = index;

				_pushStep( steps, col, row, cmd + faceDir, count );

				continue;

			}

			b8.Utilities.fatal( `Invalid path command: ${cmd} in path code: ${code}` );

		}

		return steps;

	}


	/**
	 * Validates the syntax of a beepmini path string.
	 *
	 * @param {string} path - The path string to validate.
	 * @returns {boolean} - True if the path syntax is valid, false otherwise.
	 */
	b8.Path.validPathSyntax = function( path ) {

		// Define valid characters and starting letters as constants for easy modification
		const VALID_CHARACTERS = 'UDLRFP'; // Valid movement and command letters

		if ( typeof path !== 'string' ) {
			return false;
		}

		// Reject empty or whitespace-only strings
		if ( path.trim().length === 0 ) {
			return false;
		}

		// Check if the path contains only valid characters
		if ( !new RegExp( `^[\\d\\s${VALID_CHARACTERS}]+$` ).test( path ) ) {
			return false;
		}

		// Check if the path contains at least one letter from the valid character set
		if ( !new RegExp( `[${VALID_CHARACTERS}]` ).test( path ) ) {
			return false;
		}

		// Check if the path starts with one of the valid starting letters
		if ( !new RegExp( `^[${VALID_CHARACTERS}]` ).test( path ) ) {
			return false;
		}

		return true;

	}


	/**
	 * Parse a number from the cleaned string starting at index i.
	 * If no number is found, defaults to 1.
	 *
	 * @param {string} cleaned - The cleaned path code string.
	 * @param {number} i - The current index in the string.
	 * @returns {number} The parsed number or 1 if no number is found.
	 */
	function _parseNumber( cleaned, i ) {

		let numStr = '';

		while ( i < cleaned.length && /[0-9]/.test( cleaned[ i ] ) ) {
			numStr += cleaned[ i++ ];
		}

		return { count: numStr ? parseInt( numStr, 10 ) : 1, index: i };

	}


	/**
	 * Push steps into the steps array.
	 *
	 * @param {Array} steps - The array to store steps.
	 * @param {number} col - The x-coordinate of the step.
	 * @param {number} row - The y-coordinate of the step.
	 * @param {string|null} dir - The direction of the step.
	 * @param {number} count - The number of steps to push.
	 */
	function _pushStep( steps, col, row, dir = null, count = 1 ) {

		for ( let n = 0; n < count; n++ ) {
			steps.push(
				{
					col,
					row,
					dir: dir,
				}
			);
		}

	}

} )( b8 );
( function( b8 ) {

	b8.Passcodes = {};


	/**
	 * The length of the level passcodes.
	 *
	 * @type {number}
	 */
	b8.Passcodes.codeLength = 4;


	/**
	 * Function to generate a passcode for a given id.
	 * This is intended for level passcodes.
	 *
	 * @param {string} id - The id to generate a code for.
	 * @returns {string} The generated code.
	 */
	b8.Passcodes.getCode = function( id ) {

		b8.Utilities.checkIsSet( "id", id );

		// Combine the id and secret key for uniqueness.
		const combined = id + b8.CONFIG.PASSKEY;

		// Generate hash of the combined string.
		let hash = hashString( combined );

		// Remove non-alphabetic characters and convert to uppercase.
		hash = hash.replace( /[^a-zA-Z]/g, '' );
		hash = hash.toUpperCase(); // Convert to uppercase

		// Return the first 'codeLength' characters.
		return hash.substring( 0, b8.Passcodes.codeLength );

	}


	/**
	 * Function to list passcodes for a given number of levels. This is useful for debugging and testing.
	 *
	 * @param {number} count - The number of levels to list codes for.
	 * @returns {void}
	 */
	b8.Passcodes.listCodes = function( count ) {

		for ( let l = 0; l < count; l++ ) {

			const levelCode = b8.Passcodes.getCode( l );
			console.log( `Level ${l + 1} Code: ${levelCode}` );

		}

	}


	/**
	 * Function to check if a given code is valid for a given id.
	 *
	 * @param {string} id - The id to check the code for.
	 * @param {string} code - The code to check.
	 * @returns {boolean} True if the code is valid, false otherwise.
	 */
	b8.Passcodes.checkCode = function( id, code ) {

		b8.Utilities.checkIsSet( "id", id );
		b8.Utilities.checkString( "code", code );

		const generatedCode = b8.Passcodes.getCode( id );
		return generatedCode === code;

	}


	/**
	 * Function to work out the id from the code.
	 *
	 * @param {string} code - The code to get the id for.
	 * @returns {int} The id for the code.
	 */
	b8.Passcodes.getId = function( code ) {

		b8.Utilities.checkString( "code", code );

		// Loop through all levels to find a match.
		code = code.toUpperCase();
		for ( c = 1; c < 999; c++ ) {
			if ( b8.Passcodes.checkCode( c, code ) ) {
				return c;
			}
		}

		// Return null if no match is found.
		return null;

	}


	/**
	 * Display a dialog to accept a passcode. This is automatically centered on
	 * the screen.
	 *
	 * The level id of the specified passcode is returned as an integer.
	 *
	 * This can be coloured with the standard b8.color function.
	 *
	 * @returns {number|null} The level id of the passcode.
	 */
	b8.Passcodes.input = async function() {

		const message = 'Enter code:';
		const width = message.length + 2 + 2;
		const height = 6;

		let xPosition = Math.round( ( b8.CONFIG.SCREEN_COLS - width ) / 2 );
		let yPosition = Math.round( ( b8.CONFIG.SCREEN_ROWS - height ) / 2 );

		b8.Core.setCursorLocation( xPosition, yPosition );
		b8.TextRenderer.printBox( width, height );

		b8.Core.setCursorLocation( xPosition + 2, yPosition + 2 );

		const passcode = await b8.Async.readLine( message, "", b8.Passcodes.codeLength );
		const value = b8.Passcodes.getId( passcode );

		return value;

	}


	/**
	 * Function to hash a string.
	 *
	 * @param {string} input - The string to hash.
	 * @returns {string} The hashed string.
	 */
	function hashString( input ) {

		input = btoa( input );
		let hash = 0;
		let result = '';

		// Loop to hash the input string.
		for ( let i = 0; i < input.length; i++ ) {
			hash = ( hash << 5 ) - hash + input.charCodeAt( i );
			hash = hash & hash; // Convert to 32bit integer
		}

		// Loop to extend the length of the result by rehashing.
		// Adjust to control string length.
		for ( let j = 0; j < 5; j++ ) {
			hash = ( hash << 5 ) - hash + b8.CONFIG.PASSKEY.charCodeAt( j % b8.CONFIG.PASSKEY.length );
			// Append base-36 to the result.
			result += Math.abs( hash ).toString( 36 );
		}

		return result;

	}


} )( b8 );

( function( b8 ) {

	/**
	 * b8.Particles handles particles.
	 */
	b8.Particles = {};

	// Private particle array.
	let particles_ = [];


	/**
	 * Adds a new particle to the system.
	 *
	 * Each particle is an object with properties:
	 * - x, y: position,
	 * - vx, vy: velocity (pixels per second, default=0),
	 * - life: remaining life time (seconds, default=1),
	 * - size: square size (pixels, default=1),
	 * - color: fill color (a b8 palette id, default=15).
	 * - gravity: gravity (pixels per second, default=0).
	 *
	 * @param {object} particle - The particle object to add.
	 * @returns {void}
	 */
	b8.Particles.add = function( x, y, props ) {

		b8.Utilities.checkNumber( 'x', x );
		b8.Utilities.checkNumber( 'y', y );
		b8.Utilities.checkObject( 'props', props );

		const defaults = {
			x: x,
			y: y,
			vx: 0,
			vy: 0,
			life: 1,
			size: 1,
			color: 15,
			gravity: 0,
		}

		const newParticle = Object.assign( {}, defaults, props );

		// Particle Color.
		if ( Array.isArray( newParticle.color ) ) {
			newParticle.color = b8.Random.pick( newParticle.color );
		}

		// Particle Size.
		if ( Array.isArray( newParticle.size ) ) {
			newParticle.size = b8.Random.pick( newParticle.size );
		}

		particles_.push( newParticle );

	};


	/**
	 * Resets the particle system, removing all particles.
	 *
	 * @returns {void}
	 */
	b8.Particles.reset = function() {

		b8.Particles.clearAll();

	}


	/**
	 * Adds an explosion of particles to the system.
	 *
	 * The explosion is created at the x, y position with a number of particles.
	 *
	 * The optional properties include:
	 * - size: The size of the particles (in pixels, default=1).
	 * - color: The color of the particles (a b8 palette id, default=fgColor).
	 * - life: The life of the particles (in seconds, default=2).
	 * - speed: The speed of the particles (in pixels per second, default=25).
	 * - gravity: The gravity of the particles (in pixels per second, default=0).
	 *
	 * @param {number} x - The x position of the explosion.
	 * @param {number} y - The y position of the explosion.
	 * @param {number} count - The number of particles to add.
	 * @param {object} props - The properties of the explosion.
	 * @returns {void}
	 */
	b8.Particles.createExplosion = function( x, y, count = 10, props = {} ) {

		b8.Utilities.checkNumber( 'x', x );
		b8.Utilities.checkNumber( 'y', y );
		b8.Utilities.checkNumber( 'count', count );
		b8.Utilities.checkObject( 'props', props );

		const defaults = {
			size: 1,
			color: b8.Core.drawState.fgColor,
			life: 2,
			speed: 25,
			gravity: 0,
		};

		const newExplosion = Object.assign( {}, defaults, props );

		for ( let i = 0; i < count; i++ ) {

			const angle = b8.Random.range( 0, Math.PI * 2 );
			const speed = b8.Random.range( newExplosion.speed / 2, newExplosion.speed );

			b8.Particles.add(
				x,
				y,
				{
					size: newExplosion.size,
					color: newExplosion.color,
					life: newExplosion.life,
					vx: Math.cos( angle ) * speed,
					vy: Math.sin( angle ) * speed,
					g: newExplosion.gravity,
				}
			);

		}

	};


	/**
	 * Updates all particles.
	 *
	 * If you are using b8 scenes or the b8 game loop (doframe) then this
	 * is called automatically and you don't need to call it manually.
	 *
	 * @param {number} dt - Delta time to update particle movement.
	 * @returns {void}
	 */
	b8.Particles.update = function( dt ) {

		// Loop backwards to allow for removal.
		for ( let i = particles_.length - 1; i >= 0; i-- ) {

			const p = particles_[ i ];
			// Apply gravity.
			p.vy += ( p.g * dt );
			// Update position based on velocity.
			p.x += p.vx * dt;
			p.y += p.vy * dt;
			// Decrease life.
			p.life -= dt;

			// Remove particle if life expired.
			if ( p.life <= 0 ) {
				particles_.splice( i, 1 );
			}

		}

	};


	/**
	 * Renders all particles as squares.
	 *
	 * This should be called in your render method. This is not called
	 * automatically which allows you to control the draw order.
	 *
	 * @returns {void}
	 */
	b8.Particles.render = function() {

		for ( let i = 0; i < particles_.length; i++ ) {

			const p = particles_[ i ];
			const center = p.size / 2;

			// Draw the square particle with p.x, p.y at the center.
			b8.Core.offCtx.fillStyle = b8.Core.getColorHex( p.color );
			b8.Core.offCtx.fillRect( Math.round( p.x - center ), Math.round( p.y - center ), Math.round( p.size ), Math.round( p.size ) );

		}

	};


	/**
	 * Clears all particles from the system.
	 *
	 * @returns {void}
	 */
	b8.Particles.clearAll = function() {

		particles_ = [];

	};


	/**
	 * Returns the particles array.
	 *
	 * This is useful for debugging or if you want to manipulate the particles
	 * directly.
	 *
	 * @returns {array} The particles array.
	 */
	b8.Particles.getParticles = function() {

		return [ ...particles_ ];

	};


	/**
	 * Sets the particles array.
	 *
	 * Can be used with Particles.getParticles to manipulate the particles directly.
	 *
	 * @param {array} particles - The particles array.
	 * @returns {void}
	 */
	b8.Particles.setParticles = function( particles ) {

		b8.Utilities.checkArray( 'particles', particles );

		particles_ = particles;

	};

} )( b8 );

( function( b8 ) {

	let timeHidden = 0;
	let isHidden = false;


	/**
	 * Called when the document is hidden (blurred).
	 *
	 * @returns {void}
	 */
	function sleep() {

		if ( isHidden ) return;

		isHidden = true;

		// Document is hidden (blurred) so start the timer.
		timeHidden = Date.now();

		// Log the event and notify listeners.
		b8.Utilities.event( 'pageVisibility.sleep' );

	};


	/**
	 * Called when the document is shown (focused).
	 *
	 * @returns {void}
	 */
	function wake() {

		if ( !isHidden ) return;
		isHidden = false;

		if ( timeHidden === 0 ) return;

		// Document is shown again so the timer is stopped and totalled.
		const timeAsleep = Date.now() - timeHidden;
		b8.Utilities.log( 'Time asleep:', ( timeAsleep / 1000 ).toFixed( 3 ) );
		b8.Utilities.event( 'pageVisibility.wake', { time: timeAsleep } );
		timeHidden = 0;

	}


	// Set an event when the document loses focus (change tab/ window).
	document.addEventListener(
		'visibilitychange',
		function() {

			if ( document.hidden ) {
				sleep();
			} else {
				wake();
			}

		}
	);

	window.addEventListener( 'blur', () => sleep() );
	window.addEventListener( 'focus', () => wake() );

} )( b8 );
/**
 * b8 Music Module.
 *
 * This module handles the creation, manipulation, and playback of procedurally generated music.
 */
( function( b8 ) {

	b8.Music = {};

	/**
	 * BeepMini uses a custom version of p1.js for music generation and playback.
	 *
	 * Alphabet used for p1.js music notation.
	 * p1.js supports 52 keys using these 52 characters.
	 */
	const p1Alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";


	/**
	 * Predefined song styles with associated tempo ranges, hold durations, and musical roles.
	 *
	 * Each style defines:
	 * - tempo: An array of possible tempos in BPM.
	 * - hold: An array of possible p1.js hold values.
	 * - channels: An array of musical roles that determine which parts to generate.
	 *
	 * Styles:
	 * - calm: Slower tempo, longer holds, and simpler instrumentation.
	 * - arcade: Medium tempo, moderate holds, and more varied instrumentation.
	 * - busy: Faster tempo, shorter holds, and complex instrumentation.
	 *
	 * @type {Object<string, {tempo: Array<number>, hold: Array<number>, channels: Array<string>}>}
	 */
	const SONG_STYLES = {
		calm: {
			tempo: [ 50, 70, 100 ],
			hold: [ 110, 120, 130, 140 ],
			noteCount: [ 64, 96, 128 ],
			chordLength: [ 8, 16 ],
			channels: [ "bass", "chords", "melody" ],
			melodyDensity: 2,
			melodyNotes: [ 0, 0, 2 ],
			chordDensity: 1,
			bassInterval: [ 8, 16 ],
			arpChance: 10,
			drumDensity: [ 0 ],
		},

		arcade: {
			tempo: [ 170, 200, 240 ],
			hold: [ 50, 60, 70, 80 ],
			noteCount: [ 48, 64, 96 ],
			chordLength: [ 4, 8 ],
			chordDensity: 4,
			channels: [ "bass", "arp", "melody", "drums" ],
			melodyDensity: 5,
			melodyNotes: [ 0, 0, 1, 2, 2 ],
			bassInterval: [ 4, 8 ],
			arpChance: 50,
			drumDensity: [ 2, 3 ],
		},

		busy: {
			tempo: [ 180, 200, 220 ],
			hold: [ 60, 70, 80 ],
			noteCount: [ 64, 96 ],
			chordLength: [ 4, 8 ],
			channels: [ "bass", "chords", "arp", "melody", "drums" ],
			melodyDensity: 6,
			melodyNotes: [ 0, 1, 2, 2, 3 ],
			chordDensity: 7,
			bassInterval: [ 2, 4 ],
			arpChance: 40,
			drumDensity: [ 2, 3 ],
		},

		menu: {
			tempo: [ 60, 70 ],
			hold: [ 140, 160, 180 ],
			noteCount: [ 64, 96, 128 ],
			chordLength: [ 16, 32 ],
			channels: [ "bass", "chords" ],
			melodyDensity: 1,
			melodyNotes: [ 0 ],
			chordDensity: 1,
			bassInterval: [ 16 ],
			arpChance: 0,
			drumDensity: [ 0 ],
		},

		adventure: {
			tempo: [ 70, 85, 100 ],
			hold: [ 120, 150, 180 ],
			noteCount: [ 128, 160, 192 ],
			chordLength: [ 16, 32 ],
			chordDensity: 1,
			channels: [ "bass", "chords", "melody" ],
			melodyDensity: 3,
			melodyNotes: [ 0, 1, 2, 2, 3 ],
			bassInterval: [ 8, 16 ],
			arpChance: 5,
			drumDensity: [ 0 ],
		},

		puzzle: {
			tempo: [ 130, 150, 170 ],
			hold: [ 70, 80, 90 ],
			noteCount: [ 80, 96, 128 ],
			chordLength: [ 4, 8 ],
			channels: [ "bass", "arp", "melody", "drums" ],
			melodyDensity: 5,
			melodyNotes: [ 0, 1, 2, 3 ],
			chordDensity: 7,
			bassInterval: [ 4, 8 ],
			arpChance: 90,
			drumDensity: [ 1, 2 ],
		},

		shooter: {
			tempo: [ 175, 200, 225 ],
			hold: [ 30, 40, 50, 60 ],
			noteCount: [ 48, 64 ],
			chordLength: [ 2 ],
			channels: [ "bass", "arp", "counter", "drums" ],
			melodyDensity: 9,
			melodyNotes: [ 1, 2, 2, 3, 3 ],
			chordDensity: 9,
			bassInterval: [ 2 ],
			arpChance: 95,
			drumDensity: [ 4, 5 ],
		},

		triumphant: {
			tempo: [ 100, 120, 140 ],
			hold: [ 80, 90, 100 ],
			noteCount: [ 64, 96 ],
			chordLength: [ 8, 16 ],
			channels: [ "bass", "chords", "melody", "drums" ],
			melodyDensity: 5,
			melodyNotes: [ 0, 0, 1, 2, 2 ],  // strong chord tones
			chordDensity: 8,
			bassInterval: [ 4, 8 ],
			arpChance: 10,
			drumDensity: [ 2, 3 ],
		},

		mysterious: {
			tempo: [ 60, 75, 90 ],
			hold: [ 130, 150, 170 ],
			noteCount: [ 96, 128, 160 ],
			chordLength: [ 16, 32 ],
			channels: [ "bass", "chords", "melody" ],
			melodyDensity: 2,
			melodyNotes: [ 0, 1, 2, 3 ],
			chordDensity: 2,
			bassInterval: [ 8, 16 ],
			arpChance: 15,
			drumDensity: [ 0 ],
		},

		stealth: {
			tempo: [ 85, 100, 115 ],
			hold: [ 90, 110, 130 ],
			noteCount: [ 64, 96, 128 ],
			chordLength: [ 8, 16 ],
			channels: [ "bass", "chords", "counter" ],
			melodyDensity: 2,
			melodyNotes: [ 0, 1, 2 ],
			chordDensity: 2,
			bassInterval: [ 8, 8, 16 ],
			arpChance: 20,
			drumDensity: [ 0, 1 ],
		},

		dreamy: {
			tempo: [ 70, 90, 110 ],
			hold: [ 110, 130, 150 ],
			noteCount: [ 96, 128, 160 ],
			chordLength: [ 8, 16, 32 ],
			channels: [ "bass", "chords", "counter", "melody" ],
			melodyDensity: 3,
			melodyNotes: [ 0, 1, 2, 2, 3 ],
			chordDensity: 2,
			bassInterval: [ 8, 16 ],
			arpChance: 25,
			drumDensity: [ 0 ],
		},

		tense: {
			tempo: [ 120, 140, 160 ],
			hold: [ 70, 80, 90 ],
			noteCount: [ 80, 96, 128 ],
			chordLength: [ 4, 8 ],
			channels: [ "bass", "arp", "melody", "drums" ],
			melodyDensity: 5,
			melodyNotes: [ 0, 1, 2, 3 ],
			chordDensity: 6,
			bassInterval: [ 4, 8 ],
			arpChance: 70,
			drumDensity: [ 1, 2 ],
		},

		march: {
			tempo: [ 95, 110, 125 ],
			hold: [ 70, 80, 90, 100 ],
			noteCount: [ 64, 96, 128 ],
			chordLength: [ 8, 16 ],
			channels: [ "bass", "chords", "melody", "drums" ],
			melodyDensity: 3,
			melodyNotes: [ 0, 0, 1, 2 ],
			chordDensity: 4,
			bassInterval: [ 4, 8 ],
			arpChance: 5,
			drumDensity: [ 2, 3 ],
		},

		exploration: {
			tempo: [ 80, 95, 110 ],
			hold: [ 100, 120, 140 ],
			noteCount: [ 96, 128, 160 ],
			chordLength: [ 8, 16, 32 ],
			channels: [ "bass", "chords", "melody" ],
			melodyDensity: 3,
			melodyNotes: [ 0, 1, 2, 2, 3 ],
			chordDensity: 2,
			bassInterval: [ 8, 16 ],
			arpChance: 10,
			drumDensity: [ 0, 1 ],
		},

	};


	/**
	 * Common chord progressions represented as arrays of Roman numeral chord
	 * identifiers.
	 *
	 * Each progression is a sequence of chords that can be used to generate the
	 * harmonic structure of a song.
	 * The Roman numerals correspond to scale degrees and chord qualities
	 * (e.g., "I" is the tonic major chord, "VIm" is the minor chord built on the sixth degree).
	 *
	 * @type {Array<Array<string>>}
	 */
	const PROGRESSIONS = [
		[ "I", "V", "VIm", "IV" ],
		[ "I", "VIm", "IV", "V" ],
		[ "VIm", "IV", "I", "V" ],
		[ "I", "IV", "V", "I" ],
		[ "I", "IIm", "V", "I" ]
	];


	/**
	 * Chord mapping for key C.
	 *
	 * @type {Object<string, Array<string>>}
	 */
	const chordMap = {
		I: [ "C4", "E4", "G4", "B4" ],
		IIIm: [ "E4", "G4", "B4", "D5" ],
		VIm: [ "A3", "C4", "E4", "G4" ],
		IV: [ "F4", "A4", "C5", "E5" ],
		IIm: [ "D4", "F4", "A4", "C5" ],
		V: [ "G3", "B3", "D4", "F4" ],
		VIIm: [ "B3", "D4", "F4", "A4" ]
	};


	/**
	 * Tonal palettes used to give songs different harmonic colours.
	 *
	 * Each palette provides scale intervals relative to its tonic. Most palettes
	 * also provide their own diatonic chord map. The pentatonic palette keeps the
	 * familiar major harmony while restricting melodic movement to five notes.
	 *
	 * @type {Object<string, {scaleIntervals: Array<number>, chordMap: Object<string, Array<string>>}>}
	 */
	const TONAL_PALETTES = {
		major: {
			scaleIntervals: [ 0, 2, 4, 5, 7, 9, 11 ],
			chordMap: chordMap
		},
		naturalMinor: {
			scaleIntervals: [ 0, 2, 3, 5, 7, 8, 10 ],
			chordMap: {
				I: [ "C4", "Eb4", "G4", "Bb4" ],
				IIIm: [ "Eb4", "G4", "Bb4", "D5" ],
				VIm: [ "Ab3", "C4", "Eb4", "G4" ],
				IV: [ "F4", "Ab4", "C5", "Eb5" ],
				IIm: [ "D4", "F4", "Ab4", "C5" ],
				V: [ "G3", "Bb3", "D4", "F4" ],
				VIIm: [ "Bb3", "D4", "F4", "Ab4" ]
			}
		},
		dorian: {
			scaleIntervals: [ 0, 2, 3, 5, 7, 9, 10 ],
			chordMap: {
				I: [ "C4", "Eb4", "G4", "Bb4" ],
				IIIm: [ "Eb4", "G4", "Bb4", "D5" ],
				VIm: [ "A3", "C4", "Eb4", "G4" ],
				IV: [ "F4", "A4", "C5", "Eb5" ],
				IIm: [ "D4", "F4", "A4", "C5" ],
				V: [ "G3", "Bb3", "D4", "F4" ],
				VIIm: [ "Bb3", "D4", "F4", "A4" ]
			}
		},
		mixolydian: {
			scaleIntervals: [ 0, 2, 4, 5, 7, 9, 10 ],
			chordMap: {
				I: [ "C4", "E4", "G4", "Bb4" ],
				IIIm: [ "E4", "G4", "Bb4", "D5" ],
				VIm: [ "A3", "C4", "E4", "G4" ],
				IV: [ "F4", "A4", "C5", "E5" ],
				IIm: [ "D4", "F4", "A4", "C5" ],
				V: [ "G3", "Bb3", "D4", "F4" ],
				VIIm: [ "Bb3", "D4", "F4", "A4" ]
			}
		},
		pentatonic: {
			scaleIntervals: [ 0, 2, 4, 7, 9 ],
			chordMap: chordMap
		}
	};


	/**
	 * Mapping of key shifts for transposition.
		 *
		 * @type {Object<string, number>}
		 */
	const keyShift = {
		C: 0,
		D: 2,
		Eb: 3,
		F: 5,
		G: 7,
		A: 9,
		Bb: 10
	};


	/**
	 * Available instrument options.
	 *
	 * @type {Array<number>}
	 */
	const instrumentOptions = [ 0, 1, 2, 3, 4, 5 ];


	/**
	 * Available drum options.
	 *
	 * @type {Array<number>}
	 */
	const drumOptions = [ 6, 7 ];


	/**
	 * Seed-plan options used to build a song blueprint.
	 *
	 * Keeping these lists in one place makes the high-level musical choices
	 * easier to scan and tweak without digging through the generator logic.
	 *
	 * @type {Object<string, Array<any>>}
	 */
	const SONG_PLAN_OPTIONS = {
		sectionForms: [
			[ "A", "A_PRIME" ],
			[ "A", "B" ],
			[ "A", "B", "A" ],
			[ "A", "B", "A_PRIME" ],
			[ "A", "B", "C" ],
			[ "A", "A_PRIME", "B", "A" ],
			[ "A", "A_PRIME", "B", "C" ],
			[ "A", "B", "A_PRIME", "C" ],
			[ "A", "B", "A", "C" ],
			[ "A", "B", "C", "A" ],
			[ "A", "B", "A_PRIME", "B", "C" ],
			[ "A", "A_PRIME", "B", "A_PRIME", "C" ]
		],
		contours: [ "rise", "fall", "arch", "wave", "hover", "stair" ],
		cadenceTarget: [ "root", "root", "third", "fifth" ],
		// Major remains the common home base, while the other palettes provide
		// predictable but noticeably different moods for some seeds.
		palettes: [ "major", "major", "major", "naturalMinor", "dorian", "mixolydian", "pentatonic" ],
		arrangements: [
			{
				name: "build",
				roles: {
					A: [ "bass", "chords" ],
					A_PRIME: [ "bass", "chords", "melody" ],
					B: [ "bass", "chords", "arp", "melody" ],
					C: [ "bass", "chords", "arp", "melody", "counter", "drums" ]
				}
			},
			{
				name: "wave",
				roles: {
					A: [ "bass", "chords", "melody" ],
					A_PRIME: [ "bass", "chords", "arp", "melody" ],
					B: [ "bass", "chords", "counter" ],
					C: [ "bass", "chords", "arp", "melody", "counter", "drums" ]
				}
			},
			{
				name: "spotlight",
				roles: {
					A: [ "bass", "chords", "counter" ],
					A_PRIME: [ "bass", "chords", "melody" ],
					B: [ "bass", "chords", "arp", "melody", "drums" ],
					C: [ "bass", "chords", "arp", "melody", "counter", "drums" ]
				}
			}
		]
	};


	/**
	 * Compact rhythm-template definitions.
	 *
	 * Each string represents one bar of eight steps, where "x" means play and
	 * "." means rest. This is much easier to read than large arrays of booleans.
	 *
	 * @type {Object<string, Object<string, Array<string>>>}
	 */
	const RHYTHM_TEMPLATE_PATTERNS = {
		bass: {
			fast: [ "x.x.x.x.", "x.xx.x.x.", "xx.xx.x." ],
			medium: [ "x...x...", "x.x.x...", "x..xx..." ],
			slow: [ "x...x...", "x.....x.", "x...x..x" ]
		},
		drums: {
			light: [ "x...x...", "x.....x." ],
			medium: [ "x...x...", "x..xx..x", "x.x.x..." ],
			heavy: [ "x.x.x.x.", "x.xx.x.x." ]
		},
		arp: {
			normal: [ "xxxxxxxx", "x.x.x.x.", "xx.xxx.x" ],
			busy: [ "xxxxxxxx", "x.xx.x.xx", "xx.xxx.x" ]
		},
		chords: {
			light: [ "x...x...", "x.x.x.x.", "x..xx..x" ],
			dense: [ "x.x.x.x.", "x..xx.x.", "xx..xx.." ]
		},
		counter: {
			light: [ "x..x.x..", ".x.x.x.x", "x.x..x.." ],
			dense: [ "x.xx.x.x", ".x.xx.x.", "x.x.x.x." ]
		},
		melody: {
			sparse: [ "x...x...", "x..x....", "x.....x." ],
			normal: [ "x.x.x...", "x..xx.x.", "x.x..x.x" ],
			dense: [ "x.xxx.x.", "xx.xx.xx", "x.x.xx.x" ]
		}
	};


	/**
	 * Stores the most recent song so it can be restarted after pausing.
	 * This is necessary because p1.js does not have a built-in pause/resume functionality.
	 *
	 * @type {string|null}
	 */
	let currentSong = null;


	/**
	 * Generates multi-track music in p1.js format.
	 * It creates a chord progression and then generates various parts (melody, chord, or drum).
	 * Optionally prepends tempo and hold information to the first part.
	 *
	 * @param {Object} [options] - Options for music generation.
	 * @param {number} [options.seed] - Random seed.
	 * @param {number} [options.noteCount] - Number of beats/positions.
	 * @param {number} [options.channelCount] - Number of parts to generate.
	 * @param {string} [options.style] - Song style name.
	 * @param {number|null} [options.tempo] - Tempo in BPM. If null, tempo info is omitted.
	 * @param {number|null} [options.hold] - Hold duration. If null, hold info is omitted.
	 * @returns {string} The generated multi-track music string.
	 */
	b8.Music.generate = function( options ) {

		if ( options && typeof options.seed !== "undefined" ) {
			b8.Random.setSeed( options.seed );
		}

		var styleName = options && options.style
			? options.style
			: b8.Random.pick( Object.keys( SONG_STYLES ) );

		var style = SONG_STYLES[ styleName ] || SONG_STYLES.arcade;

		const defaultOptions = {
			seed: b8.Random.int( 10000, 99999 ),
			style: styleName,
			noteCount: b8.Random.pick( style.noteCount || [ 32, 48, 64 ] ),
			channelCount: style.channels.length,
			tempo: b8.Random.pick( style.tempo ),
			hold: b8.Random.pick( style.hold )
		};

		const opts = Object.assign( {}, defaultOptions, options );

		b8.Music.currentSongProperties = opts;

		b8.Random.setSeed( opts.seed );

		var songPlan = createSongPlan( opts.noteCount );
		var progressionContext = generateChordProgression( opts.noteCount, style, songPlan );
		var roles = style.channels.slice( 0, opts.channelCount );

		var parts = roles.map(
			function( role ) {
				return generatePartByRole( role, opts.noteCount, progressionContext, style );
			}
		);

		if ( opts.tempo !== null ) {
			var tempoStr = String( opts.tempo );

			if ( opts.hold !== null ) {
				tempoStr += "." + String( opts.hold );
			}

			parts[ 0 ] = tempoStr + "\n" + parts[ 0 ];
		}

		return parts.join( "\n" );

	};


	/**
	 * Plays a p1.js music string.
	 *
	 * @param {string} song - The music string to play.
	 * @returns {void}
	 */
	b8.Music.play = function( song ) {

		p1( song );

		if ( song ) {
			currentSong = song;
		}

	};


	/**
	 * Stops the current music playback.
	 * If `clearCurrentSong` is true, it will also clear the current song reference.
	 * This is disabled when the music is paused to allow resuming playback.
	 *
	 * @param {boolean} [clearCurrentSong=true] - Whether to clear the current song reference.
	 * @returns {void}
	 */
	b8.Music.stop = function( clearCurrentSong = true ) {

		// Clear the currently stored song.
		b8.Utilities.checkBoolean( "clearCurrentSong", clearCurrentSong );
		if ( clearCurrentSong ) currentSong = null;

		// Stop the music playback.
		b8.Music.play( "" );

	};


	/**
	 * Pauses the current music playback.
	 *
	 * @returns {void}
	 */
	b8.Music.pause = function() {

		if ( b8.Music.isPlaying() ) {
			b8.Music.stop( false );
		}

	};


	/**
	 * Resumes the current music playback.
	 *
	 * Restarts the most recent song after it has been paused.
	 *
	 * p1.js does not resume from the exact paused position here. It starts the
	 * stored song again from the beginning.
	 *
	 * @returns {void}
	 */
	b8.Music.resume = function() {

		// Restart the stored song if playback is currently stopped.
		if ( currentSong && !b8.Music.isPlaying() ) {
			b8.Music.play( currentSong );
		}

	};


	/**
	 * Sets the volume for the music playback.
	 *
	 * @param {number} volume - The volume level (0 to 1).
	 * @returns {void}
	 */
	b8.Music.setVolume = function( volume ) {

		b8.Utilities.checkNumber( "volume", volume );

		p1.setVolume( volume );

	};


	/**
	 * Set the tempo of a currently playing song.
	 *
	 * @param {number} tempo - The new tempo in BPM.
	 * @returns {void}
	 */
	b8.Music.setTempo = function( tempo ) {

		b8.Utilities.checkInt( "tempo", tempo );

		// Ensure tempo is within a valid range.
		if ( tempo < 50 ) {
			tempo = 50;
		}

		p1.setTempo( tempo );

	};


	/**
	 * Checks if music is currently playing.
	 *
	 * @returns {boolean} True if music is playing, otherwise false.
	 */
	b8.Music.isPlaying = function() {

		return p1.isPlaying();

	};


	/**
	 * Gets the available style keys for music generation.
	 *
	 * @returns {Array<string>} An array of style keys.
	 */
	b8.Music.getStyleKeys = function() {

		return Object.keys( SONG_STYLES );

	};


	/**
	 * Resets the music module, stopping any playback and clearing the current song.
	 *
	 * @returns {void}
	 */
	b8.Music.reset = function() {

		b8.Music.stop();

	};


	// Handle page visibility changes to pause/resume music.
	document.addEventListener( 'b8.pageVisibility.wake', b8.Music.resume );
	document.addEventListener( 'b8.pageVisibility.sleep', b8.Music.pause );


	/**
	 * Converts a note string (e.g. "C4" or "D#4") to a MIDI note number.
	 * Expects a format: letter, optional accidental (# or b), then octave digit.
	 *
	 * @param {string} note - The musical note string.
	 * @returns {number|null} The MIDI note number, or null if the note format is invalid.
	 */
	function noteToMidi( note ) {

		var regex = /^([A-Ga-g])([#b]?)(\d)$/;
		var match = note.match( regex );

		if ( !match ) return null;

		var letter = match[ 1 ].toUpperCase();
		var accidental = match[ 2 ];
		var octave = parseInt( match[ 3 ], 10 );

		// Map letter to its base semitone number.
		var semitones = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 }[ letter ];
		if ( accidental === "#" ) {
			semitones += 1;
		} else if ( accidental === "b" ) {
			semitones -= 1;
		}

		// MIDI formula: (octave + 1) * 12 + semitones.
		return ( octave + 1 ) * 12 + semitones;

	}


	/**
	 * Converts a musical note (e.g. "C4" or "D#4") to a p1.js note letter.
	 * The MIDI note is clamped to the range [36, 87] before conversion.
	 *
	 * @param {string} note - The musical note string.
	 * @returns {string} The corresponding p1.js letter, or "?" if invalid.
	 */
	function noteToP1( note ) {

		let midi = noteToMidi( note );

		if ( midi === null ) return "?";

		midi = b8.Utilities.clamp( midi, 36, 87 );

		return p1Alphabet.charAt( midi - 36 );

	}


	/**
	 * Converts a MIDI note number back into a note string.
	 *
	 * @param {number} midi - MIDI note number.
	 * @returns {string} The note string.
	 */
	function midiToNote( midi ) {

		var octave = Math.floor( midi / 12 ) - 1;
		var index = ( ( midi % 12 ) + 12 ) % 12;
		var noteNames = [ "C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B" ];

		return noteNames[ index ] + octave;

	}


	/**
	 * Compresses an array of note characters by replacing consecutive repeats
	 * (except for spaces and bars) with dashes to indicate sustained notes.
	 *
	 * @param {Array<string>} arr - Array of note characters.
	 * @returns {string} Compressed note string.
	 */
	function compressNotes( arr ) {

		if ( arr.length === 0 ) return "";

		var result = arr[ 0 ];

		for ( var i = 1; i < arr.length; i++ ) {
			if ( arr[ i ] === arr[ i - 1 ] && arr[ i ] !== " " && arr[ i ] !== "|" ) {
				result += "-";
			} else {
				result += arr[ i ];
			}
		}

		return result;

	}


	/**
	 * Calls a function n times and collects the results in an array.
	 *
	 * @param {number} n - Number of times to call the function.
	 * @param {function(number): any} fn - Function to be called with the current index.
	 * @returns {Array<any>} An array of results.
	 */
	function times( n, fn ) {

		var result = [];
		for ( var i = 0; i < n; i++ ) {
			result.push( fn( i ) );
		}
		return result;

	}


	/**
 * Gets the chord notes for a given key, Roman numeral, and tonal palette.
 *
 * @param {string} key - The musical key (e.g., "C").
 * @param {string} roman - The Roman numeral chord identifier (e.g., "I", "IIIm").
 * @param {Object} tonalPalette - Palette containing the chord qualities to use.
 * @returns {Array<string>} An array of chord note strings.
 */
	function getChordNotes( key, roman, tonalPalette ) {

		const paletteChordMap = tonalPalette && tonalPalette.chordMap
			? tonalPalette.chordMap
			: chordMap;
		const base = paletteChordMap[ roman ] || paletteChordMap.I;
		const shift = keyShift[ key ] || 0;

		return base.map(
			function( note ) {
				return transpose( note, shift );
			}
		);

	}


	/**
	 * Transposes a note by a specified number of semitones.
	 *
	 * @param {string} note - The note to transpose (e.g., "C4").
	 * @param {number} shift - The number of semitones to shift.
	 * @returns {string} The transposed note.
	 */
	function transpose( note, shift ) {

		var midi = noteToMidi( note );
		if ( midi === null ) {
			return note;
		}

		return midiToNote( midi + shift );

	}


	/**
	 * Returns the pulse count used as one bar for phrase-aware patterns.
	 *
	 * @returns {number} The number of note positions per bar.
	 */
	function getBarLength() {

		return 8;

	}


	/**
	 * Returns the relative strength of a step within a bar.
	 *
	 * Lower values represent stronger rhythmic positions.
	 *
	 * @param {number} stepIndex - Step index in the song.
	 * @returns {number} The step strength.
	 */
	function getStepStrength( stepIndex ) {

		var stepInBar = stepIndex % getBarLength();

		if ( stepInBar === 0 ) return 0;
		if ( stepInBar === 4 ) return 1;
		if ( stepInBar === 2 || stepInBar === 6 ) return 2;

		return 3;

	}


	/**
 * Creates a seed-derived song plan.
	 *
	 * The profile acts as the musical blueprint for a seed. Styles then interpret
	 * that blueprint with their own density and instrumentation settings, which
	 * keeps the "same seed, same family" behaviour while making different seeds
	 * diverge more clearly.
	 *
	 * @param {number} noteCount - Number of note positions in the song.
 * @returns {Object} Song plan used across the generator.
 */
	function createSongPlan( noteCount ) {

		var totalBars = Math.max( 1, Math.ceil( noteCount / getBarLength() ) );
		// The plan is the complete high-level identity of the song. Every later
		// generator reads these four decisions instead of making structural choices.
		var sectionForm = pickSectionForm( totalBars );

		return {
			sectionForm: sectionForm,
			phraseBarCount: getPhraseBarCount( totalBars, sectionForm ),
			contour: b8.Random.pick( SONG_PLAN_OPTIONS.contours ),
			cadenceTarget: b8.Random.pick( SONG_PLAN_OPTIONS.cadenceTarget ),
			tonalPaletteName: b8.Random.pick( SONG_PLAN_OPTIONS.palettes ),
			arrangement: b8.Random.pick( SONG_PLAN_OPTIONS.arrangements )
		};

	}


	/**
 * Derives a phrase size that fits the available song length and form.
	 *
	 * Longer songs are allowed broader phrases, while shorter songs stay more
	 * compact so the structure remains audible.
	 *
	 * @param {number} totalBars - Total bar count for the song.
 * @param {Array<string>} sectionForm - Chosen section form for the song.
 * @returns {number} Phrase size in bars.
 */
	function getPhraseBarCount( totalBars, sectionForm ) {

		var barsPerSection = Math.max( 2, Math.floor( totalBars / sectionForm.length ) );

		if ( barsPerSection >= 8 ) return 8;
		if ( barsPerSection >= 4 ) return 4;

		return 2;

	}


	/**
	 * Picks a section form that makes good use of the available number of phrases.
	 *
	 * Faster or longer songs benefit from more sections so they do not reveal the
	 * whole idea too quickly. Shorter songs use simpler forms to avoid feeling
	 * over-composed.
	 *
	 * @param {number} totalBars - Total bar count for the song.
 * @returns {Array<string>} Section form such as A-B-A or A-A-B-A.
 */
	function pickSectionForm( totalBars ) {

		var phraseCapacity = Math.max( 1, Math.floor( totalBars / 2 ) );
		var availableForms = SONG_PLAN_OPTIONS.sectionForms.filter(
			function( form ) {
				return form.length <= phraseCapacity;
			}
		);

		if ( availableForms.length === 0 ) {
			availableForms = [ [ "A", "B" ] ];
		}

		if ( phraseCapacity >= 4 ) {
			availableForms = availableForms.filter(
				function( form ) {
					return form.length >= 3;
				}
			);
		}

		if ( availableForms.length === 0 ) {
			availableForms = SONG_PLAN_OPTIONS.sectionForms.filter(
				function( form ) {
					return form.length <= phraseCapacity;
				}
			);
		}

		return b8.Random.pick( availableForms );

	}


	/**
	 * Generates a chord progression context for every note position.
	 *
	 * The old generator only returned chord notes. This version keeps that
	 * information and also annotates phrasing and cadence points so parts can
	 * make more musical decisions without changing the public API.
	 *
	 * @param {number} noteCount - Number of note positions to generate chord data for.
	 * @param {Object} style - The song style object containing chord length options.
	 * @param {Object} songPlan - Seed-derived musical plan for the song.
	 * @returns {Object} Progression context used by part generators.
	 */
	function generateChordProgression( noteCount, style, songPlan ) {

		var keys = [ "C", "D", "Eb", "F", "G", "A", "Bb" ];
		var key = b8.Random.pick( keys );
		var progression = b8.Random.pick( PROGRESSIONS ).slice();
		var chordLength = b8.Random.pick( style.chordLength || [ 4, 8, 8, 16 ] );
		var barLength = getBarLength();
		var phraseLength = barLength * songPlan.phraseBarCount;
		var tonalPalette = TONAL_PALETTES[ songPlan.tonalPaletteName ] || TONAL_PALETTES.major;
		// The selected palette supplies both the notes melodies may use and the
		// chord qualities the progression is built from.
		var scale = buildScaleMidi( key, tonalPalette.scaleIntervals, 36, 87 );
		var chordSequence = [];
		var steps = [];

		for ( var i = 0; i < noteCount; i++ ) {
			// We work out where we are in the larger form first, then derive the
			// local harmony from that section and phrase position.
			var phraseIndex = Math.floor( i / phraseLength );
			var sectionName = getSectionName( songPlan, phraseIndex );
			var activeProgression = getSectionProgression( progression, sectionName );
			var activeChordLength = chordLength;
			var sectionStepIndex = i % phraseLength;
			var sequenceIndex = Math.floor( sectionStepIndex / activeChordLength ) % activeProgression.length;
			var roman = activeProgression[ sequenceIndex ];
			var chordNotes = getChordNotes( key, roman, tonalPalette );
			var chordMidi = toMidiList( chordNotes );

			chordSequence.push( roman );

			steps.push( {
				index: i,
				key: key,
				tonalPaletteName: songPlan.tonalPaletteName,
				scale: scale,
				roman: roman,
				sectionName: sectionName,
				chordNotes: chordNotes,
				chordMidi: chordMidi,
				chordStart: sectionStepIndex % activeChordLength === 0,
				chordEnd: ( sectionStepIndex + 1 ) % activeChordLength === 0 || i === noteCount - 1,
				barStart: i % barLength === 0,
				phraseStart: i % phraseLength === 0,
				phraseEnd: ( i + 1 ) % phraseLength === 0 || i === noteCount - 1,
				stepStrength: getStepStrength( i )
			} );
		}

		return {
			key: key,
			scale: scale,
			chordLength: chordLength,
			barLength: barLength,
			phraseLength: phraseLength,
			songPlan: songPlan,
			progression: chordSequence,
			steps: steps
		};

	}


	/**
	 * Generates a p1.js part for a named musical role.
	 *
	 * This keeps style definitions simple. Styles only need to list role names,
	 * and this function decides which generator should be used for each role.
	 *
	 * @param {string} role - The part role: "bass", "drums", "arp", "chords", "counter", or "melody".
	 * @param {number} noteCount - Number of note positions to generate.
	 * @param {Object} progressionContext - Harmonic context for the generated song.
	 * @param {Object} style - The song style object containing additional parameters.
	 * @returns {string} A compressed p1.js part.
	 */
	function generatePartByRole( role, noteCount, progressionContext, style ) {

		switch ( role ) {

			case "bass":
				return generateBassNote( noteCount, progressionContext, style );

			case "drums":
				return generateDrumNote( noteCount, progressionContext, style );

			case "arp":
			case "chords":
				return generateChordNote( role, noteCount, progressionContext, style );

			case "counter":
			case "melody":
			default:
				return generateMelodyNote( role, noteCount, progressionContext, style );

		}

	}


	/**
	 * Generates a bass part with a steadier pulse and simple approach notes.
	 *
	 * @param {number} noteCount - Number of note positions to generate.
	 * @param {Object} progressionContext - Harmonic context for the generated song.
	 * @param {Object} style - The song style object containing additional parameters.
	 * @returns {string} A compressed p1.js bass part.
	 */
	function generateBassNote( noteCount, progressionContext, style ) {

		var notes = [ b8.Random.pick( instrumentOptions ), "|" ];
		var pattern = getPartPattern( "bass", noteCount, progressionContext, style );

		for ( var i = 0; i < noteCount; i++ ) {
			var step = progressionContext.steps[ i ];

			if ( !pattern[ i ] || !isRoleActiveInSection( "bass", step.sectionName, progressionContext.songPlan ) ) {
				notes.push( " " );
				continue;
			}

			var rootMidi = normalizeMidiToOctave( step.chordMidi[ 0 ], 2 );
			var fifthMidi = normalizeMidiToOctave( step.chordMidi[ 2 ] || step.chordMidi[ 0 ], 2 );
			var selectedMidi = step.stepStrength > 0 ? fifthMidi : rootMidi;

			// A chord change is announced by its root. Just before one, the bass
			// walks one scale step toward the next root instead of guessing a note.
			if ( step.chordEnd && i < noteCount - 1 ) {
				var nextRoot = normalizeMidiToOctave(
					progressionContext.steps[ i + 1 ].chordMidi[ 0 ],
					2
				);
				selectedMidi = moveOneStepToward( rootMidi, nextRoot, progressionContext.scale );
			}
			notes.push( noteToP1( midiToNote( selectedMidi ) ) );
		}

		notes.push( "|" );

		return compressNotes( notes );

	}


	/**
	 * Generates the main melody or counter melody.
	 *
	 * The melody now prefers stepwise motion, repeats phrase fragments, and lands
	 * on stable tones at the ends of phrases so the result feels less random.
	 *
	 * @param {string} role - The part role being generated.
	 * @param {number} noteCount - The number of beats/positions.
	 * @param {Object} progressionContext - Harmonic context for the generated song.
	 * @param {Object} style - The song style object containing additional parameters.
	 * @returns {string} The compressed melody note string.
	 */
	function generateMelodyNote( role, noteCount, progressionContext, style ) {

		var notes = [ b8.Random.pick( instrumentOptions ), "|" ];
		var pattern = getPartPattern( role, noteCount, progressionContext, style );
		var phraseLength = progressionContext.phraseLength;
		var targetOctave = getRoleTargetOctave( role );
		var previousMidi = null;

		for ( var i = 0; i < noteCount; i++ ) {
			var step = progressionContext.steps[ i ];

			if ( !pattern[ i ] || !isRoleActiveInSection( role, step.sectionName, progressionContext.songPlan ) ) {
				notes.push( " " );
				continue;
			}

			var contourTarget = getContourTargetMidi(
				step,
				progressionContext.scale,
				targetOctave,
				progressionContext.songPlan,
				i % phraseLength,
				phraseLength
			);
			var selectedMidi = getMelodyStepMidi(
				step,
				previousMidi,
				contourTarget,
				progressionContext.scale,
				targetOctave,
				progressionContext.songPlan
			);
			previousMidi = selectedMidi;
			notes.push( noteToP1( midiToNote( selectedMidi ) ) );
		}

		notes.push( "|" );

		return compressNotes( notes );

	}


	/**
	 * Generates a harmonic support line or arpeggio.
	 *
	 * @param {string} role - The part role being generated.
	 * @param {number} noteCount - The number of beats/positions.
	 * @param {Object} progressionContext - Harmonic context for the generated song.
	 * @param {Object} style - The song style object containing additional parameters.
	 * @returns {string} The compressed chord or arpeggio note string.
	 */
	function generateChordNote( role, noteCount, progressionContext, style ) {

		var notes = [ b8.Random.pick( instrumentOptions ), "|" ];
		var harmonicRole = role;
		var isArpeggio = harmonicRole === "arp";
		var pattern = getPartPattern( harmonicRole, noteCount, progressionContext, style );

		for ( var i = 0; i < noteCount; i++ ) {
			var step = progressionContext.steps[ i ];

			if ( !pattern[ i ] || !isRoleActiveInSection( harmonicRole, step.sectionName, progressionContext.songPlan ) ) {
				notes.push( " " );
				continue;
			}

			var targetOctave = getRoleTargetOctave( harmonicRole );
			var chordIndex = 0;

			if ( isArpeggio ) {
				// This fixed shape makes arpeggios recognisable without a second set
				// of seed settings: root, third, fifth, then a gentle way back down.
				chordIndex = [ 0, 1, 2, 1, 3, 2, 1, 2 ][ i % 8 ];
			} else {
				// Held accompaniment alternates through chord tones between chord
				// changes, which prevents slow songs from sitting on one note.
				chordIndex = step.chordStart ? 0 : step.stepStrength <= 1 ? 1 : 2;
			}

			var selectedMidi = normalizeMidiToOctave(
				step.chordMidi[ chordIndex % step.chordMidi.length ],
				targetOctave
			);
			notes.push( noteToP1( midiToNote( selectedMidi ) ) );
		}

		notes.push( "|" );

		return compressNotes( notes );

	}


	/**
	 * Generates a steadier drum note string using repeated bar templates.
	 *
	 * @param {number} noteCount - The number of beats/positions.
	 * @param {Object} progressionContext - Harmonic context for the generated song.
	 * @param {Object} style - The song style object containing drum density options.
	 * @returns {string} The compressed drum note string.
	 */
	function generateDrumNote( noteCount, progressionContext, style ) {

		var notes = [ b8.Random.pick( drumOptions ), "|" ];
		var pattern = getPartPattern( "drums", noteCount, progressionContext, style );
		var drumHit = noteToP1( "C4" );

		for ( var i = 0; i < noteCount; i++ ) {
			var step = progressionContext.steps[ i ];
			var shouldPlay = pattern[ i ] && isRoleActiveInSection(
				"drums", step.sectionName, progressionContext.songPlan
			);
			notes.push( shouldPlay ? drumHit : " " );
		}

		notes.push( "|" );

		return compressNotes( notes );

	}


	/**
	 * Decides whether a role belongs in the current section.
	 *
	 * Rhythm patterns decide individual hits; this list decides which instruments
	 * are present at all. Reading the chosen arrangement tells us the song's
	 * build immediately, without translating probability ranges.
	 *
	 * @param {string} role - Musical role being considered.
	 * @param {string} sectionName - Current section name.
	 * @param {Object} songPlan - Seed-derived musical plan.
	 * @returns {boolean} Whether the role belongs in this section.
	 */
	function isRoleActiveInSection( role, sectionName, songPlan ) {

		var roles = songPlan.arrangement.roles[ sectionName ] || songPlan.arrangement.roles.A;

		return roles.indexOf( role ) !== -1;

	}


	/**
	 * Creates a role-specific pattern by repeating bars and mutating them gently.
	 *
	 * @param {string} role - The part role to generate a pattern for.
	 * @param {number} noteCount - Number of note positions to fill.
	 * @param {Object} style - Style information that can influence density.
	 * @param {Object} songPlan - Seed-derived musical plan for the song.
	 * @param {number} phraseLength - Number of note positions in one phrase.
	 * @returns {Array<boolean>} The generated rhythmic pattern.
	 */
	function createRolePattern( role, noteCount, style, songPlan, phraseLength ) {

		var templates = getRoleTemplates( role, style );
		var barLength = getBarLength();
		var pattern = [];

		for ( var start = 0; start < noteCount; start += phraseLength ) {
			var phraseIndex = Math.floor( start / phraseLength );
			var sectionName = getSectionName( songPlan, phraseIndex );
			var phrasePattern = [];
			// The first bar states the groove clearly, then later bars mutate it so
			// the phrase feels related rather than freshly random.
			var baseTemplate = pickSectionTemplate( templates, sectionName );
			var barCount = Math.max( 1, Math.ceil( phraseLength / barLength ) );

			for ( var barIndex = 0; barIndex < barCount; barIndex++ ) {
				var barPattern = barIndex === 0
					? baseTemplate.slice()
					: mutateBarPattern( phrasePattern.slice( -barLength ), role, style, sectionName );
				phrasePattern = phrasePattern.concat( barPattern );
			}

			pattern = pattern.concat( phrasePattern );
		}

		pattern = pattern.slice( 0, noteCount );

		if ( pattern.length > 0 ) {
			pattern[ 0 ] = true;
			if ( role !== "drums" ) {
				pattern[ pattern.length - 1 ] = true;
			}
		}

		return pattern;

	}


	/**
	 * Builds a rhythmic pattern for a part using the shared progression context.
	 *
	 * This keeps the part generators shorter and makes it clearer that they all
	 * derive rhythm from the same phrase and song-profile information.
	 *
	 * @param {string} role - The part role to generate a pattern for.
	 * @param {number} noteCount - Number of note positions to fill.
	 * @param {Object} progressionContext - Harmonic context for the generated song.
	 * @param {Object} style - Style information that can influence density.
	 * @returns {Array<boolean>} The generated rhythmic pattern.
	 */
	function getPartPattern( role, noteCount, progressionContext, style ) {

		return createRolePattern(
			role,
			noteCount,
			style,
			progressionContext.songPlan,
			progressionContext.phraseLength
		);

	}


	/**
	 * Converts compact rhythm strings into boolean bar patterns.
	 *
	 * The parser keeps the data representation small and readable while the rest
	 * of the generator can continue working with plain boolean arrays.
	 *
	 * @param {Array<string>} patterns - Compact rhythm strings using "x" and ".".
	 * @returns {Array<Array<boolean>>} Parsed boolean patterns.
	 */
	function parseRhythmTemplates( patterns ) {

		return patterns.map( parseRhythmPattern );

	}


	/**
	 * Converts one compact rhythm string into a boolean bar pattern.
	 *
	 * @param {string} pattern - Compact rhythm string using "x" and ".".
	 * @returns {Array<boolean>} Parsed boolean pattern.
	 */
	function parseRhythmPattern( pattern ) {

		return pattern.split( "" ).map(
			function( char ) {
				return char === "x";
			}
		);

	}


	/**
	 * Returns one-bar rhythm templates for each musical role.
	 *
	 * @param {string} role - The role to generate a pattern for.
	 * @param {Object} style - Style information for optional density tweaks.
	 * @returns {Array<Array<boolean>>} Candidate one-bar patterns.
	 */
	function getRoleTemplates( role, style ) {

		var templateRole = RHYTHM_TEMPLATE_PATTERNS[ role ] ? role : "melody";
		var templateFamily = getRhythmTemplateFamily( templateRole, style );

		return parseRhythmTemplates(
			RHYTHM_TEMPLATE_PATTERNS[ templateRole ][ templateFamily ]
		);

	}


	/**
	 * Selects the named rhythm-template family for a role.
	 *
	 * This is the single bridge from broad style settings to the compact rhythm
	 * data above. Keeping the decision here prevents each role from needing a
	 * separate near-identical selector function.
	 *
	 * @param {string} role - The role whose rhythm templates are needed.
	 * @param {Object} style - Style information that controls rhythm density.
	 * @returns {string} Key of the template family to use.
	 */
	function getRhythmTemplateFamily( role, style ) {

		switch ( role ) {
			case "bass":
				var fastestBass = Math.min.apply( null, style.bassInterval || [ 4, 8 ] );
				return fastestBass <= 2 ? "fast" : fastestBass <= 4 ? "medium" : "slow";

			case "drums":
				var drumDensity = b8.Random.pick( style.drumDensity || [ 1, 2, 3 ] );
				return drumDensity >= 4 ? "heavy" : drumDensity >= 2 ? "medium" : "light";

			case "arp":
				return ( style.arpChance || 0 ) >= 80 ? "busy" : "normal";

			case "chords":
				return ( style.chordDensity || 0 ) >= 6 ? "dense" : "light";

			case "counter":
				return ( style.melodyDensity || 0 ) >= 6 ? "dense" : "light";

			case "melody":
			default:
				if ( ( style.melodyDensity || 0 ) <= 2 ) return "sparse";
				return ( style.melodyDensity || 0 ) >= 7 ? "dense" : "normal";
		}

	}


	/**
	 * Returns the preferred octave for a musical role.
	 *
	 * @param {string} role - The role being generated.
	 * @returns {number} Preferred octave number.
	 */
	function getRoleTargetOctave( role ) {
		var defaults = {
			bass: 2,
			drums: 4,
			arp: 4,
			chords: 5,
			counter: 4,
			melody: 5
		};
		return defaults[ role ] || 5;

	}


	/**
	 * Applies a small mutation to a bar pattern while preserving the main groove.
	 *
	 * @param {Array<boolean>} pattern - The source pattern to vary.
	 * @param {string} role - The part role being mutated.
	 * @param {Object} style - Style information that can influence variation.
	 * @param {string} sectionName - Current section name such as A or B.
	 * @returns {Array<boolean>} The gently varied pattern.
	 */
	function mutateBarPattern( pattern, role, style, sectionName ) {

		var result = pattern.slice();
		var candidateIndexes = role === "drums"
			? [ 2, 3, 5, 6, 7 ]
			: [ 1, 2, 3, 5, 6, 7 ];
		var changeCount = role === "arp" ? 1 : b8.Random.int( 0, 1 );

		if ( role === "melody" && ( style.melodyDensity || 0 ) >= 7 ) {
			changeCount = 1;
		}

		if ( role === "chords" && ( style.chordDensity || 0 ) >= 6 ) {
			changeCount = 1;
		}

		if ( sectionName === "B" || sectionName === "A_PRIME" ) {
			// Variant sections are allowed one extra mutation so the back half of
			// the song reads as an intentional development of the first half.
			changeCount += 1;
		}

		for ( var i = 0; i < changeCount; i++ ) {
			var index = candidateIndexes[ b8.Random.int( 0, candidateIndexes.length - 1 ) ];
			result[ index ] = !result[ index ];
		}

		result[ 0 ] = pattern[ 0 ];

		return result;

	}


	/**
	 * Picks a base rhythm template, with related sections using nearby templates.
	 *
	 * @param {Array<Array<boolean>>} templates - Templates available for the role.
	 * @param {string} sectionName - Current section name.
	 * @returns {Array<boolean>} The selected bar template.
	 */
	function pickSectionTemplate( templates, sectionName ) {

		var templateIndex = b8.Random.int( 0, templates.length - 1 );

		if ( sectionName === "B" ) {
			templateIndex = ( templateIndex + 1 ) % templates.length;
		} else if ( sectionName === "A_PRIME" ) {
			templateIndex = ( templateIndex + templates.length - 1 ) % templates.length;
		}

		return templates[ templateIndex ];

	}


	/**
	 * Gets the section name for a phrase index using the song plan.
	 *
	 * @param {Object} songPlan - Seed-derived musical plan for the song.
	 * @param {number} phraseIndex - Phrase index in the song.
	 * @returns {string} The section name.
	 */
	function getSectionName( songPlan, phraseIndex ) {

		return songPlan.sectionForm[ phraseIndex % songPlan.sectionForm.length ];

	}


	/**
	 * Returns the progression to use for the current section.
	 *
	 * Each named section has one understandable harmonic variation.
	 *
	 * @param {Array<string>} progression - Base progression.
	 * @param {string} sectionName - Current section name.
	 * @returns {Array<string>} Progression for the current section.
	 */
	function getSectionProgression( progression, sectionName ) {

		if ( sectionName === "B" ) {
			// Section B keeps the same harmonic family but starts from a different
			// point, which usually feels like motion without sounding unrelated.
			return rotateArray( progression, 1 );
		}

		if ( sectionName === "C" ) {
			// Section C is the most contrasting section: we rotate further and push
			// the start and end toward tension so it feels like a new chapter.
			var contrasted = rotateArray( progression, 2 );
			contrasted[ 0 ] = "VIm";
			contrasted[ contrasted.length - 1 ] = "V";
			return contrasted;
		}

		if ( sectionName === "A_PRIME" ) {
			var varied = progression.slice();
			varied[ varied.length - 2 ] = "V";
			return varied;
		}

		return progression;

	}


	/**
	 * Rotates an array by a fixed offset.
	 *
	 * @param {Array<any>} values - Values to rotate.
	 * @param {number} amount - Rotation amount.
	 * @returns {Array<any>} Rotated array.
	 */
	function rotateArray( values, amount ) {

		var result = [];

		for ( var i = 0; i < values.length; i++ ) {
			result.push( values[ ( i + amount ) % values.length ] );
		}

		return result;

	}


	/**
 * Collects tonal-palette notes for a key across a MIDI range.
 *
 * @param {string} key - Musical key.
 * @param {Array<number>} scaleIntervals - Semitone offsets that define the palette.
 * @param {number} minMidi - Minimum MIDI value.
 * @param {number} maxMidi - Maximum MIDI value.
 * @returns {Array<number>} MIDI notes that belong to the tonal palette.
 */
	function buildScaleMidi( key, scaleIntervals, minMidi, maxMidi ) {

		var tonic = keyShift[ key ] || 0;
		var pitchClasses = scaleIntervals.map(
			function( value ) {
				return ( value + tonic ) % 12;
			}
		);
		var scale = [];

		for ( var midi = minMidi; midi <= maxMidi; midi++ ) {
			if ( pitchClasses.indexOf( ( ( midi % 12 ) + 12 ) % 12 ) !== -1 ) {
				scale.push( midi );
			}
		}

		return scale;

	}


	/**
	 * Converts an array of note strings to MIDI notes.
	 *
	 * @param {Array<string>} notes - Notes to convert.
	 * @returns {Array<number>} MIDI note values.
	 */
	function toMidiList( notes ) {

		return notes.map( noteToMidi ).filter(
			function( midi ) {
				return midi !== null;
			}
		);

	}


	/**
	 * Normalises a MIDI note so it sits near the requested octave.
	 *
	 * @param {number} midi - MIDI note to move.
	 * @param {number} octave - Target octave number.
	 * @returns {number} Adjusted MIDI note.
	 */
	function normalizeMidiToOctave( midi, octave ) {

		var target = ( octave + 1 ) * 12;
		var pitchClass = ( ( midi % 12 ) + 12 ) % 12;

		return target + pitchClass;

	}


	/**
	 * Moves one step through the supplied scale toward a target MIDI note.
	 *
	 * @param {number} currentMidi - Current MIDI note.
	 * @param {number} targetMidi - Desired destination note.
	 * @param {Array<number>} scale - Available scale notes.
	 * @returns {number} The next scale step.
	 */
	function moveOneStepToward( currentMidi, targetMidi, scale ) {

		var orderedScale = scale.slice().sort(
			function( a, b ) {
				return a - b;
			}
		);
		var currentIndex = findNearestIndex( orderedScale, currentMidi );
		var targetIndex = findNearestIndex( orderedScale, targetMidi );

		if ( currentIndex === targetIndex ) {
			return orderedScale[ currentIndex ];
		}

		return orderedScale[ currentIndex + ( targetIndex > currentIndex ? 1 : -1 ) ];

	}


	/**
	 * Finds the nearest index in a sorted array of values.
	 *
	 * @param {Array<number>} values - Sorted values.
	 * @param {number} target - Target value.
	 * @returns {number} Index of the nearest value.
	 */
	function findNearestIndex( values, target ) {

		var nearestIndex = 0;
		var nearestDistance = Infinity;

		for ( var i = 0; i < values.length; i++ ) {
			var distance = Math.abs( values[ i ] - target );

			if ( distance < nearestDistance ) {
				nearestIndex = i;
				nearestDistance = distance;
			}
		}

		return nearestIndex;

	}


	/**
	 * Returns the preferred melodic target for a phrase step.
	 *
	 * Each contour type maps the phrase onto a different high-level shape so
	 * seeds differ more strongly in their melodic identity.
	 *
	 * @param {Object} step - Current progression step.
	 * @param {Array<number>} scale - MIDI notes in the key.
	 * @param {number} targetOctave - Preferred melodic octave.
	 * @param {Object} songPlan - Seed-derived musical plan for the song.
	 * @param {number} phraseStep - Step index within the phrase.
	 * @param {number} phraseLength - Total steps in the phrase.
	 * @returns {number} Preferred MIDI note.
	 */
	function getContourTargetMidi( step, scale, targetOctave, songPlan, phraseStep, phraseLength ) {

		var anchorMidi = normalizeMidiToOctave( step.chordMidi[ 0 ], targetOctave );
		var ratio = phraseLength <= 1 ? 0 : phraseStep / ( phraseLength - 1 );
		var degreeOffset = 0;

		switch ( songPlan.contour ) {
			case "rise":
				degreeOffset = Math.round( ratio * 4 );
				break;

			case "fall":
				degreeOffset = Math.round( ( 1 - ratio ) * 4 );
				break;

			case "arch":
				degreeOffset = Math.round( ( 1 - Math.abs( 0.5 - ratio ) * 2 ) * 4 );
				break;

			case "wave":
				degreeOffset = Math.round( Math.sin( ratio * Math.PI * 2 ) * 2 ) + 2;
				break;

			case "stair":
				degreeOffset = Math.floor( ratio * 5 );
				break;

			case "hover":
			default:
				degreeOffset = phraseStep % 2 === 0 ? 1 : 2;
				break;
		}

		return findScaleNoteByDegreeOffset( scale, anchorMidi, degreeOffset );

	}


	/**
	 * Returns the next melody note from a short, predictable set of rules.
	 *
	 * Phrase endings settle on a chord tone. A new bar starts near the harmony.
	 * Other notes take one scale step toward the phrase contour.
	 *
	 * @param {Object} step - Current progression step.
	 * @param {number|null} previousMidi - Previously played melodic note.
	 * @param {number} contourTarget - Pitch the contour is heading toward.
	 * @param {Array<number>} scale - MIDI notes in the key.
	 * @param {number} targetOctave - Preferred melodic octave.
	 * @param {Object} songPlan - Seed-derived musical plan.
	 * @returns {number} The selected MIDI note.
	 */
	function getMelodyStepMidi( step, previousMidi, contourTarget, scale, targetOctave, songPlan ) {

		if ( step.phraseEnd ) {
			return getCadenceMidi( step, targetOctave, songPlan );
		}

		if ( previousMidi === null || step.barStart ) {
			return getNearestChordMidi( step, contourTarget, targetOctave );
		}

		return moveOneStepToward( previousMidi, contourTarget, scale );

	}


	/**
	 * Finds the current chord tone nearest to a target pitch.
	 *
	 * @param {Object} step - Current progression step.
	 * @param {number} targetMidi - Pitch to compare chord tones against.
	 * @param {number} targetOctave - Preferred melodic octave.
	 * @returns {number} Nearest chord-tone MIDI note.
	 */
	function getNearestChordMidi( step, targetMidi, targetOctave ) {

		var nearestMidi = normalizeMidiToOctave( step.chordMidi[ 0 ], targetOctave );

		for ( var i = 1; i < step.chordMidi.length; i++ ) {
			var candidate = normalizeMidiToOctave( step.chordMidi[ i ], targetOctave );

			if ( Math.abs( candidate - targetMidi ) < Math.abs( nearestMidi - targetMidi ) ) {
				nearestMidi = candidate;
			}
		}

		return nearestMidi;

	}


	/**
	 * Returns the cadence tone preferred for the end of a phrase.
	 *
	 * @param {Object} step - Current progression step.
	 * @param {number} targetOctave - Preferred melodic octave.
	 * @param {Object} songPlan - Seed-derived musical plan for the song.
	 * @returns {number} Cadential MIDI note.
	 */
	function getCadenceMidi( step, targetOctave, songPlan ) {

		var chordIndex = 0;

		if ( songPlan.cadenceTarget === "third" ) {
			chordIndex = 1;
		} else if ( songPlan.cadenceTarget === "fifth" ) {
			chordIndex = 2;
		}

		return normalizeMidiToOctave(
			step.chordMidi[ chordIndex ] || step.chordMidi[ 0 ],
			targetOctave
		);

	}


	/**
	 * Finds a nearby scale tone by moving a number of scale degrees from an anchor.
	 *
	 * @param {Array<number>} scale - MIDI notes in the key.
	 * @param {number} anchorMidi - Starting note.
	 * @param {number} degreeOffset - Number of scale steps to move.
	 * @returns {number} The resulting scale note.
	 */
	function findScaleNoteByDegreeOffset( scale, anchorMidi, degreeOffset ) {

		var orderedScale = scale.slice().sort(
			function( a, b ) {
				return a - b;
			}
		);
		var anchorIndex = findNearestIndex( orderedScale, anchorMidi );
		var targetIndex = b8.Utilities.clamp(
			anchorIndex + degreeOffset,
			0,
			orderedScale.length - 1
		);

		return orderedScale[ targetIndex ];

	}


	/**
	 * Moves a note by a number of scale degrees inside the current key.
	 *
	 * @param {Array<number>} scale - MIDI notes in the key.
	 * @param {number} midi - Starting MIDI note.
	 * @param {number} degreeOffset - Number of scale steps to move.
	 * @returns {number|null} The destination note, or null if unavailable.
	 */
	function moveByScaleDegrees( scale, midi, degreeOffset ) {

		if ( degreeOffset === 0 ) {
			return midi;
		}

		var orderedScale = scale.slice().sort(
			function( a, b ) {
				return a - b;
			}
		);
		var anchorIndex = findNearestIndex( orderedScale, midi );
		var targetIndex = anchorIndex + degreeOffset;

		if ( targetIndex < 0 || targetIndex >= orderedScale.length ) {
			return null;
		}

		return orderedScale[ targetIndex ];

	}


	/**
	 * Collects nearby scale notes around a starting note.
	 *
	 * This gives the picker a few extra colour tones so the music can sometimes
	 * make a more interesting choice than the safest immediate winner.
	 *
	 * @param {Array<number>} scale - MIDI notes in the key.
	 * @param {number} midi - Starting MIDI note.
	 * @param {number} minDegrees - Minimum scale-step distance to include.
	 * @param {number} maxDegrees - Maximum scale-step distance to include.
	 * @returns {Array<number>} Nearby scale notes.
	 */
	function getNearbyScaleCandidates( scale, midi, minDegrees, maxDegrees ) {

		var candidates = [];

		for ( var degrees = minDegrees; degrees <= maxDegrees; degrees++ ) {
			candidates.push( moveByScaleDegrees( scale, midi, degrees ) );
			candidates.push( moveByScaleDegrees( scale, midi, -degrees ) );
		}

		return candidates.filter(
			function( candidate ) {
				return candidate !== null;
			}
		);

	}


	/**
	 * Chooses the most musical candidate note from a set of MIDI values.
	 *
	 * The scoring favours nearby notes, stepwise motion, and an optional pitch
	 * target so parts move smoothly instead of jumping around aimlessly.
	 *
	 * @param {Array<number>} candidates - Candidate MIDI notes.
	 * @param {number|null} previousMidi - Previously played MIDI note.
	 * @param {Object} options - Selection options.
	 * @param {number|null} [options.prefer] - Preferred pitch target.
	 * @param {number} [options.maxLeap=7] - Preferred maximum leap size in semitones.
	 * @param {boolean} [options.preferMotion=false] - Whether to favour local movement over exact repeats.
	 * @param {boolean} [options.avoidRepeat=false] - Whether to penalise repeating the previous pitch.
	 * @param {number} [options.explorationChance=0] - Chance of picking from the near-best set instead of the single best note.
	 * @returns {number|null} The selected MIDI note.
	 */
	function chooseCandidateMidi( candidates, previousMidi, options ) {

		if ( !candidates || candidates.length === 0 ) {
			return null;
		}

		options = options || {};

		var preferredMidi = typeof options.prefer === "number" ? options.prefer : null;
		var maxLeap = typeof options.maxLeap === "number" ? options.maxLeap : 7;
		var preferMotion = options.preferMotion === true;
		var avoidRepeat = options.avoidRepeat === true;
		var explorationChance = typeof options.explorationChance === "number"
			? options.explorationChance
			: 0;
		var bestScore = -Infinity;
		var bestCandidates = [];
		var scoredCandidates = [];

		for ( var i = 0; i < candidates.length; i++ ) {
			var candidate = candidates[ i ];
			var score = 0;

			// The score blends three goals: stay singable, respect the current
			// musical target, and avoid getting trapped in lifeless repetition.
			if ( previousMidi !== null ) {
				var leap = Math.abs( candidate - previousMidi );
				score -= leap;

				if ( leap === 0 ) {
					score -= avoidRepeat ? 4 : 1;
				} else if ( leap <= 2 ) {
					score += 5;
				} else if ( leap <= 4 ) {
					score += 2;
				} else if ( leap > maxLeap ) {
					score -= 8;
				}

				if ( preferMotion && leap > 0 && leap <= 2 ) {
					score += 2;
				}
			}

			if ( preferredMidi !== null ) {
				score -= Math.abs( candidate - preferredMidi ) * ( preferMotion ? 0.35 : 0.75 );
			}

			// A tiny nudge prevents every phrase from resolving identically.
			score += b8.Random.int( 0, 2 ) * 0.25;
			scoredCandidates.push( { candidate: candidate, score: score } );

			if ( score > bestScore ) {
				bestScore = score;
				bestCandidates = [ candidate ];
			} else if ( score === bestScore ) {
				bestCandidates.push( candidate );
			}
		}

		if ( explorationChance > 0 && b8.Random.chance( explorationChance ) ) {
			// Sometimes we intentionally pick from the near-best set. That keeps
			// the music lively while still staying within musically sensible bounds.
			var nearBestCandidates = scoredCandidates.filter(
				function( entry ) {
					return entry.score >= bestScore - 2;
				}
			).map(
				function( entry ) {
					return entry.candidate;
				}
			);

			if ( nearBestCandidates.length > 0 ) {
				return b8.Random.pick( uniqueNumbers( nearBestCandidates ) );
			}
		}

		return b8.Random.pick( bestCandidates );

	}

} )( b8 );

( function( b8 ) {

	b8.Menu = {};


	/**
	 * Displays a menu with the given choices and returns the index of the selected choice.
	 *
	 * Displaying a menu will pause the execution of other elements of your game
	 * until a choice has been made.
	 *
	 * The menu automatically beeps and boops as the player navigates through the choices.
	 *
	 * The options object can contain the following properties:
	 * - options.title - The title of the menu.
	 * - options.prompt - The prompt to display above the choices.
	 * - options.selBgColor - The background color of the selected choice. Defaults to the current foreground colour.
	 * - options.selFgColor - The foreground color of the selected choice. Defaults to the current background colour.
	 * - options.border - Whether to draw a border around the menu. Defaults to true.
	 * - options.borderChar - The character to use for the border.
	 * - options.center - Whether to center the menu horizontally and vertically.
	 * - options.centerH - Whether to center the menu horizontally.
	 * - options.centerV - Whether to center the menu vertically.
	 * - options.padding - The padding around the prompt and choices.
	 * - options.selIndex - The index of the initially selected choice.
	 * - options.typewriter - display the prompt as a typewriter effect.
	 *
	 * @param {string[]} choices - The choices to display.
	 * @param {object} [options] - Options for the menu.
	 * @returns {Promise<number>} A promise that resolves to the index of the selected choice.
	 */
	b8.Menu.display = async function( choices, options ) {

		options = options || {};
		b8.Utilities.checkArray( "choices", choices );
		b8.Utilities.checkObject( "options", options );

		options = Object.assign(
			{
				title: "",
				prompt: "",
				selBgColor: b8.Core.drawState.fgColor,
				selFgColor: b8.Core.drawState.bgColor,
				border: true,
				style: "border",
				borderChar: b8.CONFIG.BORDER_CHAR,
				center: false,
				centerH: false,
				centerV: false,
				padding: 1,
				selIndex: 0,
				typewriter: false
			},
			options
		);

		let startCol = b8.Core.drawState.cursorCol;
		let startRow = b8.Core.drawState.cursorRow;

		options.prompt = b8.wrapText( options.prompt, b8.CONFIG.SCREEN_COLS - 2 * options.padding - 2 * ( options.border ? 1 : 0 ) );

		const promptSize = b8.TextRenderer.measure( options.prompt );
		const prompt01 = options.prompt ? 1 : 0;
		const border01 = options.borderChar ? 1 : 0;
		let choicesCols = 0;
		const choicesRows = choices.length;

		choices.forEach(
			( choice ) => {
				choicesCols = Math.ceil( Math.max( choicesCols, b8.TextRenderer.measure( choice ).cols ) );
			}
		);

		let totalCols = Math.ceil( Math.max( promptSize.cols, choicesCols ) ) + 2 * options.padding + 2 * border01;
		totalCols = Math.min( totalCols, b8.CONFIG.SCREEN_COLS );

		const totalRows = prompt01 * ( promptSize.rows + 1 ) + choicesRows + 2 * options.padding + 2 * border01;

		if ( options.centerH || options.center ) {
			startCol = Math.round( ( b8.CONFIG.SCREEN_COLS - totalCols ) / 2 );
		}

		if ( options.centerV || options.center ) {
			startRow = Math.round( ( b8.CONFIG.SCREEN_ROWS - totalRows ) / 2 );
		}

		b8.Core.drawState.cursorCol = startCol;
		b8.Core.drawState.cursorRow = startRow;

		if ( options.style ) {

			b8.TextRenderer.printStyledBox(
				totalCols, totalRows,
				{
					style: options.style,
					fill: true,
				}
			);

		}

		if ( !options.style && options.border ) {

			b8.TextRenderer.printBox( totalCols, totalRows, true, options.borderChar );

		}

		if ( options.title ) {

			const t = " " + options.title + " ";
			b8.Core.drawState.cursorCol = startCol + 1;
			b8.TextRenderer.print( t );

		}

		if ( options.prompt ) {

			b8.Core.drawState.cursorCol = promptSize.cols <= totalCols ?
				( startCol + border01 + options.padding ) :
				( startCol + Math.round( ( totalCols - promptSize.cols ) / 2 ) );
			b8.Core.drawState.cursorRow = startRow + border01 + options.padding;

			if ( options.typewriter ) {
				await b8.Async.typewriter( options.prompt );
			} else {
				b8.TextRenderer.print( options.prompt );
			}

		}

		// TODO: save the screen image before showing the menu and restore it later.

		let selIndex = options.selIndex;

		while ( true ) {

			// Draw choices.
			b8.Core.drawState.cursorRow = startRow + border01 + options.padding + prompt01 * ( promptSize.rows + 1 );
			if ( promptSize.cols <= choicesCols ) {
				// Align with prompt if prompt is wider than choices.
				b8.Core.drawState.cursorCol = startCol + border01 + options.padding;
			} else {
				// All on one line and centered if prompt is wider than choices.
				b8.Core.drawState.cursorCol = startCol + ( totalCols - choicesCols ) / 2;
			}

			printChoices( choices, selIndex, options );

			const k = await b8.Input.readKeyAsync();

			if ( k.includes( "ArrowUp" ) ) {

				// Go up the menu.
				selIndex = selIndex > 0 ? selIndex - 1 : choices.length - 1;
				if ( choices.length > 1 ) b8.Sfx.play( b8.CONFIG.SFX.MENU_UP );

			} else if ( k.includes( "ArrowDown" ) ) {

				// Go down the menu.
				selIndex = ( selIndex + 1 ) % choices.length;
				if ( choices.length > 1 ) b8.Sfx.play( b8.CONFIG.SFX.MENU_DOWN );

			} else if (
				k.includes( "Enter" ) ||
				k.includes( "ButtonA" ) ||
				k.includes( "ButtonB" ) ||
				k.includes( " " )
			) {

				// Select menu item.
				b8.Sfx.play( b8.CONFIG.SFX.MENU_SELECT );
				return selIndex;

			}

		}

	}


	/**
	 * Prints the choices for a menu highlighting the current choice.
	 *
	 * The options object should contain the following properties:
	 * - options.selBgColor - The background color of the selected choice.
	 * - options.selFgColor - The foreground color of the selected choice.
	 *
	 * @param {string[]} choices - The choices to print.
	 * @param {number} selIndex - The index of the selected choice.
	 * @param {object} options - Options for the menu.
	 * @returns {void}
	 */
	const printChoices = function( choices, selIndex, options ) {

		const origBg = b8.Core.drawState.bgColor;
		const origFg = b8.Core.drawState.fgColor;

		for ( let i = 0; i < choices.length; i++ ) {
			const isSel = i === selIndex;
			b8.Core.setColor( isSel ? options.selFgColor : origFg, isSel ? options.selBgColor : origBg );
			b8.TextRenderer.print( choices[ i ] + "\n" );
		}

		b8.Core.setColor( origFg, origBg );

	}

} )( b8 );

( function( b8 ) {

	b8.Math = {};


	/**
	 * Calculates a 2D distance between points (x0, y0) and (x1, y1).
	 *
	 * @param {number} x0 - The x-coordinate of the first point.
	 * @param {number} y0 - The y-coordinate of the first point.
	 * @param {number} x1 - The x-coordinate of the second point.
	 * @param {number} y1 - The y-coordinate of the second point.
	 * @returns {number} The distance between the two points.
	 */
	b8.Math.dist2D = function( x0, y0, x1, y1 ) {

		b8.Utilities.checkNumber( "x0", x0 );
		b8.Utilities.checkNumber( "y0", y0 );
		b8.Utilities.checkNumber( "x1", x1 );
		b8.Utilities.checkNumber( "y1", y1 );

		const dx = x0 - x1;
		const dy = y0 - y1;

		return Math.sqrt( dx * dx + dy * dy );

	}


	// Alias dist2d to dist2D for consistency.
	b8.Math.dist2d = b8.Math.dist2D;


	/**
	 * Calculate the Manhattan distance between two locations.
	 *
	 * The Manhattan distance is the sum of the absolute differences
	 * of their Cartesian coordinates. It represents the distance
	 * traveled along axes at right angles (like navigating a grid). It's not
	 * a straight-line distance, but rather the total number of steps required
	 * to move from one point to another when only moving horizontally and
	 * vertically.
	 *
	 * @param {Object} a - The first location with col and row properties.
	 * @param {Object} b - The second location with col and row properties.
	 * @returns {number} The Manhattan distance between the two locations.
	 */
	b8.Math.distManhattan = function( a, b ) {

		return Math.abs( a.col - b.col ) + Math.abs( a.row - b.row );

	};


	/**
	 * Linearly interpolates between two values.
	 *
	 * @param {number} a - The start value.
	 * @param {number} b - The end value.
	 * @param {number} t - The interpolation factor (0.0 to 1.0).
	 * @returns {number} The interpolated value.
	 */
	b8.Math.lerp = function( a, b, t ) {

		return a + ( b - a ) * t;

	}


	/**
	 * A smoothing function for interpolation.
	 *
	 * This is Perlin's classic fade function 6t^5 - 15t^4 + 10t^3.
	 * It eases coordinate values so that they will ease towards integral values.
	 * This ends up smoothing the final output.
	 *
	 * @param {number} t - The interpolation factor (0.0 to 1.0).
	 * @returns {number} The smoothed interpolation factor.
	 */
	b8.Math.fade = function( t ) {

		return t * t * t * ( t * ( t * 6 - 15 ) + 10 );

	}


	/**
	 * A simpler smoothing function for interpolation.
	 *
	 * This is the smoothstep function 3t^2 - 2t^3.
	 * It eases coordinate values so that they will ease towards integral values.
	 * This ends up smoothing the final output.
	 *
	 * @param {number} t - The interpolation factor (0.0 to 1.0).
	 * @returns {number} The smoothed interpolation factor.
	 */
	b8.Math.smoothstep = function( t ) {

		return t * t * ( 3 - 2 * t );

	}


	// Ken Perlin's quintic fade (smoother than smoothstep)
	function fade( t ) {

		return t * t * t * ( t * ( t * 6 - 15 ) + 10 );

	}


} )( b8 );

( function( b8 ) {

	b8.Inventory = {};


	document.addEventListener(
		'b8.initComplete',
		() => {

			b8.Inventory.reset();

		},
		{ once: true }
	);


	/**
	 * Reset the inventory to empty.
	 *
	 * This clears all item counts and flags.
	 *
	 * @returns {void}
	 */
	b8.Inventory.reset = function() {

		b8.data.inventory = {
			counts: {},
			flags: {}
		};

	};


	/**
	 * Get the count of a specific item in the inventory.
	 *
	 * @param {string} id The item ID to check.
	 * @returns {number} The count of the item (default: 0 if not found).
	 */
	b8.Inventory.getCount = function( id ) {

		return b8.data.inventory.counts[ id ] ?? 0;

	}


	/**
	 * Add a certain amount of an item to the inventory.
	 *
	 * If a max is provided, the item count will not exceed that max.
	 *
	 * @param {string} id The item ID to add.
	 * @param {number} [amount=1] The amount to add (default: 1).
	 * @param {number} [max=Infinity] The maximum amount allowed (default: Infinity).
	 */
	b8.Inventory.add = function( id, amount = 1, max = Infinity ) {

		const current = b8.Inventory.getCount( id );
		b8.data.inventory.counts[ id ] = Math.min( current + amount, max );

	}


	/**
	 * Remove a certain amount of an item from the inventory.
	 *
	 * If the item count goes below zero, it is set to zero.
	 *
	 * @param {string} id The item ID to remove.
	 * @param {number} [amount=1] The amount to remove (default: 1).
	 */
	b8.Inventory.remove = function( id, amount = 1 ) {

		const current = b8.Inventory.getCount( id );
		b8.data.inventory.counts[ id ] = Math.max( current - amount, 0 );

	}


	/**
	 * Check if the inventory has at least a certain amount of an item.
	 *
	 * @param {string} id The item ID to check.
	 * @param {number} [amount=1] The minimum amount required (default: 1).
	 * @returns {boolean} True if the inventory has at least the specified amount, false otherwise.
	 */
	b8.Inventory.has = function( id, amount = 1 ) {

		return b8.Inventory.getCount( id ) >= amount;

	}


	/**
	 * Set a flag in the inventory.
	 *
	 * For things like "door opened", "cutscene played"
	 *
	 * @param {string} flag The flag name to set.
	 * @param {boolean} [value=true] The value to set the flag to (default: true).
	 */
	b8.Inventory.setFlag = function( flag, value = true ) {

		b8.data.inventory.flags[ flag ] = value;

	}


	/**
	 * Get a flag from the inventory.
	 *
	 * Returns true/false if the flag is set.
	 *
	 * @param {string} flag The flag name to get.
	 * @returns {boolean} The value of the flag (default: false).
	 */
	b8.Inventory.getFlag = function( flag ) {

		b8.Utilities.checkString( 'flag', flag );

		return !!b8.data.inventory.flags[ flag ];

	}


	/**
	 * Filters inventory items based on a prefix or a regular expression.
	 *
	 * This function returns an array of objects, each containing the `id` and `count`
	 * of items that match the given prefix or regular expression.
	 *
	 * Examples:
	 * - To get all keys by prefix:
	 *   const keys = b8.Inventory.filter("key-");
	 *
	 * - To use a regex for finer control:
	 *   const special = b8.Inventory.filter(/^potion-|^scroll-/);
	 *
	 * @param {string|RegExp} match Item ID prefix or regex to match.
	 * @returns {Array<{id:string,count:number}>} Matching items with counts.
	 */
	b8.Inventory.filter = function( match ) {

		// Array to store matching items
		const out = [];
		// Reference to the inventory counts
		const counts = b8.data.inventory.counts;
		// Check if `match` is a regular expression
		const isRegex = match instanceof RegExp;
		// Ensure the `match` parameter is a valid string or RegExp
		if ( !isRegex ) b8.Utilities.checkString( 'match', match );


		// Iterate over all item IDs in the inventory
		for ( const id in counts ) {

			if ( !Object.hasOwn( counts, id ) ) continue;
			const count = counts[ id ];

			// Skip items with a count of 0 or less
			if ( count <= 0 ) continue;

			// Check if the item ID matches the given prefix or regex
			if (
				( isRegex && match.test( id ) ) || // If `match` is a regex, test the ID
				( !isRegex && id.includes( match ) ) // If `match` is a string, check if the ID includes it
			) {
				out.push( { id, count } );
			}
		}

		return out;

	}



} )( b8 );

( function( b8 ) {

	b8.Intro = {};

	const bgIntro = `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`;


	/**
	 * Display a loading screen.
	 *
	 * @returns {Promise<void>} A promise that resolves when the loading screen is displayed.
	 */
	b8.Intro.loading = async function() {

		// Colour count.
		const prefix = "8> ";

		b8.color( 0, 4 );
		b8.cls();
		b8.locate( 1, 1 );
		b8.print( prefix + "b8 Loading...\n" );

		await b8.Async.wait( 0.4 );

	}


	/**
	 * Display a splash screen.
	 *
	 * @param {string} [name="b8 Project"] The name of the project.
	 * @returns {Promise<void>} A promise that resolves when the splash screen is dismissed.
	 */
	b8.Intro.splash = async function() {

		// Load title screen image.
		const titleScreen = b8.Tilemap.load( bgIntro );

		// Draw title screen.
		b8.locate( 0, 0 );
		b8.Tilemap.draw( titleScreen );

		// Click to start.
		let message = "Click to start";
		if ( b8.Core.isTouchDevice() ) message = "Tap to start";

		b8.color( 4, 5 );
		b8.locate( 0, b8.CONFIG.SCREEN_ROWS - 2 );
		b8.printCentered( message, b8.CONFIG.SCREEN_COLS );

		// Wait for user input.
		await b8.Input.readPointerAsync();

		b8.color( 15, 0 );
		b8.cls();

	}


} )( b8 );

( function( b8 ) {

	/**
	 * Input class handles keyboard input and provides methods to check key states.
	 */
	b8.Input = {};


	/**
	 * ─────────────────────────────────────────────────────────────
	 * Input Modes & Contexts
	 * ─────────────────────────────────────────────────────────────
	 *
	 * BeepMini supports two styles of input:
	 *
	 * 1) Polled input (gameplay)
	 *    - keyHeld()
	 *    - keyJustPressed()
	 *
	 * 2) Captured input (modal / async)
	 *    - readKeyAsync()
	 *    - readLine()
	 *
	 * These two styles MUST NOT interfere with each other.
	 *
	 * To guarantee this, input is routed through a stack of
	 * "input contexts".
	 *
	 * ─────────────────────────────────────────────────────────────
	 * Core Concepts
	 * ─────────────────────────────────────────────────────────────
	 *
	 * • Raw input state is always tracked.
	 *   - heldRaw_ reflects the physical keyboard state.
	 *   - It is never paused, cleared, or restored.
	 *
	 * • Gameplay input is contextual.
	 *   - gameJustPressed_ is only populated when the active
	 *     context allows gameplay input.
	 *
	 * • Modal input captures key events.
	 *   - When a context has { capture: true }, key events are
	 *     routed to async readers instead of gameplay polling.
	 *
	 * • Contexts are stacked.
	 *   - Opening a menu, prompt, or dialog pushes a context.
	 *   - Closing it pops the context.
	 *   - Nested modals are fully supported.
	 *
	 * ─────────────────────────────────────────────────────────────
	 * Context Behaviour
	 * ─────────────────────────────────────────────────────────────
	 *
	 * capture: true
	 *   - Gameplay keyJustPressed() returns false.
	 *   - Key events are queued for readKeyAsync().
	 *
	 * capture: false
	 *   - Gameplay polling behaves normally.
	 *
	 * passthrough: true
	 *   - Gameplay keyJustPressed() still fires even when capturing.
	 *   - Useful for overlays or non-blocking UI.
	 *
	 * ─────────────────────────────────────────────────────────────
	 * Why this exists
	 * ─────────────────────────────────────────────────────────────
	 *
	 * Mixing polled input with async input is error-prone if both
	 * listen to the same key events.
	 *
	 * Input contexts ensure:
	 *   - Async input never steals gameplay input accidentally.
	 *   - Gameplay never triggers while awaiting text or prompts.
	 *   - Key order does not depend on frame timing or code order.
	 *
	 * This design avoids:
	 *   - Saving/restoring key state
	 *   - Frame-order bugs
	 *   - Re-entrancy issues with async input
	 *
	 * ─────────────────────────────────────────────────────────────
	 */

	/**
	 * Maps physical keys to their BeepMini aliases.
	 *
	 * @type {Object<string, string[]>}
	 */
	const KEY_ALIASES_ = {
		W: [ "W", "ArrowUp" ],
		A: [ "A", "ArrowLeft" ],
		S: [ "S", "ArrowDown" ],
		D: [ "D", "ArrowRight" ],
		ENTER: [ "Enter", "Escape" ],
		Z: [ "Z", "ButtonA" ],
		N: [ "N", "ButtonA" ],
		X: [ "X", "ButtonB" ],
		M: [ "M", "ButtonB" ],
		' ': [ " ", "Space" ],
	};


	/**
	 * List of keys currently held down.
	 * This is a raw list of keys without any context applied.
	 * This is not gated by input contexts.
	 *
	 * @type {Set<string>}
	 */
	let keysHeldRaw_ = new Set();


	/**
	 * List of keys that were just pressed in the current frame.
	 * These are real time game key presses, not asynchronous reads.
	 *
	 * @type {Set<string>}
	 */
	let gameJustPressed_ = new Set();


	/**
	 * Retrieves the currently active input context.
	 * The active context is the last one added to the `contexts_` stack.
	 *
	 * @returns {Object} The currently active input context.
	 */
	const active_ = () => contexts_[ contexts_.length - 1 ];


	/**
	 * List of input contexts.
	 * Each context defines its name, whether it captures input, allows passthrough,
	 * and maintains a queue of input events.
	 *
	 * @type {Array<{name: string, capture: boolean, passthrough: boolean, queue: Array}>}
	 */
	let contexts_ = [ createDefaultContext_() ];


	/**
	 * ─────────────────────────────────────────────────────────────
	 * Pointer Input (Mouse / Touch / Pen)
	 * ─────────────────────────────────────────────────────────────
	 *
	 * BeepMini uses Pointer Events to unify mouse, touch, and pen
	 * input into a single system.
	 *
	 * Pointer state is tracked per-frame and exposed through a
	 * simple polling API, similar to keyboard input.
	 *
	 * ─────────────────────────────────────────────────────────────
	 * Core Concepts
	 * ─────────────────────────────────────────────────────────────
	 *
	 * • pointer_.x / pointer_.y
	 *   - Position of the pointer in canvas pixel space.
	 *
	 * • pointer_.isDown
	 *   - Whether the pointer is currently held down.
	 *
	 * • pointer_.justPressed
	 *   - True only on the frame the pointer was pressed.
	 *
	 * • pointer_.justReleased
	 *   - True only on the frame the pointer was released.
	 *
	 * These flags are automatically reset at the end of each frame.
	 *
	 * ─────────────────────────────────────────────────────────────
	 * Why this exists
	 * ─────────────────────────────────────────────────────────────
	 *
	 * Immediate-mode UI (buttons, regions, etc.) requires knowing
	 * when a pointer interaction starts and ends within a single
	 * frame window.
	 *
	 * This system provides a minimal, consistent API that works for:
	 *   - Mouse clicks
	 *   - Touch taps
	 *   - Stylus input
	 *
	 * Without requiring separate code paths.
	 *
	 * ─────────────────────────────────────────────────────────────
	 */


	/**
	 * Internal pointer state.
	 * This is updated via pointer events and reset per frame.
	 *
	 * @type {{x: number, y: number, justPressed: boolean, justReleased: boolean}}
	 */
	let pointer_ = createPointerState_();


	/**
	 * Gets the pointer X position in canvas pixels.
	 *
	 * @returns {number}
	 */
	b8.Input.pointerX = function() {
		return pointer_.x;
	}


	/**
	 * Gets the pointer Y position in canvas pixels.
	 *
	 * @returns {number}
	 */
	b8.Input.pointerY = function() {
		return pointer_.y;
	}


	/**
	 * Checks if the pointer was pressed this frame.
	 *
	 * @returns {boolean}
	 */
	b8.Input.pointerJustPressed = function() {
		return pointer_.justPressed;
	}


	/**
	 * Checks if the pointer was released this frame.
	 *
	 * @returns {boolean}
	 */
	b8.Input.pointerJustReleased = function() {
		return pointer_.justReleased;
	}


	/**
	 * Gets the pointer position in tile coordinates.
	 *
	 * @returns {{col: number, row: number}}
	 */
	b8.Input.pointerTile = function() {

		return {
			col: Math.floor( pointer_.x / b8.CONFIG.CHR_WIDTH ),
			row: Math.floor( pointer_.y / b8.CONFIG.CHR_HEIGHT ),
		};

	}


	/**
	 * Pushes a new input context onto the stack.
	 *
	 * @param {string} name - The name of the context.
	 * @param {Object} [opts={}] - Options for the context.
	 * @param {boolean} [opts.capture=false] - If true, gameplay polling is blocked.
	 * @param {boolean} [opts.passthrough=false] - If true, allows gameplay justPressed events even when capturing.
	 */
	b8.Input.pushContext = function( name, opts = {} ) {

		contexts_.push(
			{
				name,
				capture: !!opts.capture, // if true, gameplay polling should be blocked
				passthrough: opts.passthrough ?? false, // if true, still allow gameplay justPressed
				queue: [],
			}
		);

	}


	/**
	 * Pops the most recent input context from the stack.
	 *
	 * Ensures that at least one context (the default "game" context) remains on the stack.
	 */
	b8.Input.popContext = function() {

		if ( contexts_.length > 1 ) contexts_.pop();

	}


	/**
	 * Executes a function within a temporary input context.
	 *
	 * Pushes a new context, runs the provided function, and then pops the context.
	 *
	 * @param {string} name - The name of the temporary context.
	 * @param {Object} opts - Options for the context.
	 * @param {Function} fn - The function to execute within the context.
	 * @returns {Promise<any>} The result of the executed function.
	 */
	b8.Input.withContext = async function( name, opts, fn ) {

		b8.Input.pushContext( name, opts );
		try {
			return await fn();
		} finally {
			b8.Input.popContext();
		}

	}


	/**
	 * Resets the input state, clearing all held and just pressed keys.
	 * This is useful when restarting a game or resetting input state.
	 *
	 * @returns {void}
	 */
	b8.Input.reset = function() {


		keysHeldRaw_ = new Set();
		gameJustPressed_ = new Set();
		contexts_ = [ createDefaultContext_() ];
		pointer_ = createPointerState_();

		unbindEvents_();
		b8.Input.onEndFrame();

	}


	/**
	 * Initializes the input system.
	 *
	 * @returns {void}
	 */
	b8.Input.init = function() {

		b8.Input.reset();
		bindEvents_();

	}


	/**
	 * Checks if a key is currently held down.
	 *
	 * @param {string} keyName The name of the key to check.
	 * @returns {boolean} Whether the key is currently held down.
	 */
	b8.Input.keyHeld = function( keyName ) {

		return keysHeldRaw_.has( keyName.toUpperCase() );

	}


	/**
	 * Checks if a key was just pressed in the current frame.
	 *
	 * @param {string} keyName The name of the key to check.
	 * @returns {boolean} Whether the key was just pressed.
	 */
	b8.Input.keyJustPressed = function( keyName ) {

		const ctx = active_();
		const key = keyName.toUpperCase();

		if ( ctx.capture && !ctx.passthrough ) return false;

		return gameJustPressed_.has( key );

	}


	/**
	 * Clears the list of keys that were just pressed.
	 * Should be called at the end of each frame.
	 *
	 * @returns {void}
	 */
	b8.Input.onEndFrame = function() {

		gameJustPressed_.clear();

		pointer_.justPressed = false;
		pointer_.justReleased = false;

	}


	/**
	 * Handles keydown events, adding the key to the just pressed and held sets.
	 * Resolves any pending asynchronous key events.
	 *
	 * @param {KeyboardEvent} e The event object.
	 * @returns {void}
	 */
	b8.Input.onKeyDown = function( e ) {

		const keys = b8.Input.getKeys( e.key );
		const ctx = active_();

		preventDefaultKeyBehaviour_( e );
		addKeysToSet_( keysHeldRaw_, keys );

		if ( !ctx.capture || ctx.passthrough ) {
			addKeysToSet_( gameJustPressed_, keys );
		}

		if ( ctx.capture ) {
			ctx.queue.push( keys );

			if ( b8.Core.hasPendingAsync( "b8.Async.key" ) ) {
				b8.Core.resolveAsync( "b8.Async.key", ctx.queue.shift() );
			}

			return;
		}

		if ( b8.Core.hasPendingAsync( "b8.Async.key" ) ) {
			b8.Core.resolveAsync( "b8.Async.key", keys );
		}

	}


	/**
	 * Handles keyup events, removing the key from the held set.
	 *
	 * @param {KeyboardEvent} e The event object.
	 * @returns {void}
	 */
	b8.Input.onKeyUp = function( e ) {

		if ( !e.key ) return;

		removeKeysFromSet_( keysHeldRaw_, b8.Input.getKeys( e.key ) );

	}


	/**
	 * Reads a key asynchronously. Returns a promise that resolves to the key that was pressed.
	 *
	 * Async key reads always pull from the active context. This prevents async input from racing gameplay polling.
	 *
	 * Resolves with the full logical key list for the next key event.
	 *
	 * @returns {Promise<string[]>} A promise that resolves to the pressed key aliases.
	 */
	b8.Input.readKeyAsync = function() {

		const ctx = active_();

		// If we are in a capture context and already have queued events, return immediately
		if ( ctx.capture && ctx.queue.length > 0 ) {
			return Promise.resolve( ctx.queue.shift() );
		}

		return new Promise(
			( resolve, reject ) => {
				b8.Core.startAsync( "b8.Async.key", resolve, reject );
			}
		);

	}


	/**
	 * Reads a pointer asynchronously. Returns a promise that resolves to the
	 * pointer position.
	 *
	 * @returns {Promise<{x: number, y: number}>} A promise that resolves to the pointer position.
	 */
	b8.Input.readPointerAsync = function() {

		return new Promise(
			( resolve, reject ) => {
				b8.Core.startAsync( "b8.Async.pointer", resolve, reject );
			}
		);

	}


	/**
	 * Gets an array of keys that correspond to a given key.
	 * For example, pressing "W" also maps to "ArrowUp".
	 *
	 * @param {string} key The key to get aliases for.
	 * @returns {string[]} An array of key names.
	 */
	b8.Input.getKeys = function( key ) {

		return KEY_ALIASES_[ key.toUpperCase() ] || [ key ];

	}


	/**
	 * Handles pointer down events.
	 *
	 * Marks the pointer as pressed and updates position.
	 *
	 * @param {PointerEvent} e
	 */
	b8.Input.onPointerDown = function( e ) {

		const pos = b8.Core.clientToCanvas( e.clientX, e.clientY );

		pointer_.x = pos.x;
		pointer_.y = pos.y;
		pointer_.pressX = pos.x;
		pointer_.pressY = pos.y;
		pointer_.isDown = true;
		pointer_.justPressed = true;

		b8.Core.realCanvas.setPointerCapture( e.pointerId );

		if ( b8.Core.hasPendingAsync( "b8.Async.pointer" ) ) {
			b8.Core.resolveAsync( "b8.Async.pointer", pos );
		}

	}


	/**
	 * Handles pointer up events.
	 *
	 * Marks the pointer as released.
	 *
	 * @param {PointerEvent} e
	 */
	b8.Input.onPointerUp = function( e ) {

		const pos = b8.Core.clientToCanvas( e.clientX, e.clientY );

		pointer_.x = pos.x;
		pointer_.y = pos.y;
		pointer_.isDown = false;
		pointer_.justReleased = true;

		pointer_.tapX = pos.x;
		pointer_.tapY = pos.y;

	}


	/**
	 * Handles pointer move events, updating the pointer position.
	 *
	 * This is used for hover states and dragging, but does not trigger any immediate-mode UI interactions on its own.
	 *
	 * @param {PointerEvent} e
	 * @return {void}
	 */
	b8.Input.onPointerMove = function( e ) {

		const pos = b8.Core.clientToCanvas( e.clientX, e.clientY );
		pointer_.x = pos.x;
		pointer_.y = pos.y;

	}


	/**
	 * Resets the game input state. This is useful when restarting a level or resetting the game.
	 *
	 * This clears all held keys, just pressed keys, and pointer state.
	 *
	 * @returns {void}
	 */
	b8.Input.onPointerCancel = function() {

		pointer_.isDown = false;
		pointer_.justPressed = false;
		pointer_.justReleased = false;

	}


	/**
	 * Checks if the pointer is currently held down.
	 *
	 * @returns {boolean} True if the pointer is held down, false otherwise.
	 */
	b8.Input.pointerHeld = function() {

		return !!pointer_.isDown;

	}


	/**
	 * Checks if there is a pending tap (pointer down and up) that has not yet
	 * been consumed.
	 *
	 * This is used for immediate-mode UI interactions, where a tap should
	 * trigger a button or region only once.
	 *
	 * @returns {boolean} True if there is a pending tap, false otherwise.
	 */
	b8.Input.hasTap = function() {

		return pointer_.tapX !== null && pointer_.tapY !== null;

	}


	/**
	 * Gets the tile coordinates of the pending tap, if any. Returns null if
	 * there is no pending tap.
	 *
	 * This is used for immediate-mode UI interactions, where a tap should
	 * trigger a button or region only once.
	 *
	 * @returns {{col: number, row: number} | null} The tile coordinates of the tap, or null if there is no tap.
	 */
	b8.Input.tapTile = function() {
		if ( pointer_.tapX === null || pointer_.tapY === null ) return null;

		return {
			col: Math.floor( pointer_.tapX / b8.CONFIG.CHR_WIDTH ),
			row: Math.floor( pointer_.tapY / b8.CONFIG.CHR_HEIGHT ),
		};
	}


	/**
	 * Consumes the pending tap, if any, so that it does not trigger multiple UI interactions.
	 *
	 * This should be called after handling a tap to ensure that the same tap does not trigger multiple buttons or regions.
	 *
	 * @returns {void}
	 */
	b8.Input.consumeTap = function() {

		pointer_.tapX = null;
		pointer_.tapY = null;

	}


	/**
	 * Reads a line of text asynchronously.
	 * Handles user input to build a string until the Enter key is pressed.
	 *
	 * readLine runs inside a capture context so that gameplay input is fully suspended while typing.
	 *
	 * @param {string} [prompt='Enter text:'] Prompt text to display.
	 * @param {string} [initString=''] Initial input value.
	 * @param {number} [maxLen=100] Maximum allowed length.
	 * @param {number} [maxWidth=-1] Maximum width before wrapping.
	 * @returns {Promise<string>} A promise that resolves to the entered text.
	 */
	b8.Input.readLine = async function( prompt = 'Enter text:', initString = '', maxLen = 100, maxWidth = -1 ) {

		return b8.Input.withContext( "text", { capture: true }, async () => {
			// If a prompt is specified, print it first.
			if ( prompt && prompt.length > 0 ) {
				b8.print( prompt + "\n> " );
			}

			// On mobile we use a prompt dialog since they don't have a keyboard for typing things in.
			if ( b8.Core.isMobile() ) {
				return await handleMobileInput( prompt, initString, maxLen, maxWidth );
			}

			// On desktop we handle the input ourselves.
			return await handleDesktopInput( initString, maxLen, maxWidth );
		} );
	};


	/**
	 * Handles text input on mobile devices using a prompt dialog.
	 *
	 * @param {string} prompt The prompt to display.
	 * @param {string} initString The initial string to display.
	 * @param {number} maxLen The maximum length of the string to read.
	 * @param {number} maxWidth The maximum width of the line.
	 * @returns {Promise<string>} A promise that resolves to the input string.
	 */
	async function handleMobileInput( prompt, initString, maxLen, maxWidth ) {

		const textInput = await readPrompt( prompt, initString, maxLen, maxWidth );
		b8.print( textInput + "\n" );
		await b8.Async.wait( 0.2 );
		return textInput;

	}


	/**
	 * Handles text input on desktop devices by capturing key presses.
	 *
	 * @param {string} initString The initial string to display.
	 * @param {number} maxLen The maximum length of the string to read.
	 * @param {number} maxWidth The maximum width of the line.
	 * @returns {Promise<string>} A promise that resolves to the input string.
	 */
	async function handleDesktopInput( initString, maxLen, maxWidth ) {

		const startCol = b8.Core.drawState.cursorCol;
		const startRow = b8.Core.drawState.cursorRow;

		let curCol = startCol;
		let curRow = startRow;
		let curStrings = [ initString ];
		let curPos = 0;

		const cursorWasVisible = b8.Core.drawState.cursorVisible;
		b8.CursorRenderer.setCursorVisible( true );

		// Loop until the user presses Enter.
		while ( true ) {

			b8.Core.setCursorLocation( curCol, curRow );
			b8.TextRenderer.print( curStrings[ curPos ] || "" );
			const keys = await b8.Input.readKeyAsync();

			for ( const key of keys ) {

				if ( key === "Backspace" ) {

					// Handle backspace: remove the last character.
					if ( curStrings[ curPos ].length === 0 ) {
						if ( curPos === 0 ) {
							continue;
						}
						curPos--;
						curRow--;
					}

					// If the current string has at least one character it removes the last character.
					// If the string is empty it leaves it unchanged.
					curStrings[ curPos ] = curStrings[ curPos ].length > 0 ? curStrings[ curPos ].substring( 0, curStrings[ curPos ].length - 1 ) : curStrings[ curPos ];
					// Position the flashing cursor and then print a space to remove the last character.
					b8.Core.setCursorLocation( curCol + b8.TextRenderer.measure( curStrings[ curPos ] ).cols, curRow );
					b8.TextRenderer.print( " " );

					b8.Sfx.play( b8.CONFIG.SFX.TYPING );

				} else if ( key === "Enter" ) {

					// Handle enter: submit the text.
					b8.CursorRenderer.setCursorVisible( cursorWasVisible );

					b8.Sfx.play( b8.CONFIG.SFX.TYPING );

					return curStrings.join( "" );

				} else if ( key.length === 1 ) {

					// Handle regular character input.
					if ( curStrings.join( "" ).length < maxLen || maxLen === -1 ) {
						curStrings[ curPos ] += key;

						// Word wrap.
						// Check if maxWidth is set and the current string has reached/exceeded it
						if ( maxWidth !== -1 && curStrings[ curPos ].length >= maxWidth ) {
							// Print the last character of the current string (to visually fill the slot)
							b8.TextRenderer.print( curStrings[ curPos ].charAt( curStrings[ curPos ].length - 1 ) );
							// Reset the column to the starting column for the next line
							curCol = startCol;
							// Move to the next string position (start a new line)
							curPos++;
							// Initialize the new line as an empty string
							curStrings[ curPos ] = "";
							// Move the row down for the new line
							curRow++;
						}
					}

					b8.Sfx.play( b8.CONFIG.SFX.TYPING );

				}

			}
		}
	}


	/**
	 * Helper function to show a prompt dialog on mobile devices.
	 * This is used because mobile devices don't have a physical keyboard.
	 *
	 * @param {string} [promptString='Enter text:'] The prompt to display.
	 * @param {string} [initString=''] The initial string to display.
	 * @param {number} [maxLen=100] The maximum length of the string to read.
	 * @param {number} [maxWidth=-1] The maximum width of the line.
	 * @returns {Promise<string>} A promise that resolves to the string that was read.
	 */
	const readPrompt = async function( promptString = 'Enter text:', initString = '', maxLen = 100, maxWidth = -1 ) {

		// If the prompted string is valid.
		let valid = false;
		// The value to return.
		let textInput = "";
		// The characters that are allowed.
		const allowedChars = b8.CONFIG.CHRS;

		do {

			// A little pause to ensure the screen updates before the prompt appears.
			await b8.Async.wait( 0.333 );

			// Show the prompt and get the input.
			textInput = prompt( promptString, initString );

			// Trim whitespace from start and end.
			textInput = textInput.trim();

			// Remove disallowed characters.
			textInput = textInput.split( '' ).filter( char => allowedChars.includes( char ) ).join( '' );

			// Enforce max length.
			if ( maxLen !== -1 ) {
				textInput = textInput.substring( 0, maxLen );
			}

			valid = textInput.length > 0;

		} while ( valid === false );

		return textInput;

	}


	/**
	 * Creates the default gameplay input context.
	 *
	 * @returns {{name: string, capture: boolean, passthrough: boolean, queue: Array}}
	 */
	function createDefaultContext_() {

		return {
			name: "game",
			capture: false,
			passthrough: true,
			queue: [],
		};

	}


	/**
	 * Creates the default pointer state.
	 *
	 * @returns {{
	 * 	x: number,
	 * 	y: number,
	 * 	isDown: boolean,
	 * 	justPressed: boolean,
	 * 	justReleased: boolean,
	 * 	pressX: number,
	 * 	pressY: number,
	 * 	tapX: ?number,
	 * 	tapY: ?number
	 * }}
	 */
	function createPointerState_() {

		return {
			x: 0,
			y: 0,
			isDown: false,
			justPressed: false,
			justReleased: false,
			pressX: 0,
			pressY: 0,
			tapX: null,
			tapY: null,
		};

	}


	/**
	 * Binds keyboard and pointer event listeners.
	 *
	 * @returns {void}
	 */
	function bindEvents_() {

		window.addEventListener( "keydown", b8.Input.onKeyDown );
		window.addEventListener( "keyup", b8.Input.onKeyUp );

		const canvas = b8.Core.realCanvas;
		canvas?.addEventListener( "pointerdown", b8.Input.onPointerDown );
		canvas?.addEventListener( "pointerup", b8.Input.onPointerUp );
		canvas?.addEventListener( "pointermove", b8.Input.onPointerMove );
		canvas?.addEventListener( "pointercancel", b8.Input.onPointerCancel );

	}


	/**
	 * Unbinds keyboard and pointer event listeners.
	 *
	 * @returns {void}
	 */
	function unbindEvents_() {

		window.removeEventListener( "keydown", b8.Input.onKeyDown );
		window.removeEventListener( "keyup", b8.Input.onKeyUp );

		const canvas = b8.Core.realCanvas;
		canvas?.removeEventListener( "pointerdown", b8.Input.onPointerDown );
		canvas?.removeEventListener( "pointerup", b8.Input.onPointerUp );
		canvas?.removeEventListener( "pointermove", b8.Input.onPointerMove );
		canvas?.removeEventListener( "pointercancel", b8.Input.onPointerCancel );

	}


	/**
	 * Adds a list of keys to a Set, normalising them to upper case.
	 *
	 * @param {Set<string>} set The target set.
	 * @param {string[]} keys The keys to add.
	 * @returns {void}
	 */
	function addKeysToSet_( set, keys ) {

		for ( const key of keys ) {
			set.add( key.toUpperCase() );
		}

	}


	/**
	 * Removes a list of keys from a Set, normalising them to upper case.
	 *
	 * @param {Set<string>} set The target set.
	 * @param {string[]} keys The keys to remove.
	 * @returns {void}
	 */
	function removeKeysFromSet_( set, keys ) {

		for ( const key of keys ) {
			set.delete( key.toUpperCase() );
		}

	}


	/**
	 * Prevents browser defaults for keys that should stay under game control.
	 *
	 * @param {KeyboardEvent} e The keyboard event.
	 * @returns {void}
	 */
	function preventDefaultKeyBehaviour_( e ) {

		if ( [ "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", " " ].includes( e.key ) ) {
			e.preventDefault();
		}

	}


} )( b8 );

( function( b8 ) {


	b8.Image = b8.Image || {};


	/**
	 * Quantise an ImageData in-place to a palette (no dithering).
	 * Returns the same ImageData for chaining.
	 *
	 * @param {ImageData} imageData - The ImageData to quantise.
	 * @param {object} lookupTable - The color lookup table.
	 * @returns {ImageData} The quantised ImageData.
	 */
	b8.Image.quantiseImageData = function( imageData, lookupTable ) {

		const data = imageData.data;

		for ( let i = 0; i < data.length; i += 4 ) {
			const r = data[ i ];
			const g = data[ i + 1 ];
			const b = data[ i + 2 ];
			const { r: pr, g: pg, b: pb } = b8.Image.findClosestColorUsingLookup( r, g, b, lookupTable );
			data[ i ] = pr;
			data[ i + 1 ] = pg;
			data[ i + 2 ] = pb;
			// alpha unchanged
		}

		return imageData;

	}


	/**
	 * Loads an image asynchronously.
	 *
	 * @param {string} src - The source URL of the image.
	 * @returns {Promise<HTMLImageElement>} A promise that resolves to the loaded image.
	 */
	b8.Image.loadImageAsync = async function( src ) {

		return new Promise(
			( resolver ) => {
				const img = new Image();
				img.crossOrigin = "anonymous";
				img.src = src;
				img.onload = () => resolver( img );
			}
		);

	}



	/**
	 * Finds the closest color in the palette to the given RGB values.
	 *
	 * @param {number} r - The red component (0-255).
	 * @param {number} g - The green component (0-255).
	 * @param {number} b - The blue component (0-255).
	 * @param {string[]} palette - The color palette (array of hex strings).
	 * @returns {string} The closest color in hex format.
	 */
	b8.Image.findClosestColorUsingLookup = function( r, g, b, lookupTable, bucketSize = 4 ) {

		// Round RGB values to the nearest bucket
		const roundedR = Math.floor( r / bucketSize ) * bucketSize;
		const roundedG = Math.floor( g / bucketSize ) * bucketSize;
		const roundedB = Math.floor( b / bucketSize ) * bucketSize;

		const key = `${roundedR},${roundedG},${roundedB}`;
		return lookupTable[ key ];

	}


	// Cache the color lookup table to avoid recomputing it.
	// Only generated when an image is loaded externally.
	// Font images are not loaded this way.
	const colorLookupTable = {};


	/**
	 * Generates a color lookup table for the given palette.
	 *
	 * @param {string[]} palette - The color palette (array of hex strings).
	 * @param {number} [bucketSize=4] - The size of the color buckets.
	 * @returns {object} The color lookup table.
	 */
	b8.Image.generateColorLookupTable = function( palette, bucketSize = 4 ) {

		if ( Object.keys( colorLookupTable ).length !== 0 ) {
			return colorLookupTable;
		}

		// Convert hex palette to rgb palette.
		const rgbPalette = palette.map( color => b8.Utilities.hexToRgb( color ) );

		for ( let r = 0; r < 256; r += bucketSize ) {
			for ( let g = 0; g < 256; g += bucketSize ) {
				for ( let b = 0; b < 256; b += bucketSize ) {
					const key = `${r},${g},${b}`;
					colorLookupTable[ key ] = findClosestColor( r, g, b, rgbPalette );
				}
			}
		}

		return colorLookupTable;

	}


	/**
	 * Finds the closest color in the palette to the given RGB values.
	 *
	 * This function calculates the Euclidean distance between the given RGB color
	 * and each color in the palette. The color with the smallest distance is
	 * considered the closest match.
	 *
	 * @param {number} r - The red component of the input color (0-255).
	 * @param {number} g - The green component of the input color (0-255).
	 * @param {number} b - The blue component of the input color (0-255).
	 * @param {object[]} palette - The color palette, an array of objects with {r, g, b} properties.
	 * @returns {object} The closest color in the palette, as an object {r, g, b}.
	 */
	function findClosestColor( r, g, b, palette ) {

		// Initialize variables to track the closest color and its distance
		let closestColor = null; // Will hold the closest color found
		let closestDistance = Infinity; // Start with the largest possible distance

		// Iterate over each color in the palette
		for ( const color of palette ) {

			// Calculate the squared Euclidean distance between the input color and the current palette color
			const distance = (
				Math.pow( color.r - r, 2 ) + // Difference in red channel, squared
				Math.pow( color.g - g, 2 ) + // Difference in green channel, squared
				Math.pow( color.b - b, 2 )   // Difference in blue channel, squared
			);

			// If this distance is smaller than the closest distance found so far, update the closest color
			if ( distance < closestDistance ) {
				closestDistance = distance; // Update the closest distance
				closestColor = color;       // Update the closest color
			}

		}

		// Return the closest color found in the palette
		return closestColor;

	}


	/**
	 * Bayer matrices (normalised to [-0.5, +0.5])
	 * Used for ordered dithering.
	 *
	 * @type {object}
	 * @private
	 */
	const _bayer = {

		2: [
			-0.375, 0.125,
			0.375, -0.125
		],
		4: [
			-0.46875, 0.03125, -0.34375, 0.15625,
			0.28125, -0.21875, 0.40625, -0.09375,
			-0.28125, 0.21875, -0.40625, 0.09375,
			0.46875, -0.03125, 0.34375, -0.15625
		],
		8: ( () => {
			// Build 8x8 from 4x4 recursively to keep it compact
			const base = [
				[ 0, 48, 12, 60, 3, 51, 15, 63 ],
				[ 32, 16, 44, 28, 35, 19, 47, 31 ],
				[ 8, 56, 4, 52, 11, 59, 7, 55 ],
				[ 40, 24, 36, 20, 43, 27, 39, 23 ],
				[ 2, 50, 14, 62, 1, 49, 13, 61 ],
				[ 34, 18, 46, 30, 33, 17, 45, 29 ],
				[ 10, 58, 6, 54, 9, 57, 5, 53 ],
				[ 42, 26, 38, 22, 41, 25, 37, 21 ]
			];
			// Normalise to [-0.5, +0.5]
			const nrm = [];
			for ( let y = 0; y < 8; y++ ) {
				for ( let x = 0; x < 8; x++ ) {
					const v = base[ y ][ x ] / 64; // [0,1)
					nrm.push( v - 0.5 );
				}
			}
			return nrm;
		} )()

	};


	/**
	 * Ordered dithering (Bayer). Biases colour before quantising.
	 * - imageData: ImageData (modified in-place)
	 * - mapFn: (r,g,b) -> {r,g,b}
	 * - opts:
	 *   - size: 2 | 4 | 8  (matrix size)
	 *   - strength: 0..1   (scales the bias)
	 *   - amplitude: number (max per-channel bias in 0..255)
	 *
	 * @param {ImageData} imageData - The ImageData to dither.
	 * @param {function} mapFn - The function to map RGB values to the nearest palette color.
	 * @param {object} [opts={}] - The options for dithering.
	 * @param {number} [opts.size=4] - The size of the Bayer matrix (2, 4, or 8).
	 * @param {number} [opts.strength=0.5] - The strength of the dithering effect (0 to 1).
	 * @param {number} [opts.amplitude=24] - The maximum bias in RGB units (0 to 255).
	 * @returns {ImageData} The dithered ImageData.
	 */
	b8.Image.ditherOrdered = function( imageData, mapFn, opts = {} ) {

		const size = opts.size || 4; // 2, 4, or 8
		const strength = opts.strength ?? 0.5; // calmer by default
		const amplitude = opts.amplitude ?? 24; // max bias in RGB units

		const mat = _bayer[ size ];
		if ( !mat ) throw new Error( 'Unsupported Bayer size. Use 2, 4, or 8.' );

		// Put the data in local variables for simple access.
		const data = imageData.data;
		const w = imageData.width;
		const h = imageData.height;

		const idx = ( x, y ) => 4 * ( y * w + x );
		const scale = amplitude * strength; // final scale in RGB units

		for ( let y = 0; y < h; y++ ) {
			for ( let x = 0; x < w; x++ ) {

				const i = idx( x, y );
				const t = mat[ ( y % size ) * size + ( x % size ) ]; // [-0.5..0.5]

				// Bias RGB before quantising
				const r = b8.Utilities.clamp( data[ i ] + t * scale, 0, 255 );
				const g = b8.Utilities.clamp( data[ i + 1 ] + t * scale, 0, 255 );
				const b = b8.Utilities.clamp( data[ i + 2 ] + t * scale, 0, 255 );

				const q = mapFn( r, g, b ); // nearest palette (or your custom)

				data[ i ] = q.r;
				data[ i + 1 ] = q.g;
				data[ i + 2 ] = q.b;

			}
		}

		return imageData;

	}


}( b8 ) );
( function( b8 ) {
	'use strict';

	/**
	 * Lightweight Entity-Component store for b8.
	 * ---------------------------------------------
	 * • Entity: A reference to a collection of data for an object in game. It is a numeric ID. Automatically assigned when using the create() method.
	 * • Component: A property for an entity. The data can be any plain object. Each entity has a key string used as a property name. Loc is reserved for location.
	 * • System: Code that executes commands on entities. There are different systems for different processes.
	 *
	 * All data is stored by reference; mutating the returned
	 * component objects is intentional and fast.
	 */
	b8.ECS = {};

	// auto-incrementing entity IDs
	let nextId = 1;

	// Map<name, Map<id, data>>
	let components = new Map();

	// Grid for positional queries.
	// To keep this up to date you must use setLoc() to move entities.
	let grid = [];

	// Map<name, function> for systems
	let systems = new Map();



	/**
	 * Create a new unique entity ID.
	 *
	 * @returns {number}
	 */
	function makeEntity() {

		return nextId++;

	}


	/**
	 * Ensure a component bucket exists and return it.
	 *
	 * @param {string} name Component name
	 * @returns {Map<number, Object>}
	 * @private
	 */
	function bucket( name ) {

		// If component store doesn't exist, create it.
		if ( !components.has( name ) ) components.set( name, new Map() );
		// Return the store.
		return components.get( name );

	}


	/**
	 * Get all entities at a specific grid location.
	 *
	 * @param {number} col Column coordinate
	 * @param {number} row Row coordinate
	 * @returns {number[]} Array of entity IDs at that location
	 */
	b8.ECS.entitiesAt = function( col, row ) {

		return grid[ row ]?.[ col ] ?? [];

	}


	/**
	 * Register a system that runs every frame.
	 *
	 * @param {Function} fn   System function (dt)=>void
	 * @param {string} name Unique system name
	 * @param {number} [order=0] Lower numbers run first
	 */
	b8.ECS.addSystem = function( name, fn, order = 100 ) {

		b8.Utilities.checkFunction( 'fn', fn );
		b8.Utilities.checkString( 'name', name );
		b8.Utilities.checkInt( 'order', order );

		if ( systems.has( name ) ) b8.Utilities.warn( `ECS: overwriting existing system "${name}"` );

		systems.set( name, { fn, order } );

	};


	/**
	 * Add a system a single time. Don't overwite existing systems or add multiple copies.
	 *
	 * @param {Function} fn System function (dt)=>void
	 * @param {string} name Unique system name
	 * @param {number} [order=0] Lower numbers run first
	 */
	b8.ECS.addSystemOnce = function( fn, name, order = 0 ) {

		b8.Utilities.checkFunction( 'fn', fn );
		b8.Utilities.checkString( 'name', name );
		b8.Utilities.checkInt( 'order', order );

		if ( systems.has( name ) ) return;

		b8.ECS.addSystem( fn, name, order );

	};


	/**
	 * Remove a previously-registered system.
	 *
	 * @param {string} name
	 */
	b8.ECS.removeSystem = function( name ) {

		systems.delete( name );

	};


	/**
	 * Run every system in order.
	 * Optionally pass a filter:  (name)=>boolean
	 *
	 * @param {number} dt Delta-time in seconds
	 * @param {(name:string)=>boolean=} filter Skip systems that return false
	 */
	b8.ECS.run = function( dt, filter = () => true ) {

		[ ...systems.entries() ]
			// Sort by order/ priority.
			.sort( ( a, b ) => a[ 1 ].order - b[ 1 ].order )   // order ASC
			// Execute each system in priority order.
			.forEach(
				( [ name, { fn } ] ) => {
					if ( filter( name ) ) fn( dt );
				}
			);

	};


	/**
	 * Attach or overwrite a component on an entity.
	 * If you use the component name "Loc", the position grid will be updated
	 * for spatial queries using b8.ECS.setLoc().
	 *
	 * @param {number} id Entity ID
	 * @param {string} name Component name
	 * @param {Object} [data={}] Component data (stored by reference)
	 * @returns {void}
	 */
	b8.ECS.addComponent = function( id, name = null, data = {} ) {

		b8.Utilities.checkInt( 'id', id );
		b8.Utilities.checkString( 'name', name );
		b8.Utilities.checkObject( 'data', data );

		// Add the new component to the store.
		bucket( name ).set( id, data );

		// If the component is Loc (location), update the position grid.
		if ( 'Loc' === name ) b8.ECS.setLoc( id, data.col, data.row );

	}


	/**
	 * Set a component on an entity, overwriting any existing data.
	 * This is a convenience method for add().
	 *
	 * @param {number} id Entity ID
	 * @param {string} name Component name
	 * @param {Object} data Component data (stored by reference)
	 * @returns {void}
	 */
	b8.ECS.setComponent = function( id, name, data ) {

		b8.ECS.addComponent( id, name, data );

	}


	/**
	 * Set the location of an entity.
	 * Also sets the position in the tilemap grid for fast spatial queries. You
	 * must use this to move entities for the grid to stay updated.
	 *
	 * @param {number} id Entity ID
	 * @param {number} col X tile coordinate
	 * @param {number} row Y tile coordinate
	 * @returns {void}
	 */
	b8.ECS.setLoc = function( id, col, row ) {

		b8.Utilities.checkInt( 'id', id );
		b8.Utilities.checkInt( 'col', col );
		b8.Utilities.checkInt( 'row', row );

		// Get the current location component.
		const loc = this.getComponent( id, 'Loc' );

		// Remove from old position.
		const oldRow = grid[ loc.row ];
		if ( oldRow ) {
			const cell = oldRow[ loc.col ];
			if ( cell ) {
				const i = cell.indexOf( id );
				if ( i !== -1 ) cell.splice( i, 1 );
			}
		}

		// Update component position.
		loc.col = col;
		loc.row = row;

		// Save new grid location.
		if ( !grid[ row ] ) grid[ row ] = [];
		if ( !grid[ row ][ col ] ) grid[ row ][ col ] = [];
		grid[ row ][ col ].push( id );

	}


	/**
	 * Get the component map for a given component name.
	 *
	 * @param {string} name Component name
	 * @returns {Map<number,Object>} Map<entityId,data>
	 */
	b8.ECS.getComponents = function( name ) {

		b8.Utilities.checkString( 'name', name );

		return components.get( name ) ?? new Map();

	}


	/**
	 * Return *all* components for a given entity.
	 *
	 * @param {number} id Entity ID
	 * @returns {Map<string, Object>} Map of name → data
	 */
	b8.ECS.getEntity = function( id ) {

		b8.Utilities.checkInt( 'id', id );

		const out = {};
		for ( const [ name, map ] of components ) {
			const data = map.get( id );
			if ( data ) out[ name ] = data;
		}
		return out;

	}


	/**
	 * Alias for getEntity().
	 */
	b8.ECS.get = b8.ECS.getEntity;


	/**
	 * Get all entity IDs.
	 *
	 * @returns {number[]}
	 */
	b8.ECS.getAllEntities = function() {

		const entitySet = new Set();

		for ( const compMap of components.values() ) {
			for ( const id of compMap.keys() ) {
				entitySet.add( id );
			}
		}

		return [ ...entitySet ];

	}


	/**
	 * Get one specific component for an entity.
	 *
	 * @param {number} id Entity ID
	 * @param {string} name Component name
	 * @returns {Object|undefined}
	 */
	b8.ECS.getComponent = function( id, name ) {

		b8.Utilities.checkInt( 'id', id );
		b8.Utilities.checkString( 'name', name );

		return components.get( name )?.get( id );

	}


	/**
	 * Check if an entity owns a component.
	 *
	 * @param {number} id Entity ID
	 * @param {string} name Component name
	 * @returns {boolean}
	 */
	b8.ECS.hasComponent = function( id, name ) {

		b8.Utilities.checkInt( 'id', id );
		b8.Utilities.checkString( 'name', name );

		return components.get( name )?.has( id ) ?? false;

	}


	/**
	 * Remove a single component from an entity.
	 * Also removes the entity from the position grid if it's a Loc component.
	 * You must use this to move entities for the grid to stay updated.
	 *
	 * @param {number} id Entity ID
	 * @param {string} name Component name
	 * @returns {void}
	 */
	b8.ECS.removeComponent = function( id, name ) {

		b8.Utilities.checkInt( 'id', id );
		b8.Utilities.checkString( 'name', name );

		components.get( name )?.delete( id );

		// If removing a Loc component, also update the position grid.
		if ( 'Loc' === name ) {
			const loc = this.getComponent( id, 'Loc' );
			if ( loc ) {
				const cell = grid[ loc.row ]?.[ loc.col ];
				if ( cell ) cell.splice( cell.indexOf( id ), 1 );
			}
		}

	}


	/**
	 * Remove an entity entirely (all its components).
	 *
	 * @param {number} id Entity ID
	 * @returns {void}
	 */
	b8.ECS.removeEntity = function( id ) {

		const loc = this.getComponent( id, 'Loc' );

		if ( loc ) {
			const cell = grid[ loc.row ]?.[ loc.col ];
			if ( cell ) cell.splice( cell.indexOf( id ), 1 );
		}

		for ( const store of components.values() ) store.delete( id );

	}


	/**
	 * Create an entity from a bundle of components.
	 *
	 * @param {Object.<string, Object>} bundle Keys = component names
	 * @returns {number} The new entity ID
	 */
	b8.ECS.create = function( bundle ) {

		b8.Utilities.checkObject( 'bundle', bundle );

		const id = makeEntity();
		for ( const [ name, data ] of Object.entries( bundle ) ) {
			b8.ECS.addComponent( id, name, data );
		}
		return id;

	}


	/**
	 * Query for entities that own *all* given components.
	 *
	 * @param {...string} names List of component names
	 * @returns {number[]} Array of entity IDs
	 */
	b8.ECS.query = function( ...names ) {

		// If no component names given, return empty array.
		if ( names.length === 0 ) return [];

		// Check if names[0] is an array (for ECS.query([ 'A', 'B' ]))
		if ( names.length === 1 && Array.isArray( names[ 0 ] ) ) names = names[ 0 ];

		// Start with the first component's entities.
		const base = components.get( names[ 0 ] );

		// If no entities have the first component, return empty array.
		if ( !base ) return [];

		// If only one component name, return all its entities.
		if ( names.length === 1 ) return [ ...base.keys() ];

		// Filter the base set by checking other components.
		return [ ...base.keys() ].filter( id =>
			names.every( n => components.get( n )?.has( id ) )
		);

	}


	/**
	 * Count entities by type.
	 *
	 * @param {string} typeName Type name
	 * @returns {number}
	 */
	b8.ECS.countByType = function( typeName ) {

		// get all entities with a Type component
		const typeMap = components.get( 'Type' );
		if ( !typeMap ) return 0;

		let count = 0;

		for ( const comp of typeMap.values() ) {
			if ( comp.name === typeName ) count++;
		}

		return count;

	}


	/**
	 * Reset the ECS (clears all entities & components).
	 *
	 * @returns {void}
	 */
	b8.ECS.reset = function() {

		components = new Map();
		systems = new Map();
		grid = [];
		nextId = 1;

	}

} )( b8 );



( function( b8 ) {

	/**
	 * b8.CursorRenderer handles the rendering and blinking of the cursor.
	 */
	b8.CursorRenderer = {};

	let blinkCycle_ = 0;
	let toggleBlinkHandle_ = null;


	/**
	 * Set the visibility of the cursor.
	 *
	 * @param {boolean} visible - Whether the cursor should be visible
	 * @returns {void}
	 */
	b8.CursorRenderer.setCursorVisible = function( visible ) {

		b8.Utilities.checkBoolean( "visible", visible );

		// If the cursor is already in the desired state, do nothing.
		if ( b8.Core.drawState.cursorVisible === visible ) return;

		b8.Core.drawState.cursorVisible = visible;

		blinkCycle_ = 0;
		b8.Renderer.render();

		if ( toggleBlinkHandle_ !== null ) {
			clearInterval( toggleBlinkHandle_ );
			toggleBlinkHandle_ = null;
		}

		// If visible, start the blink cycle.
		if ( visible ) {
			toggleBlinkHandle_ = setInterval(
				() => advanceBlink_(),
				b8.CONFIG.CURSOR.BLINK_INTERVAL
			);
		}

	}


	/**
	 * Draws the flashing cursor.
	 * This is called automatically in the b8 render function so does not
	 * need to be called manually.
	 *
	 * @param {CanvasRenderingContext2D} targetCtx - The context to draw the cursor on
	 * @returns {void}
	 */
	b8.CursorRenderer.draw = function( targetCtx ) {

		b8.Utilities.checkInstanceOf( "targetCtx", targetCtx, CanvasRenderingContext2D );

		// If the cursor is not visible or it is not time to blink, do nothing.
		if ( !b8.Core.drawState.cursorVisible || blinkCycle_ <= 0 ) return;

		const font = b8.TextRenderer.getFont();

		// Calculate the real position of the cursor.
		const realX = b8.Core.drawState.cursorCol * b8.CONFIG.CHR_WIDTH;
		const realY = b8.Core.drawState.cursorRow * b8.CONFIG.CHR_HEIGHT;

		// Draw the cursor.
		targetCtx.fillStyle = b8.Core.getColorHex( b8.Core.drawState.fgColor );

		targetCtx.fillRect(
			realX + 1, realY + 1,
			font.charWidth_ - 2, font.charHeight_ - 2
		);

	}


	/**
	 * Advances the cursor blink cycle.
	 *
	 * @private
	 * @returns {void}
	 */
	function advanceBlink_() {

		blinkCycle_ = ( blinkCycle_ + 1 ) % 2;
		b8.Renderer.render();

	}


} )( b8 );

( function( b8 ) {

	b8.Core = {};

	b8.Core.realCanvas = null;
	b8.Core.realCtx = null;
	b8.Core.offCanvas = null;
	b8.Core.offCtx = null;
	b8.Core.container = null;
	b8.Core.startTime = 0;
	b8.Core.deltaTime = 0;
	b8.Core.crashed = false;
	b8.Core.crashing = false;

	b8.Core.drawState = {
		fgColor: 7,
		bgColor: 0,  // -1 means transparent

		cursorCol: 0,
		cursorRow: 0,

		cursorVisible: false, // Don't change this directly, use cursorRenderer.setCursorVisible()
	};

	// Time for last frame.
	let lastFrameTime = null;
	// Whether initAsync() has been called.
	let initDone = false;
	// Current frame handlers.
	let updateHandler = null;
	let renderHandler = null;
	// Target delta time between frames (in seconds).
	let targetDt = 0;
	// Accumulated time to next frame (in seconds).
	let timeToNextFrame = 0;
	// Pending asynchronous tasks.
	let pendingAsync = null;
	// Whether the engine is currently running.
	let running = false;
	// The ID of the current requestAnimationFrame.
	let animationFrameId = null;


	/**
	 * Initializes the engine.
	 *
	 * This merges config properties and then calls b8.Core.asyncInit() to
	 * prepare assets.
	 *
	 * @param {Function} callback - The function to call when the engine is initialized.
	 * @param {Object} [options={}] - Optional configuration overrides.
	 * @return {void}
	 */
	b8.Core.init = function( callback, options = {} ) {

		b8.Utilities.checkFunction( "callback", callback );
		if ( options ) {
			b8.Utilities.checkObject( "options", options );
		}

		// Merge the options with the default configuration.
		b8.CONFIG = {
			...b8.CONFIG,
			...options,
		};

		b8.Hooks.doAction( 'beforeInit' );

		b8.Core.resetAll();

		b8.Core.setColor( 0, 15 );

		// Setup screenshot taking.
		b8.Core.initScreenshot();

		// Initialize the engine asynchronously.
		b8.Core.asyncInit( callback );

		// Initialize the game clock.
		b8.Core.startTime = b8.Core.getNow();

		b8.Hooks.doAction( 'afterInit' );

		b8.Utilities.event( 'initComplete' );

	}


	/**
	 * Resets the engine to its initial state.
	 *
	 * @returns {void}
	 */
	b8.Core.resetAll = function() {

		// Loop through all b8 namespaces and reset them if they have a
		// reset() method.
		for ( const ns in b8 ) {
			if ( b8[ ns ] && typeof b8[ ns ].reset === "function" ) {
				b8[ ns ].reset();
			}
		}

	}


	/**
	 * Resets the core state.
	 *
	 * @returns {void}
	 */
	b8.Core.reset = function() {

		b8.Core.drawState.fgColor = 7;
		b8.Core.drawState.bgColor = 0;

		b8.Core.drawState.cursorCol = 0;
		b8.Core.drawState.cursorRow = 0;

		b8.Core.drawState.cursorVisible = false;
		b8.Core.crashed = false;

		pendingAsync = false;

		lastFrameTime = null;

		// Remove event listeners.
		window.removeEventListener( "resize", b8.Core.updateLayout );
		document.removeEventListener( 'pointerup', _takeScreenshot );
		document.removeEventListener( 'keyup', _takeScreenshot );

		b8.Core.stopFrame();

	}


	/**
	 * Checks if the engine has been initialized.
	 *
	 * @returns {boolean} True if the engine has been initialized.
	 */
	b8.Core.initialized = function() {

		return initDone;

	}


	/**
	 * Asynchronously initializes the engine.
	 *
	 * This function sets up the canvas, initializes subsystems, and then calls
	 * the callback function if it's set.
	 *
	 * @param {Function} callback - The function to call when the engine is initialized.
	 * @returns {void}
	 */
	b8.Core.asyncInit = async function( callback = null ) {

		b8.Utilities.log( "b8 System initializing" );

		// Computed values: width and height of screen in virtual pixels.
		b8.CONFIG.SCREEN_WIDTH = b8.CONFIG.SCREEN_COLS * b8.CONFIG.CHR_WIDTH;
		b8.CONFIG.SCREEN_HEIGHT = b8.CONFIG.SCREEN_ROWS * b8.CONFIG.CHR_HEIGHT;

		// Set up the real canvas (the one that really exists onscreen).
		b8.Core.realCanvas = document.createElement( "canvas" );

		if ( b8.CONFIG.CANVAS_SETTINGS && b8.CONFIG.CANVAS_SETTINGS.CANVAS_ID ) {
			b8.Core.realCanvas.setAttribute( "id", b8.CONFIG.CANVAS_SETTINGS.CANVAS_ID );
		}

		if ( b8.CONFIG.CANVAS_SETTINGS && b8.CONFIG.CANVAS_SETTINGS.CANVAS_CLASSES ) {
			for ( const className of b8.CONFIG.CANVAS_SETTINGS.CANVAS_CLASSES ) {
				b8.Core.realCanvas.classList.add( className );
			}
		}

		b8.Core.realCanvas.style.touchAction = "none";
		b8.Core.realCanvas.style.userSelect = "none";
		b8.Core.realCanvas.style.imageRendering = "pixelated";

		// Prevent default touch events on touch devices.
		b8.Core.realCanvas.addEventListener( "touchstart", e => e.preventDefault() );

		// Work out where to put the canvas.
		b8.Core.container = document.createElement( 'div' );
		b8.Core.container.setAttribute( "style", "" );
		b8.Core.container.id = "b8";
		b8.Core.container.style.display = "block";
		b8.Core.container.style.lineHeight = "0";
		b8.Core.container.style.position = "relative";

		// Add the canvas to the container.
		b8.Core.container.appendChild( b8.Core.realCanvas );

		// Put the canvas in the container.
		b8.Core.getBeepContainerEl().appendChild( b8.Core.container );

		// Set up the virtual canvas (the one we render to). This canvas isn't
		// part of the document( it's not added to document.body), it only
		// exists off-screen.
		b8.Core.offCanvas = new OffscreenCanvas( b8.CONFIG.SCREEN_WIDTH, b8.CONFIG.SCREEN_HEIGHT );
		b8.Core.offCtx = b8.Core.offCanvas.getContext(
			"2d",
			{
				alpha: false,
				colorSpace: 'srgb',
				desynchronized: true
			}
		);
		b8.Core.offCtx.imageSmoothingEnabled = false;

		// Load and initialize default fonts.
		await b8.TextRenderer.initAsync();
		b8.Input.init();

		// Update the positioning and size of the canvas.
		b8.Core.updateLayout( false );
		window.addEventListener( "resize", _resizeHandler );

		if ( b8.Core.isMobile() ) {
			b8.Joystick.setup();
		}

		initDone = true;

		b8.Utilities.log( "b8 System initialized" );

		await b8.Intro.loading();
		await b8.Intro.splash();

		// Call the callback function if it's set.
		if ( callback ) {
			await callback();
		}

	}


	/**
	 * Gets the container element for the engine.
	 *
	 * This is the element under which the rendering canvas is created. If the
	 * container is not specified in the configuration, this will be the body element.
	 *
	 * @returns {HTMLElement} The container element.
	 */
	b8.Core.getBeepContainerEl = function() {

		let container = document.body;

		if ( b8.CONFIG.CANVAS_SETTINGS && b8.CONFIG.CANVAS_SETTINGS.CONTAINER ) {

			const containerSpec = b8.CONFIG.CANVAS_SETTINGS.CONTAINER;

			if ( typeof ( containerSpec ) === "string" ) {

				container = document.getElementById( containerSpec.replace( '#', '' ) );

				if ( !container ) {
					b8.Utilities.fatal( "b8: Could not find container element with ID: " + containerSpec );
				}

			} else if ( containerSpec instanceof HTMLElement ) {

				container = containerSpec;

			} else {

				b8.Utilities.error( "b8: b8.CONFIG.CANVAS_SETTINGS.CONTAINER must be either an ID of an HTMLElement." );
				container = document.body;

			}

		}

		return container;

	}


	/**
	 * Checks if the engine (and specified api method) is ready to run.
	 *
	 * This function checks if the engine has crashed, if the initAsync() method
	 * has been called, and if there is a pending asynchronous operation.
	 *
	 * This should be called at the start of any async operation.
	 *
	 * @param {string} apiMethod - The name of the API method being called.
	 * @returns {void}
	 */
	b8.Core.preflight = function( apiMethod ) {

		if ( b8.Core.crashed ) {
			throw new Error( `Can't call API method ${apiMethod}() because the engine has crashed.` );
		}

		if ( !initDone ) {

			b8.Utilities.fatal(
				`Can't call API method ${apiMethod}(): API not initialized. ` +
				`Call initAsync() wait until it finishes before calling API methods.`
			);

		}

		if ( pendingAsync ) {

			b8.Utilities.fatal(
				`Can't call API method ${apiMethod}() because there is a pending async ` +
				`call to ${pendingAsync.name}() that hasn't finished running yet. Are you missing ` +
				`an 'await' keyword to wait for the async method? Use 'await ${pendingAsync.name}()',` +
				`not just '${pendingAsync.name}()'`
			);

		}

	}


	/**
	 * Starts an asynchronous operation.
	 *
	 * This function should be called at the beginning of an asynchronous method.
	 * It sets up the pendingAsync object, which is used to track the state of the
	 * asynchronous operation.
	 *
	 * @param {string} asyncMethodName - The name of the asynchronous method.
	 * @param {Function} resolve - The function to call when the operation is successful.
	 * @param {Function} reject - The function to call when the operation fails.
	 * @returns {void}
	 */
	b8.Core.startAsync = function( asyncMethodName, resolve, reject ) {

		if ( pendingAsync ) {

			throw new Error(
				"Internal bug: startAsync called while pendingAsync is not null. " +
				"Missing preflight() call?"
			);

		}

		pendingAsync = {
			name: asyncMethodName,
			resolve,
			reject,
		};

		b8.Renderer.render();

	}


	/**
	 * Checks if there is a pending asynchronous operation.
	 *
	 * @param {string} asyncMethodName - The name of the asynchronous method.
	 * @returns {boolean} True if there is a pending asynchronous operation.
	 */
	b8.Core.hasPendingAsync = function( asyncMethodName = null ) {

		if ( null === asyncMethodName ) {
			return !!pendingAsync;
		}

		return pendingAsync && pendingAsync.name === asyncMethodName;

	}


	/**
	 * Ends an asynchronous operation.
	 *
	 * @param {string} asyncMethodName - The name of the asynchronous method.
	 * @param {boolean} isError - Whether the operation failed.
	 * @param {any} result - The result of the operation.
	 * @returns {void}
	 */
	b8.Core.endAsyncImpl = function( asyncMethodName, isError, result ) {

		if ( !pendingAsync ) {
			throw new Error( `Internal bug: endAsync(${asyncMethodName}) called with no pendingAsync` );
		}

		if ( pendingAsync.name !== asyncMethodName ) {
			throw new Error(
				`Internal bug: endAsync(${asyncMethodName}) called but pendingAsync.name ` +
				`is ${pendingAsync.name}`
			);
		}

		const fun = isError ? pendingAsync.reject : pendingAsync.resolve;
		pendingAsync = null;
		fun( result );

	}


	/**
	 * Resolves an asynchronous operation.
	 *
	 * This function should be called at the end of an asynchronous method.
	 *
	 * @param {string} asyncMethodName - The name of the asynchronous method.
	 * @param {any} result - The result of the operation.
	 * @returns {void}
	 */
	b8.Core.resolveAsync = function( asyncMethodName, result ) {

		b8.Core.endAsyncImpl( asyncMethodName, false, result );

	}


	/**
	 * Fails an asynchronous operation.
	 *
	 * @param {string} asyncMethodName - The name of the asynchronous method.
	 * @param {any} error - The error that occurred.
	 * @returns {void}
	 */
	b8.Core.failAsync = function( asyncMethodName, error ) {

		b8.Core.endAsyncImpl( asyncMethodName, true, error );

	}


	/**
	 * Set the update and render callbacks for the game loop.
	 *
	 * @param {Function|null} renderCallback - The render function.
	 * @param {Function|null} updateCallback - The update function. Optional.
	 * @param {number} [targetFps=30] - Target frames per second.
	 * @returns {void}
	 */
	b8.Core.setFrameHandlers = function( renderCallback = null, updateCallback = null, targetFps = 30 ) {

		updateHandler = updateCallback || ( () => { } );
		renderHandler = renderCallback || ( () => { } );
		targetDt = 1 / targetFps;
		timeToNextFrame = 0;
		lastFrameTime = b8.Core.getNow();

		// Cancel current animation frame if running.
		b8.Core.stopFrame();

		running = true;

		animationFrameId = window.requestAnimationFrame( b8.Core.doFrame );

	}


	/**
	 * Update the game loop for realtime games.
	 *
	 * This function calls the update phase as many times as needed
	 * (capped to prevent spiraling) and then calls the render phase.
	 *
	 * @returns {void}
	 */
	b8.Core.doFrame = async function() {

		// Stop if not running.
		if ( !running ) return;

		// Get current time and compute delta (in seconds).
		const now = b8.Core.getNow();
		let delta = ( now - lastFrameTime ) / 1000;
		lastFrameTime = now;

		// Save actual delta time.
		b8.Core.deltaTime = delta;

		// Cap delta to avoid large time steps.
		delta = Math.min( delta, 0.05 );

		// Accumulate time.
		timeToNextFrame += delta;

		// Determine how many update steps to run based upon target delta time
		// and actual delta time.
		// This ensures updates are run even if the frame rate is lower than
		// intended.
		let numUpdates = Math.floor( timeToNextFrame / targetDt );
		const MAX_UPDATES = 10;
		if ( numUpdates > MAX_UPDATES ) {
			numUpdates = MAX_UPDATES;
			// Reset accumulator to prevent spiral of death.
			timeToNextFrame = 0;
		}

		// Run fixed update steps.
		for ( let i = 0; i < numUpdates; i++ ) {
			if ( updateHandler ) {
				updateHandler( targetDt );
			}
			if ( b8.Input && typeof b8.Input.onEndFrame === 'function' ) {
				b8.Input.onEndFrame();
			}
			b8.Particles.update( targetDt );
		}

		// Retain the fractional remainder for accurate timing.
		timeToNextFrame %= targetDt;

		// if pending async then skip render.
		if ( renderHandler ) {
			renderHandler();
		}

		b8.Renderer.render();

		animationFrameId = window.requestAnimationFrame( b8.Core.doFrame );

	}


	/**
	 * Stop the game loop.
	 *
	 * @returns {void}
	 */
	b8.Core.stopFrame = function() {

		running = false;
		if ( animationFrameId ) {
			window.cancelAnimationFrame( animationFrameId );
			animationFrameId = null;
		}

	}


	/**
	 * Clears the screen and resets the cursor to the top-left corner.
	 *
	 * It will optionally also set the background colour. By default it uses the
	 * specified background but you can override this yourself.
	 *
	 * @param {number} [bgColor] - Optional background color index.
	 * @returns {void}
	 */
	b8.Core.cls = function( bgColor = undefined ) {

		// If bgColor is explicitly set to 0, use it instead of falling back to drawState.bgColor.
		bgColor = ( bgColor === undefined ) ? b8.Core.drawState.bgColor : bgColor;

		b8.Utilities.checkNumber( "bgColor", bgColor );

		b8.Core.offCtx.fillStyle = b8.Core.getColorHex( bgColor );
		b8.Core.offCtx.fillRect( 0, 0, b8.Core.offCanvas.width, b8.Core.offCanvas.height );

		b8.Core.setCursorLocation( 0, 0 );
		b8.Renderer.markDirty();

	}


	/**
	 * Sets the colors used for text and background.
	 *
	 * @param {array} colors - A list of colours.
	 * @returns {void}
	 */
	b8.Core.defineColors = function( colors ) {

		b8.Utilities.checkArray( "colors", colors );

		b8.CONFIG.COLORS = colors.slice();
		b8.TextRenderer.regenColors();

	}


	/**
	 * Sets the colors used for text and background.
	 *
	 * @param {number} fg - The foreground color.
	 * @param {number} [bg=undefined] - The background color.
	 * @returns {void}
	 */
	b8.Core.setColor = function( fg, bg = undefined ) {

		b8.Utilities.checkNumber( "fg", fg );
		b8.Core.drawState.fgColor = Math.round( fg );

		if ( bg !== undefined ) {
			b8.Utilities.checkNumber( "bg", bg );
			b8.Core.drawState.bgColor = Math.round( bg );
		}

	}


	/**
	 * Sets the cursor location.
	 *
	 * The cursor location is used for text rendering and is the position where
	 * the next character will be drawn.
	 *
	 * Characters can be text or images, drawn using the loaded fonts.
	 *
	 * Rounds the position to the nearest 0.5.
	 *
	 * @param {number} col - The column.
	 * @param {number} row - The row.
	 * @returns {void}
	 */
	b8.Core.setCursorLocation = function( col, row ) {

		// Columns.
		b8.Utilities.checkNumber( "col", col );
		b8.Core.drawState.cursorCol = Math.round( col * 2 ) / 2;

		// Rows.
		if ( row !== undefined ) {
			b8.Utilities.checkNumber( "row", row );
			b8.Core.drawState.cursorRow = Math.round( row * 2 ) / 2;
		}

	}


	/**
	 * Move the cursor to the next character.
	 *
	 * This adjusts the drawing position without actually drawing anything.
	 *
	 * @returns {void}
	 */
	b8.Core.nextCursorLocation = function() {

		let currentCursorCol = b8.Core.drawState.cursorCol;
		let currentCursorRow = b8.Core.drawState.cursorRow;

		b8.Core.setCursorLocation( currentCursorCol + 1, currentCursorRow );

	}


	/**
	 * Gets the color for the specified index.
	 *
	 * @param {number} c - The color index.
	 * @returns {string} The color.
	 */
	b8.Core.getColorHex = function( c ) {

		if ( typeof ( c ) !== "number" ) {
			return "#f0f";
		}

		if ( c < 0 ) {
			return b8.CONFIG.COLORS[ 0 ];
		}

		c = b8.Utilities.clamp( Math.round( c ), 0, b8.CONFIG.COLORS.length - 1 );

		return b8.CONFIG.COLORS[ c ];

	}


	/**
	 * Gets the current time in milliseconds.
	 *
	 * This is used for rendering and animation timing.
	 *
	 * Note: this is not tied to game delta time and is not guaranteed
	 * to start at zero.
	 *
	 * @returns {number} The current time in milliseconds.
	 */
	b8.Core.getNow = function() {

		if ( window.performance && window.performance.now ) {
			return window.performance.now();
		}

		return new Date().getTime();

	}


	/**
	 * Draws an image.
	 *
	 * This function is a wrapper around the canvas drawImage() function and will
	 * draw the image at any x,y position. It does not use the cursor position.
	 *
	 * @param {HTMLImageElement} img - The image to draw.
	 * @param {number} x - The x-coordinate of the upper-left corner of the image.
	 * @param {number} y - The y-coordinate of the upper-left corner of the image.
	 * @param {number} [srcX] - The x-coordinate of the upper-left corner of the source image.
	 * @param {number} [srcY] - The y-coordinate of the upper-left corner of the source image.
	 * @param {number} [width] - The width of the image.
	 * @param {number} [height] - The height of the image.
	 * @returns {void}
	 */
	b8.Core.drawImage = function( img, x, y, srcX, srcY, width, height ) {

		b8.Utilities.checkInstanceOf( "img", img, HTMLImageElement );
		b8.Utilities.checkNumber( "x", x );
		b8.Utilities.checkNumber( "y", y );

		if ( srcX !== undefined ) b8.Utilities.checkNumber( "srcX", srcX );
		if ( srcY !== undefined ) b8.Utilities.checkNumber( "srcY", srcY );
		if ( width !== undefined ) b8.Utilities.checkNumber( "width", width );
		if ( height !== undefined ) b8.Utilities.checkNumber( "height", height );

		if (
			srcX !== undefined && srcY !== undefined &&
			width !== undefined && height !== undefined
		) {
			b8.Core.offCtx.drawImage( img, srcX, srcY, width, height, x, y, width, height );
		} else {
			b8.Core.offCtx.drawImage( img, x, y );
		}

	}


	/**
	 * Loads an image from the given URL.
	 *
	 * This function loads an image and converts its colors to the closest
	 * colors in the b8 color palette.
	 *
	 * Remember to keep images as small as possible. The larger the image the
	 * longer it will take to process.
	 *
	 * @param {string} url - The URL of the image to load.
	 * @returns {Promise<HTMLImageElement>} The loaded image.
	 */
	b8.Core.loadImage = async function( url, options = { dither: 'ordered' } ) {

		const mode = options.dither || 'none';

		b8.Utilities.log( 'load image', url );

		const img = await b8.Image.loadImageAsync( url );

		const canvas = document.createElement( 'canvas' );
		const ctx = canvas.getContext( '2d' );
		canvas.width = img.width;
		canvas.height = img.height;
		ctx.drawImage( img, 0, 0 );

		const imageData = ctx.getImageData( 0, 0, canvas.width, canvas.height );

		// Precompute once
		const lookupTable = b8.Image.generateColorLookupTable( b8.CONFIG.COLORS );

		const mapFn = ( r, g, b ) => b8.Image.findClosestColorUsingLookup( r, g, b, lookupTable );

		if ( mode === 'ordered' ) {

			b8.Image.ditherOrdered(
				imageData,
				mapFn,
				{
					size: options.size || 4,
					strength: options.strength || 0.5,
					amplitude: options.amplitude || 16
				}
			);

		} else {

			b8.Image.quantiseImageData( imageData, lookupTable );

		}

		ctx.putImageData( imageData, 0, 0 );

		const out = new Image();

		return new Promise(
			( resolve ) => {
				out.onload = () => resolve( out );
				out.src = canvas.toDataURL();
			}
		);

	}


	/**
	 * Draws a rectangle of the specified width and height.
	 * This ignores the cursor position.
	 *
	 * The rectangle is drawn using the current foreground color. It is drawn
	 * with 4 rects to ensure pixel perfect lines.
	 *
	 * @param {number} x - The x-coordinate of the upper-left corner of the rectangle.
	 * @param {number} y - The y-coordinate of the upper-left corner of the rectangle.
	 * @param {number} width - The width of the rectangle.
	 * @param {number} height - The height of the rectangle.
	 * @returns {void}
	 */
	b8.Core.drawRect = function( x, y, width, height, lineWidth = 1 ) {

		b8.Utilities.checkNumber( "x", x );
		b8.Utilities.checkNumber( "y", y );
		b8.Utilities.checkNumber( "width", width );
		b8.Utilities.checkNumber( "height", height );
		b8.Utilities.checkNumber( "lineWidth", lineWidth );

		x = Math.round( x );
		y = Math.round( y );
		width = Math.round( width );
		height = Math.round( height );
		// Clamp lineWidth to prevent it from being larger than half the width or height of the rectangle.
		lineWidth = Math.min( lineWidth, Math.floor( width / 2 ), Math.floor( height / 2 ) );

		const oldFillStyle = b8.Core.offCtx.fillStyle;

		b8.Core.offCtx.fillStyle = b8.Core.getColorHex( b8.Core.drawState.fgColor );

		// Top.
		b8.Core.offCtx.fillRect( x, y, width, lineWidth );

		// Bottom.
		b8.Core.offCtx.fillRect( x, y + height - lineWidth, width, lineWidth );

		// Left.
		b8.Core.offCtx.fillRect( x, y, lineWidth, height );

		// Right.
		b8.Core.offCtx.fillRect( x + width - lineWidth, y, lineWidth, height );

		b8.Core.offCtx.fillStyle = oldFillStyle;

	};


	/**
	 * Draws a filled rectangle using the current colours.
	 *
	 * Ignores the cursor position.
	 *
	 * @param {number} x - The x-coordinate of the upper-left corner of the rectangle.
	 * @param {number} y - The y-coordinate of the upper-left corner of the rectangle.
	 * @param {number} width - The width of the rectangle.
	 * @param {number} height - The height of the rectangle.
	 * @returns {void}
	 */
	b8.Core.fillRect = function( x, y, width, height ) {

		b8.Utilities.checkNumber( "x", x );
		b8.Utilities.checkNumber( "y", y );
		b8.Utilities.checkNumber( "width", width );
		b8.Utilities.checkNumber( "height", height );

		b8.Core.offCtx.fillStyle = b8.Core.getColorHex( b8.Core.drawState.fgColor );
		b8.Core.offCtx.fillRect(
			Math.round( x ), Math.round( y ),
			Math.round( width ), Math.round( height )
		);

	}


	/**
	 * Take a screenshot when the 0 key is pressed.
	 *
	 * @returns {void}
	 */
	b8.Core.initScreenshot = function() {

		if ( b8.Core.initialized() ) {
			return;
		}

		// Take a screenshot when the 0 key is pressed.
		document.addEventListener( 'pointerup', _takeScreenshot );
		document.addEventListener( 'keyup', _takeScreenshot );

	}


	/**
	 * Generates the bitmap data for the current screen and returns it to you.
	 *
	 * @returns {ImageData} The saved screen.
	 */
	b8.Core.saveScreen = function() {

		return b8.Core.offCtx.getImageData(
			0, 0,
			b8.Core.offCanvas.width, b8.Core.offCanvas.height
		);

	}


	/**
	 * Download the current screen as a PNG image.
	 *
	 * @returns {void}
	 */
	b8.Core.downloadScreenshot = function() {

		// Grab the image data from the drawn canvas (to include screen effects).
		// const dataUrl = b8.Core.realCanvas.toDataURL( "image/png" );
		const dataUrl = b8.Core.getHighResDataURL( b8.Core.realCanvas );

		// Save as a file.
		b8.Utilities.downloadFile( "b8-screenshot.png", dataUrl );

	}

	let offscreenCanvas = null;

	/**
	 * Get a high-resolution data URL for the specified canvas.
	 *
	 * @param {HTMLCanvasElement} canvas - The canvas to get the data URL for.
	 * @param {number} [scale=4] - The scale factor.
	 * @param {string} [mimeType="image/png"] - The MIME type.
	 * @param {number} [quality=1] - The quality.
	 * @returns {string} The data URL.
	 */
	b8.Core.getHighResDataURL = function( canvas, scale = 4, mimeType = "image/png", quality = 1 ) {

		// Create an offscreen canvas (if needed) to draw the scaled image.
		// We keep this around to avoid creating a new canvas each time.
		if ( !offscreenCanvas ) {
			offscreenCanvas = document.createElement( "canvas" );
		}
		offscreenCanvas.width = canvas.width * scale;
		offscreenCanvas.height = canvas.height * scale;

		// Copy and scale the content
		const offscreenCtx = offscreenCanvas.getContext( "2d" );
		offscreenCtx.imageSmoothingEnabled = false; // Disable filtering
		offscreenCtx.scale( scale, scale ); // Use nearest-neighbor scaling
		offscreenCtx.drawImage( canvas, 0, 0 );

		// Get the data URL
		return offscreenCanvas.toDataURL( mimeType, quality );

	}


	/**
	 * Restores the screen.
	 *
	 * @param {ImageData} screenData - The screen to restore.
	 * @returns {void}
	 */
	b8.Core.restoreScreen = function( screenData ) {

		b8.Utilities.checkInstanceOf( "screenData", screenData, ImageData );
		b8.Core.offCtx.putImageData( screenData, 0, 0 );

	}


	/**
	 * Updates the layout.
	 *
	 * @param {boolean} renderNow - Whether to render immediately.
	 * @returns {void}
	 */
	b8.Core.updateLayout = function( renderNow ) {

		b8.Core.updateLayout2d();

		if ( renderNow ) {
			b8.Renderer.render();
		}

	}


	/**
	 * Updates the layout of the 2D canvas.
	 *
	 * @returns {void}
	 */
	b8.Core.updateLayout2d = function() {

		b8.Core.realCtx = b8.Core.realCanvas.getContext(
			"2d",
			{
				alpha: false,
				desynchronized: true
			}
		);

		b8.Core.realCtx.imageSmoothingEnabled = false;
		b8.Core.realCtx.imageSmoothingQuality = 'pixelated';

		b8.CONFIG.SCREEN_EL_WIDTH = b8.CONFIG.SCREEN_WIDTH;
		b8.CONFIG.SCREEN_EL_HEIGHT = b8.CONFIG.SCREEN_HEIGHT;
		b8.CONFIG.SCREEN_REAL_WIDTH = b8.CONFIG.SCREEN_WIDTH;
		b8.CONFIG.SCREEN_REAL_HEIGHT = b8.CONFIG.SCREEN_HEIGHT;

		b8.Core.realCanvas.style.width = '100%';
		b8.Core.realCanvas.style.height = '100%';
		b8.Core.realCanvas.style.filter = 'blur(0.5px)';
		b8.Core.realCanvas.width = b8.CONFIG.SCREEN_REAL_WIDTH;
		b8.Core.realCanvas.height = b8.CONFIG.SCREEN_REAL_HEIGHT;

		b8.Core.container.style.aspectRatio = `${b8.CONFIG.SCREEN_COLS} / ${b8.CONFIG.SCREEN_ROWS}`;

	}


	/**
	 * Converts a client-space position to internal canvas coordinates.
	 *
	 * This accounts for CSS scaling between the displayed canvas size
	 * and the actual internal resolution.
	 *
	 * @param {number} clientX
	 * @param {number} clientY
	 * @returns {{x: number, y: number}}
	 */
	b8.Core.clientToCanvas = function( clientX, clientY ) {

		const rect = b8.Core.realCanvas.getBoundingClientRect();

		const scaleX = b8.Core.realCanvas.width / rect.width;
		const scaleY = b8.Core.realCanvas.height / rect.height;

		return {
			x: ( clientX - rect.left ) * scaleX,
			y: ( clientY - rect.top ) * scaleY,
		};

	}


	/**
	 * Handles a crash.
	 *
	 * @param {string} [error="Fatal error"] - The error message to display.
	 * @returns {void}
	 */
	b8.Core.handleCrash = function( error = "Fatal error" ) {

		if ( b8.Core.crashed || b8.Core.crashing ) return;

		b8.Core.crashing = true;

		const errorObj = ( error instanceof Error ) ? error : new Error( String( error ) );
		const stackLines = _cleanStackTrace( String( errorObj.stack || "" ) );

		const text =
			"*** CRASH ***\n" +
			( errorObj.name ? ( errorObj.name + ": " ) : "" ) + errorObj.message + "\n" +
			( stackLines.length > 0 ? ( "\nStack:\n" + stackLines.join( "\n" ) ) : "" );

		b8.Core.setColor( b8.CONFIG.COLORS.length - 1, 0 );
		b8.Core.cls();

		b8.Core.setCursorLocation( 1, 1 );
		b8.TextRenderer.print( text, null, b8.CONFIG.SCREEN_COLS - 2 );
		b8.Renderer.render();

		b8.Core.crashing = false;
		b8.Core.crashed = true;

	}


	/**
	 * Is this a touch device?
	 *
	 * @returns {boolean} True if this is a touch device.
	 */
	b8.Core.isTouchDevice = function() {

		return 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;

	}


	/**
	 * Is this a mobile device?
	 *
	 * @returns {boolean} True if this is a mobile device.
	 */
	b8.Core.isMobile = function() {

		return b8.Core.isIOS() || b8.Core.isAndroid() || b8.Core.isTouchDevice();

	}


	/**
	 * Is this an iOS device?
	 *
	 * @returns {boolean} True if this is an iOS device.
	 */
	b8.Core.isIOS = function() {

		return /(ipad|ipod|iphone)/i.test( navigator.userAgent );

	}


	/**
	 * Is this an Android device?
	 *
	 * @returns {boolean} True if this is an Android device.
	 */
	b8.Core.isAndroid = function() {

		return /android/i.test( navigator.userAgent );

	}


	/**
	 * Handles window resize events.
	 *
	 * @return {void}
	 */
	function _resizeHandler() {

		b8.Core.updateLayout( true );

	}


	/**
	 * Handles screenshot taking events.
	 *
	 * @param {KeyboardEvent|PointerEvent} e - The event object.
	 * @return {void}
	 */
	function _takeScreenshot( e ) {

		if ( e.key === '0' ) {
			b8.Core.downloadScreenshot();
		}

	}


	/**
	 * Cleans a stack trace string.
	 *
	 * @param {string} stackStr - The stack trace string.
	 * @returns {string[]} The cleaned stack trace lines.
	 */
	function _cleanStackTrace( stackStr ) {

		const lines = String( stackStr ).split( "\n" ).map( s => s.trim() );
		const cleanedLines = [];

		for ( let line of lines ) {

			// Remove anything after and including @ or "at"
			line = line.replace( /^at\s+/, "" );
			line = line.replace( /@.*/, "" );
			line = line.trim();
			line = line.replace( /^b8\d+\./, "b8." );

			// If empty continue.
			if ( line.length === 0 ) continue;

			// Remove internal frames.
			// These are debug & error functions.
			const isInternal =
				line.includes( "Utilities.fatal" ) ||
				line.includes( "Utilities.check" ) ||
				line.includes( "Utilities.assert" ) ||
				line.includes( "Core.doFrame" ) ||
				line.includes( "Core.handleCrash" );

			console.log( 'stack line', line, 'isInternal=', isInternal );

			if ( isInternal ) continue;

			cleanedLines.push( line.trim() );

		}

		return cleanedLines;

	}

} )( b8 );

( function( b8 ) {

	b8.Collision = {};

	b8.COLLISION = {};
	b8.COLLISION.NONE = 0;
	b8.COLLISION.N = 1;
	b8.COLLISION.E = 2;
	b8.COLLISION.S = 4;
	b8.COLLISION.W = 8;
	b8.COLLISION.NE = b8.COLLISION.N + b8.COLLISION.E;
	b8.COLLISION.NS = b8.COLLISION.N + b8.COLLISION.S;
	b8.COLLISION.NW = b8.COLLISION.N + b8.COLLISION.W;
	b8.COLLISION.SE = b8.COLLISION.S + b8.COLLISION.E;
	b8.COLLISION.SW = b8.COLLISION.S + b8.COLLISION.W;
	b8.COLLISION.WE = b8.COLLISION.W + b8.COLLISION.E;
	b8.COLLISION.WNE = b8.COLLISION.W + b8.COLLISION.N + b8.COLLISION.E;
	b8.COLLISION.WES = b8.COLLISION.W + b8.COLLISION.E + b8.COLLISION.S;
	b8.COLLISION.NES = b8.COLLISION.N + b8.COLLISION.E + b8.COLLISION.S;
	b8.COLLISION.ALL = b8.COLLISION.N + b8.COLLISION.E + b8.COLLISION.S + b8.COLLISION.W;

} )( b8 );

( function( b8 ) {

	b8.Cart = {};


	// Magic string used to identify the custom trailer in the PNG file
	const MAGIC_STR = "B8CART";

	// Encoded version of the magic string as a Uint8Array
	const MAGIC = new TextEncoder().encode( MAGIC_STR );

	// Version number for the trailer format
	const VERSION = 1;


	/**
	 * Saves the current canvas as a PNG file with a custom trailer.
	 *
	 * The trailer includes metadata (e.g., game data) appended to the PNG file.
	 * This metadata is stored as a JSON object and can be extracted later using the `load` function.
	 *
	 * @param {HTMLCanvasElement} canvas - The canvas element to save as a PNG file.
	 * @param {Object|String} data - The metadata object to append as a trailer. This object will be serialized as JSON.
	 * @param {string} [filename="cart.png"] - The name of the file to save. Defaults to "cart.png".
	 * @returns {Promise<void>} - A promise that resolves when the file is saved.
	 */
	b8.Cart.save = async function( canvas, data, filename = "cart.png" ) {

		b8.Utilities.checkInstanceOf( "canvas", canvas, HTMLCanvasElement );
		b8.Utilities.checkString( "data", data );
		b8.Utilities.checkString( "filename", filename );

		const pngBlob = await new Promise( res => canvas.toBlob( res, "image/png" ) );

		// Append the custom trailer to the PNG Blob
		const cartBlob = await _appendTrailer( pngBlob, data );

		// Save the resulting Blob as a file (requires FileSaver.js)
		saveAs( cartBlob, filename );

	}


	/**
	 * Loads a PNG file with a custom trailer (set of game data) and extracts the appended data.
	 *
	 * The PNG file should be created using the b8.Cart.save function which appends the required trailer data.
	 *
	 * @param {Blob|string} pngBlobOrUrl - The PNG file as a Blob or a URL.
	 * @returns {Promise<Object>} - A promise that resolves to the extracted data object.
	 */
	b8.Cart.load = async function( pngBlobOrUrl ) {

		// Fetch the PNG data as an ArrayBuffer
		const buffer = await ( typeof pngBlobOrUrl === "string"
			? ( await fetch( pngBlobOrUrl ) ).arrayBuffer()
			: pngBlobOrUrl.arrayBuffer() );

		// Convert the ArrayBuffer to a Uint8Array
		const bytes = new Uint8Array( buffer );

		// Find the position of the magic string in the byte array
		const offset = _findMagicFromEnd( bytes, MAGIC_STR );
		if ( offset < 0 ) b8.Utilities.fatal( "Error Loading Cart: No b8 trailer found" );

		// Layout:
		// [ MAGIC (MAGIC.length) ][ VERSION (1) ][ LENGTH (4) ][ PAYLOAD (N) ][ CRC32 (4) ]
		const versionOffset = offset + MAGIC.length;
		const lengthOffset = versionOffset + 1;
		const payloadStart = lengthOffset + 4;

		// Read the version number
		const version = bytes[ versionOffset ];
		if ( version !== VERSION ) b8.Utilities.fatal( `Error Loading Cart: Unsupported version ${version}` );

		const payloadLength = _readU32BE( bytes, lengthOffset );
		const payloadEnd = payloadStart + payloadLength;

		// Bounds check: payload plus CRC must be inside file
		if ( payloadEnd + 4 > bytes.length ) {
			b8.Utilities.fatal( "Error Loading Cart: Trailer length out of range" );
		}

		// Extract payload and check CRC
		const payload = bytes.subarray( payloadStart, payloadEnd );

		// Verify the CRC32 checksum
		const expectedCrc = _readU32BE( bytes, payloadEnd );
		if ( _crc32( payload ) !== expectedCrc ) {
			b8.Utilities.fatal( "Error Loading Cart: Bad payload CRC" );
		}

		// Decode and return the payload as a JSON object
		return JSON.parse( new TextDecoder().decode( payload ) );

	}


	/**
	 * Appends a custom trailer to a PNG file.
	 *
	 * The trailer includes a magic string, version, payload length, payload data, and a CRC32 checksum.
	 *
	 * @param {Blob} pngBlob - The PNG file as a Blob.
	 * @param {Object} dataObj - The data object to append as a trailer.
	 * @returns {Blob} - A new Blob containing the original PNG data and the appended trailer.
	 */
	async function _appendTrailer( pngBlob, dataObj ) {

		// Encode the data object as a JSON string and then to a Uint8Array
		const payload = new TextEncoder().encode( JSON.stringify( dataObj ) );

		// Calculate the total header size
		const headerSize = MAGIC.length + 1 + 4 + payload.length + 4; // MAGIC + VERSION + LENGTH + PAYLOAD + CRC32
		const header = new Uint8Array( headerSize );

		let offset = 0;

		// Add the magic string
		header.set( MAGIC, offset );
		offset += MAGIC.length;

		// Add the version number
		header[ offset++ ] = VERSION;

		// Add the payload length (big-endian)
		_writeU32BE( header, offset, payload.length );
		offset += 4;

		// Add the payload data
		header.set( payload, offset );
		offset += payload.length;

		// Add the CRC32 checksum of the payload
		_writeU32BE( header, offset, _crc32( payload ) );
		offset += 4;

		// Combine the original PNG data with the new header
		const pngBytes = new Uint8Array( await pngBlob.arrayBuffer() );
		return new Blob( [ pngBytes, header ], { type: "image/png" } );

	}


	/**
	 * Finds the position of a magic string in a byte array, searching from the end.
	 *
	 * @param {Uint8Array} bytes - The byte array to search in.
	 * @param {string} magicStr - The magic string to find.
	 * @returns {number} - The index of the magic string, or -1 if not found.
	 */
	function _findMagicFromEnd( bytes, magicStr ) {

		// Encode the magic string into a Uint8Array
		const magic = new TextEncoder().encode( magicStr );

		// Start searching from the end of the byte array
		for ( let i = bytes.length - magic.length; i >= 0; i-- ) {
			let found = true;

			// Check if the magic string matches at the current position
			for ( let j = 0; j < magic.length; j++ ) {
				if ( bytes[ i + j ] !== magic[ j ] ) {
					found = false;
					break;
				}
			}

			// If the magic string was found, return its starting index
			if ( found ) return i;
		}

		// Return -1 if the magic string was not found
		return -1;

	}


	/**
	 * Reads a 32-bit unsigned integer from a byte array in big-endian order.
	 *
	 * @param {Uint8Array} byteArray - The byte array to read from.
	 * @param {number} offset - The offset (index) in the byte array to start reading.
	 * @returns {number} - The 32-bit unsigned integer.
	 */
	function _readU32BE( byteArray, offset ) {

		// Extract four bytes starting at the given offset
		const byte1 = byteArray[ offset ];
		const byte2 = byteArray[ offset + 1 ];
		const byte3 = byteArray[ offset + 2 ];
		const byte4 = byteArray[ offset + 3 ];

		// Combine the bytes into a 32-bit unsigned integer (big-endian order)
		return ( ( byte1 << 24 ) | ( byte2 << 16 ) | ( byte3 << 8 ) | byte4 ) >>> 0;

	}


	/**
	 * Writes a 32-bit unsigned integer to a byte array in big-endian order.
	 *
	 * @param {Uint8Array} byteArray - The byte array to write to.
	 * @param {number} offset - The offset (index) in the byte array to start writing.
	 * @param {number} value - The 32-bit unsigned integer to write.
	 * @returns {void}
	 */
	function _writeU32BE( byteArray, offset, value ) {

		// Write each byte of the 32-bit integer in big-endian order
		byteArray[ offset ] = ( value >>> 24 ) & 0xFF; // Most significant byte
		byteArray[ offset + 1 ] = ( value >>> 16 ) & 0xFF;
		byteArray[ offset + 2 ] = ( value >>> 8 ) & 0xFF;
		byteArray[ offset + 3 ] = value & 0xFF; // Least significant byte

	}


	/**
	 * Calculates the CRC32 checksum of a byte array.
	 *
	 * @param {Uint8Array} byteArray - The byte array to calculate the checksum for.
	 * @returns {number} - The CRC32 checksum as an unsigned 32-bit integer.
	 */
	function _crc32( byteArray ) {

		// Initialize the CRC value to all 1s
		let crc = ~0 >>> 0;

		// Process each byte in the array
		for ( let i = 0; i < byteArray.length; i++ ) {
			crc ^= byteArray[ i ]; // XOR the byte with the current CRC value

			// Process each bit in the byte
			for ( let bit = 0; bit < 8; bit++ ) {
				// If the least significant bit is 1, apply the polynomial
				if ( crc & 1 ) {
					crc = ( crc >>> 1 ) ^ 0xEDB88320;
				} else {
					crc >>>= 1; // Otherwise, just shift right
				}
			}
		}

		// Return the bitwise NOT of the final CRC value
		return ~crc >>> 0;

	}

} )( b8 );

( function( b8 ) {

	b8.AStar = {};


	/**
	 * Generates a unique key for a grid cell.
	 *
	 * This function creates a string key for a grid cell based on its column and row coordinates.
	 * The key is used to uniquely identify cells in the grid, which is essential for tracking
	 * visited nodes and managing the open and closed lists in the A* algorithm.
	 *
	 * @param {number} col - The column of the cell.
	 * @param {number} row - The row of the cell.
	 * @returns {string} - A unique string key for the cell.
	 */
	function nodeKey( col, row ) {

		return `${col},${row}`;

	}


	/**
	 * Prepares the final path from the goal node by tracing back to the start node.
	 *
	 * This function reconstructs the path from the goal node to the start node by following
	 * the parent references of each node. The resulting path is reversed to provide the
	 * correct order from start to goal, and the starting node is removed since the entity
	 * is already at that position.
	 *
	 * @param {Object} current - The goal node from which to trace back the path.
	 * @returns {Array} - An array of path steps from start to goal.
	 */
	function preparePath( current ) {

		let path = [];

		// Trace back the path using parent nodes.
		let node = current;
		while ( node ) {
			path.push( { col: node.col, row: node.row } );
			node = node.parent;
		}

		// Reverse the path to get it from start to goal
		path = path.reverse();

		// Remove the starting node since the entity is already in this position.
		path.shift();

		return path;

	}


	/**
	 * Calculates the Manhattan distance heuristic between two points.
	 *
	 * The Manhattan distance is the sum of the absolute differences of the column and row coordinates.
	 * It represents the shortest path between two points if movement is restricted to horizontal
	 * and vertical directions (like a grid-based game).
	 *
	 * A heuristic is a function used in pathfinding algorithms to estimate the cost of reaching
	 * the goal from a given point. It helps prioritize which paths to explore first, improving
	 * efficiency by guiding the algorithm toward the goal.
	 *
	 * In this implementation, the Manhattan distance is used as the heuristic because it is
	 * computationally inexpensive and works well for grid-based movement where diagonal movement
	 * is not allowed.
	 *
	 * @param {Object} start - The target position with properties `col` and `row`.
	 * @param {Object} goal - The goal position with properties `col` and `row`.
	 * @returns {number} - The Manhattan distance to the goal.
	 */
	function heuristic( start, goal ) {

		return b8.Math.distManhattan( start, goal );

	}


	/**
	 * A* Pathfinding Algorithm for grid-based games.
	 *
	 * The A* algorithm is a popular pathfinding and graph traversal algorithm. It is widely used
	 * in games and other applications to find the shortest path between two points. The algorithm
	 * combines the benefits of Dijkstra's algorithm and a heuristic approach to efficiently find
	 * the optimal path.
	 *
	 * Key Concepts:
	 * - Open List: A priority queue of nodes to be explored, sorted by their total cost (f).
	 * - Closed List: A set of nodes that have already been explored.
	 * - Heuristic (h): An estimate of the cost to reach the goal from a given node.
	 * - Cost (g): The actual cost to reach a node from the start.
	 * - Total Cost (f): The sum of g and h (f = g + h).
	 *
	 * This implementation assumes a grid-based game where movement is restricted to horizontal
	 * and vertical directions. The algorithm explores neighboring cells and calculates their
	 * costs to determine the optimal path.
	 *
	 * @param {Object} start - The starting position with properties `col` and `row`.
	 * @param {Object} goal - The goal position with properties `col` and `row`.
	 * @param {Function} isWalkable - A function that takes `col` and `row` coordinates and returns `true` if the cell is walkable.
	 * @param {number} gridWidth - The width of the grid.
	 * @param {number} gridHeight - The height of the grid.
	 * @returns {Array<Object>|null} - The shortest path as an array of positions `{col, row}`, or `null` if no path is found.
	 */
	b8.AStar.pathfind = function( start, goal, isWalkable, gridWidth, gridHeight ) {

		b8.Utilities.checkObject( 'start', start );
		b8.Utilities.checkObject( 'goal', goal );
		b8.Utilities.checkInt( 'start.col', start.col );
		b8.Utilities.checkInt( 'start.row', start.row );
		b8.Utilities.checkInt( 'goal.col', goal.col );
		b8.Utilities.checkInt( 'goal.row', goal.row );
		b8.Utilities.checkFunction( 'isWalkable', isWalkable );
		b8.Utilities.checkInt( 'gridWidth', gridWidth );
		b8.Utilities.checkInt( 'gridHeight', gridHeight );

		// Already at the goal.
		if ( start.col === goal.col && start.row === goal.row ) return [];

		// Priority queue for nodes to explore
		const openList = [];

		// Set of explored nodes
		const closedList = new Set();

		// Map of all nodes by their unique keys
		const nodes = {};

		// Initialize the start node
		const startNode = {
			col: start.col,
			row: start.row,
			g: 0, // Cost from start to this node
			h: heuristic( start, goal ), // Heuristic cost to goal
			f: heuristic( start, goal ), // Total cost (g + h)
			parent: null, // Parent node for path reconstruction
		};
		openList.push( startNode );
		nodes[ nodeKey( start.col, start.row ) ] = startNode;

		while ( openList.length > 0 ) {

			// Sort openList by `f` value and get the node with the lowest `f`
			openList.sort( ( a, b ) => a.f - b.f );
			const current = openList.shift(); // Remove the node with the lowest cost
			const currentKey = nodeKey( current.col, current.row );
			closedList.add( currentKey ); // Mark the current node as explored

			// If the goal has been reached return the path.
			if ( current.col === goal.col && current.row === goal.row ) {
				return preparePath( current );
			}

			// Explore neighbors (up, down, left, right)
			const neighbors = [
				{ col: current.col - 1, row: current.row }, // Left
				{ col: current.col + 1, row: current.row }, // Right
				{ col: current.col, row: current.row - 1 }, // Up
				{ col: current.col, row: current.row + 1 }, // Down
			];

			// Explore each neighbor
			for ( let neighbor of neighbors ) {

				// Skip out-of-bound cells
				if (
					neighbor.col < 0 ||
					neighbor.col >= gridWidth ||
					neighbor.row < 0 ||
					neighbor.row >= gridHeight
				) continue;

				// Skip non-walkable cells, excluding start and goal.
				// The start and goal positions must always be considered
				// walkable to ensure a path can be found when these tiles
				// contain obstacles that are considered unwalkable.
				if (
					!( neighbor.col === start.col && neighbor.row === start.row ) &&
					!( neighbor.col === goal.col && neighbor.row === goal.row ) &&
					!isWalkable( neighbor.col, neighbor.row )
				) continue;

				const neighborKey = nodeKey( neighbor.col, neighbor.row );
				if ( closedList.has( neighborKey ) ) continue; // Skip already explored nodes

				const tentativeG = current.g + 1; // Cost to move to the neighbor
				let neighborNode = nodes[ neighborKey ];

				if ( !neighborNode ) {

					const h = heuristic( neighbor, goal );

					// Create a new node if it doesn't exist
					neighborNode = {
						col: neighbor.col,
						row: neighbor.row,
						g: tentativeG, // Set the cost from start to this node
						h: h, // Estimate cost to goal
						f: tentativeG + h, // Total cost
						parent: current, // Set the parent node for path reconstruction
					};

					nodes[ neighborKey ] = neighborNode; // Store the node in the nodes map
					openList.push( neighborNode ); // Add the node to the open list

				} else if ( tentativeG < neighborNode.g ) {

					// Update the node if a better path is found
					neighborNode.g = tentativeG; // Update the cost from start
					neighborNode.f = tentativeG + neighborNode.h; // Update the total cost
					neighborNode.parent = current; // Update the parent node

				}
			}
		}

		return null; // No path found

	}

} )( b8 );
( function( b8 ) {

	b8.Animation = {};


	/**
	 * Get the current frame of an animation.
	 * This is used internally to automatically determine what frame to draw.
	 * This uses delta time to determine the current frame.
	 *
	 * @param {string} animation The animation to get the frame for.
	 * @param {number|null} startTime The start time of the animation. If null, uses the core start time.
	 * @returns {number} The frame to draw for the animation.
	 */
	b8.Animation.frame = function( anim, startTime = null ) {

		// Does the animation exist.
		if ( anim === undefined ) {
			b8.Utilities.fatal( "Animation not found" );
		}

		// If the animation has a start time, use that.
		if ( startTime === null ) {
			startTime = b8.Core.startTime;
		}

		let frame = 0;

		if ( !anim.frames || anim.frames.length === 0 ) {
			b8.Utilities.fatal( "Animation has no frames" );
		}

		// If there's only one frame, return it.
		if ( anim.frames.length === 1 ) {
			frame = anim.frames[ 0 ];
		}

		// If there's more than one frame, calculate the frame to display.
		if ( anim.frames.length > 1 ) {

			const elapsedTime = b8.Core.getNow() - startTime;
			const frameCount = anim.frames.length;
			const FPS = anim.fps || 1;
			const frameDuration = 1000 / FPS;

			// Dividing totalTime by 1000 to convert ms to seconds.
			// Dividing by frameDuration to get the current frame.
			// Modulo frameCount to loop the animation.
			const frameIndex = Math.floor( elapsedTime / frameDuration ) % frameCount;

			frame = anim.frames[ frameIndex ];

		}

		return frame;

	};


	/**
	 * Get animation by id.
	 *
	 * @param {string} animation The animation id.
	 * @returns {Object|undefined} The animation object, or undefined if not found.
	 */
	b8.Animation.get = function( animation ) {

		b8.Utilities.checkString( "animation", animation );
		if ( b8.Animation.animations[ animation ] === undefined ) {
			b8.Utilities.fatal( "Invalid Animation: " + animation );
		}

		return b8.Animation.animations[ animation ];

	};


	/**
	 * Checks if the animation has finished looping.
	 *
	 * @param {Object} anim - The animation object.
	 * @param {number} startTime - The start time of the animation.
	 * @returns {boolean} - Returns true if the animation should continue, false if it has finished looping.
	 */
	b8.Animation.shouldLoop = function( anim, startTime ) {

		if ( typeof anim === 'string' ) {
			anim = b8.Animation.get( anim );
		}

		b8.Utilities.checkObject( "anim", anim );

		if ( anim === undefined ) {
			b8.Utilities.fatal( "Animation not found" );
		}

		// If the animation has not started or is set to loop, continue the animation.
		if ( startTime === null || anim.loop === true ) return true;

		// Calculate the total length of the animation in milliseconds.
		const FPS = anim.fps || 1;
		const animationLength = anim.frames.length * ( 1000 / FPS );

		// Check if the current time exceeds the animation length.
		if ( b8.Core.getNow() - startTime >= animationLength ) return false;

		return true;

	};

} )( b8 );

( function( b8 ) {

	b8.Actors = {};


	/**
	 * The default animations for the actors.
	 * This can be extended or replaced by your own animations.
	 *
	 * @type {Object}
	 */
	b8.Actors.animations = {
		'idle': {
			frames: [ 0 ],
			fps: 1,
		},
		'idle-down': {
			frames: [ 0 ],
			fps: 1,
		},
		'idle-right': {
			frames: [ 1 ],
			fps: 1,
		},
		'idle-left': {
			frames: [ -1 ],
			fps: 1,
		},
		'idle-up': {
			frames: [ 4 ],
			fps: 1,
		},
		'move-right': {
			frames: [ 1, 2 ],
			fps: 4,
			loop: true
		},
		'move-left': {
			frames: [ -1, -2 ],
			fps: 4,
			loop: true
		},
		'move-up': {
			frames: [ 5, -5 ],
			fps: 4,
			loop: true
		},
		'move-down': {
			frames: [ 3, -3 ],
			fps: 4,
			loop: true,
		},
		'jump-right': {
			frames: [ 5 ],
			fps: 1,
			loop: false
		},
		'jump-left': {
			frames: [ -5 ],
			fps: 1,
			loop: false
		},
		'spin-left': {
			frames: [ 0, 1, 4, -1 ],
			fps: 4,
			loop: true
		},
		'spin-right': {
			frames: [ 0, -1, 4, 1 ],
			fps: 4,
			loop: true
		}

	};


	/**
	 * Draw an actor at the specified x, y position.
	 *
	 * @param {number} ch - The character to draw.
	 * @param {string} animation - The animation to draw.
	 * @param {number} x - The x coordinate to draw the actor at.
	 * @param {number} y - The y coordinate to draw the actor at.
	 * @param {number} direction - The direction to draw the actor in. 0 = right, 1 = left.
	 * @returns {void}
	 */
	const drawActor = function( ch, animation, x, y, direction ) {

		const font = b8.TextRenderer.curActors_;
		const chrIndex = ( ch * font.getColCount() ) + Math.abs( b8.Animation.frame( animation ) );

		b8.TextRenderer.spr(
			chrIndex,
			x, y,
			font,
			direction || 0
		);

	}


	/**
	 * Draw an actor at the current cursor position.
	 *
	 * @param {number} ch - The character to draw.
	 * @param {string} animation - The animation to draw.
	 * @param {number} [offsetCol=0] - The x offset to apply to the drawing position.
	 * @param {number} [offsetRow=0] - The y offset to apply to the drawing position.
	 * @returns {void}
	 */
	b8.Actors.draw = function( ch, animation, offsetCol = 0, offsetRow = 0 ) {

		b8.Utilities.checkInt( "ch", ch );
		b8.Utilities.checkString( "animation", animation );
		if ( b8.Actors.animations[ animation ] === undefined ) {
			b8.Utilities.fatal( "Invalid actor animation: " + animation );
		}
		b8.Utilities.checkNumber( "offsetCol", offsetCol );
		b8.Utilities.checkNumber( "offsetRow", offsetRow );

		const anim = b8.Actors.animations[ animation ];
		const frame = b8.Animation.frame( anim );
		const direction = frame >= 0 ? 0 : 1;

		drawActor(
			ch, anim,
			( b8.Core.drawState.cursorCol + offsetCol ) * b8.CONFIG.CHR_WIDTH,
			( b8.Core.drawState.cursorRow + offsetRow ) * b8.CONFIG.CHR_HEIGHT,
			direction || 0
		);

	};


	/**
	 * Draw an actor at the specified x, y position.
	 * This ignores the cursor position and draws at specific x, y coordinates.
	 * This is useful for drawing actors in the game world and real-time apps.
	 *
	 * By default the animations will start playing based upon the current game
	 * time. This means they may loop from anywhere in the animation sequence.
	 * If you specify the startTime then the animation will start from the
	 * beginning. This is particularly useful for non-looping animations.
	 *
	 * The startTime should be stored and not changed each time the animation is
	 * drawn.
	 *
	 * The function will return false if the animation has finished playing.
	 *
	 * @param {number} ch The character to draw.
	 * @param {string} animation The animation to draw.
	 * @param {number} x The x coordinate to draw the actor at.
	 * @param {number} y The y coordinate to draw the actor at.
	 * @param {number} [startTime=null] The time the animation started.
	 * @returns {boolean} True if the animation is still playing, false if it has finished.
	 */
	b8.Actors.spr = function( ch, animation, x, y, startTime = null ) {

		b8.Utilities.checkInt( "ch", ch );
		b8.Utilities.checkString( "animation", animation );
		if ( b8.Actors.animations[ animation ] === undefined ) {
			b8.Utilities.fatal( "Invalid actor animation: " + animation );
		}
		b8.Utilities.checkNumber( "x", x );
		b8.Utilities.checkNumber( "y", y );
		if ( startTime !== null ) b8.Utilities.checkNumber( "startTime", startTime );

		const anim = b8.Actors.animations[ animation ];
		const frame = b8.Animation.frame( anim, startTime );
		const direction = frame >= 0 ? 0 : 1;

		if ( !b8.Animation.shouldLoop( anim, startTime ) ) return false;

		drawActor( ch, anim, x, y, direction || 0 );

		// The animation is still playing.
		return true;

	};

} )( b8 );

// ZzFX - Zuper Zmall Zound Zynth - Micro Edition
// MIT License - Copyright 2019 Frank Force
// https://github.com/KilledByAPixel/ZzFX

// This is a minified build of zzfx for use in size coding projects.
// You can use zzfxV to set volume.
// Feel free to minify it further for your own needs!

'use strict';

///////////////////////////////////////////////////////////////////////////////

// ZzFXMicro - Zuper Zmall Zound Zynth - v1.3.1 by Frank Force

// ==ClosureCompiler==
// @compilation_level ADVANCED_OPTIMIZATIONS
// @output_file_name ZzFXMicro.min.js
// @js_externs zzfx, zzfxG, zzfxP, zzfxV, zzfxX
// @language_out ECMASCRIPT_2019
// ==/ClosureCompiler==

const zzfx = ( ...z ) => zzfxP( zzfxG( ...z ) ); // generate and play sound
const zzfxV = .3;    // volume
const zzfxR = 44100; // sample rate
const zzfxX = new AudioContext; // audio context
const zzfxP = ( ...samples ) =>  // play samples
{
	// create buffer and source
	let buffer = zzfxX.createBuffer( samples.length, samples[ 0 ].length, zzfxR ),
		source = zzfxX.createBufferSource();

	// copy samples to buffer and play
	samples.map( ( d, i ) => buffer.getChannelData( i ).set( d ) );
	source.buffer = buffer;
	source.connect( zzfxX.destination );
	source.start();
	return source;
}
const zzfxG = // generate samples
	(
		// parameters
		volume = 1, randomness = .05, frequency = 220, attack = 0, sustain = 0,
		release = .1, shape = 0, shapeCurve = 1, slide = 0, deltaSlide = 0,
		pitchJump = 0, pitchJumpTime = 0, repeatTime = 0, noise = 0, modulation = 0,
		bitCrush = 0, delay = 0, sustainVolume = 1, decay = 0, tremolo = 0, filter = 0
	) => {
		// init parameters
		let PI2 = Math.PI * 2, sign = v => v < 0 ? -1 : 1,
			startSlide = slide *= 500 * PI2 / zzfxR / zzfxR,
			startFrequency = frequency *=
				( 1 + randomness * 2 * Math.random() - randomness ) * PI2 / zzfxR,
			b = [], t = 0, tm = 0, i = 0, j = 1, r = 0, c = 0, s = 0, f, length,

			// biquad LP/HP filter
			quality = 2, w = PI2 * Math.abs( filter ) * 2 / zzfxR,
			cos = Math.cos( w ), alpha = Math.sin( w ) / 2 / quality,
			a0 = 1 + alpha, a1 = -2 * cos / a0, a2 = ( 1 - alpha ) / a0,
			b0 = ( 1 + sign( filter ) * cos ) / 2 / a0,
			b1 = -( sign( filter ) + cos ) / a0, b2 = b0,
			x2 = 0, x1 = 0, y2 = 0, y1 = 0;

		// scale by sample rate
		attack = attack * zzfxR + 9; // minimum attack to prevent pop
		decay *= zzfxR;
		sustain *= zzfxR;
		release *= zzfxR;
		delay *= zzfxR;
		deltaSlide *= 500 * PI2 / zzfxR ** 3;
		modulation *= PI2 / zzfxR;
		pitchJump *= PI2 / zzfxR;
		pitchJumpTime *= zzfxR;
		repeatTime = repeatTime * zzfxR | 0;
		volume *= zzfxV;

		// generate waveform
		for ( length = attack + decay + sustain + release + delay | 0;
			i < length; b[ i++ ] = s * volume )               // sample
		{
			if ( !( ++c % ( bitCrush * 100 | 0 ) ) )                   // bit crush
			{
				s = shape ? shape > 1 ? shape > 2 ? shape > 3 ?      // wave shape
					Math.sin( t ** 3 ) :                       // 4 noise
					Math.max( Math.min( Math.tan( t ), 1 ), -1 ) :  // 3 tan
					1 - ( 2 * t / PI2 % 2 + 2 ) % 2 :                     // 2 saw
					1 - 4 * Math.abs( Math.round( t / PI2 ) - t / PI2 ) : // 1 triangle
					Math.sin( t );                           // 0 sin

				s = ( repeatTime ?
					1 - tremolo + tremolo * Math.sin( PI2 * i / repeatTime ) // tremolo
					: 1 ) *
					sign( s ) * ( Math.abs( s ) ** shapeCurve ) *      // curve
					( i < attack ? i / attack :                 // attack
						i < attack + decay ?                     // decay
							1 - ( ( i - attack ) / decay ) * ( 1 - sustainVolume ) : // decay falloff
							i < attack + decay + sustain ?          // sustain
								sustainVolume :                          // sustain volume
								i < length - delay ?                     // release
									( length - i - delay ) / release *           // release falloff
									sustainVolume :                          // release volume
									0 );                                      // post release

				s = delay ? s / 2 + ( delay > i ? 0 :           // delay
					( i < length - delay ? 1 : ( length - i ) / delay ) * // release delay
					b[ i - delay | 0 ] / 2 / volume ) : s;              // sample delay

				if ( filter )                                   // apply filter
					s = y1 = b2 * x2 + b1 * ( x2 = x1 ) + b0 * ( x1 = s ) - a2 * y2 - a1 * ( y2 = y1 );
			}

			f = ( frequency += slide += deltaSlide ) *// frequency
				Math.cos( modulation * tm++ );          // modulation
			t += f + f * noise * Math.sin( i ** 5 );        // noise

			if ( j && ++j > pitchJumpTime )           // pitch jump
			{
				frequency += pitchJump;             // apply pitch jump
				startFrequency += pitchJump;        // also apply to start
				j = 0;                              // stop pitch jump time
			}

			if ( repeatTime && !( ++r % repeatTime ) )  // repeat
			{
				frequency = startFrequency;         // reset frequency
				slide = startSlide;                 // reset slide
				j = j || 1;                         // reset pitch jump time
			}
		}

		return b;
	}
( function() {

	// AudioContext.
	const audioCtx = new AudioContext();

	// Master gain node for volume control.
	const masterGain = audioCtx.createGain();
	masterGain.gain.value = 0.8;  // default volume (0.0–1.0)
	masterGain.connect( audioCtx.destination );

	// Cache for generated note buffers.
	let noteBuffers = {};
	let noteBufferKeys = [];
	const maxNoteBuffers = 200;

	// Scheduler variables.
	let schedulerInterval = null;
	let schedules = []; // Array of event arrays per track.
	let schedulePointers = []; // Next event index per track.
	let songLengthInSteps = 0; // Shared loop length across all tracks.
	let playbackStartTime = 0; // When playback starts.
	let tempo = 125; // Default tempo.
	const defaultTempo = 125;
	const stepsPerBeat = 4;
	const lookaheadTime = 0.5; // Only schedule events within the next 0.5 seconds.
	const schedulerIntervalMs = 50; // Check every 50ms.
	const volumeMultiplier = 0.1; // Volume multiplier.

	// iOS audio unlock flag.
	let unlocked = false;

	// -----------------------------
	// Instrument synthesis functions.
	// -----------------------------
	const sineComponent = ( x, offset ) => Math.sin( x * 6.28 + offset );

	const pianoWaveform = ( x ) => {
		return sineComponent( x, Math.pow( sineComponent( x, 0 ), 2 ) +
			sineComponent( x, 0.25 ) * 0.75 +
			sineComponent( x, 0.5 ) * 0.1 ) * volumeMultiplier;
	}

	const piano2WaveForm = ( x ) => {
		return ( Math.sin( x * 6.28 ) * Math.sin( x * 3.14 ) ) * volumeMultiplier;
	}
	const sineWaveform = ( x ) => {
		return Math.sin( 2 * Math.PI * x ) * volumeMultiplier;
	}
	const squareWaveform = ( x ) => {
		return ( Math.sin( 2 * Math.PI * x ) >= 0 ? 1 : -1 ) * volumeMultiplier;
	}
	const sawtoothWaveform = ( x ) => {
		let t = x - Math.floor( x );
		return ( 2 * t - 1 ) * volumeMultiplier;
	};
	const triangleWaveform = ( x ) => {
		let t = x - Math.floor( x );
		return ( 2 * Math.abs( 2 * t - 1 ) - 1 ) * volumeMultiplier;
	};
	const drumWaveform = ( x ) => {
		return ( ( Math.random() * 2 - 1 ) * Math.exp( -x / 10 ) ) * volumeMultiplier;
	};
	const softDrumWaveform = ( x ) => {
		return ( Math.sin( x * 2 ) + 0.3 * ( Math.random() - 0.5 ) ) *
			Math.exp( -x / 15 ) * volumeMultiplier * 2;
	};

	// Mapping of instrument ids to synthesis functions.
	// 0: Piano, 1: Piano 2, 2: Sine, 3: Sawtooth, 4: Square, 5: Triangle, 6: Drum, 7: Soft Drum
	const instrumentMapping = [
		pianoWaveform,
		piano2WaveForm,
		sineWaveform,
		sawtoothWaveform,
		squareWaveform,
		triangleWaveform,
		drumWaveform,
		softDrumWaveform,
	];

	// -----------------------------
	// Main Music Player Function (p1)
	// -----------------------------
	/**
	 * Use as a tag template literal:
	 *
	 *     p1`
	 *     0|f  dh   d T-X   X  T    X X V|
	 *     1|Y   Y Y Y Y Y Y Y   Y Y Y Y Y Y|
	 *     0|V   X   T   T   c   c   T   X|
	 *     0|c fVa a-   X T R  aQT Ta   RO- X|
	 *     [70.30]
	 *     `
	 */
	function p1( params ) {

		if ( Array.isArray( params ) ) {
			params = params[ 0 ];
		}
		if ( !params || params.trim() === '' ) {
			p1.stop();
			return;
		}

		// Reset defaults.
		tempo = defaultTempo;
		let baseNoteDuration = 0.5; // seconds per note.
		schedules = [];

		// Split input into lines.
		const rawLines = params.split( '\n' ).map( line => line.trim() );

		// Regular expression for track lines: instrument|track data|
		const trackLineRegex = /^([0-9])\|(.*)\|$/;

		rawLines.forEach( line => {
			if ( !line ) return;

			// Tempo/note duration lines.
			if ( ( line.startsWith( '[' ) && line.endsWith( ']' ) ) || ( /^\d+(\.\d+)?$/.test( line ) ) ) {
				const timing = line.replace( /[\[\]]/g, '' ).split( '.' );
				tempo = parseFloat( timing[ 0 ] ) || tempo;
				baseNoteDuration = ( parseFloat( timing[ 1 ] ) || 50 ) / 100;
				return;
			}

			// Track lines.
			const match = line.match( trackLineRegex );
			if ( !match ) {
				console.error( "Track lines must be in the format 'instrument|track data|': " + line );
				return;
			}

			const instrumentId = parseInt( match[ 1 ], 10 );
			const instrumentFn = instrumentMapping[ instrumentId ] || instrumentMapping[ 0 ];
			/**
			 * Preserve spaces inside the track data.
			 *
			 * Trailing spaces represent explicit rests at the end of a track,
			 * so trimming here shortens the channel and makes it loop early.
			 */
			const trackData = match[ 2 ];

			let events = [];
			// Parse trackData character by character.
			for ( let i = 0; i < trackData.length; i++ ) {
				const char = trackData[ i ];
				let dashCount = 1;
				while ( i + dashCount < trackData.length && trackData[ i + dashCount ] === '-' ) {
					dashCount++;
				}
				if ( char === ' ' ) {
					events.push( null );
					i += dashCount - 1;
					continue;
				}
				let noteValue = char.charCodeAt( 0 );
				noteValue -= noteValue > 90 ? 71 : 65;
				let noteDuration = dashCount * baseNoteDuration * ( defaultTempo / tempo );
				let noteBuffer = createNoteBuffer( noteValue, noteDuration, audioCtx.sampleRate, instrumentFn, instrumentId );
				events.push( noteBuffer );
				i += dashCount - 1;
			}
			schedules.push( events );
		} );

		/**
		 * Use one shared song length for every channel.
		 *
		 * This keeps all tracks aligned at the loop boundary even when some
		 * channels contain fewer notes than others.
		 */
		songLengthInSteps = schedules.reduce( ( longestTrack, events ) => {
			return Math.max( longestTrack, events.length );
		}, 0 );

		// Initialize schedule pointers and calculate loop duration.
		schedulePointers = schedules.map( () => 0 );
		playbackStartTime = audioCtx.currentTime + 0.1;

		p1.stop();
		schedulerInterval = setInterval( schedulerFunction, schedulerIntervalMs );
	}


	/**
	 * The scheduler function ensures the notes are played at the right time.
	 *
	 * This function is called every 50ms to check if any notes need to be played.
	 *
	 * The scheduler keeps track of the current time and the current note interval.
	 * It then checks each track to see if a note needs to be played.
	 *
	 * @returns {void}
	 */
	function schedulerFunction() {

		const currentTime = audioCtx.currentTime;
		const noteInterval = getNoteInterval(); // note duration in seconds
		// Use the larger of the fixed lookahead and the current note interval.
		const effectiveLookahead = Math.max( lookaheadTime, noteInterval );
		schedules.forEach( ( events, trackIndex ) => {
			const trackLength = events.length;
			if ( songLengthInSteps === 0 ) return;

			while ( true ) {
				let pointer = schedulePointers[ trackIndex ];
				const step = pointer % songLengthInSteps;
				const eventTime = playbackStartTime + ( pointer * noteInterval );
				if ( eventTime >= currentTime + effectiveLookahead ) {
					break;
				}
				/**
				 * Treat anything beyond the channel's own length as silence until
				 * the shared song loop comes back around.
				 */
				const noteBuffer = step < trackLength ? events[ step ] : null;
				if ( noteBuffer ) {
					playNoteBuffer( noteBuffer, audioCtx, eventTime );
				}
				schedulePointers[ trackIndex ]++;
			}
		} );
		if ( !p1.loop ) {
			const done = schedulePointers.every( pointer => pointer >= songLengthInSteps );
			if ( done ) {
				p1.stop();
			}
		}
	}


	/**
	 * Stop playback by clearing the scheduler and stopping all playing sources.
	 */
	p1.stop = function() {
		if ( schedulerInterval !== null ) {
			clearInterval( schedulerInterval );
			schedulerInterval = null;
		}
		/**
		 * Stop every active source before clearing the collection.
		 *
		 * A Set is used here so add/remove operations stay simple and cheap
		 * even when many short notes are being scheduled.
		 */
		playingSources.forEach( source => source.stop() );
		playingSources.clear();
	};


	/**
	 * Check if music is currently playing.
	 */
	p1.isPlaying = function() {
		return schedulerInterval !== null;
	};


	/**
	 * Set the tempo (in BPM).
	 */
	p1.setTempo = function( newTempo ) {

		if ( newTempo < 50 ) newTempo = 50;

		// Calculate old and new note intervals in seconds.
		const oldNoteInterval = getNoteInterval();
		tempo = newTempo;
		const newNoteInterval = getNoteInterval();

		// Determine how much time has elapsed since playback started.
		const elapsed = audioCtx.currentTime - playbackStartTime;

		// Compute the current note position (could be fractional).
		const currentIndex = elapsed / oldNoteInterval;

		// Rebase playbackStartTime so that the currentIndex now corresponds to the current time.
		playbackStartTime = audioCtx.currentTime - currentIndex * newNoteInterval;

	};

	p1.setVolume = function( value ) {
		// clamp 0.0–1.0
		masterGain.gain.value = Math.min( 1, Math.max( 0, value ) );
	};


	p1.clearCache = function() {
		noteBuffers = {};
		noteBufferKeys = [];
	};


	// Loop property: set to true to repeat playback.
	p1.loop = true;

	/**
	 * Create an audio buffer for a given note.
	 */
	const createNoteBuffer = ( note, durationSeconds, sampleRate, instrumentFn, instrumentId ) => {
		const key = note + '-' + durationSeconds + '-' + instrumentId;
		let buffer = noteBuffers[ key ];
		if ( note >= 0 && !buffer ) {
			const frequencyFactor = 65.406 * Math.pow( 1.06, note ) / sampleRate;
			const totalSamples = Math.floor( sampleRate * durationSeconds );
			const attackSamples = 88;
			const decaySamples = sampleRate * ( durationSeconds - 0.002 );
			const decayExponent = Math.pow( Math.log( 1e4 * frequencyFactor ) / 2, 2 );
			const fadeOutSamples = Math.min( 32, totalSamples );
			buffer = noteBuffers[ key ] = audioCtx.createBuffer( 1, totalSamples, sampleRate );
			const channelData = buffer.getChannelData( 0 );
			for ( let i = 0; i < totalSamples; i++ ) {
				let amplitude;
				if ( i < attackSamples ) {
					amplitude = i / ( attackSamples + 0.2 );
				} else {
					amplitude = Math.pow(
						1 - ( ( i - attackSamples ) / decaySamples ),
						decayExponent
					);
				}
				if ( i >= totalSamples - fadeOutSamples ) {
					amplitude *= ( totalSamples - i ) / fadeOutSamples;
				}
				channelData[ i ] = amplitude * instrumentFn( i * frequencyFactor );
			}
			// Unlock audio on iOS if needed.
			if ( !unlocked ) {
				playNoteBuffer( buffer, audioCtx, audioCtx.currentTime, true );
				unlocked = true;
			}
			noteBufferKeys.push( key );
			if ( noteBufferKeys.length > maxNoteBuffers ) {
				delete noteBuffers[ noteBufferKeys.shift() ];
			}
		}
		return buffer;
	};

	/**
	 * Keep track of currently playing sources.
	 *
	 * A Set is a better fit than an array here because sources are only
	 * added, removed, and iterated. That avoids repeated array searches
	 * as notes finish playing.
	 *
	 * @type {Set<AudioBufferSourceNode>}
	 */
	let playingSources = new Set();


	/**
	 * Play an audio buffer at a scheduled time.
	 */
	const playNoteBuffer = ( buffer, context, when, stopImmediately = false ) => {
		const source = context.createBufferSource();
		source.buffer = buffer;

		source.connect( masterGain );
		source.start( when );
		playingSources.add( source );

		if ( stopImmediately ) {
			source.stop();
		}
		source.onended = () => {
			playingSources.delete( source );
		};
	};

	function getNoteInterval() {

		return 60 / tempo / stepsPerBeat;

	}

	// Expose the p1 function globally.
	window.p1 = p1;

} )();

/**
 * A simple implementation of a MinHeap (binary heap) for managing priority queues.
 * This data structure ensures efficient insertion and extraction of the smallest element.
 *
 * Time Complexity:
 * - Insertion: O(log n)
 * - Extraction: O(log n)
 * - Accessing the smallest element: O(1)
 */
class MinHeap {

	/**
	 * Initializes an empty heap.
	 */
	constructor() {
		/**
		 * Internal array to store heap elements.
		 * @type {Array<Object>}
		 */
		this.heap = [];
	}

	/**
	 * Inserts a new node into the heap.
	 * @param {Object} node - The node to insert. Must have a property `f` for priority comparison.
	 */
	push( node ) {
		this.heap.push( node );
		this.bubbleUp( this.heap.length - 1 );
	}

	/**
	 * Removes and returns the smallest node (root) from the heap.
	 * @returns {Object} The node with the smallest `f` value.
	 */
	pop() {
		if ( this.heap.length === 1 ) return this.heap.pop();
		const min = this.heap[ 0 ];
		this.heap[ 0 ] = this.heap.pop();
		this.bubbleDown( 0 );
		return min;
	}

	/**
	 * Returns the number of elements in the heap.
	 * @returns {number} The size of the heap.
	 */
	get length() {
		return this.heap.length;
	}

	/**
	 * Moves the element at the given index up to restore the heap property.
	 * @param {number} index - The index of the element to bubble up.
	 */
	bubbleUp( index ) {
		const parent = Math.floor( ( index - 1 ) / 2 );
		if ( parent >= 0 && this.heap[ index ].f < this.heap[ parent ].f ) {
			[ this.heap[ index ], this.heap[ parent ] ] = [ this.heap[ parent ], this.heap[ index ] ];
			this.bubbleUp( parent );
		}
	}

	/**
	 * Moves the element at the given index down to restore the heap property.
	 * @param {number} index - The index of the element to bubble down.
	 */
	bubbleDown( index ) {
		const left = 2 * index + 1;
		const right = 2 * index + 2;
		let smallest = index;

		if ( left < this.heap.length && this.heap[ left ].f < this.heap[ smallest ].f ) {
			smallest = left;
		}
		if ( right < this.heap.length && this.heap[ right ].f < this.heap[ smallest ].f ) {
			smallest = right;
		}
		if ( smallest !== index ) {
			[ this.heap[ index ], this.heap[ smallest ] ] = [ this.heap[ smallest ], this.heap[ index ] ];
			this.bubbleDown( smallest );
		}
	}

}
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2014 Patrick Gansterer <paroga@paroga.com>
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

( function( global, undefined ) {
	"use strict";
	var POW_2_24 = Math.pow( 2, -24 ),
		POW_2_32 = Math.pow( 2, 32 ),
		POW_2_53 = Math.pow( 2, 53 );

	function encode( value ) {
		var data = new ArrayBuffer( 256 );
		var dataView = new DataView( data );
		var lastLength;
		var offset = 0;

		function ensureSpace( length ) {
			var newByteLength = data.byteLength;
			var requiredLength = offset + length;
			while ( newByteLength < requiredLength )
				newByteLength *= 2;
			if ( newByteLength !== data.byteLength ) {
				var oldDataView = dataView;
				data = new ArrayBuffer( newByteLength );
				dataView = new DataView( data );
				var uint32count = ( offset + 3 ) >> 2;
				for ( var i = 0; i < uint32count; ++i )
					dataView.setUint32( i * 4, oldDataView.getUint32( i * 4 ) );
			}

			lastLength = length;
			return dataView;
		}
		function write() {
			offset += lastLength;
		}
		function writeFloat64( value ) {
			write( ensureSpace( 8 ).setFloat64( offset, value ) );
		}
		function writeUint8( value ) {
			write( ensureSpace( 1 ).setUint8( offset, value ) );
		}
		function writeUint8Array( value ) {
			var dataView = ensureSpace( value.length );
			for ( var i = 0; i < value.length; ++i )
				dataView.setUint8( offset + i, value[ i ] );
			write();
		}
		function writeUint16( value ) {
			write( ensureSpace( 2 ).setUint16( offset, value ) );
		}
		function writeUint32( value ) {
			write( ensureSpace( 4 ).setUint32( offset, value ) );
		}
		function writeUint64( value ) {
			var low = value % POW_2_32;
			var high = ( value - low ) / POW_2_32;
			var dataView = ensureSpace( 8 );
			dataView.setUint32( offset, high );
			dataView.setUint32( offset + 4, low );
			write();
		}
		function writeTypeAndLength( type, length ) {
			if ( length < 24 ) {
				writeUint8( type << 5 | length );
			} else if ( length < 0x100 ) {
				writeUint8( type << 5 | 24 );
				writeUint8( length );
			} else if ( length < 0x10000 ) {
				writeUint8( type << 5 | 25 );
				writeUint16( length );
			} else if ( length < 0x100000000 ) {
				writeUint8( type << 5 | 26 );
				writeUint32( length );
			} else {
				writeUint8( type << 5 | 27 );
				writeUint64( length );
			}
		}

		function encodeItem( value ) {
			var i;

			if ( value === false )
				return writeUint8( 0xf4 );
			if ( value === true )
				return writeUint8( 0xf5 );
			if ( value === null )
				return writeUint8( 0xf6 );
			if ( value === undefined )
				return writeUint8( 0xf7 );

			switch ( typeof value ) {
				case "number":
					if ( Math.floor( value ) === value ) {
						if ( 0 <= value && value <= POW_2_53 )
							return writeTypeAndLength( 0, value );
						if ( -POW_2_53 <= value && value < 0 )
							return writeTypeAndLength( 1, -( value + 1 ) );
					}
					writeUint8( 0xfb );
					return writeFloat64( value );

				case "string":
					var utf8data = [];
					for ( i = 0; i < value.length; ++i ) {
						var charCode = value.charCodeAt( i );
						if ( charCode < 0x80 ) {
							utf8data.push( charCode );
						} else if ( charCode < 0x800 ) {
							utf8data.push( 0xc0 | charCode >> 6 );
							utf8data.push( 0x80 | charCode & 0x3f );
						} else if ( charCode < 0xd800 ) {
							utf8data.push( 0xe0 | charCode >> 12 );
							utf8data.push( 0x80 | ( charCode >> 6 ) & 0x3f );
							utf8data.push( 0x80 | charCode & 0x3f );
						} else {
							charCode = ( charCode & 0x3ff ) << 10;
							charCode |= value.charCodeAt( ++i ) & 0x3ff;
							charCode += 0x10000;

							utf8data.push( 0xf0 | charCode >> 18 );
							utf8data.push( 0x80 | ( charCode >> 12 ) & 0x3f );
							utf8data.push( 0x80 | ( charCode >> 6 ) & 0x3f );
							utf8data.push( 0x80 | charCode & 0x3f );
						}
					}

					writeTypeAndLength( 3, utf8data.length );
					return writeUint8Array( utf8data );

				default:
					var length;
					if ( Array.isArray( value ) ) {
						length = value.length;
						writeTypeAndLength( 4, length );
						for ( i = 0; i < length; ++i )
							encodeItem( value[ i ] );
					} else if ( value instanceof Uint8Array ) {
						writeTypeAndLength( 2, value.length );
						writeUint8Array( value );
					} else {
						var keys = Object.keys( value );
						length = keys.length;
						writeTypeAndLength( 5, length );
						for ( i = 0; i < length; ++i ) {
							var key = keys[ i ];
							encodeItem( key );
							encodeItem( value[ key ] );
						}
					}
			}
		}

		encodeItem( value );

		if ( "slice" in data )
			return data.slice( 0, offset );

		var ret = new ArrayBuffer( offset );
		var retView = new DataView( ret );
		for ( var i = 0; i < offset; ++i )
			retView.setUint8( i, dataView.getUint8( i ) );
		return ret;
	}

	function decode( data, tagger, simpleValue ) {
		var dataView = new DataView( data );
		var offset = 0;

		if ( typeof tagger !== "function" )
			tagger = function( value ) { return value; };
		if ( typeof simpleValue !== "function" )
			simpleValue = function() { return undefined; };

		function read( value, length ) {
			offset += length;
			return value;
		}
		function readArrayBuffer( length ) {
			return read( new Uint8Array( data, offset, length ), length );
		}
		function readFloat16() {
			var tempArrayBuffer = new ArrayBuffer( 4 );
			var tempDataView = new DataView( tempArrayBuffer );
			var value = readUint16();

			var sign = value & 0x8000;
			var exponent = value & 0x7c00;
			var fraction = value & 0x03ff;

			if ( exponent === 0x7c00 )
				exponent = 0xff << 10;
			else if ( exponent !== 0 )
				exponent += ( 127 - 15 ) << 10;
			else if ( fraction !== 0 )
				return fraction * POW_2_24;

			tempDataView.setUint32( 0, sign << 16 | exponent << 13 | fraction << 13 );
			return tempDataView.getFloat32( 0 );
		}
		function readFloat32() {
			return read( dataView.getFloat32( offset ), 4 );
		}
		function readFloat64() {
			return read( dataView.getFloat64( offset ), 8 );
		}
		function readUint8() {
			return read( dataView.getUint8( offset ), 1 );
		}
		function readUint16() {
			return read( dataView.getUint16( offset ), 2 );
		}
		function readUint32() {
			return read( dataView.getUint32( offset ), 4 );
		}
		function readUint64() {
			return readUint32() * POW_2_32 + readUint32();
		}
		function readBreak() {
			if ( dataView.getUint8( offset ) !== 0xff )
				return false;
			offset += 1;
			return true;
		}
		function readLength( additionalInformation ) {
			if ( additionalInformation < 24 )
				return additionalInformation;
			if ( additionalInformation === 24 )
				return readUint8();
			if ( additionalInformation === 25 )
				return readUint16();
			if ( additionalInformation === 26 )
				return readUint32();
			if ( additionalInformation === 27 )
				return readUint64();
			if ( additionalInformation === 31 )
				return -1;
			throw "Invalid length encoding";
		}
		function readIndefiniteStringLength( majorType ) {
			var initialByte = readUint8();
			if ( initialByte === 0xff )
				return -1;
			var length = readLength( initialByte & 0x1f );
			if ( length < 0 || ( initialByte >> 5 ) !== majorType )
				throw "Invalid indefinite length element";
			return length;
		}

		function appendUtf16data( utf16data, length ) {
			for ( var i = 0; i < length; ++i ) {
				var value = readUint8();
				if ( value & 0x80 ) {
					if ( value < 0xe0 ) {
						value = ( value & 0x1f ) << 6
							| ( readUint8() & 0x3f );
						length -= 1;
					} else if ( value < 0xf0 ) {
						value = ( value & 0x0f ) << 12
							| ( readUint8() & 0x3f ) << 6
							| ( readUint8() & 0x3f );
						length -= 2;
					} else {
						value = ( value & 0x0f ) << 18
							| ( readUint8() & 0x3f ) << 12
							| ( readUint8() & 0x3f ) << 6
							| ( readUint8() & 0x3f );
						length -= 3;
					}
				}

				if ( value < 0x10000 ) {
					utf16data.push( value );
				} else {
					value -= 0x10000;
					utf16data.push( 0xd800 | ( value >> 10 ) );
					utf16data.push( 0xdc00 | ( value & 0x3ff ) );
				}
			}
		}

		function decodeItem() {
			var initialByte = readUint8();
			var majorType = initialByte >> 5;
			var additionalInformation = initialByte & 0x1f;
			var i;
			var length;

			if ( majorType === 7 ) {
				switch ( additionalInformation ) {
					case 25:
						return readFloat16();
					case 26:
						return readFloat32();
					case 27:
						return readFloat64();
				}
			}

			length = readLength( additionalInformation );
			if ( length < 0 && ( majorType < 2 || 6 < majorType ) )
				throw "Invalid length";

			switch ( majorType ) {
				case 0:
					return length;
				case 1:
					return -1 - length;
				case 2:
					if ( length < 0 ) {
						var elements = [];
						var fullArrayLength = 0;
						while ( ( length = readIndefiniteStringLength( majorType ) ) >= 0 ) {
							fullArrayLength += length;
							elements.push( readArrayBuffer( length ) );
						}
						var fullArray = new Uint8Array( fullArrayLength );
						var fullArrayOffset = 0;
						for ( i = 0; i < elements.length; ++i ) {
							fullArray.set( elements[ i ], fullArrayOffset );
							fullArrayOffset += elements[ i ].length;
						}
						return fullArray;
					}
					return readArrayBuffer( length );
				case 3:
					var utf16data = [];
					if ( length < 0 ) {
						while ( ( length = readIndefiniteStringLength( majorType ) ) >= 0 )
							appendUtf16data( utf16data, length );
					} else
						appendUtf16data( utf16data, length );
					return String.fromCharCode.apply( null, utf16data );
				case 4:
					var retArray;
					if ( length < 0 ) {
						retArray = [];
						while ( !readBreak() )
							retArray.push( decodeItem() );
					} else {
						retArray = new Array( length );
						for ( i = 0; i < length; ++i )
							retArray[ i ] = decodeItem();
					}
					return retArray;
				case 5:
					var retObject = {};
					for ( i = 0; i < length || length < 0 && !readBreak(); ++i ) {
						var key = decodeItem();
						retObject[ key ] = decodeItem();
					}
					return retObject;
				case 6:
					return tagger( decodeItem(), length );
				case 7:
					switch ( length ) {
						case 20:
							return false;
						case 21:
							return true;
						case 22:
							return null;
						case 23:
							return undefined;
						default:
							return simpleValue( length );
					}
			}
		}

		var ret = decodeItem();
		if ( offset !== data.byteLength )
			throw "Remaining bytes";
		return ret;
	}

	var obj = { encode: encode, decode: decode };

	if ( typeof define === "function" && define.amd )
		define( "cbor/cbor", obj );
	else if ( !global.CBOR )
		global.CBOR = obj;

} )( this );
